import React, { useEffect, useState, useRef, useCallback, useMemo, Suspense } from 'react';
import Map, { Marker, Source, Layer, NavigationControl, Popup } from 'react-map-gl/maplibre';
import Supercluster from 'supercluster';
import Hls from 'hls.js';
import 'maplibre-gl/dist/maplibre-gl.css';
import './App.css';
import { authHeaders as sharedAuthHeaders, withSessionToken } from './auth';
import { useAuth } from './context/AuthContext';
import { Icon, resolveIcon, resolvePointIcon, markerOverride, customMarkerImage, MARKER_CHOICES, type IconKey } from './icons';
import { Login } from './components/Login';
import { Signup } from './components/Signup';
import { PendingApproval } from './components/PendingApproval';
import { fetchMqttDevices, fetchActiveStreams, sendFlightTask, type MqttDevice } from './components/mqtt/mqttApi';
import { IncidentCreateModal, IncidentDraft, IncidentType } from './components/IncidentCreateModal';
import { GroupPanel } from './components/GroupPanel';
import { useToast } from './context/ToastContext';
import { useConfirm } from './context/ConfirmContext';
import { API_URL } from './config';
import { toMGRS, generateMGRSGrid, parseMGRS, mgrsZoomLevel } from './utils/mgrs';
import { destinationPoint } from './utils/geo';
import exifr from 'exifr';
import VideoPanel from './components/video/VideoPanel';
import FloatingPanel from './components/video/FloatingPanel';
import EdgeNodesPanel from './components/edge/EdgeNodesPanel';
import { useEdgeNodes } from './components/edge/useEdgeNodes';
import { livenessClass, livenessLabel } from './components/edge/edge';
import MatrixView from './components/video/MatrixView';
import AiLayerMenu from './components/video/AiLayerMenu';
import { useAiLayers } from './components/video/useAiLayers';
import {
  OFF, ROAD_SEG, PLAIN_DETECT, CAMERA_ONLY_MODELS, channelValue, feedPrivacy, privacyUnavailableReason, isLoosening,
  type LayerId, type OverlayState, type PrivacyMode,
} from './components/video/aiLayers';
import { assignBody, feedTarget, type FeedSource } from './components/video/aiChannels';
import { activateModel, deactivateModel } from './components/marketplace/marketplaceApi';
import {
  isPullUrl, pullTypeOf, isPullSource, feedAccessType, videoSourceBody, PULL_URL_HINT,
  type PullType,
} from './components/video/streamTypes';
import {
  isUserAddedSource, readSources, sourcesKey, writeSources,
} from './components/video/sourceStorage';
import {
  createServerSourceStore, httpSourceApi, hydrateSources,
} from './components/video/serverSourceStore';
import { needsServerProcessing } from './components/video/serverProcessing';
import type { ProcessedStreamCredentials as ProcessedStreamCreds } from './components/video/VideoRenderer';
import MapSearch from './components/MapSearch';
import AssistantGhost from './components/assistant/AssistantGhost';
import GeolocatedPreview, { type PendingFix } from './components/map/GeolocatedPreview';
import type { GeoFix } from './components/video/geolocate';
import { useAssistantBridge } from './components/assistant/useAssistantBridge';
import type { GhostFeature } from './components/assistant/assistantTypes';
import SystemStats from './components/SystemStats';
import { getActiveLog, startLog, stopLog } from './components/oplog/oplogApi';
import { getViewerToken, deletePhoneSensor, refreshPhoneSensorGps, listRecordings, getRecordingStreamUrl, deleteRecording, getTrack, getTrackKmlUrl, clearTrack, type Recording, type TrackPoint } from './components/sensors/phoneSensorApi';
import { MmwaveSection } from './components/sensors/MmwaveSection';
import { applyMmwaveState, mmwaveView, sectorPolygon, type MmwaveState } from './components/sensors/mmwave';
import HallOverlay, { HALL_STAND_LAYER, type HallCamera } from './components/hall/HallOverlay';
import { hallPlanForPoint, type HallPlan } from './data/hallPlans';
import {
  isTankPolygon, getStatus as getTankGaugeStatus, getCandidates as getTankCandidates,
  type TankReading,
} from './data/tankGauge';
import TankGaugePanel from './components/tank/TankGaugePanel';
import TankGaugeLayer from './components/tank/TankGaugeLayer';
import type { FieldUnit, UnitType, UnitStatus, UnitStream } from './components/units/unitsTypes';
import { UNIT_TYPES, UNIT_STATUSES, STATUS_COLOR, unitIcon, unitStream } from './components/units/unitsTypes';
import { fetchUnits as apiFetchUnits, createUnit, updateUnit, changeUnitStatus, deleteUnit } from './components/units/unitsApi';
import { belongsToScope, type ScopeIdentity } from './utils/dataScope';
import { COLORS } from './theme';
import { topmostLast } from './mapLayerOrder';

// No stream selected. Not a source you can pick — the video panel falls back to
// the server's NO SIGNAL frame whenever the active id resolves to nothing.
const NO_SOURCE_ID = '';

// Camera tilt used for the indoor 3D hall view.
const HALL_PITCH = 60;

// Zoom that frames a whole hall for the current map size.
//
// The view is rotated to the hall's bearing, so its across-plan size lands on
// screen x and its along-plan size on screen y. Tilting the camera foreshortens
// y by roughly cos(pitch), and the fractions below leave room for the floating
// panels. maplibre's fitBounds is not used because it ignores pitch in v4.
function hallZoom(map: any, plan: HallPlan): number {
  const container = map?.getContainer?.();
  const cw = container?.clientWidth || 1200;
  const ch = container?.clientHeight || 800;
  const [acrossM, alongM] = plan.sizeM;
  // Tilting magnifies the near half of the hall, so the fill fraction has to
  // shrink as the hall gets longer relative to the screen — the ratio that
  // frames a compact hall lets an elongated one run off both edges.
  const elongation = Math.max(acrossM, alongM) / Math.max(1, Math.min(acrossM, alongM));
  const fill = elongation > 2 ? 0.34 : 0.6;
  const metresPerPx = Math.max(
    acrossM / (cw * fill),
    (alongM * Math.cos((HALL_PITCH * Math.PI) / 180)) / (ch * fill * 0.8),
  );
  const groundRes = 156543.03392 * Math.cos((plan.center[1] * Math.PI) / 180);
  return Math.min(21.5, Math.log2(groundRes / metresPerPx));
}

const AdminPanel = React.lazy(() => import('./components/AdminPanel').then(m => ({ default: m.AdminPanel })));
const FleetPanel = React.lazy(() => import('./components/FleetPanel').then(m => ({ default: m.FleetPanel })));
const MqttPanel = React.lazy(() => import('./components/MqttPanel').then(m => ({ default: m.MqttPanel })));
const PhoneSensorSetup = React.lazy(() => import('./components/sensors/PhoneSensorSetup'));
const OpLogPanel = React.lazy(() => import('./components/oplog/OpLogPanel').then(m => ({ default: m.OpLogPanel })));
const CapturesPanel = React.lazy(() => import('./components/video/CapturesPanel'));
const FloorplanViewer = React.lazy(() => import('./components/floorplan/FloorplanViewer'));
const MarketplacePanel = React.lazy(() => import('./components/MarketplacePanel').then(m => ({ default: m.MarketplacePanel })));
const SurveillancePanel = React.lazy(() => import('./components/surveillance/SurveillancePanel'));
const AnalyticsPanel = React.lazy(() => import('./components/analytics/AnalyticsPanel'));
const UspacePanel = React.lazy(() => import('./components/uspace/UspacePanel').then(m => ({ default: m.UspacePanel })));
const AssistantPanel = React.lazy(() => import('./components/assistant/AssistantPanel'));

interface DroneStatus {
  drone_id: string;
  status: string;
  battery_percent: number;
  latitude: number;
  longitude: number;
  altitude_m: number;
  speed_ms: number;
  heading_degrees: number;
  satellites: number;
  video_streaming: boolean;
  last_update: string;
}

interface DronePosition {
  latitude: number;
  longitude: number;
  altitude: number;
  battery: number;
  heading: number;
  speed: number;
  connected: boolean;
  gimbal: { pitch: number; roll: number; yaw: number } | null;
  lastSeen: number;
  /** Flight mode and armed state — sent by aircraft that report them (the Pi-guided
   *  ArduPilot one over MAVLink); undefined for DJI, which sends a mode code instead. */
  mode?: string | null;
  armed?: boolean | null;
}

interface Attachment {
  id: string;
  filename: string;
  stored_name: string;
  url: string;
  type: 'image' | 'video' | 'file';
  size: number;
  uploaded_at: string;
  uploaded_by?: string;
}

interface MapPoint {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  description?: string;
  icon: string;
  color: string;
  comments?: string;
  attachments?: Attachment[];
  metadata?: Record<string, string>;
  task_set_id?: number;
  line_id?: number;
  created_by_email?: string;
  created_at?: string;
  kaek_parcel_id?: number;
  kaek_code?: string;
  parcel_area?: number;
  parcel_perimeter?: number;
  land_use?: string;
  land_use_code?: string;
  registry_link?: string;
  parcel_geometry?: GeoJSON.Polygon;
}

interface MapPolygon {
  id: number;
  name: string;
  coordinates: number[][];
  // Returned by GET /api/polygons via serialize_row and used by tank gauging to tell a
  // storage tank from any other area. The detail panel does not render it.
  metadata?: Record<string, unknown> | null;
  description?: string;
  color: string;
  fill_opacity: number;
  comments?: string;
  attachments?: Attachment[];
  task_set_id?: number;
  created_by_email?: string;
  created_at?: string;
}

interface MapLine {
  id: number;
  name: string;
  coordinates: number[][];
  description?: string;
  color: string;
  line_width: number;
  comments?: string;
  attachments?: Attachment[];
  task_set_id?: number;
  created_by_email?: string;
  created_at?: string;
}

interface Mission {
  id: number;
  name: string;
  line_id: number | null;
  status: 'created' | 'executing' | 'paused' | 'completed' | 'failed' | 'cancelled';
  altitude: number;
  speed: number;
  waypoints: Array<{lng: number; lat: number; alt: number; index: number; site_id?: number; site_name?: string; site_distance_m?: number; site_ids?: number[]; site_names?: string[]; site_distances_m?: number[]}>;
  current_waypoint_index: number;
  progress_percent: number;
  drone_sn: string | null;
  flight_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  created_by?: number;
  group_id?: number | null;
}

interface MapSensor {
  id: number;
  name: string;
  sensor_type: 'camera' | 'microphone' | 'seismic' | 'sonar' | 'phone-camera' | 'mmwave';
  latitude: number;
  longitude: number;
  description?: string;
  status: 'active' | 'inactive' | 'maintenance';
  icon: string;
  color: string;
  comments?: string;
  attachments?: Attachment[];
  metadata?: Record<string, unknown>;
  task_set_id?: number;
  created_by_email?: string;
  created_at?: string;
}

interface Incident {
  id: number;
  type: IncidentType;
  severity: 1 | 2 | 3 | 4 | 5;
  title: string;
  description?: string;
  latitude: number;
  longitude: number;
  location_label?: string;
  status: 'Open' | 'Assigned' | 'In Progress' | 'Resolved' | 'Closed';
  assigned_units: string[];
  attachments: Attachment[];
  linked_poi_ids: number[];
  linked_items: LinkedItem[];
  audit_log: Array<{ ts: string; actor: string; from?: string; to?: string; event?: string; note?: string }>;
  created_at: string;
  updated_at: string;
}

interface LinkedItem {
  type: 'point' | 'polygon' | 'line' | 'sensor';
  id: number;
}

interface LinkedItemDetail extends LinkedItem {
  name: string;
}

interface IncidentNote {
  id: number;
  incident_id: number;
  content: string;
  activity_type: 'note' | 'mission' | 'trial' | 'observation';
  author_email?: string;
  created_at: string;
  updated_at: string;
}

const INCIDENT_ICONS: Record<IncidentType, IconKey> = {
  Threat: 'threat',
  Medical: 'medical',
  Infrastructure: 'infra',
  Environmental: 'environ',
  Unknown: 'unknown',
};

const INCIDENT_COLORS: Record<IncidentType, string> = {
  Threat: '#ff4444',
  Medical: COLORS.accent,
  Infrastructure: '#ffaa00',
  Environmental: '#00aaff',
  Unknown: '#888888',
};

const SEVERITY_LABELS = ['', 'Routine', 'Priority', 'Immediate', 'Flash', 'Override'];

const STATUS_TRANSITIONS: Record<string, string[]> = {
  'Open': ['Assigned', 'In Progress'],
  'Assigned': ['In Progress', 'Open'],
  'In Progress': ['Resolved'],
  'Resolved': ['Closed', 'In Progress'],
  'Closed': [],
};

interface TaskSet {
  id: number;
  name: string;
  description?: string;
  icon: string;
  color: string;
  item_count: number;
  created_by?: number;
  created_by_email?: string;
  created_at?: string;
  group_id?: number | null;
  is_shared?: boolean;
}

interface AnnotationObject {
  type: 'rect' | 'arrow' | 'freehand' | 'text' | 'polygon';
  color: string;
  points: Array<{ x: number; y: number }>;
  text?: string;
}

interface DcimPhoto {
  id: number;
  upload_id: number;
  filename: string;
  stored_name?: string;
  folder_path: string;
  photo_type: 'waypoint' | 'panorama_tile' | 'video';
  latitude?: number;
  longitude?: number;
  altitude?: number;
  captured_at?: string;
  camera_make?: string;
  camera_model?: string;
  image_width?: number;
  image_height?: number;
  file_size?: number;
  mission_name?: string;
  waypoint_index?: number;
  panorama_group?: string;
  lens_type?: string;
  point_id?: number;
  url?: string;
  uploaded: boolean;
  transcode_status?: string;
  heading?: number;
  notes?: string;
  annotations?: AnnotationObject[];
  s3_original_version_id?: string;
  s3_original_size?: number;
  presigned_upload?: {
    url: string;
    fields: Record<string, string>;
  };
}

const SENSOR_TYPES: { value: MapSensor['sensor_type']; label: string; icon: IconKey }[] = [
  { value: 'camera', label: 'Camera', icon: 'camera' },
  { value: 'microphone', label: 'Microphone', icon: 'mic' },
  { value: 'seismic', label: 'Seismic', icon: 'seismic' },
  { value: 'sonar', label: 'Undersea Sonar', icon: 'sonar' },
  { value: 'phone-camera', label: 'PHONECAM', icon: 'phone' },
  { value: 'mmwave', label: 'mmWave radar', icon: 'radar' },
];


interface PointNote {
  id: number;
  point_id: number;
  content: string;
  created_at: string;
  updated_at: string;
  created_by_email?: string;
}

interface AircraftPosition {
  hex: string;
  flight: string;
  lat: number;
  lon: number;
  alt_baro: number | string | null;
  alt_geom: number | null;
  gs: number | null;
  track: number | null;
  squawk: string | null;
  emergency: string | null;
  type: string;
  registration: string;
  seen: number | null;
}

interface VesselPosition {
  mmsi: number;
  name: string;
  lat: number;
  lon: number;
  heading: number;
  course: number;
  speed: number;
  ship_type: number;
  ship_type_name: string;
  destination: string;
  length: number;
  width: number;
  last_seen: string;
}

interface KaekParcel {
  id: number;
  kaek_code: string;
  original_kaek_code?: string;
  address: string;
  geometry: GeoJSON.Polygon;
  area: number;
  perimeter: number;
  land_use: string;
  land_use_code: string;
  registry_link: string;
  color: string;
  task_set_id?: number;
  created_at: string;
  linked_sites?: { id: number; name: string }[];
  metadata?: { kaek_history?: Array<{ old: string; new: string; changed_at: string; changed_by: string }> };
}

interface GeoZone {
  id: number;
  name: string;
  zone_type: 'no_fly' | 'restricted' | 'danger' | 'warning' | 'reserved';
  geometry: GeoJSON.Polygon;
  lower_alt_m: number;
  upper_alt_m: number;
  alt_reference: string;
  time_start?: string | null;
  time_end?: string | null;
  reason?: string;
  source: string;
  color: string;
  active: boolean;
  is_global: boolean;
}

interface GeoAuthConflict {
  kind: string;
  severity: string;
  ref_name?: string;
  detail: string;
}

interface GeoAuthorisation {
  id: number;
  mission_id: number | null;
  name: string;
  status: 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED';
  volume: GeoJSON.Polygon;
  lower_alt_m: number;
  upper_alt_m: number;
  alt_reference: string;
  time_start: string;
  time_end: string;
  conflicts: GeoAuthConflict[];
}

interface OperationalIntentVolume {
  outline: GeoJSON.Polygon;
  lower_alt_m: number;
  upper_alt_m: number;
  time_start: string;
  time_end: string;
}

interface OperationalIntent {
  id: string;
  manager: string;
  is_own: boolean;
  name: string | null;
  state: 'Accepted' | 'Activated' | 'Nonconforming' | 'Contingent' | 'Ended';
  ovn: string;
  priority: number;
  volumes: OperationalIntentVolume[];
}

interface RidFlight {
  id: string;
  uss: string;
  operator_id: string;
  position: { lat: number; lng: number; alt: number | null };
  track: number | null;
  speed: number | null;
  rid_state: string;
  timestamp: string | null;
}

interface KaekAnalysis {
  metadata_summary: {
    total_parcels: number;
    fields: Record<string, { populated: number; min?: number; max?: number; mean?: number; distinct_values?: string[] }>;
  };
  matches: { point_id: number; point_name: string; parcel_id: number; kaek_code: string; match_type: string; conflict_detail?: string }[];
  orphan_points: { id: number; name: string }[];
  orphan_parcels: { id: number; kaek_code: string; address: string }[];
  summary: { total_points: number; total_parcels: number; matched: number; orphan_points: number; orphan_parcels: number; already_linked: number };
  available_task_sets: { id: number; name: string; icon: string; point_count: number }[];
  points_task_set_id: number | null;
}

interface LiveCamera {
  id: string;
  name: string;
  lat: number;
  lon: number;
  source: string;
  provider: string;
  video_id: string;
}

const naturalSort = (a: string, b: string): number => {
  const pa = a.split(/(\d+)/);
  const pb = b.split(/(\d+)/);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const sa = pa[i] || '', sb = pb[i] || '';
    const na = parseInt(sa, 10), nb = parseInt(sb, 10);
    if (!isNaN(na) && !isNaN(nb)) { if (na !== nb) return na - nb; }
    else { const c = sa.localeCompare(sb); if (c !== 0) return c; }
  }
  return 0;
};


// No REACT_APP_API_KEY: CRA bakes those into the bundle, so the shared key was readable
// by every visitor. The session is the credential now; the backend accepts it everywhere
// the key used to be the only option. See frontend/src/auth.ts.
const getAuthHeaders = (contentType = false): HeadersInit => sharedAuthHeaders(contentType);

// Hand-added stream sources live on the server. localStorage is now only the one-time
// migration source and the offline fallback -- it is no longer where an RTSP URL, and the
// camera password in its userinfo, comes to rest. See serverSourceStore.ts.
const safeLocalStorage = (() => {
  try { return window.localStorage; } catch { return null; }
})();
const sourceStore = createServerSourceStore(httpSourceApi, safeLocalStorage ?? {
  getItem: () => null, setItem: () => {}, removeItem: () => {},
});

const TILE_URL = process.env.REACT_APP_TILE_URL || '';

const MAP_STYLES: Record<string, string | maplibregl.StyleSpecification> = {
  dark: TILE_URL
    ? `${TILE_URL}/styles/osm-bright/style.json`
    : 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  street: TILE_URL
    ? `${TILE_URL}/styles/osm-bright/style.json`
    : 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
  positron: TILE_URL
    ? `${TILE_URL}/styles/osm-bright/style.json`
    : 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
  satellite: {
    version: 8,
    glyphs: TILE_URL
      ? `${TILE_URL}/fonts/{fontstack}/{range}.pbf`
      : 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      satellite: {
        type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        // Esri World_Imagery only has z19 over major cities (Athens, Thessaloniki).
        // Corinth, Karditsa and the Plastira basin stop at z18 and return a grey
        // 'Map data not yet available' tile above it. 18 is the limit that holds
        // everywhere we demo — above it MapLibre over-zooms the z18 tile instead.
        maxzoom: 18,
        attribution: '© Esri'
      }
    },
    layers: [{
      id: 'satellite',
      type: 'raster',
      source: 'satellite',
      minzoom: 0,
      maxzoom: 22
    }]
  }
};

function DcimLightbox({ photo, photos, apiUrl, onClose, onChange, waypoints, dataScope, onPhotoUpdated, matchedSiteNames }: {
  photo: DcimPhoto;
  photos: DcimPhoto[];
  apiUrl: string;
  onClose: () => void;
  onChange: (p: DcimPhoto) => void;
  waypoints?: Array<{ index: number; lat: number; lng: number; site_id?: number; site_name?: string; site_ids?: number[]; site_names?: string[] }>;
  dataScope?: string;
  onPhotoUpdated?: (photo: DcimPhoto) => void;
  matchedSiteNames?: string[];
}) {
  type DrawTool = 'select' | 'rect' | 'arrow' | 'freehand' | 'polygon' | 'text';
  const idx = photos.findIndex(p => p.id === photo.id);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const viewRef = useRef({ zoom: 1, panX: 0, panY: 0, rotation: 0 });
  const imgRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [northAligned, setNorthAligned] = useState(false);
  const [liveHeading, setLiveHeading] = useState<number | null>(null);
  const [annotating, setAnnotating] = useState(false);
  const parseAnns = (a: unknown) => Array.isArray(a) ? a.filter((x: unknown) => typeof x === 'object' && x !== null) as AnnotationObject[] : [];
  const [annotations, setAnnotations] = useState<AnnotationObject[]>(parseAnns(photo.annotations));
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [undoStack, setUndoStack] = useState<AnnotationObject[][]>([]);
  const [drawTool, setDrawTool] = useState<DrawTool>('rect');
  const [drawColor, setDrawColor] = useState('#ff0000');
  const [drawing, setDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<Array<{ x: number; y: number }>>([]);
  const [polygonPoints, setPolygonPoints] = useState<Array<{ x: number; y: number }>>([]);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(photo.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setZoom(1); setPan({ x: 0, y: 0 }); setNorthAligned(false); setLiveHeading(null);
    setAnnotations(parseAnns(photo.annotations));
    setAnnotating(false); setShowAnnotations(true); setNotesDraft(photo.notes || '');
    setUndoStack([]); setSelectedIdx(null); setPolygonPoints([]); setCurrentPoints([]);
  }, [photo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (photo.heading != null) { setLiveHeading(((photo.heading % 360) + 360) % 360); return; }
    if (photo.photo_type === 'video') return;
    let cancelled = false;
    (async () => {
      try {
        const url = `${apiUrl}${photo.url}`;
        const exif = await exifr.parse(url, { gps: true, xmp: true });
        if (cancelled || !exif) return;
        let heading: number | undefined;
        if (typeof exif.GPSImgDirection === 'number') heading = exif.GPSImgDirection;
        if (heading == null) {
          for (const key of Object.keys(exif)) {
            if (/gimbalyaw/i.test(key)) {
              const v = typeof exif[key] === 'number' ? exif[key] as number : parseFloat(String(exif[key]));
              if (!isNaN(v)) { heading = v; break; }
            }
          }
        }
        if (heading != null && !cancelled) setLiveHeading(((heading % 360) + 360) % 360);
      } catch { /* image may not support EXIF extraction from URL */ }
    })();
    return () => { cancelled = true; };
  }, [photo.id, photo.heading, photo.url, photo.photo_type, apiUrl]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (showNotes && (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      if (annotating && (e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight' && idx < photos.length - 1) { onChange(photos[idx + 1]); }
      else if (e.key === 'ArrowLeft' && idx > 0) { onChange(photos[idx - 1]); }
      else if (e.key === 'Escape') {
        if (polygonPoints.length > 0) { setPolygonPoints([]); }
        else if (selectedIdx != null) { setSelectedIdx(null); }
        else if (annotating) { setAnnotating(false); setZoom(1); setPan({ x: 0, y: 0 }); }
        else if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); }
        else onClose();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIdx != null && annotating) {
        e.preventDefault();
        setUndoStack(s => [...s, annotations]);
        const updated = annotations.filter((_, i) => i !== selectedIdx);
        setAnnotations(updated);
        saveAnnotations(updated);
        setSelectedIdx(null);
      } else if (e.key === '+' || e.key === '=') { setZoom(z => Math.min(z * 1.3, 10)); }
      else if (e.key === '-') { setZoom(z => { const next = Math.max(z / 1.3, 1); if (next === 1) setPan({ x: 0, y: 0 }); return next; }); }
      else if (e.key === '0') { setZoom(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [idx, photos, onChange, onClose, zoom, annotating, showNotes, selectedIdx, annotations, polygonPoints]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderAnnotations = useCallback((canvas: HTMLCanvasElement, anns: AnnotationObject[], opts?: {
    current?: AnnotationObject; selectedIdx?: number | null;
    polyPreview?: Array<{ x: number; y: number }>; hoverPt?: { x: number; y: number } | null;
    skipClear?: boolean;
  }) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width, h = canvas.height;
    if (!opts?.skipClear) ctx.clearRect(0, 0, w, h);

    const drawOne = (ann: AnnotationObject, highlight: boolean) => {
      ctx.save();
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = highlight ? 3 : 2;
      if (highlight) { ctx.shadowColor = '#fff'; ctx.shadowBlur = 6; }
      if (ann.type === 'rect' && ann.points.length >= 2) {
        const x1 = ann.points[0].x * w, y1 = ann.points[0].y * h;
        const x2 = ann.points[1].x * w, y2 = ann.points[1].y * h;
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      } else if (ann.type === 'arrow' && ann.points.length >= 2) {
        const x1 = ann.points[0].x * w, y1 = ann.points[0].y * h;
        const x2 = ann.points[1].x * w, y2 = ann.points[1].y * h;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const hl = 15;
        ctx.beginPath();
        ctx.moveTo(x2, y2); ctx.lineTo(x2 - hl * Math.cos(angle - Math.PI / 6), y2 - hl * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(x2, y2); ctx.lineTo(x2 - hl * Math.cos(angle + Math.PI / 6), y2 - hl * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
      } else if ((ann.type === 'freehand' || ann.type === 'polygon') && ann.points.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x * w, ann.points[0].y * h);
        for (let i = 1; i < ann.points.length; i++) ctx.lineTo(ann.points[i].x * w, ann.points[i].y * h);
        if (ann.type === 'polygon') { ctx.closePath(); ctx.globalAlpha = 0.15; ctx.fill(); ctx.globalAlpha = 1; }
        ctx.stroke();
      } else if (ann.type === 'text' && ann.text && ann.points.length >= 1) {
        ctx.font = 'bold 16px sans-serif';
        ctx.globalAlpha = 0.6;
        const metrics = ctx.measureText(ann.text);
        ctx.fillStyle = '#000';
        ctx.fillRect(ann.points[0].x * w - 2, ann.points[0].y * h - 14, metrics.width + 4, 18);
        ctx.globalAlpha = 1;
        ctx.fillStyle = ann.color;
        ctx.fillText(ann.text, ann.points[0].x * w, ann.points[0].y * h);
      }
      if (highlight) {
        ctx.shadowBlur = 0;
        const b = { minX: Math.min(...ann.points.map(p => p.x)), maxX: Math.max(...ann.points.map(p => p.x)),
          minY: Math.min(...ann.points.map(p => p.y)), maxY: Math.max(...ann.points.map(p => p.y)) };
        ctx.setLineDash([4, 4]); ctx.strokeStyle = '#4da6ff'; ctx.lineWidth = 1.5;
        ctx.strokeRect(b.minX * w - 4, b.minY * h - 4, (b.maxX - b.minX) * w + 8, (b.maxY - b.minY) * h + 8);
        ctx.setLineDash([]);
      }
      ctx.restore();
    };

    for (let i = 0; i < anns.length; i++) drawOne(anns[i], i === (opts?.selectedIdx ?? -1));
    if (opts?.current) drawOne(opts.current, false);

    if (opts?.polyPreview && opts.polyPreview.length > 0) {
      ctx.save();
      ctx.strokeStyle = '#ffaa00'; ctx.setLineDash([6, 3]); ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(opts.polyPreview[0].x * w, opts.polyPreview[0].y * h);
      for (let i = 1; i < opts.polyPreview.length; i++) ctx.lineTo(opts.polyPreview[i].x * w, opts.polyPreview[i].y * h);
      if (opts.hoverPt) ctx.lineTo(opts.hoverPt.x * w, opts.hoverPt.y * h);
      ctx.stroke(); ctx.setLineDash([]);
      for (const pt of opts.polyPreview) {
        ctx.fillStyle = '#ffaa00'; ctx.beginPath(); ctx.arc(pt.x * w, pt.y * h, 4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    }
  }, []);

  const redrawCanvas = useCallback((overrideZoom?: number, overridePan?: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    if (container) { canvas.width = container.clientWidth; canvas.height = container.clientHeight; }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const v = viewRef.current;
    const z = overrideZoom ?? v.zoom;
    const p = overridePan ?? { x: v.panX, y: v.panY };
    const r = v.rotation;
    const cw = canvas.width, ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);
    ctx.save();
    ctx.translate(cw / 2 + p.x, ch / 2 + p.y);
    ctx.rotate(r * Math.PI / 180);
    ctx.scale(z, z);
    ctx.translate(-cw / 2, -ch / 2);
    const current = drawing && currentPoints.length >= 2 && drawTool !== 'select' ? { type: drawTool as AnnotationObject['type'], color: drawColor, points: currentPoints } : undefined;
    renderAnnotations(canvas, (annotating || showAnnotations) ? annotations : [], {
      current, selectedIdx: annotating ? selectedIdx : null,
      polyPreview: drawTool === 'polygon' ? polygonPoints : undefined,
      hoverPt: drawTool === 'polygon' ? hoverPoint : null,
      skipClear: true,
    });
    ctx.restore();
  }, [drawing, currentPoints, drawTool, drawColor, annotating, showAnnotations, annotations, selectedIdx, polygonPoints, hoverPoint, renderAnnotations]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (annotating) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.85 : 1.18;
    setZoom(prev => {
      const next = Math.min(Math.max(prev * delta, 1), 10);
      const nextPan = next === 1 ? { x: 0, y: 0 } : undefined;
      if (nextPan) setPan(nextPan);
      viewRef.current.zoom = next;
      if (nextPan) { viewRef.current.panX = 0; viewRef.current.panY = 0; }
      const img = imgRef.current?.querySelector('img') as HTMLImageElement | null;
      if (img) {
        const r = viewRef.current.rotation;
        const p = nextPan || { x: viewRef.current.panX, y: viewRef.current.panY };
        img.style.transform = `rotate(${r}deg) scale(${next}) translate(${p.x / next}px, ${p.y / next}px)`;
      }
      redrawCanvas(next, nextPan || undefined);
      return next;
    });
  }, [annotating, redrawCanvas]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (annotating) return;
    if (zoom <= 1 || e.button !== 0) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }, [zoom, pan, annotating]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return;
    const newPan = { x: dragStart.current.panX + (e.clientX - dragStart.current.x), y: dragStart.current.panY + (e.clientY - dragStart.current.y) };
    viewRef.current.panX = newPan.x;
    viewRef.current.panY = newPan.y;
    const img = imgRef.current?.querySelector('img') as HTMLImageElement | null;
    if (img) {
      const v = viewRef.current;
      img.style.transform = `rotate(${v.rotation}deg) scale(${v.zoom}) translate(${newPan.x / v.zoom}px, ${newPan.y / v.zoom}px)`;
    }
    redrawCanvas(undefined, newPan);
    setPan(newPan);
  }, [dragging, redrawCanvas]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (annotating) return;
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); } else {
      const rect = imgRef.current?.getBoundingClientRect();
      if (rect) { const cx = e.clientX - rect.left - rect.width / 2; const cy = e.clientY - rect.top - rect.height / 2; setZoom(3); setPan({ x: -cx * 2, y: -cy * 2 }); }
    }
  }, [zoom, annotating]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const res = await fetch(`${apiUrl}/api/dcim/photos/${photo.id}/notes?scope=${dataScope || 'group'}`, {
        method: 'PUT', headers: { ...getAuthHeaders(true) } as Record<string, string>,
        body: JSON.stringify({ notes: notesDraft }),
      });
      if (res.ok && onPhotoUpdated) onPhotoUpdated({ ...photo, notes: notesDraft || undefined });
    } catch { /* ignore */ }
    setSavingNotes(false);
  };

  const saveAnnotations = async (anns: AnnotationObject[]) => {
    try {
      const res = await fetch(`${apiUrl}/api/dcim/photos/${photo.id}/annotations?scope=${dataScope || 'group'}`, {
        method: 'PUT', headers: { ...getAuthHeaders(true) } as Record<string, string>,
        body: JSON.stringify({ annotations: anns }),
      });
      if (res.ok && onPhotoUpdated) onPhotoUpdated({ ...photo, annotations: anns });
      else console.error('Failed to save annotations:', res.status, await res.text().catch(() => ''));
    } catch (err) { console.error('Failed to save annotations:', err); }
  };

  const commitAnnotation = (ann: AnnotationObject) => {
    setUndoStack(s => [...s, annotations]);
    const updated = [...annotations, ann];
    setAnnotations(updated);
    saveAnnotations(updated);
  };

  const handleAnnotationUndo = () => {
    if (undoStack.length > 0) {
      const prev = undoStack[undoStack.length - 1];
      setUndoStack(s => s.slice(0, -1));
      setAnnotations(prev);
      saveAnnotations(prev);
      setSelectedIdx(null);
    }
  };

  const effectiveHeading = liveHeading;
  const rotation = northAligned && effectiveHeading != null ? effectiveHeading : 0;

  const canvasToNorm = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) / rect.width;
    const sy = (e.clientY - rect.top) / rect.height;
    const cx = sx - 0.5, cy = sy - 0.5;
    const dx = (cx - pan.x / rect.width) / zoom;
    const dy = (cy - pan.y / rect.height) / zoom;
    const rad = -rotation * Math.PI / 180;
    const rx = dx * Math.cos(rad) - dy * Math.sin(rad);
    const ry = dx * Math.sin(rad) + dy * Math.cos(rad);
    return { x: rx + 0.5, y: ry + 0.5 };
  };

  const hitTest = (x: number, y: number, ann: AnnotationObject, tolerance = 0.02): boolean => {
    if (ann.type === 'rect' && ann.points.length >= 2) {
      const [p1, p2] = ann.points;
      const minX = Math.min(p1.x, p2.x) - tolerance, maxX = Math.max(p1.x, p2.x) + tolerance;
      const minY = Math.min(p1.y, p2.y) - tolerance, maxY = Math.max(p1.y, p2.y) + tolerance;
      return x >= minX && x <= maxX && y >= minY && y <= maxY;
    }
    if ((ann.type === 'polygon' || ann.type === 'freehand') && ann.points.length >= 3) {
      let inside = false;
      const pts = ann.points;
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        if ((pts[i].y > y) !== (pts[j].y > y) && x < (pts[j].x - pts[i].x) * (y - pts[i].y) / (pts[j].y - pts[i].y) + pts[i].x) inside = !inside;
      }
      if (inside) return true;
      for (const pt of pts) { if (Math.hypot(pt.x - x, pt.y - y) < tolerance) return true; }
      return false;
    }
    if (ann.type === 'arrow' && ann.points.length >= 2) {
      const [a, b] = ann.points;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 0.001) return Math.hypot(x - a.x, y - a.y) < tolerance;
      const t = Math.max(0, Math.min(1, ((x - a.x) * (b.x - a.x) + (y - a.y) * (b.y - a.y)) / (len * len)));
      const px = a.x + t * (b.x - a.x), py = a.y + t * (b.y - a.y);
      return Math.hypot(x - px, y - py) < tolerance;
    }
    if (ann.type === 'text' && ann.points.length >= 1) {
      const p = ann.points[0];
      return x >= p.x - tolerance && x <= p.x + 0.15 && y >= p.y - 0.03 && y <= p.y + tolerance;
    }
    if (ann.type === 'freehand' && ann.points.length >= 2) {
      for (const pt of ann.points) { if (Math.hypot(pt.x - x, pt.y - y) < tolerance) return true; }
    }
    return false;
  };

  const getAnnotationBounds = (ann: AnnotationObject) => {
    const xs = ann.points.map(p => p.x), ys = ann.points.map(p => p.y);
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!annotating) return;
    const pt = canvasToNorm(e);
    if (!pt) return;
    const { x, y } = pt;

    if (drawTool === 'select') {
      for (let i = annotations.length - 1; i >= 0; i--) {
        if (hitTest(x, y, annotations[i])) {
          setSelectedIdx(i);
          const b = getAnnotationBounds(annotations[i]);
          setDragOffset({ x: x - b.minX, y: y - b.minY });
          setDrawing(true);
          return;
        }
      }
      setSelectedIdx(null);
      return;
    }
    if (drawTool === 'text') {
      const text = prompt('Enter text label:');
      if (text) commitAnnotation({ type: 'text', color: drawColor, points: [{ x, y }], text });
      return;
    }
    if (drawTool === 'polygon') return;
    setSelectedIdx(null);
    setDrawing(true);
    setCurrentPoints([{ x, y }]);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pt = canvasToNorm(e);
    if (!pt) return;
    const { x, y } = pt;

    if (drawTool === 'polygon' && polygonPoints.length > 0) {
      setHoverPoint({ x, y });
      return;
    }

    if (drawTool === 'select' && drawing && selectedIdx != null && dragOffset) {
      const ann = annotations[selectedIdx];
      const b = getAnnotationBounds(ann);
      const dx = x - dragOffset.x - b.minX, dy = y - dragOffset.y - b.minY;
      const moved = { ...ann, points: ann.points.map(p => ({ x: p.x + dx, y: p.y + dy })) };
      setAnnotations(prev => prev.map((a, i) => i === selectedIdx ? moved : a));
      return;
    }

    if (!drawing) return;
    if (drawTool === 'freehand') setCurrentPoints(p => [...p, { x, y }]);
    else setCurrentPoints(p => [p[0], { x, y }]);
  };

  const handleCanvasMouseUp = () => {
    if (drawTool === 'select' && drawing && selectedIdx != null) {
      setDrawing(false);
      setDragOffset(null);
      setUndoStack(s => [...s.slice(0, -1), annotations]);
      saveAnnotations(annotations);
      return;
    }
    if (!drawing || currentPoints.length < 2) { setDrawing(false); setCurrentPoints([]); return; }
    commitAnnotation({ type: drawTool as AnnotationObject['type'], color: drawColor, points: [...currentPoints] });
    setDrawing(false);
    setCurrentPoints([]);
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (drawTool !== 'polygon' || !annotating) return;
    const pt = canvasToNorm(e);
    if (!pt) return;
    setPolygonPoints(prev => [...prev, pt]);
  };

  const handleCanvasDblClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (drawTool !== 'polygon' || polygonPoints.length < 3) return;
    e.stopPropagation();
    commitAnnotation({ type: 'polygon', color: drawColor, points: [...polygonPoints] });
    setPolygonPoints([]);
    setHoverPoint(null);
  };

  useEffect(() => {
    viewRef.current = { zoom, panX: pan.x, panY: pan.y, rotation };
    redrawCanvas();
  }, [zoom, pan, rotation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  const exportAnnotatedImage = () => {
    const imgEl = imgRef.current?.querySelector('img');
    if (!imgEl) return;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = imgEl.naturalWidth; exportCanvas.height = imgEl.naturalHeight;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(imgEl, 0, 0);
    renderAnnotations(exportCanvas, annotations);
    const link = document.createElement('a');
    link.download = `annotated_${photo.filename}`;
    link.href = exportCanvas.toDataURL('image/png');
    link.click();
  };

  const deleteSelected = () => {
    if (selectedIdx == null) return;
    setUndoStack(s => [...s, annotations]);
    const updated = annotations.filter((_, i) => i !== selectedIdx);
    setAnnotations(updated);
    saveAnnotations(updated);
    setSelectedIdx(null);
  };

  const siteNames = (() => {
    if (waypoints && waypoints.length > 0) {
      const collected = new Set<string>();
      const exactWp = photo.waypoint_index != null ? waypoints.find(w => w.index === photo.waypoint_index) : null;
      if (exactWp) { for (const n of (exactWp.site_names || (exactWp.site_name ? [exactWp.site_name] : []))) collected.add(n); }
      if (collected.size === 0 && photo.latitude != null && photo.longitude != null) {
        const toRad = Math.PI / 180;
        for (const wp of waypoints) {
          const names = wp.site_names || (wp.site_name ? [wp.site_name] : []);
          if (names.length === 0) continue;
          const dLat = (wp.lat - photo.latitude) * toRad, dLon = (wp.lng - photo.longitude) * toRad;
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(photo.latitude * toRad) * Math.cos(wp.lat * toRad) * Math.sin(dLon / 2) ** 2;
          if (6371000 * 2 * Math.asin(Math.sqrt(a)) <= 20) { for (const n of names) collected.add(n); }
        }
      }
      if (collected.size > 0) return [...collected];
    }
    return matchedSiteNames || [];
  })();

  const TOOLS: { id: DrawTool; icon: IconKey; label: string }[] = [
    { id: 'select', icon: 'select', label: 'Select' },
    { id: 'rect', icon: 'rect', label: 'Rect' },
    { id: 'polygon', icon: 'polygon', label: 'Polygon' },
    { id: 'arrow', icon: 'arrow', label: 'Arrow' },
    { id: 'freehand', icon: 'freehand', label: 'Draw' },
    { id: 'text', icon: 'text', label: 'Text' },
  ];

  const canvasCursor = drawTool === 'select' ? (selectedIdx != null && drawing ? 'grabbing' : 'default') : 'crosshair';

  return (
    <div className="attachment-lightbox" onClick={() => { if (zoom <= 1 && !annotating) onClose(); }}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: annotating ? '95vw' : undefined }}>
        <button className="lightbox-close" onClick={onClose} aria-label="Close"><Icon name="clear" /></button>
        {idx > 0 && <button className="lightbox-nav lightbox-prev" onClick={() => onChange(photos[idx - 1])} title="Previous (←)">‹</button>}
        {idx < photos.length - 1 && <button className="lightbox-nav lightbox-next" onClick={() => onChange(photos[idx + 1])} title="Next (→)">›</button>}

        {/* === Top action bar === */}
        <div className="lb-action-bar">
          <button
            className={`lb-action-btn${northAligned ? ' lb-action-active' : ''}${effectiveHeading == null ? ' lb-action-disabled' : ''}`}
            onClick={() => effectiveHeading != null && setNorthAligned(a => !a)}
            title={effectiveHeading != null ? (northAligned ? 'Reset orientation' : `Align to North (${effectiveHeading.toFixed(0)}°)`) : 'No compass data'}
          >
            <span className="lb-action-icon"><Icon name={northAligned ? 'north' : 'compass'} /></span>
            <span className="lb-action-label">{northAligned ? 'North' : 'Compass'}</span>
          </button>
          <button
            className={`lb-action-btn${annotating ? ' lb-action-editing' : ''}`}
            onClick={() => { if (annotating) { setAnnotating(false); setZoom(1); setPan({ x: 0, y: 0 }); setPolygonPoints([]); setSelectedIdx(null); } else { setAnnotating(true); } }}
          >
            <span className="lb-action-icon"><Icon name={annotating ? 'done' : 'annotate'} /></span>
            <span className="lb-action-label">{annotating ? 'Done' : 'Annotate'}</span>
          </button>
          {annotations.length > 0 && !annotating && (
            <button className={`lb-action-btn${showAnnotations ? ' lb-action-active' : ''}`} onClick={() => setShowAnnotations(a => !a)}>
              <span className="lb-action-icon"><Icon name="tags" /></span>
              <span className="lb-action-label">{annotations.length}</span>
            </button>
          )}
          <button className={`lb-action-btn${showNotes ? ' lb-action-active' : ''}`} onClick={() => setShowNotes(n => !n)}>
            <span className="lb-action-icon"><Icon name="notes" /></span>
            <span className="lb-action-label">Notes</span>
          </button>
        </div>

        {/* === Annotation toolbar === */}
        {annotating && (
          <div className="lb-toolbar">
            <div className="lb-toolbar-group">
              {TOOLS.map(t => (
                <button key={t.id} className={`lb-tool-btn${drawTool === t.id ? ' lb-tool-active' : ''}`} onClick={() => { setDrawTool(t.id); setSelectedIdx(null); if (t.id !== 'polygon') setPolygonPoints([]); }} title={t.label}>
                  <span className="lb-tool-icon"><Icon name={t.icon} /></span>
                  <span className="lb-tool-label">{t.label}</span>
                </button>
              ))}
            </div>
            <div className="lb-toolbar-sep" />
            <div className="lb-toolbar-group">
              <div className="lb-color-wrap">
                <div className="lb-color-swatch" style={{ background: drawColor }} />
                <input type="color" value={drawColor} onChange={e => setDrawColor(e.target.value)} className="lb-color-input" />
              </div>
            </div>
            <div className="lb-toolbar-sep" />
            <div className="lb-toolbar-group">
              <button className="lb-tool-btn" onClick={handleAnnotationUndo} disabled={undoStack.length === 0} title="Undo (Ctrl+Z)">
                <span className="lb-tool-icon"><Icon name="undo" /></span><span className="lb-tool-label">Undo</span>
              </button>
              {selectedIdx != null && (
                <button className="lb-tool-btn lb-tool-danger" onClick={deleteSelected} title="Delete selected (Del)">
                  <span className="lb-tool-icon"><Icon name="delete" /></span><span className="lb-tool-label">Delete</span>
                </button>
              )}
              <button className="lb-tool-btn lb-tool-danger" onClick={() => { setUndoStack(s => [...s, annotations]); setAnnotations([]); saveAnnotations([]); setSelectedIdx(null); }} title="Clear all">
                <span className="lb-tool-icon"><Icon name="clear" /></span><span className="lb-tool-label">Clear</span>
              </button>
              <button className="lb-tool-btn lb-tool-success" onClick={exportAnnotatedImage} title="Export with annotations">
                <span className="lb-tool-icon"><Icon name="download" /></span><span className="lb-tool-label">Export</span>
              </button>
            </div>
            {drawTool === 'polygon' && polygonPoints.length > 0 && (
              <>
                <div className="lb-toolbar-sep" />
                <span style={{ color: 'var(--color-warning)', fontSize: 11, padding: '0 6px' }}>{polygonPoints.length} pts — double-click to close</span>
              </>
            )}
          </div>
        )}

        {/* === Image + Canvas === */}
        <div
          ref={imgRef}
          className={`lightbox-zoom-container${dragging ? ' dragging' : ''}`}
          onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onDoubleClick={handleDoubleClick}
          style={{ position: 'relative' }}
        >
          <img
            src={`${apiUrl}${photo.url}`} alt={photo.filename} draggable={false}
            style={{
              transform: `rotate(${rotation}deg) scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              cursor: annotating ? 'crosshair' : zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
            }}
          />
          {(annotating || (showAnnotations && annotations.length > 0)) && (
            <canvas
              ref={canvasRef}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onClick={handleCanvasClick}
              onDoubleClick={handleCanvasDblClick}
              style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: annotating ? 'auto' : 'none',
                cursor: annotating ? canvasCursor : 'default',
              }}
            />
          )}
        </div>

        {/* === Bottom info === */}
        {zoom > 1 && (
          <div className="lightbox-zoom-controls">
            <button onClick={() => setZoom(z => Math.min(z * 1.3, 10))} title="Zoom in (+)">+</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => { const next = Math.max(z / 1.3, 1); if (next === 1) setPan({ x: 0, y: 0 }); return next; })} title="Zoom out (-)">-</button>
            <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Reset zoom (0)">Reset</button>
          </div>
        )}
        <div className="lightbox-filename">
          {photo.filename}
          {photo.image_width && ` (${photo.image_width}x${photo.image_height})`}
          {photo.camera_model && ` - ${photo.camera_model}`}
          {photo.lens_type && ` [${photo.lens_type}]`}
          {effectiveHeading != null && ` | ${effectiveHeading.toFixed(0)}°`}
          <span className="lightbox-counter"> {idx + 1} / {photos.length}</span>
        </div>
        {siteNames.length > 0 && (
          <div style={{ textAlign: 'center', padding: '4px 0' }}>
            {siteNames.map((name, i) => (
              <span key={i} style={{ display: 'inline-block', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', padding: '2px 8px', borderRadius: 10, fontSize: 12, margin: '0 3px' }}>{name}</span>
            ))}
          </div>
        )}
        {photo.latitude != null && photo.longitude != null && (
          <div className="lightbox-coords">
            <a href={`https://www.google.com/maps/search/?api=1&query=${photo.latitude},${photo.longitude}`} target="_blank" rel="noopener noreferrer" className="lightbox-coords-link">
              {photo.latitude.toFixed(7)}, {photo.longitude.toFixed(7)}
            </a>
            {photo.altitude != null && ` | Alt: ${photo.altitude.toFixed(1)}m`}
            {photo.captured_at && ` | ${new Date(photo.captured_at).toLocaleString()}`}
          </div>
        )}
        {showNotes && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--color-border)' }}>
            <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} placeholder="Add notes about this photo..." rows={3}
              style={{ width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '6px 8px', fontSize: 13, resize: 'vertical' }} />
            <button onClick={saveNotes} disabled={savingNotes || notesDraft === (photo.notes || '')}
              style={{ background: notesDraft !== (photo.notes || '') ? 'var(--color-accent)' : '#444', color: notesDraft !== (photo.notes || '') ? '#1a1a1a' : 'var(--color-text-secondary)', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: notesDraft !== (photo.notes || '') ? 'pointer' : 'default', fontSize: 12, fontWeight: 600, marginTop: 4 }}>
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function KaekCodeEditor({ parcel, apiUrl, getAuthHeaders, onUpdated }: {
  parcel: KaekParcel;
  apiUrl: string;
  getAuthHeaders: (json?: boolean) => HeadersInit;
  onUpdated: (p: KaekParcel) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(parcel.kaek_code);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const save = async () => {
    if (value === parcel.kaek_code) { setEditing(false); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/kaek/parcels/${parcel.id}/kaek-code`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ kaek_code: value }),
      });
      if (res.ok) {
        const updated = await res.json();
        onUpdated(updated);
        setEditing(false);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to update');
      }
    } catch { setError('Network error'); }
    setSaving(false);
  };

  const history = parcel.metadata?.kaek_history || [];

  return (
    <div>
      {editing ? (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input value={value} onChange={e => setValue(e.target.value)} style={{ flex: 1, background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: 3, padding: '2px 6px', fontSize: 12 }} />
          <button onClick={save} disabled={saving} style={{ background: 'var(--color-accent)', color: '#1a1a1a', border: 'none', borderRadius: 3, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>{saving ? '...' : '✓'}</button>
          <button onClick={() => { setEditing(false); setValue(parcel.kaek_code); setError(null); }} style={{ background: '#555', color: '#fff', border: 'none', borderRadius: 3, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>✗</button>
        </div>
      ) : (
        <span style={{ cursor: 'pointer' }} onClick={() => setEditing(true)} title="Click to edit KAEK code">
          {parcel.kaek_code} <span style={{ color: 'var(--color-text-secondary)', fontSize: 10 }}><Icon name="edit" /></span>
        </span>
      )}
      {error && <div style={{ color: 'var(--color-danger)', fontSize: 11, marginTop: 2 }}>{error}</div>}
      {parcel.original_kaek_code && parcel.original_kaek_code !== parcel.kaek_code && (
        <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 2 }}>Original: {parcel.original_kaek_code}</div>
      )}
      {history.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <span style={{ fontSize: 10, color: 'var(--color-text-secondary)', cursor: 'pointer' }} onClick={() => setShowHistory(h => !h)}>
            <Icon name={showHistory ? 'chev-down' : 'chev-right'} /> {history.length} change{history.length > 1 ? 's' : ''}
          </span>
          {showHistory && (
            <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 2 }}>
              {history.map((h, i) => (
                <div key={i} style={{ padding: '1px 0' }}>
                  {new Date(h.changed_at).toLocaleString()}: {h.old} → {h.new} <span style={{ color: 'var(--color-text-muted)' }}>by {h.changed_by}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function createFovGeoJSON(drones: Record<string, DronePosition>): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  const FOV_ANGLE = 84;
  const ARC_SEGMENTS = 24;

  for (const [droneId, drone] of Object.entries(drones)) {
    if (!drone.connected || drone.altitude < 2) continue;

    const cameraBearing = drone.gimbal?.yaw ?? drone.heading;
    const range = Math.min(Math.max(drone.altitude * 1.5, 30), 500);
    const halfFov = FOV_ANGLE / 2;
    const startBearing = cameraBearing - halfFov;
    const droneCoord: [number, number] = [drone.longitude, drone.latitude];

    const arcPoints: [number, number][] = [];
    for (let i = 0; i <= ARC_SEGMENTS; i++) {
      const bearing = startBearing + (FOV_ANGLE * i) / ARC_SEGMENTS;
      arcPoints.push(destinationPoint(drone.latitude, drone.longitude, bearing, range));
    }

    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[droneCoord, ...arcPoints, droneCoord]] },
      properties: { type: 'fov', droneId },
    });

    const headingEnd = destinationPoint(drone.latitude, drone.longitude, drone.heading, range * 0.7);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [droneCoord, headingEnd] },
      properties: { type: 'heading', droneId },
    });
  }

  return { type: 'FeatureCollection', features };
}


/** Matches the "Narrow layout" breakpoint in App.css. */
const PHONE_LAYOUT_QUERY = '(max-width: 900px)';
function isPhoneLayout(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(PHONE_LAYOUT_QUERY).matches;
}

function App() {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const { user, isLoading, isAuthenticated, isPending, isAdmin, logout, refreshUser } = useAuth();
  const [authView, setAuthView] = useState<'login' | 'signup'>('login');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showFleetPanel, setShowFleetPanel] = useState(false);
  const [showMqttPanel, setShowMqttPanel] = useState(false);
  const [showEdgePanel, setShowEdgePanel] = useState(false);
  const [edgeFocusId, setEdgeFocusId] = useState<number | null>(null);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [showOpLogPanel, setShowOpLogPanel] = useState(false);
  const [showCapturesPanel, setShowCapturesPanel] = useState(false);
  const [showMarketplacePanel, setShowMarketplacePanel] = useState(false);
  const [showSurveillancePanel, setShowSurveillancePanel] = useState(false);
  const [showAnalyticsPanel, setShowAnalyticsPanel] = useState(false);
  // The hall whose indoor 3D plan is open, or null for the normal map view.
  const [activeHall, setActiveHall] = useState<HallPlan | null>(null);
  const [selectedStand, setSelectedStand] = useState<string | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<HallCamera | null>(null);
  const [showUspacePanel, setShowUspacePanel] = useState(false);
  // Restored on reload, so an assistant left minimised comes back as the corner button it was
  // left as, with its conversation (components/assistant/chatStorage.ts) still in it.
  const [showAssistant, setShowAssistant] = useState(
    () => localStorage.getItem('toc_showAssistant') === 'true');
  const [assistantMinimized, setAssistantMinimized] = useState(
    () => localStorage.getItem('toc_assistantMinimized') === 'true');
  useEffect(() => {
    localStorage.setItem('toc_showAssistant', String(showAssistant));
    localStorage.setItem('toc_assistantMinimized', String(assistantMinimized));
  }, [showAssistant, assistantMinimized]);
  // A list, not one: a research answer proposes many features under a single confirm.
  const [assistantGhosts, setAssistantGhosts] = useState<GhostFeature[]>([]);

  // Geolocated detections: a fix is a PROPOSAL until the operator confirms it. Nothing is
  // written from the video panel. See components/map/GeolocatedPreview.tsx.
  const [pendingFix, setPendingFix] = useState<PendingFix | null>(null);
  const [savingFix, setSavingFix] = useState(false);
  // The calibration editor asks for one map click at a time. A ref, not state, because
  // handleMapClick must see the current handler without being rebuilt around it.
  const groundPickRef = useRef<((p: { lat: number; lng: number }) => void) | null>(null);
  const [groundPickActive, setGroundPickActive] = useState(false);

  const handleRequestGroundPick = useCallback(
    (handler: ((p: { lat: number; lng: number }) => void) | null) => {
      groundPickRef.current = handler;
      setGroundPickActive(Boolean(handler));
    }, []);

  const handleGeoFix = useCallback(
    (fix: Extract<GeoFix, { ok: true }>, label: string) => {
      setPendingFix({
        lat: fix.lat, lng: fix.lng, accuracy_m: fix.accuracy_m,
        label: label.charAt(0).toUpperCase() + label.slice(1),
        warnings: fix.warnings || [],
      });
    }, []);
  const [activeOpLog, setActiveOpLog] = useState<{ id: string; started_at: string; title?: string } | null>(null);
  const [dataScope, setDataScope] = useState<'group' | 'personal'>(() => {
    const saved = localStorage.getItem('toc_dataScope');
    return saved === 'personal' ? 'personal' : 'group';
  });
  const [status, setStatus] = useState<DroneStatus | null>(null);
  const [showVideo, setShowVideo] = useState(() => localStorage.getItem('toc_showVideo') !== 'false');
  const [showTelemetry, setShowTelemetry] = useState(() => localStorage.getItem('toc_showTelemetry') !== 'false');
  const [mapStyle, setMapStyle] = useState(() => localStorage.getItem('toc_mapStyle') || 'dark');
  const [drawMode, setDrawMode] = useState<'none' | 'point' | 'polygon' | 'line' | 'sensor' | 'unit'>('none');
  const [sidebarFilter, setSidebarFilter] = useState('');
  const [points, setPoints] = useState<MapPoint[]>([]);
  const [polygons, setPolygons] = useState<MapPolygon[]>([]);
  const [tempPolygonCoords, setTempPolygonCoords] = useState<number[][]>([]);
  const [selectedPoint, setSelectedPoint] = useState<MapPoint | null>(null);
  const [nearbyPointPicker, setNearbyPointPicker] = useState<{points: MapPoint[], lng: number, lat: number} | null>(null);
  const [editingPointName, setEditingPointName] = useState<string | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<MapPolygon | null>(null);
  // Tank gauging. `tankGaugeFor` holds the polygon whose panel is open; `tankReadings`
  // feeds the map layer and is keyed by polygon id. Both are inert unless the backend
  // reports the feature enabled, which only the demo box does.
  const [tankGaugeEnabled, setTankGaugeEnabled] = useState(false);
  const [tankGaugeFor, setTankGaugeFor] = useState<MapPolygon | null>(null);
  // The site rollup opens from the user menu rather than from a polygon, because it is a
  // question about the whole farm ('how much oil is on this site') rather than about any
  // one tank. Same shape as the other feature panels there.
  const [showTankRollup, setShowTankRollup] = useState(false);
  const [tankReadings, setTankReadings] = useState<Record<number, TankReading>>({});
  const [tankHighlight, setTankHighlight] = useState<TankReading | null>(null);

  // Ask the backend once whether tank gauging runs here. Everywhere but the demo box this
  // answers false, and every entry point below stays hidden — the map assistant's pattern,
  // so a button never 404s on a node where the feature is off. Keyed on isAuthenticated:
  // App mounts under the login screen, so an ask-once-on-mount probe went out with no
  // token, got 401, and hid the feature for the whole session after a fresh sign-in.
  useEffect(() => {
    if (!isAuthenticated) { setTankGaugeEnabled(false); return; }
    let cancelled = false;
    getTankGaugeStatus()
      .then((s) => { if (!cancelled) setTankGaugeEnabled(Boolean(s.enabled)); })
      .catch(() => { if (!cancelled) setTankGaugeEnabled(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Seed the map layer from readings already in the database. Without this the tank farm
  // renders in its plain polygon colour until somebody opens a panel and measures
  // something, which made a durable measurement look like a per-session one — the whole
  // point of storing readings is that the map shows the site's state on arrival.
  useEffect(() => {
    if (!tankGaugeEnabled) return;
    let cancelled = false;
    getTankCandidates(dataScope)
      .then((r) => {
        if (cancelled) return;
        const seeded: Record<number, TankReading> = {};
        r.tanks.forEach((t) => {
          if (t.last_reading?.ok) seeded[t.polygon_id] = t.last_reading;
        });
        setTankReadings(seeded);
      })
      .catch(() => { /* the layer simply stays uncoloured */ });
    return () => { cancelled = true; };
  }, [tankGaugeEnabled, dataScope]);
  const [selectedLine, setSelectedLine] = useState<MapLine | null>(null);
  const [newPointName, setNewPointName] = useState('');
  const [newPolygonName, setNewPolygonName] = useState('');
  const [showDronePosition, setShowDronePosition] = useState(() => localStorage.getItem('toc_showDronePosition') !== 'false');
  const [showFovCone, setShowFovCone] = useState(() => localStorage.getItem('toc_showFovCone') === 'true');
  const [showMap, setShowMap] = useState(true);
  const [hiddenPoints, setHiddenPoints] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem('toc_hiddenPoints'); if (s) return new Set(JSON.parse(s) as number[]); } catch {} return new Set();
  });
  const [hiddenPolygons, setHiddenPolygons] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem('toc_hiddenPolygons'); if (s) return new Set(JSON.parse(s) as number[]); } catch {} return new Set();
  });
  const [hiddenLines, setHiddenLines] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem('toc_hiddenLines'); if (s) return new Set(JSON.parse(s) as number[]); } catch {} return new Set();
  });
  const [lines, setLines] = useState<MapLine[]>([]);
  const [tempLineCoords, setTempLineCoords] = useState<number[][]>([]);
  const [tempLinePointIds, setTempLinePointIds] = useState<(number | null)[]>([]);
  const [newLineName, setNewLineName] = useState('');
  const [sensors, setSensors] = useState<MapSensor[]>([]);
  const [selectedSensor, setSelectedSensor] = useState<MapSensor | null>(null);
  const [newSensorName, setNewSensorName] = useState('');
  const [newSensorType, setNewSensorType] = useState<MapSensor['sensor_type']>('camera');
  const [newSensorRtmpUrl, setNewSensorRtmpUrl] = useState('');
  const [editingSensorRtmpUrl, setEditingSensorRtmpUrl] = useState<string | null>(null);
  const [sensorPosDraft, setSensorPosDraft] = useState<{ lat: string; lon: string } | null>(null);
  const [locatingSensor, setLocatingSensor] = useState(false);
  const [hiddenSensors, setHiddenSensors] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem('toc_hiddenSensors'); if (s) return new Set(JSON.parse(s) as number[]); } catch {} return new Set();
  });

  const [units, setUnits] = useState<FieldUnit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<FieldUnit | null>(null);
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitType, setNewUnitType] = useState<UnitType>('fixed_post');
  const [showUnitsLayer, setShowUnitsLayer] = useState(() => localStorage.getItem('toc_showUnitsLayer') !== 'false');
  const [showEdgeLayer, setShowEdgeLayer] = useState(() => localStorage.getItem('toc_showEdgeLayer') !== 'false');
  // One poll shared by the edge markers and the Edge nodes panel.
  const edgeNodes = useEdgeNodes(getAuthHeaders, !!user?.group_id && (showEdgePanel || showEdgeLayer), user?.group_id);
  const [hiddenUnits, setHiddenUnits] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem('toc_hiddenUnits'); if (s) return new Set(JSON.parse(s) as number[]); } catch {} return new Set();
  });

  const [missions, setMissions] = useState<Mission[]>([]);
  const [activeMission, setActiveMission] = useState<Mission | null>(null);
  const [siteFilter, setSiteFilter] = useState<'all' | 'with-missions' | 'without-missions'>(() => {
    const saved = localStorage.getItem('toc_siteFilter');
    return (saved === 'with-missions' || saved === 'without-missions') ? saved : 'all';
  });
  const [siteFilterTaskSetId, setSiteFilterTaskSetId] = useState<number | null>(() => {
    const saved = localStorage.getItem('toc_siteFilterTaskSet');
    return saved ? Number(saved) : null;
  });
  const [missionPopupCoords, setMissionPopupCoords] = useState<{lng: number, lat: number} | null>(null);
  const [missionPopupWpIndex, setMissionPopupWpIndex] = useState<number | null>(null);
  const [showMissionPanel, setShowMissionPanel] = useState(true);
  const [matchSitesTaskSetId, setMatchSitesTaskSetId] = useState<number | undefined>(undefined);
  const [missionAltitude, setMissionAltitude] = useState(50);
  const [missionSpeed, setMissionSpeed] = useState(5);
  const [missionDroneModel, setMissionDroneModel] = useState('67-1');
  const [missionName, setMissionName] = useState('');
  const [creatingMission, setCreatingMission] = useState(false);
  const [downloadingMission, setDownloadingMission] = useState<string | null>(null);
  const [showMissionForm, setShowMissionForm] = useState(false);
  const [sendToDrone, setSendToDrone] = useState<{
    missionId: number; missionName: string; flightId: string;
    devices: MqttDevice[]; selectedSn: string | null;
    loading: boolean; sending: boolean; error: string | null;
  } | null>(null);

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [pendingIncidentCoords, setPendingIncidentCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [incidentDraft, setIncidentDraft] = useState<IncidentDraft>({
    type: 'Unknown', severity: 3, title: '', description: '', location_label: ''
  });
  const [submittingIncident, setSubmittingIncident] = useState(false);
  const [incidentNotes, setIncidentNotes] = useState<IncidentNote[]>([]);
  const [incidentLinkedDetails, setIncidentLinkedDetails] = useState<LinkedItemDetail[]>([]);
  const [newIncidentNote, setNewIncidentNote] = useState('');
  const [incidentNoteType, setIncidentNoteType] = useState<'note' | 'mission' | 'trial' | 'observation'>('note');
  const [savingIncidentNote, setSavingIncidentNote] = useState(false);
  const [editingIncidentNoteId, setEditingIncidentNoteId] = useState<number | null>(null);
  const [editIncidentNoteContent, setEditIncidentNoteContent] = useState('');
  const [uploadingIncidentAttachment, setUploadingIncidentAttachment] = useState(false);
  const incidentFileInputRef = useRef<HTMLInputElement>(null);
  const [showIncidentLinkPicker, setShowIncidentLinkPicker] = useState(false);

  const [taskSets, setTaskSets] = useState<TaskSet[]>([]);
  const [hiddenTaskSetIds, setHiddenTaskSetIds] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('toc_hiddenTaskSets');
      if (saved) return new Set(JSON.parse(saved) as number[]);
    } catch {}
    return new Set();
  });
  const [isCreatingTaskSet, setIsCreatingTaskSet] = useState(false);
  const [newTaskSetName, setNewTaskSetName] = useState('');
  const [selectedTaskSetId, setSelectedTaskSetId] = useState<number | undefined>(undefined);
  const [confirmingDeleteTaskSetId, setConfirmingDeleteTaskSetId] = useState<number | null>(null);
  const [renamingTaskSetId, setRenamingTaskSetId] = useState<number | null>(null);
  const [renamingTaskSetName, setRenamingTaskSetName] = useState('');

  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<{points: Set<number>; polygons: Set<number>; lines: Set<number>; sensors: Set<number>}>({points: new Set(), polygons: new Set(), lines: new Set(), sensors: new Set()});
  const bulkTotal = bulkSelected.points.size + bulkSelected.polygons.size + bulkSelected.lines.size + bulkSelected.sensors.size;

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('toc_collapsedSections');
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch {}
    return new Set(['vehicles', 'drawing', 'tasksets', 'layers', 'incidents', 'pt-marker', 'pt-notes', 'pt-dcim', 'pt-floorplans', 'pt-attachments', 'ln-notes', 'ln-dcim', 'ln-attachments', 'ln-mission', 'poly-notes', 'poly-attachments', 'sensor-notes', 'inc-notes', 'inc-attachments', 'inc-linked', 'inc-audit', 'ac-details', 'kaek-color']);
  });
  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      localStorage.setItem('toc_collapsedSections', JSON.stringify([...next]));
      return next;
    });
  };

  const [videoKey, setVideoKey] = useState(Date.now());
  const [feedToken, setFeedToken] = useState<string | null>(null);
  // These three start false and are corrected by the /api/drone/overlays poll
  // (see syncOverlayState). They are NOT the source of truth: the pipeline is
  // process-global, so a model armed at boot or by another operator is already
  // running before this dashboard renders a single frame.
  const [detectionEnabled, setDetectionEnabled] = useState(false);
  const [roadSegEnabled, setRoadSegEnabled] = useState(false);
  const [roadSegMode, setRoadSegMode] = useState<'blur' | 'overlay'>('blur');
  const [aiLayerBusy, setAiLayerBusy] = useState(false);
  const [aiLayerError, setAiLayerError] = useState<string | null>(null);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);
  // Lives here rather than in VideoPanel because the floating header's copy of
  // the AI layer menu has to be able to open it too.
  const [roadSegSettingsOpen, setRoadSegSettingsOpen] = useState(false);
  const [geoMaskEnabled, setGeoMaskEnabled] = useState(false);
  const [geoMaskMode, setGeoMaskMode] = useState<'blur' | 'outline'>('blur');
  const [geoMaskAltSource, setGeoMaskAltSource] = useState<'elevation' | 'height' | 'computed' | 'altitude'>('elevation');
  const [geoMaskYawOffset, setGeoMaskYawOffset] = useState(5);
  const [geoMaskCameraPreset, setGeoMaskCameraPreset] = useState<string | null>(null);
  const [geoMaskActivePreset, setGeoMaskActivePreset] = useState<string>('');
  const [geoMaskCameraLabel, setGeoMaskCameraLabel] = useState<string>('');
  const [geoMaskPitchOffset, setGeoMaskPitchOffset] = useState(0);
  const [geoMaskHeightM, setGeoMaskHeightMState] = useState<number | null>(null);
  const [geoMaskRefused, setGeoMaskRefused] = useState<string | null>(null);
  const [processedStreamCreds, setProcessedStreamCreds] = useState<ProcessedStreamCreds | null>(null);
  const [processedStreamRetry, setProcessedStreamRetry] = useState(0);

  // Hand-added sources are NOT restored here: they are scoped to the signed-in
  // account and its active group, and neither is known on the first render. The
  // effect next to the persist effect loads them once `useAuth` has an answer.
  const [streamSources, setStreamSources] = useState<Array<{id: string; name: string; url: string; type: 'dummy' | 'rtmp' | 'rtsp' | 'dji' | 'phone' | 'hls' | 'demo' | 'picam'}>>(() => ([
    { id: 'demo', name: 'DJI Demo Flight', url: '', type: 'demo' as const },
  ]));
  // Which account+group the hand-added entries in `streamSources` belong to.
  // `null` until the first load, and it is what stops the persist effect from
  // writing the outgoing group's list under the incoming group's key.
  const [loadedSourcesOwner, setLoadedSourcesOwner] = useState<string | null>(null);
  const [hlsCredentialsMap, setHlsCredentialsMap] = useState<Record<string, {hlsUrl: string; deviceSn: string; displayName?: string}>>({});
  const [activeSourceId, setActiveSourceId] = useState(NO_SOURCE_ID);
  const [djiStreamCredentials, setDjiStreamCredentials] = useState<{appId: string; channel: string; token: string; uid: number} | null>(null);
  const [djiStreamError, setDjiStreamError] = useState<string | null>(null);
  const [phoneSensorCredentialsMap, setPhoneSensorCredentialsMap] = useState<Record<string, {livekitUrl: string; token: string; sensorId: number}>>({});
  const [showPhoneSensorModal, setShowPhoneSensorModal] = useState(false);
  const [phoneSensorLocation, setPhoneSensorLocation] = useState<{lat: number; lng: number} | null>(null);
  const [reconnectSensorId, setReconnectSensorId] = useState<number | undefined>(undefined);
  const [sensorRecordings, setSensorRecordings] = useState<Recording[]>([]);
  const [playingRecording, setPlayingRecording] = useState<Recording | null>(null);
  const [sensorTrack, setSensorTrack] = useState<TrackPoint[]>([]);
  const [showStreamPanel, setShowStreamPanel] = useState(false);
  const [isAddingSource, setIsAddingSource] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [allowedFeedTypes, setAllowedFeedTypes] = useState<Set<string>>(new Set(['dummy', 'demo', 'rtmp', 'rtsp', 'dji', 'phone', 'hls']));

  const [videoFloating, setVideoFloating] = useState(false);
  const [floatingVideoPos, setFloatingVideoPos] = useState<{x: number; y: number; w: number; h: number}>(() => {
    try {
      const saved = localStorage.getItem('toc_floatingVideoPos');
      return saved ? JSON.parse(saved) : { x: 100, y: 100, w: 480, h: 360 };
    } catch { return { x: 100, y: 100, w: 480, h: 360 }; }
  });
  const [showMatrixView, setShowMatrixView] = useState(false);

  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [editSourceName, setEditSourceName] = useState('');
  const [editSourceUrl, setEditSourceUrl] = useState('');
  
  const [uploadingAttachment, setUploadingAttachment] = useState<{[key: string]: boolean}>({});
  const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);
  const [floorplanViewer, setFloorplanViewer] = useState<{ pointId: number; pointName: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pointNotes, setPointNotes] = useState<PointNote[]>([]);
  const [polygonNotes, setPolygonNotes] = useState<PointNote[]>([]);
  const [lineNotes, setLineNotes] = useState<PointNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const polygonFileInputRef = useRef<HTMLInputElement>(null);
  const lineFileInputRef = useRef<HTMLInputElement>(null);
  const csvImportRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [selectedMarkerIcon, setSelectedMarkerIcon] = useState<IconKey | ''>('');

  const dcimFolderRef = useRef<HTMLInputElement>(null);
  const [dcimImporting, setDcimImporting] = useState(false);
  const [dcimProgress, setDcimProgress] = useState<{phase: string; current: number; total: number} | null>(null);
  const [dcimTaskSetPicker, setDcimTaskSetPicker] = useState(false);
  const dcimTaskSetResolve = useRef<((val: number | null) => void) | null>(null);
  const [dcimPhotosForPoint, setDcimPhotosForPoint] = useState<DcimPhoto[]>([]);
  const [dcimVideosForLine, setDcimVideosForLine] = useState<DcimPhoto[]>([]);
  const [dcimViewingPhoto, setDcimViewingPhoto] = useState<DcimPhoto | null>(null);
  const [dcimVideoQuality, setDcimVideoQuality] = useState<Record<number, string>>({});
  const [dcimPanoramaView, setDcimPanoramaView] = useState(false);

  const [showAircraftLayer, setShowAircraftLayer] = useState(() => localStorage.getItem('toc_showAircraftLayer') === 'true');
  const [aircraftPositions, setAircraftPositions] = useState<AircraftPosition[]>([]);
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftPosition | null>(null);

  const [showVesselLayer, setShowVesselLayer] = useState(() => localStorage.getItem('toc_showVesselLayer') === 'true');
  // AIS runs only where an AISstream key is configured — production today. Everywhere
  // else the checkbox used to be offered anyway and ticking it fetched a 404 in silence,
  // so the layer is gated on the backend's own answer, like tank gauging above.
  const [aisEnabled, setAisEnabled] = useState(false);
  const [vesselPositions, setVesselPositions] = useState<VesselPosition[]>([]);
  const [selectedVessel, setSelectedVessel] = useState<VesselPosition | null>(null);
  const showVesselLayerRef = useRef(false);

  // connectWebSocket is a useCallback([]) — its handler closes over first-render
  // values, so the active group has to reach it through a ref rather than the
  // captured `user`. Kept in sync by the effect next to handleGroupChange.
  const scopeIdentityRef = useRef<ScopeIdentity>({ scope: 'group', groupId: null, userId: null });
  // The /ws/drone effect mounts once with an empty dep array, so anything it calls is
  // captured at mount and goes stale. These refs are how it reaches the CURRENT fetchers
  // — same reason scopeIdentityRef above exists.
  const fetchLinesRef = useRef<() => void>(() => {});
  const fetchMissionsRef = useRef<() => void>(() => {});

  const [showLiveCamLayer, setShowLiveCamLayer] = useState(() => localStorage.getItem('toc_showLiveCamLayer') === 'true');
  const [liveCameras, setLiveCameras] = useState<LiveCamera[]>([]);
  const [selectedLiveCam, setSelectedLiveCam] = useState<LiveCamera | null>(null);
  const [liveCamStreamUrl, setLiveCamStreamUrl] = useState<string | null>(null);
  const liveCamVideoRef = useRef<HTMLVideoElement | null>(null);
  const liveCamHlsRef = useRef<any>(null);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const [showMGRSGrid, setShowMGRSGrid] = useState(() => localStorage.getItem('toc_showMGRSGrid') === 'true');
  const [mgrsGridData, setMgrsGridData] = useState<GeoJSON.FeatureCollection>({ type: 'FeatureCollection', features: [] });
  const [cursorMGRS, setCursorMGRS] = useState<string>('');
  const [mgrsSearchQuery, setMgrsSearchQuery] = useState('');
  const [mgrsSearchError, setMgrsSearchError] = useState('');
  const [mgrsSearchResult, setMgrsSearchResult] = useState<{ lat: number; lng: number } | null>(null);

  const [weatherLayer, setWeatherLayer] = useState<string>(() => {
    const saved = localStorage.getItem('toc_weatherLayer');
    if (saved) return saved;
    if (localStorage.getItem('toc_showWeatherRadar') === 'true') return 'radar';
    return 'off';
  });
  const [weatherOpacity, setWeatherOpacity] = useState(() => {
    const saved = localStorage.getItem('toc_weatherOpacity') || localStorage.getItem('toc_weatherRadarOpacity');
    return saved ? parseFloat(saved) : 0.6;
  });
  const [weatherRadarTilePath, setWeatherRadarTilePath] = useState<string | null>(null);
  const [weatherRadarTimestamp, setWeatherRadarTimestamp] = useState<number | null>(null);
  const [owmApiKey, setOwmApiKey] = useState(() => localStorage.getItem('toc_owmApiKey') || '');
  const [weatherPoint, setWeatherPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [weatherConditions, setWeatherConditions] = useState<{
    lat: number; lng: number;
    temp: number; windSpeed: number; windDir: number; windGust: number;
    cloudCover: number; humidity: number; visibility: number; precipitation: number;
  } | null>(null);

  const [showKaekLayer, setShowKaekLayer] = useState(() => localStorage.getItem('toc_showKaekLayer') !== 'false');
  const [kaekParcels, setKaekParcels] = useState<KaekParcel[]>([]);
  const [selectedKaekParcel, setSelectedKaekParcel] = useState<KaekParcel | null>(null);
  const [showUspaceLayer, setShowUspaceLayer] = useState(() => localStorage.getItem('toc_showUspaceLayer') !== 'false');
  const [geozones, setGeozones] = useState<GeoZone[]>([]);
  const [geozonePopup, setGeozonePopup] = useState<{ lng: number; lat: number; zone: GeoZone } | null>(null);
  const [authorisations, setAuthorisations] = useState<GeoAuthorisation[]>([]);
  const [requestingAuthMissionId, setRequestingAuthMissionId] = useState<number | null>(null);
  const [operationalIntents, setOperationalIntents] = useState<OperationalIntent[]>([]);
  const [ridFlights, setRidFlights] = useState<RidFlight[]>([]);
  const [kaekLookupCode, setKaekLookupCode] = useState('');
  const [kaekLookupLoading, setKaekLookupLoading] = useState(false);
  const [kaekPreview, setKaekPreview] = useState<any>(null);
  const [kaekImporting, setKaekImporting] = useState(false);
  const [kaekAnalysis, setKaekAnalysis] = useState<KaekAnalysis | null>(null);
  const [kaekAnalyzing, setKaekAnalyzing] = useState(false);
  const [kaekLinking, setKaekLinking] = useState(false);
  const [kaekMatchTaskSet, setKaekMatchTaskSet] = useState<number | null>(null);

  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('toc_leftPanelWidth');
    return saved ? parseInt(saved, 10) : 250;
  });
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    const saved = localStorage.getItem('toc_rightPanelWidth');
    return saved ? parseInt(saved, 10) : 360;
  });
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);
  // On a narrow screen both side panels are drawers over a full-width map (App.css,
  // "Narrow layout"), so they open closed and a toggle there is not persisted — it would
  // otherwise collapse the desktop layout on the next wide-screen visit.
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(() => isPhoneLayout() || localStorage.getItem('toc_leftPanelCollapsed') === 'true');
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(() => isPhoneLayout() || localStorage.getItem('toc_rightPanelCollapsed') === 'true');
  // A window resized INTO the narrow layout would otherwise keep both desktop panels
  // open as two drawers stacked over the whole map. Not persisted, same as a toggle.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(PHONE_LAYOUT_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) { setLeftPanelCollapsed(true); setRightPanelCollapsed(true); }
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  
  const [dronePositions, setDronePositions] = useState<Record<string, DronePosition>>({});
  const [selectedDroneId, setSelectedDroneId] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const targetPositionsRef = useRef<Record<string, { latitude: number; longitude: number }>>({});
  const animationFrameRef = useRef<number | null>(null);
  const selectedDrone = selectedDroneId ? dronePositions[selectedDroneId] ?? null : null;
  const connectedDroneCount = Object.values(dronePositions).filter(d => d.connected).length;

  const mapRef = useRef(null);
  const leftPaneRef = useRef<HTMLElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const scrollRestored = useRef(false);

  useEffect(() => {
    if (scrollRestored.current) return;
    const left = leftPaneRef.current;
    const right = rightPaneRef.current;
    if (!left || left.scrollHeight <= left.clientHeight) return;
    scrollRestored.current = true;
    const savedLeft = localStorage.getItem('toc_leftPaneScroll');
    const savedRight = localStorage.getItem('toc_rightPaneScroll');
    if (savedLeft) left.scrollTop = parseInt(savedLeft, 10);
    if (right && savedRight) right.scrollTop = parseInt(savedRight, 10);
  }, [points, taskSets, incidents]);

  useEffect(() => {
    const left = leftPaneRef.current;
    const right = rightPaneRef.current;

    let leftTimer: ReturnType<typeof setTimeout>;
    let rightTimer: ReturnType<typeof setTimeout>;
    const onLeftScroll = () => {
      clearTimeout(leftTimer);
      leftTimer = setTimeout(() => {
        localStorage.setItem('toc_leftPaneScroll', String(left!.scrollTop));
      }, 200);
    };
    const onRightScroll = () => {
      clearTimeout(rightTimer);
      rightTimer = setTimeout(() => {
        localStorage.setItem('toc_rightPaneScroll', String(right!.scrollTop));
      }, 200);
    };
    left?.addEventListener('scroll', onLeftScroll);
    right?.addEventListener('scroll', onRightScroll);
    return () => {
      left?.removeEventListener('scroll', onLeftScroll);
      right?.removeEventListener('scroll', onRightScroll);
      clearTimeout(leftTimer);
      clearTimeout(rightTimer);
    };
  }, []);

  useEffect(() => {
    localStorage.removeItem('toc_mqttStreaming');
    localStorage.removeItem('toc_mqttFeedUrl');
  }, []);

  const activeStreamSource = streamSources.find(s => s.id === activeSourceId);

  const activateSource = useCallback(async (source: typeof streamSources[0]) => {
    if (isSwitching) return;

    setIsSwitching(true);
    setDjiStreamError(null);
    try {
      if (source.type === 'phone') {
        const sensorId = parseInt(source.id.replace('phone-', ''), 10);
        const data = await getViewerToken(sensorId, getAuthHeaders);
        setDjiStreamCredentials(null);
        setPhoneSensorCredentialsMap(prev => ({ ...prev, [source.id]: { livekitUrl: data.livekit_url, token: data.livekit_token, sensorId } }));
        setActiveSourceId(source.id);
      } else if (source.type === 'hls') {
        setDjiStreamCredentials(null);
        setActiveSourceId(source.id);
      } else {
        setDjiStreamCredentials(null);

        if (source.type === 'dummy' || source.type === 'picam') {
          // picam: the backend relays the aircraft Pi's own MJPEG; there is no
          // server-side reader to start (docs/PICAM_GUIDANCE.md).
          setActiveSourceId(source.id);
          setVideoKey(Date.now());
        } else {
          const config = videoSourceBody(source.type, source.url);

          const response = await fetch(`${API_URL}/api/drone/video-source`, {
            method: 'POST',
            headers: getAuthHeaders(true),
            body: JSON.stringify(config)
          });

          if (response.ok) {
            setActiveSourceId(source.id);
            await new Promise(resolve => setTimeout(resolve, 500));
            setVideoKey(Date.now());
          } else if (response.status === 429) {
            // Show the server's own reason. It names the AI channels pinning
            // readers, which is the difference between a dead end and an
            // action: persisted channels can hold every slot before a browser
            // is open, and 'close unused sources first' then asks the operator
            // to close sources they never opened.
            const detail = await response.json()
              .then((b: any) => b?.detail)
              .catch(() => null);
            toast(detail || 'Too many active streams — close unused sources first', 'warning');
          } else if (response.status === 400) {
            toast(`${PULL_URL_HINT}, and resolve to a host this deployment may reach`, 'error');
          } else {
            console.error('Failed to switch source:', await response.text());
            toast('Failed to switch stream source', 'error');
          }
        }
      }
    } catch (error) {
      console.error('Error switching source:', error);
      toast('Error switching stream source', 'error');
    } finally {
      setIsSwitching(false);
    }
  }, [isSwitching, toast]);

  const terminateStream = useCallback(async () => {
    const source = activeStreamSource;
    if (!source || (!isPullSource(source.type) && source.type !== 'demo')) return;
    // Switch the viewer off the stream first so the MJPEG connection closes
    setActiveSourceId(NO_SOURCE_ID);
    setVideoKey(Date.now());
    try {
      // `force` is what makes END STREAM actually end the stream. Without it the
      // backend refuses any feed an AI channel has a model on, and used to do so
      // with a 200 — so this branch reported success while the reader stayed up
      // and went on spending from MAX_STREAM_READERS. A Matrix tile close still
      // sends no force: closing one of nine tiles must not unassign a model.
      const config = videoSourceBody(source.type, source.url, { force: true });

      const response = await fetch(`${API_URL}/api/drone/video-source/stop`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(config)
      });

      if (response.ok) {
        const body = await response.json().catch(() => ({} as any));
        // Say what the override cost. Clearing a channel also stops every zone
        // bound to it by name, and that has no other symptom than the incident
        // board quietly going still.
        const cleared = body?.cleared_channel
          ? ` — model ${body.cleared_channel} cleared`
            + (body.cleared_zone_count
                ? `, ${body.cleared_zone_count} zone(s) stopped observing`
                : '')
          : '';
        toast(`Stream terminated — ${source.name}${cleared}`, 'success');
      } else {
        const detail = await response.text();
        console.error('Failed to terminate stream:', detail);
        toast('Failed to terminate stream on server', 'error');
      }
    } catch (error) {
      console.error('Error terminating stream:', error);
      toast('Failed to terminate stream on server', 'error');
    }
  }, [activeStreamSource, toast]);

  /**
   * Mirror a detection state change that the backend has already applied.
   * Activating a marketplace model calls set_detection_state() server-side, so
   * posting to /api/drone/detection here would just fight it.
   */
  const syncDetectionState = useCallback((enabled: boolean) => {
    setDetectionEnabled(enabled);
    if (enabled) setRoadSegEnabled(false);
  }, []);

  /** Adopt what GET /api/drone/overlays says is actually running. */
  const syncOverlayState = useCallback((s: OverlayState) => {
    setDetectionEnabled(s.detection_enabled);
    setRoadSegEnabled(s.road_seg_enabled);
    setRoadSegMode(s.road_seg_mode);
    setGeoMaskEnabled(s.geo_mask_enabled);
  }, []);

  /**
   * The feed the AI LAYER popover is describing — the video panel's own source.
   *
   * Same shape MatrixView builds for its rows, `deviceSn` included: an aircraft
   * is named by SERIAL because its tile holds an `.m3u8` that matches no stream
   * key, while the channel holds the RTMP URL the backend reads.
   */
  const aiLayerFeed = useMemo<FeedSource | null>(() => {
    if (!activeStreamSource) return null;
    return {
      id: activeStreamSource.id,
      name: activeStreamSource.name,
      type: activeStreamSource.type,
      url: activeStreamSource.url,
      deviceSn: hlsCredentialsMap[activeStreamSource.id]?.deviceSn,
    };
  }, [activeStreamSource, hlsCredentialsMap]);

  const aiLayers = useAiLayers(API_URL, getAuthHeaders, {
    // The Matrix grid renders VideoRenderer tiles of its own, and they read the
    // DJI transport policy out of this reading — so polling only while the docked
    // panel is open left every Matrix tile on HLS with nothing saying why.
    enabled: isAuthenticated && (showVideo || showMatrixView),
    busy: aiLayerBusy,
    onState: syncOverlayState,
    feed: aiLayerFeed,
  });
  const { active: aiLayerActive, refresh: refreshAiLayers } = aiLayers;
  /**
   * Whether a DJI tile leaves its HLS player for the server-composed picture.
   *
   * Deployment policy (`DJI_LOW_LATENCY`), read from the server rather than
   * assumed: a tile holds an `.m3u8` and nothing about that URL says whether
   * this box would rather serve the same aircraft from the reader it is already
   * running on its RTMP. Undefined until the first poll lands, which reads as
   * off — so a tile plays HLS immediately and swaps once, rather than swapping
   * off and back on page load.
   */
  const djiLowLatency = !!aiLayers.state?.dji_low_latency;
  const aiLayerChannel = aiLayers.channel;

  /**
   * Switch the feed's AI layer. One layer runs at a time — see aiLayers.ts.
   *
   * Standing the current layer down BEFORE bringing the next one up is
   * load-bearing on a combined-inference server (the demo box): there
   * `set_road_seg` does not clear detection, so enabling road seg on top of a
   * running detector would leave both going while the radio showed one.
   * Everywhere else the backend enforces exclusivity both ways and step 1 is
   * merely tidy.
   *
   * Step 1 has a second job since 2026-09-01: standing down a per-feed CHANNEL,
   * which no primary endpoint can reach. Only the clear is routed that way —
   * bringing the next layer up still goes through the primary pipeline, as it
   * always has. Assigning through the channel API instead would arm
   * `apply_primary` AND leave `ChannelManager` running a second runtime on the
   * same feed, two models where the radio shows one.
   */
  const selectAiLayer = useCallback(async (next: LayerId) => {
    if (aiLayerBusy || next === aiLayerActive) return;
    const current = aiLayerActive;
    setAiLayerBusy(true);
    setAiLayerError(null);

    const post = async (path: string, body: unknown) => {
      const res = await fetch(`${API_URL}${path}`, {
        method: 'POST', headers: getAuthHeaders(true), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || `Request failed (${res.status})`);
      }
    };

    try {
      // A channel on this feed is not reachable from the primary endpoints, and
      // that asymmetry was the bug: the operator saw a model, selected Off, the
      // deactivate returned 200, and the channel thread kept compositing. Clear
      // it FIRST — which also stands the primary down when this is the
      // processing feed — and let the rest of the switch run as it always has,
      // so the popover keeps driving one pipeline rather than arming a second
      // model on a feed that already has one.
      if (aiLayerChannel && aiLayerFeed) {
        await post('/api/drone/ai-channels', assignBody(aiLayerFeed, channelValue(OFF)));
      } else if (current === ROAD_SEG) {
        await post('/api/drone/road-seg', { enabled: false, mode: roadSegMode });
      } else if (current === PLAIN_DETECT) {
        await post('/api/drone/detection', { enabled: false });
      } else if (current !== OFF) {
        await deactivateModel(getAuthHeaders);
      }

      if (next === ROAD_SEG) {
        await post('/api/drone/road-seg', { enabled: true, mode: roadSegMode });
        // Road seg reroutes the feed through the processed stream — remount it.
        setVideoKey(Date.now());
      } else if (next === PLAIN_DETECT) {
        await post('/api/drone/detection', { enabled: true });
      } else if (CAMERA_ONLY_MODELS.has(next) && aiLayerFeed) {
        // ANPR reads its own full-resolution frames, so it runs as this camera's
        // channel — the same one the Matrix row assigns — never on the primary.
        await post('/api/drone/ai-channels', assignBody(aiLayerFeed, channelValue(next)));
      } else if (next !== OFF) {
        await activateModel(getAuthHeaders, next);
      }
    } catch (e) {
      setAiLayerError(e instanceof Error ? e.message : 'Failed to switch AI layer');
    } finally {
      // Whatever happened, including a half-applied switch, the server is the
      // authority on what is running now — never guess from what we asked for.
      await refreshAiLayers();
      setAiLayerBusy(false);
    }
  }, [aiLayerBusy, aiLayerActive, aiLayerChannel, aiLayerFeed, roadSegMode, refreshAiLayers]);

  /**
   * What the watched feed's picture shows to viewers — see feed_privacy.py.
   *
   * Any loosening (data_only -> blur -> picture) puts more of the scene on
   * every viewer's screen, so that direction asks first; tightening is silent. The server is the authority afterwards, as
   * with the AI layer: whatever the PUT did, re-read rather than assume.
   */
  const privacyMode = feedPrivacy(aiLayers.state, aiLayerFeed);
  const privacyTarget = useMemo(() => (aiLayerFeed ? feedTarget(aiLayerFeed) : null), [aiLayerFeed]);
  const privacyUnavailable = privacyUnavailableReason(aiLayerFeed);
  const selectPrivacyMode = useCallback(async (next: PrivacyMode) => {
    if (privacyBusy || next === privacyMode || privacyUnavailable || !privacyTarget) return;
    if (isLoosening(privacyMode, next) && !window.confirm(next === 'picture'
      ? 'Show full video to every viewer of this feed?'
      : 'Show blurred video to every viewer of this feed?')) return;
    setPrivacyBusy(true);
    setPrivacyError(null);
    try {
      const res = await fetch(`${API_URL}/api/drone/privacy`, {
        method: 'PUT', headers: getAuthHeaders(true),
        body: JSON.stringify({ mode: next, ...privacyTarget }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(typeof detail?.detail === 'string' ? detail.detail : `Request failed (${res.status})`);
      }
    } catch (e) {
      setPrivacyError(e instanceof Error ? e.message : 'Failed to change privacy mode');
    } finally {
      await refreshAiLayers();
      setPrivacyBusy(false);
    }
  }, [privacyBusy, privacyMode, privacyUnavailable, privacyTarget, refreshAiLayers]);

  const switchRoadSegMode = useCallback(async () => {
    const newMode = roadSegMode === 'blur' ? 'overlay' : 'blur';
    try {
      const response = await fetch(`${API_URL}/api/drone/road-seg`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ enabled: true, mode: newMode })
      });

      if (response.ok) {
        setRoadSegMode(newMode);
      } else {
        console.error('Failed to switch road seg mode:', await response.text());
      }
    } catch (error) {
      console.error('Error switching road seg mode:', error);
    }
  }, [roadSegMode]);

  // The geo mask's settings are process-global on the server; every response
  // (POST or the poll below) carries all of them, so this is the one writer.
  const _updateGeoMaskFromResponse = useCallback((data: Record<string, unknown>) => {
    if (data.active_preset) setGeoMaskActivePreset(data.active_preset as string);
    if (data.camera_label) setGeoMaskCameraLabel(data.camera_label as string);
    if (data.camera_preset !== undefined) setGeoMaskCameraPreset(data.camera_preset as string | null);
    if (data.geo_mask_mode === 'blur' || data.geo_mask_mode === 'outline') setGeoMaskMode(data.geo_mask_mode);
    if (typeof data.alt_source === 'string') {
      setGeoMaskAltSource(data.alt_source as 'elevation' | 'height' | 'computed' | 'altitude');
    }
    if (typeof data.yaw_offset === 'number') setGeoMaskYawOffset(data.yaw_offset);
    if (typeof data.pitch_offset === 'number') setGeoMaskPitchOffset(data.pitch_offset);
    if (data.height_m !== undefined) setGeoMaskHeightMState((data.height_m as number | null) ?? null);
    if (data.refused !== undefined) setGeoMaskRefused((data.refused as string | null) ?? null);
  }, []);

  const postGeoMask = useCallback(async (body: Record<string, unknown>) => {
    try {
      const response = await fetch(`${API_URL}/api/drone/geo-mask`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ enabled: true, ...body })
      });
      if (response.ok) {
        _updateGeoMaskFromResponse(await response.json());
      } else {
        toast(`Geo mask: ${await response.text()}`, 'error');
      }
    } catch (error) {
      console.error('Error updating geo mask:', error);
    }
  }, [toast, _updateGeoMaskFromResponse]);

  const setGeoMaskHeight = useCallback((heightM: number | null) => {
    void postGeoMask({ height_m: heightM ?? 0 });
  }, [postGeoMask]);

  const adjustPitchOffset = useCallback((delta: number) => {
    const next = Math.round(Math.max(-10, Math.min(10, geoMaskPitchOffset + delta)) * 10) / 10;
    void postGeoMask({ pitch_offset: next });
  }, [geoMaskPitchOffset, postGeoMask]);

  // While the mask is on, read the server's view: the camera it detected and
  // whether it refused to project (a parked aircraft with no height set).
  useEffect(() => {
    if (!geoMaskEnabled || !isAuthenticated) {
      setGeoMaskRefused(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/drone/geo-mask`, { headers: getAuthHeaders() });
        if (response.ok && !cancelled) _updateGeoMaskFromResponse(await response.json());
      } catch {
        // transient — the next tick retries
      }
    };
    void poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [geoMaskEnabled, isAuthenticated, _updateGeoMaskFromResponse]);

  const toggleGeoMask = useCallback(async () => {
    const newState = !geoMaskEnabled;
    // The mask projects through the PROCESSING feed, and selecting an aircraft's
    // tile does not make it that feed. Naming the aircraft being watched lets the
    // server move the feed there; without it, the mask follows whatever an
    // operator last selected — the demo clip, say — and shows nothing.
    const deviceSn = activeStreamSource ? hlsCredentialsMap[activeStreamSource.id]?.deviceSn : undefined;
    try {
      const response = await fetch(`${API_URL}/api/drone/geo-mask`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(newState && deviceSn
          ? { enabled: newState, device_sn: deviceSn }
          : { enabled: newState })
      });
      if (response.ok) {
        setGeoMaskEnabled(newState);
        const data = await response.json();
        _updateGeoMaskFromResponse(data);
      } else {
        console.error('Failed to toggle geo mask:', await response.text());
        toast('Failed to toggle geo mask', 'error');
      }
    } catch (error) {
      console.error('Error toggling geo mask:', error);
      toast('Error toggling geo mask', 'error');
    }
  }, [geoMaskEnabled, activeStreamSource, hlsCredentialsMap, toast, _updateGeoMaskFromResponse]);

  const switchGeoMaskMode = useCallback(async () => {
    const newMode = geoMaskMode === 'blur' ? 'outline' : 'blur';
    try {
      const response = await fetch(`${API_URL}/api/drone/geo-mask`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ enabled: true, mode: newMode })
      });
      if (response.ok) {
        setGeoMaskMode(newMode);
      }
    } catch (error) {
      console.error('Error switching geo mask mode:', error);
    }
  }, [geoMaskMode]);

  const cycleGeoMaskAlt = useCallback(async () => {
    const order: Array<'elevation' | 'height' | 'computed' | 'altitude'> = ['elevation', 'height', 'computed', 'altitude'];
    const next = order[(order.indexOf(geoMaskAltSource) + 1) % order.length];
    try {
      const response = await fetch(`${API_URL}/api/drone/geo-mask`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ enabled: true, alt_source: next })
      });
      if (response.ok) {
        setGeoMaskAltSource(next);
      }
    } catch (error) {
      console.error('Error cycling geo mask alt source:', error);
    }
  }, [geoMaskAltSource]);

  const adjustYawOffset = useCallback(async (delta: number) => {
    const next = Math.max(-180, Math.min(180, geoMaskYawOffset + delta));
    try {
      const response = await fetch(`${API_URL}/api/drone/geo-mask`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ enabled: true, yaw_offset: next })
      });
      if (response.ok) {
        setGeoMaskYawOffset(next);
      }
    } catch (error) {
      console.error('Error adjusting yaw offset:', error);
    }
  }, [geoMaskYawOffset]);

  // A hand-added source belongs to the account and group it was added in, so
  // switching either swaps the list. These two effects are a pair and the order
  // matters: the loader runs first and moves `loadedSourcesOwner` forward, and
  // the writer refuses to run until the two agree again — otherwise the commit
  // in which the group changed would write the *outgoing* group's sources under
  // the incoming group's key, which is the leak this replaces.
  const sourcesOwner = sourcesKey(user?.id, user?.group_id);

  const streamSourcesRef = useRef(streamSources);
  useEffect(() => { streamSourcesRef.current = streamSources; }, [streamSources]);

  const activeSourceIdRef = useRef(activeSourceId);
  useEffect(() => { activeSourceIdRef.current = activeSourceId; }, [activeSourceId]);

  useEffect(() => {
    if (loadedSourcesOwner === sourcesOwner) return;
    let cancelled = false;
    void (async () => {
        // The list comes from the server now, so nothing can be read until this resolves.
        // The store answers null rather than [] until then, deliberately: an empty answer
        // here would be persisted straight back over the real list by the writer effect.
        await hydrateSources(sourceStore, user?.id, user?.group_id);
        if (cancelled) return;
        const custom = readSources(sourceStore, user?.id, user?.group_id);
        const keep = new Set(custom.map(s => s.id));
        setStreamSources(prev => [...prev.filter(s => !isUserAddedSource(s)), ...custom]);
        setLoadedSourcesOwner(sourcesOwner);
        // Watching a feed the incoming group cannot see is worse than watching
        // nothing: the tile keeps playing and the source list no longer names it.
        // Narrow on purpose — only a hand-added source that this swap removed, so
        // the asynchronously-added phone/HLS ids are never touched here.
        const active = activeSourceIdRef.current;
        if (active !== NO_SOURCE_ID && !keep.has(active)
            && streamSourcesRef.current.some(s => s.id === active && isUserAddedSource(s))) {
          setActiveSourceId(NO_SOURCE_ID);
          setVideoKey(Date.now());
        }
    })();
    // A group switched again mid-load must not let the outgoing group's list land.
    return () => { cancelled = true; };
  }, [sourcesOwner, loadedSourcesOwner, user?.id, user?.group_id]);

  useEffect(() => {
    if (loadedSourcesOwner !== sourcesOwner) return;
    writeSources(sourceStore, user?.id, user?.group_id, streamSources);
  }, [streamSources, loadedSourcesOwner, sourcesOwner, user?.id, user?.group_id]);

  const addStreamSource = useCallback(() => {
    if (!newSourceName.trim() || !newSourceUrl.trim()) {
      toast('Please enter both name and URL', 'warning');
      return;
    }

    const url = newSourceUrl.trim();
    if (!isPullUrl(url)) {
      toast(PULL_URL_HINT, 'warning');
      return;
    }
    const newSource = {
      id: Date.now().toString(),
      name: newSourceName.trim(),
      url,
      type: pullTypeOf(url),
    };

    setStreamSources(prev => [...prev, newSource]);
    setNewSourceName('');
    setNewSourceUrl('');
    setIsAddingSource(false);

    activateSource(newSource);
  }, [newSourceName, newSourceUrl, activateSource, toast]);

  const deleteStreamSource = useCallback((id: string) => {
    setStreamSources(prev => prev.filter(s => s.id !== id));

    // Fall back to no source rather than auto-starting whichever stream is
    // first in the list — deleting one is not a request to play another.
    if (activeSourceId === id) {
      setActiveSourceId(NO_SOURCE_ID);
      setVideoKey(Date.now());
    }
  }, [activeSourceId]);

  const startEditingSource = useCallback((source: typeof streamSources[0]) => {
    setEditingSourceId(source.id);
    setEditSourceName(source.name);
    setEditSourceUrl(source.url);
  }, []);

  const cancelEditingSource = useCallback(() => {
    setEditingSourceId(null);
    setEditSourceName('');
    setEditSourceUrl('');
  }, []);

  const saveEditedSource = useCallback(() => {
    if (!editingSourceId || !editSourceName.trim()) {
      toast('Please enter a name', 'warning');
      return;
    }

    const url = editSourceUrl.trim();
    if (url && !isPullUrl(url)) {
      toast(PULL_URL_HINT, 'warning');
      return;
    }
    const type: 'dummy' | PullType = url ? pullTypeOf(url) : 'dummy';

    setStreamSources(prev => prev.map(s =>
      s.id === editingSourceId
        ? { ...s, name: editSourceName.trim(), url, type }
        : s
    ));

    const updatedSource = {
      id: editingSourceId,
      name: editSourceName.trim(),
      url,
      type
    };
    
    setEditingSourceId(null);
    setEditSourceName('');
    setEditSourceUrl('');
    
    if (activeSourceId === editingSourceId) {
      activateSource(updatedSource);
    }
  }, [editingSourceId, editSourceName, editSourceUrl, activeSourceId, activateSource, toast]);

  const handleLeftResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingLeft(true);
    document.body.classList.add('resizing');
  }, []);

  const handleRightResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingRight(true);
    document.body.classList.add('resizing');
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const minMain = 200;
    const handleWidth = 8;
    if (isDraggingLeft) {
      const newWidth = e.clientX;
      const maxLeft = window.innerWidth - rightPanelWidth - handleWidth - minMain;
      if (newWidth >= 200 && newWidth <= Math.min(600, maxLeft)) {
        setLeftPanelWidth(newWidth);
      }
    } else if (isDraggingRight) {
      const newWidth = window.innerWidth - e.clientX;
      const maxRight = window.innerWidth - leftPanelWidth - handleWidth - minMain;
      if (newWidth >= 300 && newWidth <= Math.min(800, maxRight)) {
        setRightPanelWidth(newWidth);
      }
    }
  }, [isDraggingLeft, isDraggingRight, leftPanelWidth, rightPanelWidth]);

  const handleMouseUp = useCallback(() => {
    setIsDraggingLeft(false);
    setIsDraggingRight(false);
    document.body.classList.remove('resizing');
    setLeftPanelWidth(w => { localStorage.setItem('toc_leftPanelWidth', String(w)); return w; });
    setRightPanelWidth(w => { localStorage.setItem('toc_rightPanelWidth', String(w)); return w; });
  }, []);

  useEffect(() => {
    if (isDraggingLeft || isDraggingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingLeft, isDraggingRight, handleMouseMove, handleMouseUp]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/drone/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch {
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const wsUrl = withSessionToken(API_URL.replace('http://', 'ws://').replace('https://', 'wss://') + '/ws/drone');
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {};

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'telemetry') {
          const droneId: string = data.drone_id || 'unknown';
          targetPositionsRef.current[droneId] = {
            latitude: data.position.lat,
            longitude: data.position.lng,
          };
          setDronePositions(prev => ({
            ...prev,
            [droneId]: {
              ...(prev[droneId] || { latitude: data.position.lat, longitude: data.position.lng }),
              altitude: data.position.alt,
              // A MAVLink aircraft with no battery monitor reports none; the panel
              // formats this number, so keep the last one rather than crash on null.
              battery: typeof data.battery === 'number' ? data.battery : (prev[droneId]?.battery ?? 0),
              heading: data.heading,
              speed: data.speed,
              connected: true,
              gimbal: data.gimbal ?? prev[droneId]?.gimbal ?? null,
              lastSeen: Date.now(),
              mode: data.mode ?? prev[droneId]?.mode ?? null,
              armed: typeof data.armed === 'boolean' ? data.armed : (prev[droneId]?.armed ?? null),
            },
          }));
          setSelectedDroneId(prev => prev ?? droneId);
        } else if (data.type === 'mission_progress') {
          setActiveMission(prev => {
            if (!prev || prev.id !== data.mission_id) return prev;
            return {
              ...prev,
              status: data.status,
              current_waypoint_index: data.current_waypoint_index,
              progress_percent: data.progress_percent,
            };
          });
        } else if (data.type === 'incident_alert' || data.type === 'incident_updated') {
          // /ws/drone is one ungrouped broadcast channel, so this fires for every
          // group's incidents. Without the scope check the previous group repaints
          // itself onto the map moments after switching.
          if (!belongsToScope(data.incident || {}, scopeIdentityRef.current)) return;
          setIncidents(prev => {
            const idx = prev.findIndex(i => i.id === data.incident.id);
            if (idx === -1) return [data.incident, ...prev];
            const next = [...prev];
            next[idx] = data.incident;
            return next;
          });
          if (data.type === 'incident_alert') {
            setCollapsedSections(prev => {
              const next = new Set(prev);
              next.delete('incidents');
              return next;
            });
          }
        } else if (data.type === 'conformance_breach') {
          toast(`U-space breach: ${data.authorisation_name} — ${data.breach_type.toUpperCase()} (${data.detail})`, 'error');
          fetchAuthorisations();
        } else if (data.type === 'conformance_cleared') {
          toast(`U-space conformance restored: ${data.authorisation_name} — ${data.breach_type}`, 'success');
        } else if (data.type === 'rid_update') {
          setRidFlights(data.flights || []);
        } else if (data.type === 'vessel_update') {
          if (showVesselLayerRef.current) {
            setVesselPositions(data.vessels || []);
          }
        } else if (data.type === 'sensor_location_update') {
          setSensors(prev => prev.map(s =>
            s.id === data.sensor_id
              ? { ...s, latitude: data.latitude, longitude: data.longitude, status: 'active' as const }
              : s
          ));
          setSensorTrack(prev => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            if (last.lat === data.latitude && last.lng === data.longitude) return prev;
            return [...prev, { lat: data.latitude, lng: data.longitude, accuracy: data.accuracy ?? null, altitude: data.altitude ?? null, heading: data.heading ?? null, speed: data.speed ?? null, battery: data.battery ?? null, recorded_at: data.timestamp }];
          });
        } else if (data.type === 'mmwave_state') {
          // Matches only sensors already in this user's list, which is scope-filtered.
          // M3: a broadcast for a sensor outside this group's list returns `prev`
          // unchanged, so it does not re-render every dashboard on every heartbeat.
          const msg = data as MmwaveState;
          setSensors(prev => (prev.some(s => s.id === msg.sensor_id)
            ? prev.map(s => (s.id === msg.sensor_id ? applyMmwaveState(s, msg) : s))
            : prev));
          setSelectedSensor(prev => (prev && prev.id === msg.sensor_id ? applyMmwaveState(prev, msg) : prev));
        } else if (data.type === 'phone_sensor_connected') {
          setShowPhoneSensorModal(false);
          setSelectedSensor(prev => prev && prev.id === data.sensor_id ? { ...prev, status: 'active' as const } : prev);
          fetchSensors();
          const sensorId = data.sensor_id;
          const phoneId = `phone-${sensorId}`;
          setStreamSources(prev => {
            if (prev.some(s => s.id === phoneId)) return prev;
            return [...prev, { id: phoneId, name: `PHONECAM ${sensorId}`, url: '', type: 'phone' as const }];
          });
          getViewerToken(sensorId, getAuthHeaders).then(tokenData => {
            setPhoneSensorCredentialsMap(prev => ({ ...prev, [phoneId]: { livekitUrl: tokenData.livekit_url, token: tokenData.livekit_token, sensorId } }));
            setDjiStreamCredentials(null);
            setActiveSourceId(phoneId);
          }).catch(() => {});
        } else if (data.type === 'phone_sensor_disconnected') {
          setSensors(prev => prev.map(s =>
            s.id === data.sensor_id ? { ...s, status: 'inactive' as const } : s
          ));
          setSelectedSensor(prev => prev && prev.id === data.sensor_id ? { ...prev, status: 'inactive' as const } : prev);
          const phoneId = `phone-${data.sensor_id}`;
          setStreamSources(prev => prev.filter(s => s.id !== phoneId));
          setActiveSourceId(prev => prev === phoneId ? NO_SOURCE_ID : prev);
          setPhoneSensorCredentialsMap(prev => {
            const next = { ...prev };
            delete next[phoneId];
            return next;
          });
        } else if (data.type === 'livestream_started' && data.device_sn) {
          const hlsUrl = data.hls_url || `/hls/${data.device_sn}.m3u8`;
          const sourceId = `hls-${data.device_sn}`;
          const label = data.display_name ? `${data.display_name} Live` : `${data.device_sn.slice(-4)} Live`;
          setStreamSources(prev => {
            if (prev.find(s => s.id === sourceId)) return prev;
            return [...prev, { id: sourceId, name: label, url: hlsUrl, type: 'hls' as const }];
          });
          setHlsCredentialsMap(prev => ({ ...prev, [sourceId]: { hlsUrl, deviceSn: data.device_sn, displayName: data.display_name } }));
        } else if (data.type === 'livestream_stopped' && data.device_sn) {
          const sourceId = `hls-${data.device_sn}`;
          setStreamSources(prev => prev.filter(s => s.id !== sourceId));
          setHlsCredentialsMap(prev => { const next = { ...prev }; delete next[sourceId]; return next; });
          setActiveSourceId(prev => prev === sourceId ? NO_SOURCE_ID : prev);
        } else if (data.type === 'surveillance_zone_state' || data.type === 'surveillance_dispatch' || data.type === 'surveillance_dispatch_update') {
          window.dispatchEvent(new CustomEvent('surveillance-ws', { detail: data }));
          // The action engine writes the intercept route (map_lines) and its mission at
          // the `dispatched` step — AFTER the incident_alert that opened the panel. Those
          // rows are never broadcast: /ws/drone has no per-group routing, so pushing map
          // items down it would leak them across groups (CLAUDE.md, Map Assistant). The
          // group-scoped REST refetch is the safe way to learn about them.
          //
          // Without this the route existed server-side and the map never knew: it only
          // appeared on a page reload, and until then clicking where it should be hit
          // whatever was underneath. The mission lookup behind a line click
          // (missions.find(m => m.line_id === ...)) missed for the same reason.
          //
          // Keyed on `dispatched` specifically, not merely on mission_id being present:
          // enroute and on_scene carry it too, and refetching on each would be three
          // round trips for one route. create_mission runs BEFORE advance_dispatch
          // broadcasts, so the rows are committed by the time this arrives.
          const disp = data.dispatch;
          if (disp && disp.mission_id && disp.status === 'dispatched'
              && belongsToScope(disp, scopeIdentityRef.current)) {
            fetchLinesRef.current();
            fetchMissionsRef.current();
          }
        } else if (data.type === 'field_unit_update') {
          setUnits(prev => prev.map(u =>
            u.id === data.unit_id
              ? {
                  ...u,
                  latitude: data.latitude,
                  longitude: data.longitude,
                  heading: data.heading ?? u.heading,
                  speed: data.speed ?? u.speed,
                  battery: data.battery ?? u.battery,
                  status: (data.status ?? u.status) as UnitStatus,
                }
              : u
          ));
          setSelectedUnit(prev => prev && prev.id === data.unit_id
            ? { ...prev, latitude: data.latitude, longitude: data.longitude, heading: data.heading ?? prev.heading, status: (data.status ?? prev.status) as UnitStatus }
            : prev);
        }
      } catch (error) {
        console.error('WebSocket message error:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setDronePositions(prev => {
        const next: Record<string, DronePosition> = {};
        for (const [id, d] of Object.entries(prev)) next[id] = { ...d, connected: false };
        return next;
      });
    };

    ws.onclose = () => {
      setDronePositions(prev => {
        const next: Record<string, DronePosition> = {};
        for (const [id, d] of Object.entries(prev)) next[id] = { ...d, connected: false };
        return next;
      });
      setTimeout(() => connectWebSocket(), 3000);
    };
    
    wsRef.current = ws;
  }, []);

  const interpolatePosition = useCallback(() => {
    setDronePositions(prev => {
      const targets = targetPositionsRef.current;
      let changed = false;
      const next: Record<string, DronePosition> = {};
      for (const [id, drone] of Object.entries(prev)) {
        const target = targets[id];
        if (!target) { next[id] = drone; continue; }
        const smoothFactor = 0.1;
        const newLat = drone.latitude + (target.latitude - drone.latitude) * smoothFactor;
        const newLng = drone.longitude + (target.longitude - drone.longitude) * smoothFactor;
        if (Math.abs(newLat - drone.latitude) > 1e-9 || Math.abs(newLng - drone.longitude) > 1e-9) {
          changed = true;
          next[id] = { ...drone, latitude: newLat, longitude: newLng };
        } else {
          next[id] = drone;
        }
      }
      return changed ? next : prev;
    });
    animationFrameRef.current = requestAnimationFrame(interpolatePosition);
  }, []);

  const fetchPoints = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/points?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPoints(data);
      } else {
        toast('Failed to load points', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch points:', error);
      toast('Connection error loading points', 'error');
    }
  }, [dataScope, toast]);

  /**
   * Turn a confirmed fix into a real map point.
   *
   * Goes through the ordinary POST /api/points, so scope, group_id and created_by are handled
   * exactly as they are for a hand-placed point — there is no machine write path here, and no
   * broadcast (same as manual creation; /ws/drone has no per-group routing).
   *
   * The accuracy and the camera that produced it go into metadata, because a point whose
   * position was computed rather than surveyed should say so forever.
   */
  const confirmPendingFix = useCallback(async (name: string) => {
    if (!pendingFix) return;
    setSavingFix(true);
    try {
      const response = await fetch(`${API_URL}/api/points?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          name,
          latitude: pendingFix.lat,
          longitude: pendingFix.lng,
          description: `Geolocated from camera video (±${pendingFix.accuracy_m.toFixed(1)} m)`,
          icon: 'site',
          color: '#ffd166',
          task_set_id: selectedTaskSetId || null,
          metadata: {
            source: 'camera_geolocation',
            accuracy_m: Number(pendingFix.accuracy_m.toFixed(2)),
            warnings: pendingFix.warnings,
          },
        }),
      });
      if (!response.ok) {
        toast('Could not save the point', 'error');
        return;
      }
      setPendingFix(null);
      await fetchPoints();
      toast(`Placed ${name} (±${pendingFix.accuracy_m.toFixed(1)} m)`, 'success');
    } catch (error) {
      console.error('Failed to place geolocated point:', error);
      toast('Connection error placing the point', 'error');
    } finally {
      setSavingFix(false);
    }
  }, [pendingFix, dataScope, selectedTaskSetId, fetchPoints, toast]);

  const fetchPolygons = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/polygons?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPolygons(data);
      } else {
        toast('Failed to load areas', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch polygons:', error);
      toast('Connection error loading areas', 'error');
    }
  }, [dataScope, toast]);

  const fetchLines = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/lines?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLines(data);
      } else {
        toast('Failed to load lines', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch lines:', error);
      toast('Connection error loading lines', 'error');
    }
  }, [dataScope, toast]);

  const fetchSensors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sensors?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSensors(data);
        // metadata.rtmp_url is a persisted field name, not a protocol claim —
        // it holds whatever pull URL the operator saved, RTMP or RTSP. Renaming
        // the column would break every stored sensor for no gain.
        const rtmpCamSources = data
          .filter((s: MapSensor) => s.sensor_type === 'camera' && s.metadata?.rtmp_url)
          .map((s: MapSensor) => ({
            id: `cam-rtmp-${s.id}`,
            name: s.name,
            url: s.metadata!.rtmp_url as string,
            type: pullTypeOf(s.metadata!.rtmp_url as string),
          }));
        if (rtmpCamSources.length > 0) {
          setStreamSources(prev => {
            const existingCamIds = new Set(prev.filter(s => s.id.startsWith('cam-rtmp-')).map(s => s.id));
            const newCamIds = new Set(rtmpCamSources.map((s: { id: string }) => s.id));
            const withoutStale = prev.filter(s => !s.id.startsWith('cam-rtmp-') || newCamIds.has(s.id));
            const toAdd = rtmpCamSources.filter((s: { id: string }) => !existingCamIds.has(s.id));
            return toAdd.length > 0 ? [...withoutStale, ...toAdd] : withoutStale;
          });
        } else {
          setStreamSources(prev => prev.filter(s => !s.id.startsWith('cam-rtmp-')));
        }
      } else {
        toast('Failed to load sensors', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch sensors:', error);
      toast('Connection error loading sensors', 'error');
    }
  }, [dataScope, toast]);

  const fetchUnits = useCallback(async () => {
    try {
      const data = await apiFetchUnits(getAuthHeaders, { scope: dataScope });
      setUnits(data);

      // Units carrying a feed (drone patrols) join the source list, same as
      // camera sensors with an rtmp_url do above.
      const unitSources = data
        .map(u => ({ u, stream: unitStream(u) }))
        .filter((x): x is { u: FieldUnit; stream: UnitStream } => x.stream !== null)
        .map(({ u, stream }) => ({
          id: `unit-feed-${u.id}`,
          name: u.name,
          url: stream.type === 'demo' ? '' : stream.url,
          type: stream.type,
        }));
      setStreamSources(prev => {
        const nextIds = new Set(unitSources.map(s => s.id));
        const existingIds = new Set(prev.filter(s => s.id.startsWith('unit-feed-')).map(s => s.id));
        const withoutStale = prev.filter(s => !s.id.startsWith('unit-feed-') || nextIds.has(s.id));
        const toAdd = unitSources.filter(s => !existingIds.has(s.id));
        return toAdd.length > 0 ? [...withoutStale, ...toAdd] : withoutStale;
      });
    } catch (error) {
      console.error('Failed to fetch units:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataScope]);

  // The Pi-guided aircraft (docs/PICAM_GUIDANCE.md) joins the source list only where
  // the backend has it enabled — local dev by env. Everywhere else /api/picam/status
  // answers enabled:false and nothing appears.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const check = async () => {
      let entry: { id: string; name: string; url: string; type: 'picam' } | null = null;
      try {
        const res = await fetch(`${API_URL}/api/picam/status`, { headers: getAuthHeaders() });
        const body = res.ok ? await res.json() : null;
        if (body?.enabled) {
          const droneId = String(body.drone_id || 'picam');
          entry = { id: `picam-${droneId}`, name: `${droneId} — onboard Pi`, url: '', type: 'picam' };
        }
      } catch { /* leave the list as it is on a network blip */ return; }
      if (cancelled) return;
      setStreamSources(prev => {
        const current = prev.filter(s => s.id.startsWith('picam-'));
        if (entry ? (current.length === 1 && current[0].id === entry.id) : current.length === 0) return prev;
        const others = prev.filter(s => !s.id.startsWith('picam-'));
        return entry ? [...others, entry] : others;
      });
    };
    check();
    const poll = setInterval(check, 60_000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [isAuthenticated]);

  const fetchMissions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/missions?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMissions(data);
        setActiveMission(prev => {
          if (prev) {
            const updated = data.find((m: Mission) => m.id === prev.id);
            if (updated) return updated;
          }
          const active = data.find((m: Mission) => m.status === 'executing' || m.status === 'paused');
          return active || null;
        });
      } else {
        toast('Failed to load missions', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch missions:', error);
      toast('Connection error loading missions', 'error');
    }
  }, [dataScope, toast]);

  // Keep the WS effect's refs pointing at the current fetchers. The effect itself has an
  // empty dep array so it cannot see these directly.
  useEffect(() => {
    fetchLinesRef.current = fetchLines;
    fetchMissionsRef.current = fetchMissions;
  }, [fetchLines, fetchMissions]);

  const fetchIncidents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/incidents?scope=${dataScope}&active_only=true`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setIncidents(await response.json());
      } else {
        toast('Failed to load incidents', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      toast('Connection error loading incidents', 'error');
    }
  }, [dataScope, toast]);

  const fetchKaekParcels = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/kaek/parcels?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setKaekParcels(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch KAEK parcels:', error);
    }
  }, [dataScope]);

  const fetchGeozones = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/uspace/geozones`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setGeozones(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch U-space geo-zones:', error);
    }
  }, []);

  const fetchAuthorisations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/uspace/authorisations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setAuthorisations(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch U-space authorisations:', error);
    }
  }, []);

  const fetchOperationalIntents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/uss/operational-intents`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setOperationalIntents(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch operational intents:', error);
    }
  }, []);

  const requestAuthorisation = useCallback(async (missionId: number) => {
    setRequestingAuthMissionId(missionId);
    try {
      const response = await fetch(`${API_URL}/api/uspace/authorisations?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ mission_id: missionId })
      });
      if (response.ok) {
        const auth: GeoAuthorisation = await response.json();
        const n = auth.conflicts?.length || 0;
        const suffix = n > 0 ? ` — ${n} conflict${n > 1 ? 's' : ''}` : '';
        const type = auth.status === 'REJECTED' ? 'error' : auth.status === 'APPROVED' ? 'success' : 'warning';
        toast(`U-space clearance: ${auth.status}${suffix}`, type);
        fetchAuthorisations();
      } else {
        const err = await response.json().catch(() => ({}));
        toast(err.detail || 'Authorisation request failed', 'error');
      }
    } catch (error) {
      toast('Connection error requesting clearance', 'error');
    } finally {
      setRequestingAuthMissionId(null);
    }
  }, [dataScope, fetchAuthorisations, toast]);

  const submitIncident = async () => {
    if (!pendingIncidentCoords || !incidentDraft.title.trim()) return;
    setSubmittingIncident(true);
    try {
      const response = await fetch(`${API_URL}/api/incidents?scope=${dataScope}&active_only=true`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...incidentDraft,
          latitude: pendingIncidentCoords.lat,
          longitude: pendingIncidentCoords.lng,
        })
      });
      if (response.ok) {
        setShowIncidentModal(false);
        setPendingIncidentCoords(null);
        setIncidentDraft({ type: 'Unknown', severity: 3, title: '', description: '', location_label: '' });
        fetchIncidents();
      }
    } catch (error) {
      console.error('Failed to create incident:', error);
    } finally {
      setSubmittingIncident(false);
    }
  };

  const updateIncidentStatus = async (incidentId: number, newStatus: string) => {
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedIncident(updated);
      }
    } catch (error) {
      console.error('Failed to update incident status:', error);
    }
  };

  const deleteIncident = async (incidentId: number) => {
    if (!await confirm({ message: 'Delete this incident?' })) return;
    try {
      await fetch(`${API_URL}/api/incidents/${incidentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      setIncidents(prev => prev.filter(i => i.id !== incidentId));
      setSelectedIncident(null);
    } catch (error) {
      console.error('Failed to delete incident:', error);
    }
  };

  const fetchIncidentNotes = useCallback(async (incidentId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/notes`, {
        headers: getAuthHeaders()
      });
      if (response.ok) setIncidentNotes(await response.json());
    } catch (error) {
      console.error('Failed to fetch incident notes:', error);
    }
  }, []);

  const addIncidentNote = useCallback(async (incidentId: number, content: string, activityType: string) => {
    setSavingIncidentNote(true);
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/notes`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ content, activity_type: activityType })
      });
      if (response.ok) {
        fetchIncidentNotes(incidentId);
        setNewIncidentNote('');
        setIncidentNoteType('note');
      }
    } catch (error) {
      console.error('Failed to add incident note:', error);
    } finally {
      setSavingIncidentNote(false);
    }
  }, [fetchIncidentNotes]);

  const updateIncidentNote = useCallback(async (incidentId: number, noteId: number, content: string) => {
    setSavingIncidentNote(true);
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/notes/${noteId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        fetchIncidentNotes(incidentId);
        setEditingIncidentNoteId(null);
        setEditIncidentNoteContent('');
      }
    } catch (error) {
      console.error('Failed to update incident note:', error);
    } finally {
      setSavingIncidentNote(false);
    }
  }, [fetchIncidentNotes]);

  const deleteIncidentNote = useCallback(async (incidentId: number, noteId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) fetchIncidentNotes(incidentId);
    } catch (error) {
      console.error('Failed to delete incident note:', error);
    }
  }, [fetchIncidentNotes]);

  const uploadIncidentAttachment = useCallback(async (incidentId: number, file: File) => {
    setUploadingIncidentAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const headers: HeadersInit = { 'Authorization': `Bearer ${token}` };
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/attachments`, {
        method: 'POST',
        headers,
        body: formData
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedIncident(updated);
        setIncidents(prev => prev.map(i => i.id === incidentId ? updated : i));
      }
    } catch (error) {
      console.error('Failed to upload incident attachment:', error);
    } finally {
      setUploadingIncidentAttachment(false);
    }
  }, []);

  const deleteIncidentAttachment = useCallback(async (incidentId: number, attachmentId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedIncident(updated);
        setIncidents(prev => prev.map(i => i.id === incidentId ? updated : i));
      }
    } catch (error) {
      console.error('Failed to delete incident attachment:', error);
    }
  }, []);

  const fetchIncidentLinkedDetails = useCallback(async (incidentId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/linked-items/details`, {
        headers: getAuthHeaders()
      });
      if (response.ok) setIncidentLinkedDetails(await response.json());
    } catch (error) {
      console.error('Failed to fetch linked items:', error);
    }
  }, []);

  const updateIncidentLinkedItems = useCallback(async (incidentId: number, items: LinkedItem[]) => {
    try {
      const response = await fetch(`${API_URL}/api/incidents/${incidentId}/linked-items`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ linked_items: items })
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedIncident(updated);
        setIncidents(prev => prev.map(i => i.id === incidentId ? updated : i));
        fetchIncidentLinkedDetails(incidentId);
      }
    } catch (error) {
      console.error('Failed to update linked items:', error);
    }
  }, [fetchIncidentLinkedDetails]);

  const createMission = async (lineId: number) => {
    if (!missionName.trim()) return;
    setCreatingMission(true);
    try {
      const response = await fetch(`${API_URL}/api/missions?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          line_id: lineId,
          name: missionName,
          altitude: missionAltitude,
          speed: missionSpeed,
          drone_model: missionDroneModel,
        })
      });
      if (response.ok) {
        const mission = await response.json();
        setMissionName('');
        setShowMissionForm(false);
        setActiveMission(mission);
        fetchMissions();
      }
    } catch (error) {
      console.error('Failed to create mission:', error);
    } finally {
      setCreatingMission(false);
    }
  };

  const sendMissionCommand = async (missionId: number, action: string) => {
    try {
      const response = await fetch(`${API_URL}/api/missions/${missionId}/command?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ action })
      });
      if (response.ok) {
        const result = await response.json();
        setActiveMission(prev => prev && prev.id === missionId ? { ...prev, status: result.status } : prev);
        if (action === 'cancel') { setMissionPopupCoords(null); setMissionPopupWpIndex(null); }
        fetchMissions();
      }
    } catch (error) {
      console.error('Failed to send mission command:', error);
    }
  };

  const downloadMissionFile = async (missionId: number, missionName: string, type: 'kmz' | 'report' | 'pdf' | 'apm') => {
    const config = {
      kmz: { endpoint: 'kmz', filename: `${missionName.replace(/[^a-zA-Z0-9_-]/g, '_')}.kmz`, label: 'KMZ' },
      report: { endpoint: 'report', filename: `mission_report_${missionName.replace(/[^a-zA-Z0-9_-]/g, '_')}.txt`, label: 'Report' },
      pdf: { endpoint: 'report/pdf', filename: `mission_report_${missionName.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`, label: 'PDF' },
      apm: { endpoint: 'ardupilot', filename: `${missionName.replace(/[^a-zA-Z0-9_-]/g, '_')}.waypoints`, label: 'APM' },
    }[type];
    setDownloadingMission(type);
    try {
      const response = await fetch(`${API_URL}/api/missions/${missionId}/${config.endpoint}?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = config.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast(`${config.label} downloaded`, 'success');
      } else {
        toast(`Failed to download ${config.label}`, 'error');
      }
    } catch (error) {
      console.error(`Failed to download ${config.label}:`, error);
      toast(`Failed to download ${config.label}`, 'error');
    } finally {
      setDownloadingMission(null);
    }
  };
  const downloadMissionKmz = (id: number, name: string) => downloadMissionFile(id, name, 'kmz');
  const downloadMissionReport = (id: number, name: string) => downloadMissionFile(id, name, 'report');
  const downloadMissionReportPdf = (id: number, name: string) => downloadMissionFile(id, name, 'pdf');
  const downloadMissionArdupilot = (id: number, name: string) => downloadMissionFile(id, name, 'apm');

  const deleteMission = async (missionId: number) => {
    if (!await confirm({ message: 'Delete this mission?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/missions/${missionId}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        if (activeMission?.id === missionId) setActiveMission(null);
        fetchMissions();
      }
    } catch (error) {
      console.error('Failed to delete mission:', error);
    }
  };

  const matchMissionSites = async (missionId: number, taskSetId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/missions/${missionId}/match-sites?task_set_id=${taskSetId}&scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        if (activeMission?.id === missionId) {
          setActiveMission({ ...activeMission, waypoints: result.waypoints });
        }
        setMissions(prev => prev.map(m => m.id === missionId ? { ...m, waypoints: result.waypoints } : m));
        toast(`Matched ${result.matched}/${result.total} waypoints to sites`, 'success');
      }
    } catch (error) {
      console.error('Failed to match sites:', error);
    }
  };

  const updateWaypointSites = async (missionId: number, wpIndex: number, siteIds: number[]) => {
    try {
      const response = await fetch(`${API_URL}/api/missions/${missionId}/waypoints/${wpIndex}/sites?scope=${dataScope}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_ids: siteIds }),
      });
      if (response.ok) {
        const result = await response.json();
        if (activeMission?.id === missionId) {
          setActiveMission({ ...activeMission, waypoints: result.waypoints });
        }
        setMissions(prev => prev.map(m => m.id === missionId ? { ...m, waypoints: result.waypoints } : m));
      }
    } catch (error) {
      console.error('Failed to update waypoint sites:', error);
    }
  };

  const openSendToDrone = async (mission: Mission) => {
    if (!mission.flight_id) { toast('Mission has no flight plan', 'error'); return; }
    setSendToDrone({
      missionId: mission.id, missionName: mission.name, flightId: mission.flight_id,
      devices: [], selectedSn: null, loading: true, sending: false, error: null,
    });
    try {
      const { devices } = await fetchMqttDevices(getAuthHeaders);
      const online = devices.filter(d => d.is_online);
      setSendToDrone(prev => prev ? { ...prev, devices: online, loading: false, selectedSn: online.length === 1 ? online[0].sn : null } : null);
    } catch {
      setSendToDrone(prev => prev ? { ...prev, loading: false, error: 'MQTT broker not connected' } : null);
    }
  };

  const executeSendToDrone = async () => {
    if (!sendToDrone?.selectedSn || !sendToDrone.flightId) return;
    setSendToDrone(prev => prev ? { ...prev, sending: true, error: null } : null);
    try {
      const r = await sendFlightTask(getAuthHeaders, sendToDrone.selectedSn, sendToDrone.flightId);
      const ok = r.result.result === 0;
      if (ok) {
        toast(`Mission "${sendToDrone.missionName}" sent to drone`, 'success');
        setSendToDrone(null);
      } else {
        setSendToDrone(prev => prev ? { ...prev, sending: false, error: `Drone returned error code ${r.result.result}` } : null);
      }
    } catch (e: unknown) {
      setSendToDrone(prev => prev ? { ...prev, sending: false, error: e instanceof Error ? e.message : 'Failed to send flight task' } : null);
    }
  };

  const fetchTaskSets = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/task-sets?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setTaskSets(await response.json());
      } else {
        toast('Failed to load task sets', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch task sets:', error);
    }
  }, [dataScope, toast]);

  const createTaskSet = async () => {
    if (!newTaskSetName.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/task-sets?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ name: newTaskSetName })
      });
      if (response.ok) {
        setNewTaskSetName('');
        setIsCreatingTaskSet(false);
        fetchTaskSets();
      }
    } catch (error) {
      console.error('Failed to create task set:', error);
    }
  };

  const deleteTaskSet = async (id: number, deleteItems: boolean) => {
    try {
      const response = await fetch(`${API_URL}/api/task-sets/${id}?scope=${dataScope}&delete_items=${deleteItems}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setConfirmingDeleteTaskSetId(null);
        setHiddenTaskSetIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          localStorage.setItem('toc_hiddenTaskSets', JSON.stringify([...next]));
          return next;
        });
        fetchTaskSets();
        fetchPoints();
        fetchPolygons();
        fetchLines();
        fetchSensors();
        fetchKaekParcels();
      }
    } catch (error) {
      console.error('Failed to delete task set:', error);
    }
  };

  const renameTaskSet = async (id: number, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) { setRenamingTaskSetId(null); return; }
    const ts = taskSets.find(t => t.id === id);
    if (!ts || ts.name === trimmed) { setRenamingTaskSetId(null); return; }
    try {
      const response = await fetch(`${API_URL}/api/task-sets/${id}?scope=${dataScope}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ name: trimmed, description: ts.description, icon: ts.icon, color: ts.color })
      });
      if (response.ok) {
        fetchTaskSets();
      }
    } catch (error) {
      console.error('Failed to rename task set:', error);
    }
    setRenamingTaskSetId(null);
  };

  const [confirmingClearTaskSetId, setConfirmingClearTaskSetId] = useState<number | null>(null);

  const clearTaskSetContent = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/api/task-sets/${id}/content?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setConfirmingClearTaskSetId(null);
        fetchTaskSets();
        fetchPoints();
        fetchPolygons();
        fetchLines();
        fetchSensors();
        fetchMissions();
        fetchIncidents();
        fetchKaekParcels();
      }
    } catch (error) {
      console.error('Failed to clear task set content:', error);
    }
  };

  const [sharingItemType, setSharingItemType] = useState<string | null>(null);
  const [sharingItemId, setSharingItemId] = useState<number | null>(null);

  interface ShareRecord {
    id: number;
    group_id: number;
    group_name: string;
    shared_by_email: string;
    shared_at: string;
  }

  const [activeShares, setActiveShares] = useState<ShareRecord[]>([]);

  const fetchShares = async (itemType: string, itemId: number) => {
    try {
      const res = await fetch(`${API_URL}/api/items/${itemType}/${itemId}/shares`, { headers: getAuthHeaders() });
      if (res.ok) setActiveShares(await res.json());
    } catch { /* ignore */ }
  };

  const shareItem = async (itemType: string, itemId: number, targetGroupId: number) => {
    try {
      const res = await fetch(`${API_URL}/api/items/${itemType}/${itemId}/share`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_group_id: targetGroupId })
      });
      if (res.ok) {
        setSharingItemType(null);
        setSharingItemId(null);
        fetchTaskSets();
        fetchPoints();
        fetchPolygons();
        fetchLines();
        fetchSensors();
        fetchMissions();
      }
    } catch (error) {
      console.error('Failed to share item:', error);
    }
  };

  const unshareItem = async (itemType: string, itemId: number, shareId: number) => {
    try {
      const res = await fetch(`${API_URL}/api/items/${itemType}/${itemId}/share/${shareId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        fetchShares(itemType, itemId);
        fetchTaskSets();
        fetchPoints();
        fetchPolygons();
        fetchLines();
        fetchSensors();
        fetchMissions();
      }
    } catch (error) {
      console.error('Failed to unshare item:', error);
    }
  };

  const handleKaekLookup = async () => {
    if (!kaekLookupCode.trim()) return;
    setKaekLookupLoading(true);
    setKaekPreview(null);
    try {
      const response = await fetch(`${API_URL}/api/kaek/lookup/${encodeURIComponent(kaekLookupCode.trim())}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setKaekPreview(data);
        if (data.geometry?.coordinates?.[0]) {
          const coords = data.geometry.coordinates[0];
          const lngs = coords.map((c: number[]) => c[0]);
          const lats = coords.map((c: number[]) => c[1]);
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          centerOnPoint(centerLng, centerLat);
        }
      } else {
        const err = await response.json().catch(() => ({ detail: 'Not found' }));
        toast(err.detail || 'KAEK not found', 'error');
      }
    } catch (error) {
      toast('Connection error looking up KAEK', 'error');
    }
    setKaekLookupLoading(false);
  };

  const handleKaekSavePreview = async () => {
    if (!kaekPreview) return;
    try {
      const response = await fetch(`${API_URL}/api/kaek/parcels?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(kaekPreview)
      });
      if (response.ok) {
        toast('Parcel saved', 'success');
        setKaekPreview(null);
        setKaekLookupCode('');
        fetchKaekParcels();
      } else {
        const err = await response.json().catch(() => ({ detail: 'Save failed' }));
        toast(err.detail || 'Failed to save parcel', 'error');
      }
    } catch (error) {
      toast('Connection error saving parcel', 'error');
    }
  };

  const handleKaekImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setKaekImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/kaek/import?scope=${dataScope}`, {
        method: 'POST',
        headers: (() => { const h = getAuthHeaders(); return h; })(),
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        toast(`Imported ${result.found} parcels (${result.not_found} not found, ${result.skipped_existing} already existed)`, 'success');
        fetchKaekParcels();
        fetchTaskSets();
      } else {
        const err = await response.json().catch(() => ({ detail: 'Import failed' }));
        toast(err.detail || 'Import failed', 'error');
      }
    } catch (error) {
      toast('Connection error during import', 'error');
    }
    setKaekImporting(false);
    e.target.value = '';
  };

  const deleteKaekParcel = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/api/kaek/parcels/${id}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSelectedKaekParcel(null);
        fetchKaekParcels();
        toast('Parcel deleted', 'success');
      }
    } catch (error) {
      toast('Failed to delete parcel', 'error');
    }
  };

  const toggleTaskSetVisibility = (taskSetId: number) => {
    setHiddenTaskSetIds(prev => {
      const next = new Set(prev);
      if (next.has(taskSetId)) {
        next.delete(taskSetId);
      } else {
        next.add(taskSetId);
      }
      localStorage.setItem('toc_hiddenTaskSets', JSON.stringify([...next]));
      return next;
    });
  };

  const isItemVisible = useCallback((taskSetId?: number) => {
    if (!taskSetId) return true;
    return !hiddenTaskSetIds.has(taskSetId);
  }, [hiddenTaskSetIds]);

  const [mapBounds, setMapBounds] = useState<[number, number, number, number]>(() => {
    try {
      const saved = localStorage.getItem('toc_mapViewState');
      if (saved) {
        const vs = JSON.parse(saved);
        const span = 360 / Math.pow(2, vs.zoom || 12);
        return [vs.longitude - span, vs.latitude - span / 2, vs.longitude + span, vs.latitude + span / 2];
      }
    } catch {}
    return [-180, -90, 180, 90];
  });
  const [mapZoom, setMapZoom] = useState(() => {
    try {
      const saved = localStorage.getItem('toc_mapViewState');
      if (saved) return Math.floor(JSON.parse(saved).zoom || 12);
    } catch {}
    return 12;
  });
  const [mapTilted, setMapTilted] = useState(() => {
    try {
      const saved = localStorage.getItem('toc_mapViewState');
      if (saved) {
        const vs = JSON.parse(saved);
        return (vs.pitch || 0) > 1 || Math.abs(vs.bearing || 0) > 1;
      }
    } catch {}
    return false;
  });

  const missionLinkedSiteIds = useMemo(() => {
    const ids = new Set<number>();
    const missionLineIds = new Set<number>();
    for (const m of missions) {
      if (m.line_id != null) missionLineIds.add(m.line_id);
      for (const wp of m.waypoints) {
        if (wp.site_ids) {
          for (const sid of wp.site_ids) ids.add(sid);
        } else if (wp.site_id != null) {
          ids.add(wp.site_id);
        }
      }
    }
    for (const p of points) {
      if (p.line_id != null && missionLineIds.has(p.line_id)) ids.add(p.id);
    }
    return ids;
  }, [missions, points]);

  const visiblePointCount = useMemo(() => {
    let total = 0, withMissions = 0;
    for (const p of points) {
      if (hiddenPoints.has(p.id) || !isItemVisible(p.task_set_id)) continue;
      if (siteFilterTaskSetId != null && p.task_set_id !== siteFilterTaskSetId) continue;
      total++;
      if (missionLinkedSiteIds.has(p.id)) withMissions++;
    }
    return { total, withMissions, withoutMissions: total - withMissions };
  }, [points, hiddenPoints, isItemVisible, missionLinkedSiteIds, siteFilterTaskSetId]);

  const passesSiteFilter = useCallback((p: MapPoint) => {
    if (siteFilter === 'all') return true;
    if (siteFilterTaskSetId != null && p.task_set_id !== siteFilterTaskSetId) return true;
    return siteFilter === 'with-missions' ? missionLinkedSiteIds.has(p.id) : !missionLinkedSiteIds.has(p.id);
  }, [siteFilter, siteFilterTaskSetId, missionLinkedSiteIds]);

  const clusterIndex = useMemo(() => {
    const index = new Supercluster({ radius: 60, maxZoom: 18 });
    const features: Supercluster.PointFeature<{ id: number }>[] = points
      .filter(p => !hiddenPoints.has(p.id) && isItemVisible(p.task_set_id) && passesSiteFilter(p))
      .map(p => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
        properties: { id: p.id }
      }));
    index.load(features);
    return index;
  }, [points, hiddenPoints, isItemVisible, passesSiteFilter]);

  const clusters = useMemo(() =>
    clusterIndex.getClusters(mapBounds, mapZoom),
  [clusterIndex, mapBounds, mapZoom]);

  const pointsById = useMemo(() => {
    const m: Record<number, MapPoint> = {};
    for (const p of points) m[p.id] = p;
    return m;
  }, [points]);

  const fovGeoJSON = useMemo(() => {
    if (!showFovCone) return { type: 'FeatureCollection' as const, features: [] as GeoJSON.Feature[] };
    return createFovGeoJSON(dronePositions);
  }, [dronePositions, showFovCone]);

  const mmwaveSectors = useMemo((): GeoJSON.FeatureCollection => ({
    type: 'FeatureCollection',
    features: sensors
      .filter(s => s.sensor_type === 'mmwave' && !hiddenSensors.has(s.id) && isItemVisible(s.task_set_id))
      .flatMap(s => {
        const v = mmwaveView(s.status, s.metadata);
        if (v.bearingDeg === null) return [];
        const f = sectorPolygon(s.latitude, s.longitude, v.bearingDeg, v.rangeM);
        f.properties = { occupied: v.presence === true, online: v.online };
        return [f];
      }),
  }), [sensors, hiddenSensors, isItemVisible]);

  const filterLower = sidebarFilter.toLowerCase();
  const sidebarMatch = useCallback((name: string) => {
    return !filterLower || name.toLowerCase().includes(filterLower);
  }, [filterLower]);

  const toggleLineVisibility = useCallback((lineId: number) => {
    const wasHidden = hiddenLines.has(lineId);
    const nextLines = new Set(hiddenLines);
    wasHidden ? nextLines.delete(lineId) : nextLines.add(lineId);
    setHiddenLines(nextLines);
    localStorage.setItem('toc_hiddenLines', JSON.stringify([...nextLines]));
    const nextPoints = new Set(hiddenPoints);
    points.filter(p => p.line_id === lineId).forEach(p => wasHidden ? nextPoints.delete(p.id) : nextPoints.add(p.id));
    setHiddenPoints(nextPoints);
    localStorage.setItem('toc_hiddenPoints', JSON.stringify([...nextPoints]));
  }, [points, hiddenLines, hiddenPoints]);

  const updateItemTaskSet = async (itemType: string, itemId: number, taskSetId: number | null) => {
    try {
      const response = await fetch(`${API_URL}/api/${itemType}/${itemId}/task-set?scope=${dataScope}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ task_set_id: taskSetId })
      });
      if (response.ok) {
        const tsId = taskSetId ?? undefined;
        if (itemType === 'points' && selectedPoint?.id === itemId) setSelectedPoint({...selectedPoint, task_set_id: tsId});
        if (itemType === 'polygons' && selectedPolygon?.id === itemId) setSelectedPolygon({...selectedPolygon, task_set_id: tsId});
        if (itemType === 'lines' && selectedLine?.id === itemId) setSelectedLine({...selectedLine, task_set_id: tsId});
        if (itemType === 'sensors' && selectedSensor?.id === itemId) setSelectedSensor({...selectedSensor, task_set_id: tsId});
        fetchPoints();
        fetchPolygons();
        fetchLines();
        fetchSensors();
        fetchTaskSets();
      }
    } catch (error) {
      console.error('Failed to update task set:', error);
    }
  };

  const deleteSensor = async (sensorId: number) => {
    if (!await confirm({ message: 'Delete this sensor?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/sensors/${sensorId}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSelectedSensor(null);
        fetchSensors();
      }
    } catch (error) {
      console.error('Failed to delete sensor:', error);
    }
  };

  // A drag moves the marker before the server has agreed, so a refused save must say so
  // and put the marker back — otherwise the map shows a position the database never took.
  const updateSensorLocation = async (sensorId: number, latitude: number, longitude: number): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/api/sensors/${sensorId}/location?scope=${dataScope}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ latitude, longitude })
      });
      if (response.ok) {
        setSensors(prev => prev.map(s => s.id === sensorId ? { ...s, latitude, longitude } : s));
        setSelectedSensor(prev => prev && prev.id === sensorId ? { ...prev, latitude, longitude } : prev);
        toast('Sensor position saved', 'success');
        return true;
      }
      const err = await response.json().catch(() => ({ detail: '' }));
      toast(err.detail || 'Failed to save sensor position', 'error');
    } catch (error) {
      console.error('Failed to update sensor location:', error);
      toast('Failed to save sensor position', 'error');
    }
    fetchSensors();
    return false;
  };

  const fetchPointNotes = useCallback(async (pointId: number) => {
    setLoadingNotes(true);
    try {
      const response = await fetch(`${API_URL}/api/points/${pointId}/notes?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPointNotes(data);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, [dataScope]);

  const addPointNote = useCallback(async (pointId: number, content: string) => {
    if (!content.trim()) return;
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/points/${pointId}/notes?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        setNewNoteContent('');
        fetchPointNotes(pointId);
      }
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setSavingNote(false);
    }
  }, [fetchPointNotes, dataScope]);

  const updatePointNote = useCallback(async (pointId: number, noteId: number, content: string) => {
    if (!content.trim()) return;
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/points/${pointId}/notes/${noteId}?scope=${dataScope}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ content })
      });
      if (response.ok) {
        setEditingNoteId(null);
        setEditNoteContent('');
        fetchPointNotes(pointId);
      }
    } catch (error) {
      console.error('Failed to update note:', error);
    } finally {
      setSavingNote(false);
    }
  }, [fetchPointNotes, dataScope]);

  const deletePointNote = useCallback(async (pointId: number, noteId: number) => {
    if (!await confirm({ message: 'Delete this note?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/points/${pointId}/notes/${noteId}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        fetchPointNotes(pointId);
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  }, [fetchPointNotes, confirm, dataScope]);

  const updatePointMarkerIcon = async (pointId: number, markerIcon: IconKey | '') => {
    try {
      const point = points.find(p => p.id === pointId);
      const currentMeta = point?.metadata || {};
      // Drop any legacy data URI so marker_icon stays the single source of truth.
      const { marker_image: _legacy, ...rest } = currentMeta as Record<string, unknown>;
      const newMeta = markerIcon ? { ...rest, marker_icon: markerIcon } : rest;
      const response = await fetch(`${API_URL}/api/points/${pointId}/metadata?scope=${dataScope}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify(newMeta)
      });
      if (response.ok) {
        const updated = await response.json();
        if (selectedPoint?.id === pointId) setSelectedPoint(updated);
        fetchPoints();
      }
    } catch (error) {
      console.error('Failed to update marker image:', error);
    }
  };

  const savePointName = async (pointId: number, name: string) => {
    try {
      const response = await fetch(`${API_URL}/api/points/${pointId}/name?scope=${dataScope}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ name })
      });
      if (response.ok) {
        const updated = await response.json();
        if (selectedPoint?.id === pointId) setSelectedPoint({ ...selectedPoint, name: updated.name });
        setPoints(prev => prev.map(p => p.id === pointId ? { ...p, name: updated.name } : p));
        setEditingPointName(null);
      }
    } catch (error) {
      console.error('Failed to rename point:', error);
    }
  };

  const fetchPolygonNotes = useCallback(async (polygonId: number) => {
    setLoadingNotes(true);
    try {
      const response = await fetch(`${API_URL}/api/polygons/${polygonId}/notes?scope=${dataScope}`, { headers: getAuthHeaders() });
      if (response.ok) setPolygonNotes(await response.json());
    } catch (error) { console.error('Failed to fetch polygon notes:', error); }
    finally { setLoadingNotes(false); }
  }, [dataScope]);

  const addPolygonNote = useCallback(async (polygonId: number, content: string) => {
    if (!content.trim()) return;
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/polygons/${polygonId}/notes?scope=${dataScope}`, {
        method: 'POST', headers: getAuthHeaders(true), body: JSON.stringify({ content })
      });
      if (response.ok) { setNewNoteContent(''); fetchPolygonNotes(polygonId); }
    } catch (error) { console.error('Failed to add polygon note:', error); }
    finally { setSavingNote(false); }
  }, [fetchPolygonNotes, dataScope]);

  const updatePolygonNote = useCallback(async (polygonId: number, noteId: number, content: string) => {
    if (!content.trim()) return;
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/polygons/${polygonId}/notes/${noteId}?scope=${dataScope}`, {
        method: 'PUT', headers: getAuthHeaders(true), body: JSON.stringify({ content })
      });
      if (response.ok) { setEditingNoteId(null); setEditNoteContent(''); fetchPolygonNotes(polygonId); }
    } catch (error) { console.error('Failed to update polygon note:', error); }
    finally { setSavingNote(false); }
  }, [fetchPolygonNotes, dataScope]);

  const deletePolygonNote = useCallback(async (polygonId: number, noteId: number) => {
    if (!await confirm({ message: 'Delete this note?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/polygons/${polygonId}/notes/${noteId}?scope=${dataScope}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (response.ok) fetchPolygonNotes(polygonId);
    } catch (error) { console.error('Failed to delete polygon note:', error); }
  }, [fetchPolygonNotes, dataScope, confirm]);

  const uploadPolygonAttachment = async (polygonId: number, file: File) => {
    setUploadingAttachment(prev => ({...prev, [`polygon-${polygonId}`]: true}));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/polygons/${polygonId}/attachments?scope=${dataScope}`, {
        method: 'POST', headers: getAuthHeaders(), body: formData
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedPolygon(prev => prev ? { ...prev, attachments: updated.attachments } : prev);
        fetchPolygons();
      } else { const err = await response.json(); toast(err.detail || 'Failed to upload file', 'error'); }
    } catch (error) { toast('Error uploading file', 'error'); }
    finally { setUploadingAttachment(prev => ({...prev, [`polygon-${polygonId}`]: false})); }
  };

  const deletePolygonAttachment = async (polygonId: number, attachmentId: string) => {
    if (!await confirm({ message: 'Delete this attachment?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/polygons/${polygonId}/attachments/${attachmentId}?scope=${dataScope}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedPolygon(prev => prev ? { ...prev, attachments: updated.attachments } : prev);
        fetchPolygons();
      }
    } catch (error) { console.error('Error deleting attachment:', error); }
  };

  const fetchLineNotes = useCallback(async (lineId: number) => {
    setLoadingNotes(true);
    try {
      const response = await fetch(`${API_URL}/api/lines/${lineId}/notes?scope=${dataScope}`, { headers: getAuthHeaders() });
      if (response.ok) setLineNotes(await response.json());
    } catch (error) { console.error('Failed to fetch line notes:', error); }
    finally { setLoadingNotes(false); }
  }, [dataScope]);

  const addLineNote = useCallback(async (lineId: number, content: string) => {
    if (!content.trim()) return;
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/lines/${lineId}/notes?scope=${dataScope}`, {
        method: 'POST', headers: getAuthHeaders(true), body: JSON.stringify({ content })
      });
      if (response.ok) { setNewNoteContent(''); fetchLineNotes(lineId); }
    } catch (error) { console.error('Failed to add line note:', error); }
    finally { setSavingNote(false); }
  }, [fetchLineNotes, dataScope]);

  const updateLineNote = useCallback(async (lineId: number, noteId: number, content: string) => {
    if (!content.trim()) return;
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/lines/${lineId}/notes/${noteId}?scope=${dataScope}`, {
        method: 'PUT', headers: getAuthHeaders(true), body: JSON.stringify({ content })
      });
      if (response.ok) { setEditingNoteId(null); setEditNoteContent(''); fetchLineNotes(lineId); }
    } catch (error) { console.error('Failed to update line note:', error); }
    finally { setSavingNote(false); }
  }, [fetchLineNotes, dataScope]);

  const deleteLineNote = useCallback(async (lineId: number, noteId: number) => {
    if (!await confirm({ message: 'Delete this note?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/lines/${lineId}/notes/${noteId}?scope=${dataScope}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (response.ok) fetchLineNotes(lineId);
    } catch (error) { console.error('Failed to delete line note:', error); }
  }, [fetchLineNotes, dataScope, confirm]);

  const uploadLineAttachment = async (lineId: number, file: File) => {
    setUploadingAttachment(prev => ({...prev, [`line-${lineId}`]: true}));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/lines/${lineId}/attachments?scope=${dataScope}`, {
        method: 'POST', headers: getAuthHeaders(), body: formData
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedLine(prev => prev ? { ...prev, attachments: updated.attachments } : prev);
        fetchLines();
      } else { const err = await response.json(); toast(err.detail || 'Failed to upload file', 'error'); }
    } catch (error) { toast('Error uploading file', 'error'); }
    finally { setUploadingAttachment(prev => ({...prev, [`line-${lineId}`]: false})); }
  };

  const deleteLineAttachment = async (lineId: number, attachmentId: string) => {
    if (!await confirm({ message: 'Delete this attachment?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/lines/${lineId}/attachments/${attachmentId}?scope=${dataScope}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedLine(prev => prev ? { ...prev, attachments: updated.attachments } : prev);
        fetchLines();
      }
    } catch (error) { console.error('Error deleting attachment:', error); }
  };

  const fetchAircraft = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/adsb/nearby?lat=37.9096&lon=23.7509&dist=250`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAircraftPositions(data.aircraft || []);
      }
    } catch (error) {
      console.error('Failed to fetch aircraft:', error);
    }
  }, []);

  const fetchVessels = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/ais/vessels`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setVesselPositions(data.vessels || []);
      }
    } catch (error) {
      console.error('Failed to fetch vessels:', error);
    }
  }, []);

  const fetchLiveCameras = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/livecams`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setLiveCameras(data.cameras || []);
      }
    } catch (error) {
      console.error('Failed to fetch live cameras:', error);
    }
  }, []);

  const fetchWeatherRadar = useCallback(async () => {
    try {
      const response = await fetch('https://api.rainviewer.com/public/weather-maps.json');
      if (response.ok) {
        const data: { radar: { past: Array<{ time: number; path: string }> } } = await response.json();
        const pastFrames = data.radar?.past;
        if (pastFrames && pastFrames.length > 0) {
          const latest = pastFrames[pastFrames.length - 1];
          setWeatherRadarTilePath(latest.path);
          setWeatherRadarTimestamp(latest.time);
        }
      }
    } catch (error) {
      console.error('Failed to fetch weather radar:', error);
    }
  }, []);

  const fetchWeatherConditions = useCallback(async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}` +
        `&current=temperature_2m,relative_humidity_2m,precipitation,cloud_cover,visibility,wind_speed_10m,wind_direction_10m,wind_gusts_10m`
      );
      if (response.ok) {
        const data = await response.json();
        const c = data.current;
        setWeatherConditions({
          lat, lng,
          temp: c.temperature_2m, windSpeed: c.wind_speed_10m, windDir: c.wind_direction_10m,
          windGust: c.wind_gusts_10m, cloudCover: c.cloud_cover, humidity: c.relative_humidity_2m,
          visibility: c.visibility, precipitation: c.precipitation,
        });
      }
    } catch (error) {
      console.error('Failed to fetch weather conditions:', error);
    }
  }, []);

  const fetchAllowedFeeds = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/feeds/allowed`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        const types = new Set<string>(data.allowed_types || []);
        if (types.has('dji')) types.add('hls');
        setAllowedFeedTypes(types);
      }
    } catch {
      // ignore — default to all allowed
    }
  }, []);

  const handleGroupChange = useCallback((homeView?: { lat: number; lng: number; zoom: number; pitch?: number; bearing?: number }) => {
    // Drop the outgoing group's items before the refetches go out. Otherwise they
    // stay drawn for the whole round-trip — and permanently if any one request
    // fails, since every fetcher below only toasts on error and leaves state as-is.
    setPoints([]);
    setPolygons([]);
    setLines([]);
    setSensors([]);
    setUnits([]);
    setTaskSets([]);
    setMissions([]);
    setActiveMission(null);
    setIncidents([]);
    setKaekParcels([]);
    setGeozones([]);
    setAuthorisations([]);
    setOperationalIntents([]);
    setSelectedPoint(null);
    setSelectedPolygon(null);
    setSelectedLine(null);
    setSelectedSensor(null);
    setSelectedUnit(null);
    setSelectedIncident(null);
    setSelectedKaekParcel(null);
    fetchPoints();
    fetchPolygons();
    fetchLines();
    fetchSensors();
    fetchUnits();
    fetchTaskSets();
    fetchMissions();
    fetchIncidents();
    fetchKaekParcels();
    fetchGeozones();
    fetchAuthorisations();
    fetchOperationalIntents();
    fetchAllowedFeeds();
    if (homeView && mapRef.current) {
      const map = (mapRef.current as any).getMap?.();
      if (map) {
        map.flyTo({
          center: [homeView.lng, homeView.lat],
          zoom: homeView.zoom,
          pitch: homeView.pitch ?? 0,
          bearing: homeView.bearing ?? 0,
          duration: 2500,
          essential: true
        });
      }
    }
  }, [fetchPoints, fetchPolygons, fetchLines, fetchSensors, fetchUnits, fetchTaskSets, fetchMissions, fetchIncidents, fetchKaekParcels, fetchGeozones, fetchAuthorisations, fetchOperationalIntents, fetchAllowedFeeds]);

  useEffect(() => {
    scopeIdentityRef.current = {
      scope: dataScope,
      groupId: user?.group_id ?? null,
      userId: user?.id ?? null,
    };
  }, [dataScope, user?.group_id, user?.id]);

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchPoints();
    fetchPolygons();
    fetchLines();
    fetchSensors();
    fetchUnits();
    fetchTaskSets();
    fetchMissions();
    fetchIncidents();
    fetchKaekParcels();
    fetchGeozones();
    fetchAuthorisations();
    fetchOperationalIntents();
    fetchAllowedFeeds();

    getActiveLog(getAuthHeaders).then(log => {
      setActiveOpLog(log ? { id: log.id, started_at: log.started_at, title: log.title || undefined } : null);
    }).catch(() => {});

    fetchActiveStreams(getAuthHeaders).then(resp => {
      for (const st of resp.streams) {
        const sourceId = `hls-${st.device_sn}`;
        const label = st.display_name ? `${st.display_name} Live` : `${st.device_sn.slice(-4)} Live`;
        setStreamSources(prev => {
          if (prev.find(s => s.id === sourceId)) return prev;
          return [...prev, { id: sourceId, name: label, url: st.hls_url, type: 'hls' as const }];
        });
        setHlsCredentialsMap(prev => ({ ...prev, [sourceId]: { hlsUrl: st.hls_url, deviceSn: st.device_sn, displayName: st.display_name } }));
      }
    }).catch(() => {});

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, connectWebSocket]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setSelectedPoint(null);
    setSelectedPolygon(null);
    setSelectedLine(null);
    setSelectedSensor(null);
    setSelectedUnit(null);
    setSelectedKaekParcel(null);
    fetchPoints();
    fetchPolygons();
    fetchLines();
    fetchSensors();
    fetchUnits();
    fetchTaskSets();
    fetchMissions();
    fetchIncidents();
    fetchKaekParcels();
    fetchGeozones();
    fetchAuthorisations();
    fetchOperationalIntents();
  }, [isAuthenticated, dataScope, fetchPoints, fetchPolygons, fetchLines, fetchSensors, fetchUnits, fetchTaskSets, fetchMissions, fetchIncidents, fetchKaekParcels, fetchGeozones, fetchAuthorisations, fetchOperationalIntents]);

  useEffect(() => {
    if (points.length === 0) return;
    const existingIds = new Set(points.map(p => p.id));
    setHiddenPoints(prev => {
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (existingIds.has(id)) next.add(id);
        else changed = true;
      }
      if (changed) localStorage.setItem('toc_hiddenPoints', JSON.stringify([...next]));
      return changed ? next : prev;
    });
  }, [points]);

  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || points.length === 0 || deepLinkHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const pointIdParam = params.get('point');
    if (!pointIdParam) return;
    const pointId = parseInt(pointIdParam, 10);
    if (isNaN(pointId)) return;
    deepLinkHandled.current = true;
    const target = points.find(p => p.id === pointId);
    if (target) {
      setSelectedPoint(target);
      if (mapRef.current) {
        const map = (mapRef.current as any).getMap();
        if (map) {
          map.flyTo({ center: [target.longitude, target.latitude], zoom: 17, duration: 1500, essential: true });
        }
      }
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [isAuthenticated, points]);

  const fetchFeedToken = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/drone/feed-token`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFeedToken(data.token);
        return true;
      }
    } catch { /* ignore */ }
    return false;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchFeedToken();
    // Server TTL is 300 s (FEED_TOKEN_EXPIRY_SECONDS), so refresh at 240 s.
    const interval = setInterval(fetchFeedToken, 240_000);
    // A background tab has its timers throttled and a sleeping machine stops
    // them outright, so that interval can lapse past the TTL. The next feed
    // request then 401s and the <img> renders black with no way back. Re-fetch
    // whenever the tab comes forward, which is exactly when it matters.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchFeedToken();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, fetchFeedToken]);

  // Recover a dead MJPEG feed. A feed can die two ways and neither heals on its
  // own: the request fails outright (expired token → 401), or the stream dies
  // mid-flight, which the browser reports through no event at all. Either way
  // the panel goes black and used to stay black until a page reload.
  // No attempt cap on purpose — a dashboard left running should come back by
  // itself when the backend does. The backoff ceiling keeps that to ~4 req/min.
  const feedRetry = useRef<{ attempt: number; timer: number | null }>({ attempt: 0, timer: null });
  const handleFeedError = useCallback(() => {
    if (feedRetry.current.timer !== null) return;   // one recovery in flight
    const attempt = feedRetry.current.attempt + 1;
    feedRetry.current.attempt = attempt;
    feedRetry.current.timer = window.setTimeout(async () => {
      feedRetry.current.timer = null;
      // A stale token is the likeliest cause, so renew before reconnecting.
      await fetchFeedToken();
      setVideoKey(Date.now());                      // remount the <img>
    }, Math.min(1000 * 2 ** (attempt - 1), 15_000));
  }, [fetchFeedToken]);

  // A deliberate source change is a fresh start — forget earlier failures.
  // Deliberately NOT keyed on feedToken as well: recovery renews the token, so
  // that would reset the backoff on every attempt and turn it into a 1 Hz loop.
  useEffect(() => {
    feedRetry.current.attempt = 0;
    if (feedRetry.current.timer !== null) {
      clearTimeout(feedRetry.current.timer);
      feedRetry.current.timer = null;
    }
  }, [activeSourceId]);

  // Demo-box autostart (DEMO_AUTOSTART): the backend is already looping the demo
  // flight with detection on, but a fresh session opens with no source selected
  // and shows NO SIGNAL. Auto-select the demo source once per session when the
  // backend reports its demo stream live — never after the user picks/clears one.
  const demoAutoSelectDone = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || demoAutoSelectDone.current) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/drone/video-source`, { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        // Gate on the backend's DEMO_AUTOSTART flag, not just a live demo slot:
        // on production a colleague watching the demo clip would otherwise make
        // fresh logins auto-join it.
        const demoLive = data.demo_autostart === true && (data.streams || []).some(
          (s: { key: string; is_streaming: boolean }) => s.key === 'demo' && s.is_streaming);
        if (demoLive && !demoAutoSelectDone.current) {
          demoAutoSelectDone.current = true;
          setActiveSourceId(prev => prev === NO_SOURCE_ID ? 'demo' : prev);
        }
      } catch { /* ignore — feature only matters on the demo box */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    // ASK ALWAYS, and let the server decide. It used to ask only when an AI layer
    // was on, which left the panel on MJPEG — ~6.5 Mbit/s for one 640x360 tile
    // against WebRTC's ~0.6 — for the whole time no model was running, i.e. most
    // of the time. The deployment's answer cannot be a REACT_APP_* flag: those
    // bake at build time (see REACT_APP_API_URL), so every environment would need
    // its own frontend rebuild to change its mind. A 404 means this deployment
    // says no; `processedStreamRetry` stops us asking for ever either way.
    //
    // The AI-layer flags stay in the deps because turning a layer on is itself a
    // reason to re-ask: a deployment with WEBRTC_ALWAYS off answers 404 until one
    // is on, and that transition has to re-trigger the fetch.
    if (!isAuthenticated) {
      setProcessedStreamCreds(null);
      setProcessedStreamRetry(0);
      return;
    }
    if (processedStreamRetry > 3) {
      console.warn('[ProcessedStream] Max WebRTC retries reached, using MJPEG fallback');
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const lease = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/drone/processed-stream-token`, {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        if (!resp.ok) {
          // 404 is permanent for as long as this state holds — no LiveKit here,
          // or the deployment serves the main feed over MJPEG. Stay on MJPEG and
          // stop asking; a layer switch changes the deps and re-runs this.
          setProcessedStreamCreds(null);
          return;
        }
        const data = await resp.json();
        if (cancelled) return;
        setProcessedStreamCreds({
          livekitUrl: data.livekit_url, token: data.token, feed: data.feed || null,
        });
        // Asking is what holds the lease, and the lease is what keeps the idle
        // reaper off the processing reader — stop renewing and the panel loses
        // its feed 15 s later. Same rule, same reason, as useFeedWebrtc.
        if (data.renew_in_s) {
          timer = setTimeout(lease, Math.max(5, data.renew_in_s) * 1000);
        }
      } catch {
        // WebRTC not available — MJPEG fallback (processedStreamCreds stays null)
      }
    };
    lease();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      // Best-effort fast release; the server's TTL covers a crashed tab.
      fetch(`${API_URL}/api/drone/processed-stream-token`,
            { method: 'DELETE', headers: getAuthHeaders() }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadSegEnabled, geoMaskEnabled, detectionEnabled, isAuthenticated, processedStreamRetry]);

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(interpolatePosition);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [interpolatePosition]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDronePositions(prev => {
        const now = Date.now();
        let changed = false;
        const next: Record<string, DronePosition> = {};
        for (const [id, d] of Object.entries(prev)) {
          if (d.connected && d.lastSeen && now - d.lastSeen > 60_000) {
            next[id] = { ...d, connected: false };
            changed = true;
          } else {
            next[id] = d;
          }
        }
        return changed ? next : prev;
      });
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedDroneId && dronePositions[selectedDroneId]?.connected) return;
    const firstConnected = Object.entries(dronePositions).find(([, d]) => d.connected);
    if (firstConnected) setSelectedDroneId(firstConnected[0]);
  }, [dronePositions, selectedDroneId]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setDronePositions(prev => {
        let changed = false;
        const next = { ...prev };
        for (const [id, drone] of Object.entries(next)) {
          if (drone.connected && drone.lastSeen && now - drone.lastSeen > 10000) {
            next[id] = { ...drone, connected: false };
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setEditingPointName(null);
    if (selectedPoint) {
      fetchPointNotes(selectedPoint.id);
      fetchDcimPhotosForPoint(selectedPoint.id);
      setNewNoteContent('');
      setEditingNoteId(null);
    } else {
      setPointNotes([]);
      setDcimPhotosForPoint([]);
      setDcimPanoramaView(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoint?.id, fetchPointNotes]);

  useEffect(() => {
    if (selectedPolygon) {
      fetchPolygonNotes(selectedPolygon.id);
      setNewNoteContent('');
      setEditingNoteId(null);
    } else {
      setPolygonNotes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolygon?.id, fetchPolygonNotes]);

  useEffect(() => {
    if (selectedLine) {
      fetchLineNotes(selectedLine.id);
      fetchDcimVideosForLine(selectedLine.id);
      setNewNoteContent('');
      setEditingNoteId(null);
    } else {
      setLineNotes([]);
      setDcimVideosForLine([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLine?.id, fetchLineNotes]);

  useEffect(() => {
    if (selectedIncident) {
      fetchIncidentNotes(selectedIncident.id);
      fetchIncidentLinkedDetails(selectedIncident.id);
      setNewIncidentNote('');
      setEditingIncidentNoteId(null);
      setIncidentNoteType('note');
      setShowIncidentLinkPicker(false);
    } else {
      setIncidentNotes([]);
      setIncidentLinkedDetails([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncident?.id, fetchIncidentNotes, fetchIncidentLinkedDetails]);

  useEffect(() => {
    if (!isAuthenticated || !showAircraftLayer) {
      setAircraftPositions([]);
      setSelectedAircraft(null);
      return;
    }
    fetchAircraft();
    const interval = setInterval(fetchAircraft, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, showAircraftLayer, fetchAircraft]);

  // Ask the backend once whether AIS runs here. `/api/ais/status` is unauthenticated and
  // registered on every node, so a failure to answer at all means an older backend that
  // only mounted the router when configured — treat that as off too.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/ais/status`)
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((s) => { if (!cancelled) setAisEnabled(Boolean(s.enabled ?? s.configured)); })
      .catch(() => { if (!cancelled) setAisEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    showVesselLayerRef.current = showVesselLayer && aisEnabled;
    if (!isAuthenticated || !showVesselLayer || !aisEnabled) {
      setVesselPositions([]);
      setSelectedVessel(null);
      return;
    }
    fetchVessels();
  }, [isAuthenticated, showVesselLayer, aisEnabled, fetchVessels]);

  useEffect(() => {
    if (!isAuthenticated || !showLiveCamLayer) {
      setLiveCameras([]);
      setSelectedLiveCam(null);
      return;
    }
    fetchLiveCameras();
  }, [isAuthenticated, showLiveCamLayer, fetchLiveCameras]);

  useEffect(() => {
    if (weatherLayer === 'off') {
      setWeatherRadarTilePath(null);
      setWeatherRadarTimestamp(null);
      setWeatherConditions(null);
      return;
    }
    if (weatherLayer === 'radar') {
      fetchWeatherRadar();
    }
    const pt = weatherPoint;
    if (!pt) {
      const map = (mapRef.current as any)?.getMap?.();
      if (map) {
        const center = map.getCenter();
        fetchWeatherConditions(center.lat, center.lng);
      }
    } else {
      fetchWeatherConditions(pt.lat, pt.lng);
    }
    const interval = setInterval(() => {
      if (weatherLayer === 'radar') fetchWeatherRadar();
      const p = weatherPoint;
      if (p) fetchWeatherConditions(p.lat, p.lng);
    }, 300_000);
    return () => clearInterval(interval);
  }, [weatherLayer, weatherPoint, fetchWeatherRadar, fetchWeatherConditions]);

  useEffect(() => {
    if (!selectedLiveCam) {
      if (liveCamHlsRef.current) {
        liveCamHlsRef.current.destroy();
        liveCamHlsRef.current = null;
      }
      setLiveCamStreamUrl(null);
      return;
    }
    if (selectedLiveCam.provider === 'youtube') return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/livecams/${selectedLiveCam.id}/stream`, { headers: getAuthHeaders() });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (!cancelled) setLiveCamStreamUrl(data.stream_url);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [selectedLiveCam]);

  useEffect(() => {
    if (!liveCamStreamUrl || !liveCamVideoRef.current) return;
    const video = liveCamVideoRef.current;
    if (liveCamHlsRef.current) {
      liveCamHlsRef.current.destroy();
      liveCamHlsRef.current = null;
    }
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDurationCount: 2,
        liveMaxLatencyDurationCount: 5,
        maxBufferLength: 3,
        backBufferLength: 0,
      });
      hls.loadSource(liveCamStreamUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      liveCamHlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = liveCamStreamUrl;
      video.play().catch(() => {});
    }
    return () => {
      if (liveCamHlsRef.current) {
        liveCamHlsRef.current.destroy();
        liveCamHlsRef.current = null;
      }
    };
  }, [liveCamStreamUrl]);

  const updateMGRSGrid = useCallback(() => {
    if (!showMGRSGrid || !mapRef.current) return;
    const map = (mapRef.current as any).getMap();
    if (!map) return;
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    const grid = generateMGRSGrid({
      west: bounds.getWest(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      north: bounds.getNorth()
    }, zoom);
    setMgrsGridData(grid);
  }, [showMGRSGrid]);

  useEffect(() => {
    if (showMGRSGrid) {
      updateMGRSGrid();
    } else {
      setMgrsGridData({ type: 'FeatureCollection', features: [] });
    }
  }, [showMGRSGrid, updateMGRSGrid]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUserMenu]);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (floorplanViewer) { return; }
        if (dcimViewingPhoto) { setDcimViewingPhoto(null); return; }
        if (dcimPanoramaView) { setDcimPanoramaView(false); return; }
        if (viewingAttachment) { setViewingAttachment(null); return; }
        if (nearbyPointPicker) { setNearbyPointPicker(null); return; }
        if (drawMode !== 'none') {
          setDrawMode('none');
          setTempPolygonCoords([]);
          setTempLineCoords([]);
          setTempLinePointIds([]);
          return;
        }
        if (selectedPoint) setSelectedPoint(null);
        else if (selectedPolygon) setSelectedPolygon(null);
        else if (selectedLine) setSelectedLine(null);
        else if (selectedSensor) setSelectedSensor(null);
        else if (selectedUnit) setSelectedUnit(null);
        else if (selectedAircraft) setSelectedAircraft(null);
        else if (selectedVessel) setSelectedVessel(null);
        else if (selectedLiveCam) setSelectedLiveCam(null);
        else if (selectedIncident) setSelectedIncident(null);
        else if (selectedKaekParcel) setSelectedKaekParcel(null);
        else if (selectedDroneId) setSelectedDroneId(null);
        return;
      }

      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (selectedPoint || selectedPolygon || selectedLine || selectedSensor || selectedUnit || selectedAircraft || selectedVessel || selectedLiveCam || selectedIncident || selectedKaekParcel) return;

      switch (e.key.toLowerCase()) {
        case 'p': setDrawMode(drawMode === 'point' ? 'none' : 'point'); break;
        case 'a': setDrawMode(drawMode === 'polygon' ? 'none' : 'polygon'); break;
        case 'l': setDrawMode(drawMode === 'line' ? 'none' : 'line'); break;
        case 's': setDrawMode(drawMode === 'sensor' ? 'none' : 'sensor'); break;
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [selectedPoint, selectedPolygon, selectedLine, selectedSensor, selectedUnit, selectedAircraft, selectedVessel, selectedLiveCam, selectedIncident,
      dcimViewingPhoto, dcimPanoramaView, viewingAttachment, floorplanViewer, nearbyPointPicker, drawMode, selectedKaekParcel]);


  const handleMapMove = useCallback((event: any) => {
    if (showMGRSGrid) {
      updateMGRSGrid();
    }
    const vs = event.viewState;
    if (vs) {
      localStorage.setItem('toc_mapViewState', JSON.stringify({
        longitude: vs.longitude,
        latitude: vs.latitude,
        zoom: vs.zoom,
        pitch: vs.pitch,
        bearing: vs.bearing,
      }));
      setMapZoom(Math.floor(vs.zoom));
      setMapTilted((vs.pitch || 0) > 1 || Math.abs(vs.bearing || 0) > 1);
    }
    const map = (mapRef.current as any)?.getMap();
    if (map) {
      const b = map.getBounds();
      setMapBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    }
  }, [showMGRSGrid, updateMGRSGrid]);

  const handleMapMouseMove = useCallback((event: any) => {
    if (showMGRSGrid && event.lngLat) {
      setCursorMGRS(toMGRS(event.lngLat.lat, event.lngLat.lng, 5));
    }
  }, [showMGRSGrid]);

  const handleMGRSSearch = useCallback(() => {
    if (!mgrsSearchQuery.trim()) return;
    setMgrsSearchError('');
    const result = parseMGRS(mgrsSearchQuery);
    if (!result) {
      setMgrsSearchError('Invalid MGRS coordinate');
      setMgrsSearchResult(null);
      return;
    }
    setMgrsSearchResult({ lat: result.lat, lng: result.lng });
    if (!showMGRSGrid) setShowMGRSGrid(true);
    if (mapRef.current) {
      const map = (mapRef.current as any).getMap();
      if (map) {
        map.flyTo({
          center: [result.lng, result.lat],
          zoom: mgrsZoomLevel(result.precision),
          duration: 1500,
          essential: true
        });
      }
    }
  }, [mgrsSearchQuery, showMGRSGrid]);

  // Point the video panel at a feed and make it the active source. Shared by the
  // sensor detail panel, the indoor hall camera card and the field-unit panel.
  const watchStream = async (sourceId: string, name: string, stream: UnitStream): Promise<boolean> => {
    const url = stream.type === 'demo' ? '' : stream.url;
    setStreamSources(prev => (
      prev.some(s => s.id === sourceId) ? prev : [...prev, { id: sourceId, name, url, type: stream.type }]
    ));
    try {
      const resp = await fetch(`${API_URL}/api/drone/video-source`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(videoSourceBody(stream.type, url)),
      });
      if (resp.ok) {
        setActiveSourceId(sourceId);
        setShowVideo(true);          // no point starting a stream into a hidden panel
        await new Promise(resolve => setTimeout(resolve, 500));
        setVideoKey(Date.now());
        return true;
      }
      if (resp.status === 400) toast('Invalid or unreachable stream URL', 'error');
      else if (resp.status === 429) toast('Too many active streams', 'warning');
      else toast('Failed to start stream', 'error');
    } catch { toast('Failed to start stream', 'error'); }
    return false;
  };

  // RTMP-only shorthand — keeps the sensor panel and hall camera card (and
  // FloorplanViewer's onWatchStream prop) on their existing signature.
  const watchRtmpStream = (sourceId: string, name: string, rtmpUrl: string): Promise<boolean> =>
    watchStream(sourceId, name, { type: 'rtmp', url: rtmpUrl });

  // Open a pavilion's indoor 3D plan: close the map panels that would cover it,
  // then fly to the hall in its own orientation.
  const enterHall3D = (plan: HallPlan) => {
    const m = (mapRef.current as any)?.getMap?.();
    setSelectedPoint(null);
    setSelectedStand(null);
    setSelectedCamera(null);
    setActiveHall(plan);
    m?.flyTo({
      center: plan.center,
      zoom: hallZoom(m, plan),
      pitch: HALL_PITCH,
      bearing: plan.bearing,
      duration: 2000,
      essential: true,
    });
  };

  const exitHall3D = () => {
    const m = (mapRef.current as any)?.getMap?.();
    setActiveHall(null);
    setSelectedStand(null);
    setSelectedCamera(null);
    m?.easeTo({ pitch: 0, bearing: 0, duration: 800 });
  };

  const handleMapClick = async (event: any) => {
    const { lngLat, features } = event;

    // Calibration has asked for one ground point. It wins over every other map-click
    // behaviour — drawing a polygon while pairing calibration points is never what is meant.
    if (groundPickRef.current) {
      const handler = groundPickRef.current;
      groundPickRef.current = null;
      setGroundPickActive(false);
      handler({ lat: lngLat.lat, lng: lngLat.lng });
      return;
    }

    if (activeHall) {
      const f = features?.find((ft: any) => ft.layer?.id === HALL_STAND_LAYER);
      if (f) {
        setSelectedStand(f.properties?.label || f.properties?.id || 'Stand');
        setSelectedCamera(null);
        return;
      }
    }

    if (weatherLayer !== 'off') {
      setWeatherPoint({ lat: lngLat.lat, lng: lngLat.lng });
    }

    if (drawMode === 'point') {
      if (!newPointName.trim()) {
        toast('Please enter a name for the point', 'warning');
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/points?scope=${dataScope}`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            name: newPointName,
            latitude: lngLat.lat,
            longitude: lngLat.lng,
            icon: 'site',
            color: COLORS.accent,
            task_set_id: selectedTaskSetId || null
          })
        });

        if (response.ok) {
          setNewPointName('');
          setDrawMode('none');
          setSelectedTaskSetId(undefined);
          fetchPoints();
          fetchTaskSets();
        }
      } catch (error) {
        console.error('Failed to create point:', error);
      }
    } else if (drawMode === 'sensor') {
      if (newSensorType === 'phone-camera') {
        if (!newSensorName.trim()) {
          toast('Please enter a name for the sensor', 'warning');
          return;
        }
        setPhoneSensorLocation({ lat: lngLat.lat, lng: lngLat.lng });
        setShowPhoneSensorModal(true);
        setDrawMode('none');
        return;
      }
      if (!newSensorName.trim()) {
        toast('Please enter a name for the sensor', 'warning');
        return;
      }

      const sensorMeta = SENSOR_TYPES.find(t => t.value === newSensorType);
      try {
        const response = await fetch(`${API_URL}/api/sensors?scope=${dataScope}`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            name: newSensorName,
            sensor_type: newSensorType,
            latitude: lngLat.lat,
            longitude: lngLat.lng,
            icon: sensorMeta?.icon,
            color: '#ffaa00',
            task_set_id: selectedTaskSetId || null,
            ...(newSensorType === 'camera' && newSensorRtmpUrl.trim() ? { rtmp_url: newSensorRtmpUrl.trim() } : {})
          })
        });

        if (response.ok) {
          setNewSensorName('');
          setNewSensorRtmpUrl('');
          setDrawMode('none');
          setSelectedTaskSetId(undefined);
          fetchSensors();
          fetchTaskSets();
        }
      } catch (error) {
        console.error('Failed to create sensor:', error);
      }
    } else if (drawMode === 'unit') {
      if (!newUnitName.trim()) {
        toast('Please enter a name for the unit', 'warning');
        return;
      }
      try {
        await createUnit(getAuthHeaders, {
          name: newUnitName,
          unit_type: newUnitType,
          latitude: lngLat.lat,
          longitude: lngLat.lng,
        }, dataScope);
        setNewUnitName('');
        setDrawMode('none');
        fetchUnits();
      } catch (error) {
        console.error('Failed to create unit:', error);
        toast('Failed to create unit', 'error');
      }
    } else if (drawMode === 'polygon') {
      setTempPolygonCoords([...tempPolygonCoords, [lngLat.lng, lngLat.lat]]);
    } else if (drawMode === 'line') {
      setTempLineCoords([...tempLineCoords, [lngLat.lng, lngLat.lat]]);
      setTempLinePointIds([...tempLinePointIds, null]);
    } else {
      if (features && features.length > 0) {
        const feature = features[0];
        const layerId = feature.layer.id;

        if (layerId.startsWith('kaek-hit-')) {
          const parcelId = parseInt(layerId.replace('kaek-hit-', ''));
          const parcel = kaekParcels.find(p => p.id === parcelId);
          if (parcel) {
            setSelectedKaekParcel(parcel);
            setSelectedPoint(null);
            setSelectedPolygon(null);
            setSelectedLine(null);
            setSelectedSensor(null);
          }
        } else if (layerId.startsWith('uspace-hit-')) {
          const zoneId = parseInt(layerId.replace('uspace-hit-', ''));
          const zone = geozones.find(z => z.id === zoneId);
          if (zone) {
            setGeozonePopup({ lng: lngLat.lng, lat: lngLat.lat, zone });
          }
        } else if (layerId.startsWith('polygon-hit-')) {
          const polygonId = parseInt(layerId.replace('polygon-hit-', ''));
          const polygon = polygons.find(p => p.id === polygonId);
          if (polygon) {
            setSelectedPolygon(polygon);
            setSelectedPoint(null);
            setSelectedLine(null);
            setSelectedSensor(null);
          }
        } else if (layerId.startsWith('line-hit-')) {
          const lineId = parseInt(layerId.replace('line-hit-', ''));
          const line = lines.find(l => l.id === lineId);
          if (line) {
            setSelectedLine(line);
            setSelectedPoint(null);
            setSelectedPolygon(null);
            setSelectedSensor(null);
            const linkedMission = missions.find(m => m.line_id === lineId);
            if (linkedMission) setActiveMission(linkedMission);
          }
        } else if (layerId === 'mission-remaining-layer' || layerId === 'mission-completed-layer') {
          setMissionPopupCoords({ lng: lngLat.lng, lat: lngLat.lat });
        }
      }
    }
  };

  const finishPolygon = async () => {
    if (tempPolygonCoords.length < 3) {
      toast('Area needs at least 3 points', 'warning');
      return;
    }
    
    if (!newPolygonName.trim()) {
      toast('Please enter a name for the area', 'warning');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/polygons?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          name: newPolygonName,
          coordinates: tempPolygonCoords,
          color: '#ff4444',
          fill_opacity: 0.3,
          task_set_id: selectedTaskSetId || null
        })
      });

      if (response.ok) {
        setTempPolygonCoords([]);
        setNewPolygonName('');
        setDrawMode('none');
        setSelectedTaskSetId(undefined);
        fetchPolygons();
        fetchTaskSets();
      }
    } catch (error) {
      console.error('Failed to create area:', error);
    }
  };

  const finishLine = async () => {
    if (tempLineCoords.length < 2) {
      toast('Line needs at least 2 points', 'warning');
      return;
    }

    if (!newLineName.trim()) {
      toast('Please enter a name for the line', 'warning');
      return;
    }

    const includedPointIds = tempLinePointIds.filter((id): id is number => id !== null);

    try {
      const response = await fetch(`${API_URL}/api/lines?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          name: newLineName,
          coordinates: tempLineCoords,
          color: '#00ffff',
          line_width: 3,
          task_set_id: selectedTaskSetId || null,
          metadata: includedPointIds.length > 0 ? { point_ids: includedPointIds, waypoint_point_map: tempLinePointIds } : undefined
        })
      });

      if (response.ok) {
        setTempLineCoords([]);
        setTempLinePointIds([]);
        setNewLineName('');
        setDrawMode('none');
        setSelectedTaskSetId(undefined);
        fetchLines();
        fetchTaskSets();
      }
    } catch (error) {
      console.error('Failed to create line:', error);
    }
  };

  const deletePoint = async (id: number) => {
    if (!await confirm({ message: 'Delete this point?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/points/${id}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        fetchPoints();
        setSelectedPoint(null);
      }
    } catch (error) {
      console.error('Failed to delete point:', error);
    }
  };

  const parseCsvLine = (line: string): string[] => {
    const cols: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        cols.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    cols.push(current.trim());
    return cols;
  };

  const downloadFile = (content: string, filename: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    const visiblePts = points.filter(p => !hiddenPoints.has(p.id) && isItemVisible(p.task_set_id) && passesSiteFilter(p));
    if (visiblePts.length === 0) { toast('No visible points to export', 'warning'); return; }
    const escape = (s: string) => s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    const rows = [['name', 'latitude', 'longitude', 'description', 'icon', 'color'].join(',')];
    for (const p of visiblePts) {
      rows.push([escape(p.name), p.latitude, p.longitude, escape(p.description || ''), escape(p.icon), p.color].join(','));
    }
    downloadFile(rows.join('\n'), 'map-points.csv', 'text/csv');
    toast(`Exported ${visiblePts.length} points as CSV`, 'success');
  };

  const exportGeoJSON = () => {
    const features: GeoJSON.Feature[] = [];
    for (const p of points.filter(pt => !hiddenPoints.has(pt.id) && isItemVisible(pt.task_set_id) && passesSiteFilter(pt))) {
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] }, properties: { name: p.name, description: p.description || '', icon: p.icon, color: p.color, type: 'point' } });
    }
    for (const s of sensors.filter(s => !hiddenSensors.has(s.id) && isItemVisible(s.task_set_id))) {
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] }, properties: { name: s.name, sensor_type: s.sensor_type, status: s.status, type: 'sensor' } });
    }
    for (const poly of polygons.filter(p => !hiddenPolygons.has(p.id) && isItemVisible(p.task_set_id))) {
      const ring = [...poly.coordinates.map((c: number[]) => [c[1], c[0]]), [poly.coordinates[0][1], poly.coordinates[0][0]]];
      features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: { name: poly.name, color: poly.color, type: 'polygon' } });
    }
    for (const line of lines.filter(l => !hiddenLines.has(l.id) && isItemVisible(l.task_set_id))) {
      const coords = line.coordinates.map((c: number[]) => [c[1], c[0]]);
      features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: { name: line.name, color: line.color, type: 'line' } });
    }
    if (features.length === 0) { toast('No visible items to export', 'warning'); return; }
    const geojson: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features };
    downloadFile(JSON.stringify(geojson, null, 2), 'map-data.geojson', 'application/geo+json');
    toast(`Exported ${features.length} features as GeoJSON`, 'success');
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast('CSV file is empty or has no data rows', 'warning');
        return;
      }

      const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
      const nameIdx = headers.indexOf('name');
      const latIdx = headers.indexOf('latitude');
      const lngIdx = headers.indexOf('longitude');
      const descIdx = headers.indexOf('description');
      const iconIdx = headers.indexOf('icon');
      const colorIdx = headers.indexOf('color');
      const metaIdx = headers.indexOf('metadata');

      if (nameIdx === -1 || latIdx === -1 || lngIdx === -1) {
        toast('CSV must have columns: name, latitude, longitude', 'warning');
        return;
      }

      const points: Array<{name: string; latitude: number; longitude: number; description?: string; icon?: string; color?: string; metadata?: Record<string, string>}> = [];
      const skipped: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);
        const name = cols[nameIdx];
        const lat = parseFloat(cols[latIdx]);
        const lng = parseFloat(cols[lngIdx]);

        if (!name || isNaN(lat) || isNaN(lng)) {
          skipped.push(name || `Row ${i + 1}`);
          continue;
        }

        const point: {name: string; latitude: number; longitude: number; description?: string; icon?: string; color?: string; metadata?: Record<string, string>} = { name, latitude: lat, longitude: lng };
        if (descIdx !== -1 && cols[descIdx]) point.description = cols[descIdx];
        if (iconIdx !== -1 && cols[iconIdx]) point.icon = cols[iconIdx];
        if (colorIdx !== -1 && cols[colorIdx]) point.color = cols[colorIdx];
        if (metaIdx !== -1 && cols[metaIdx]) {
          try { point.metadata = JSON.parse(cols[metaIdx]); } catch { /* skip invalid json */ }
        }
        if (selectedMarkerIcon) {
          point.metadata = { ...point.metadata, marker_icon: selectedMarkerIcon };
        }
        points.push(point);
      }

      if (points.length === 0) {
        toast('No valid points found in CSV', 'warning');
        return;
      }

      const msg = `Import ${points.length} points?` + (skipped.length ? `\n(${skipped.length} rows skipped — missing coordinates)` : '');
      if (!await confirm({ message: msg, confirmLabel: 'Import', danger: false })) return;

      let importTaskSetId = selectedTaskSetId || null;
      if (!importTaskSetId) {
        const tsName = file.name.replace(/\.csv$/i, '');
        try {
          const tsRes = await fetch(`${API_URL}/api/task-sets?scope=${dataScope}`, {
            method: 'POST',
            headers: getAuthHeaders(true),
            body: JSON.stringify({ name: tsName })
          });
          if (tsRes.ok) {
            const ts = await tsRes.json();
            importTaskSetId = ts.id;
          }
        } catch {}
      }

      const response = await fetch(`${API_URL}/api/points/bulk?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ points, task_set_id: importTaskSetId }),
      });

      if (response.ok) {
        const result = await response.json();
        toast(`Imported ${result.created} points successfully`, 'success');
        fetchPoints();
        fetchTaskSets();
      } else {
        const err = await response.json();
        toast(err.detail || 'Import failed', 'error');
      }
    } catch (error) {
      console.error('CSV import error:', error);
      toast('Error importing CSV file', 'error');
    } finally {
      setImporting(false);
    }
  };

  const fetchDcimPhotosForPoint = async (pointId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/dcim/photos/by-point/${pointId}?scope=${dataScope}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const photos: DcimPhoto[] = await response.json();
        setDcimPhotosForPoint(photos);
        const hasPanorama = photos.some(p => p.photo_type === 'panorama_tile' && p.uploaded);
        setDcimPanoramaView(hasPanorama);
      }
    } catch (error) {
      console.error('Failed to fetch DCIM photos:', error);
    }
  };

  const fetchDcimVideosForLine = async (lineId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/dcim/videos/by-line/${lineId}?scope=${dataScope}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setDcimVideosForLine(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch DCIM videos:', error);
    }
  };

  const deleteDcimPhoto = async (photoId: number) => {
    if (!await confirm({ message: 'Delete this photo?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/dcim/photos/${photoId}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const result = await response.json();
        setDcimPhotosForPoint(prev => prev.filter(p => p.id !== photoId));
        if (result.point_removed && selectedPoint) {
          fetchPoints();
        }
      } else {
        toast('Failed to delete photo', 'error');
      }
    } catch (error) {
      console.error('Failed to delete DCIM photo:', error);
    }
  };

  const deleteDcimUpload = async (uploadId: number) => {
    if (!await confirm({ message: 'Delete this entire DCIM upload and all its photos? This cannot be undone.' })) return;
    try {
      const response = await fetch(`${API_URL}/api/dcim/uploads/${uploadId}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const result = await response.json();
        setDcimPhotosForPoint([]);
        if (result.removed_points?.length > 0) {
          fetchPoints();
        }
      } else {
        toast('Failed to delete upload', 'error');
      }
    } catch (error) {
      console.error('Failed to delete DCIM upload:', error);
    }
  };

  const downloadDcimReport = async (uploadId: number, folderName: string) => {
    try {
      const response = await fetch(`${API_URL}/api/dcim/uploads/${uploadId}/report?scope=${dataScope}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dcim_report_${folderName.replace(/[^a-zA-Z0-9_-]/g, '_')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download DCIM report:', error);
    }
  };

  const handleDcimImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    e.target.value = '';

    setDcimImporting(true);
    setDcimProgress({ phase: 'Scanning files...', current: 0, total: files.length });

    try {
      const mediaFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (/\.(jpe?g|mp4|mov)$/i.test(f.name) && !f.name.startsWith('.')) {
          mediaFiles.push(f);
        }
      }

      if (mediaFiles.length === 0) {
        toast('No photos or videos found in folder', 'warning');
        return;
      }

      setDcimProgress({ phase: 'Reading EXIF data...', current: 0, total: mediaFiles.length });

      const folderName = mediaFiles[0].webkitRelativePath.split('/')[0] || 'DCIM';

      interface PhotoMeta {
        file: File;
        filename: string;
        folder_path: string;
        photo_type: 'waypoint' | 'panorama_tile' | 'video';
        latitude?: number;
        longitude?: number;
        altitude?: number;
        captured_at?: string;
        camera_make?: string;
        camera_model?: string;
        image_width?: number;
        image_height?: number;
        file_size: number;
        mission_name?: string;
        waypoint_index?: number;
        panorama_group?: string;
        lens_type?: string;
        heading?: number;
      }

      const photoMetas: PhotoMeta[] = [];

      const missionNumToName: Record<string, string> = {};
      const missionSeqMax: Record<string, Record<string, number>> = {};
      for (const f of mediaFiles) {
        const p = f.webkitRelativePath.split('/');
        const djiFolder = p.find(s => /^DJI_\d+_\d+_/.test(s));
        if (djiFolder) {
          const m = djiFolder.match(/^DJI_\d+_(\d+)_(.+)$/);
          if (m) {
            missionNumToName[m[1]] = m[2];
            const mName = m[2];
            const seqNum = m[1];
            const fileMatch = f.name.match(/_(\d{4})_[A-Z]\./i);
            if (fileMatch) {
              const idx = parseInt(fileMatch[1], 10);
              if (!missionSeqMax[mName]) missionSeqMax[mName] = {};
              missionSeqMax[mName][seqNum] = Math.max(missionSeqMax[mName][seqNum] || 0, idx);
            }
          }
        }
      }

      const missionSeqOffset: Record<string, Record<string, number>> = {};
      for (const [mName, seqs] of Object.entries(missionSeqMax)) {
        const sortedSeqs = Object.keys(seqs).sort();
        if (sortedSeqs.length <= 1) continue;
        missionSeqOffset[mName] = {};
        let cumOffset = 0;
        for (const seq of sortedSeqs) {
          missionSeqOffset[mName][seq] = cumOffset;
          cumOffset += seqs[seq];
        }
      }

      for (let i = 0; i < mediaFiles.length; i++) {
        const file = mediaFiles[i];
        const relPath = file.webkitRelativePath;
        const parts = relPath.split('/');

        const isVideo = /\.(mp4|mov)$/i.test(file.name);
        setDcimProgress({ phase: isVideo ? 'Processing videos...' : 'Reading EXIF data...', current: i + 1, total: mediaFiles.length });

        let exif: Record<string, unknown> = {};
        if (!isVideo) {
          try {
            exif = await exifr.parse(file, { gps: true, xmp: true }) || {};
          } catch {
            // skip files without EXIF
          }
        }

        let photoType: 'waypoint' | 'panorama_tile' | 'video' = isVideo ? 'video' : 'waypoint';
        let missionName: string | undefined;
        let waypointIndex: number | undefined;
        let panoramaGroup: string | undefined;
        let lensType: string | undefined;
        const folderPath = parts.slice(1, -1).join('/');
        let folderSeqNum: string | undefined;

        if (!isVideo && parts.some(p => p.toUpperCase() === 'PANORAMA')) {
          photoType = 'panorama_tile';
          const panoFolder = parts.find((_, idx) => idx > 0 && parts[idx - 1]?.toUpperCase() === 'PANORAMA');
          if (panoFolder) {
            panoramaGroup = panoFolder;
            const pgParts = panoFolder.split('_');
            if (pgParts.length >= 2) {
              const missionNum = pgParts[0];
              missionName = missionNumToName[missionNum] || missionNum;
              waypointIndex = parseInt(pgParts[1], 10) || undefined;
            }
          }
        } else {
          const djiFolderMatch = parts.find(p => /^DJI_\d+_\d+_/.test(p));
          if (djiFolderMatch) {
            const m = djiFolderMatch.match(/^DJI_\d+_(\d+)_(.+)$/);
            if (m) {
              folderSeqNum = m[1];
              missionName = m[2];
            }
          }
          const fileMatch = file.name.match(/_(\d{4})_([A-Z])\./i);
          if (fileMatch) {
            waypointIndex = parseInt(fileMatch[1], 10);
            lensType = fileMatch[2].toUpperCase();
          }
          if (waypointIndex != null && missionName && folderSeqNum && missionSeqOffset[missionName]?.[folderSeqNum]) {
            waypointIndex += missionSeqOffset[missionName][folderSeqNum];
          }
        }

        const capturedAt = exif.DateTimeOriginal
          ? (exif.DateTimeOriginal instanceof Date ? exif.DateTimeOriginal.toISOString() : String(exif.DateTimeOriginal))
          : undefined;

        photoMetas.push({
          file,
          filename: file.name,
          folder_path: folderPath,
          photo_type: photoType,
          latitude: typeof exif.latitude === 'number' ? exif.latitude : undefined,
          longitude: typeof exif.longitude === 'number' ? exif.longitude : undefined,
          altitude: typeof exif.GPSAltitude === 'number' ? exif.GPSAltitude : undefined,
          captured_at: capturedAt,
          camera_make: typeof exif.Make === 'string' ? exif.Make : undefined,
          camera_model: typeof exif.Model === 'string' ? exif.Model : undefined,
          image_width: typeof exif.ExifImageWidth === 'number' ? exif.ExifImageWidth : undefined,
          image_height: typeof exif.ExifImageHeight === 'number' ? exif.ExifImageHeight : undefined,
          file_size: file.size,
          mission_name: missionName,
          waypoint_index: waypointIndex,
          panorama_group: panoramaGroup,
          lens_type: lensType,
          heading: (() => {
            const dir = exif.GPSImgDirection;
            if (typeof dir === 'number') return ((dir % 360) + 360) % 360;
            const gimbalYaw = exif.GimbalYawDegree;
            if (typeof gimbalYaw === 'number') return ((gimbalYaw % 360) + 360) % 360;
            if (typeof gimbalYaw === 'string') { const v = parseFloat(gimbalYaw); if (!isNaN(v)) return ((v % 360) + 360) % 360; }
            for (const key of Object.keys(exif)) {
              if (/gimbalyaw/i.test(key)) {
                const v = typeof exif[key] === 'number' ? exif[key] as number : parseFloat(String(exif[key]));
                if (!isNaN(v)) return ((v % 360) + 360) % 360;
              }
            }
            return undefined;
          })(),
        });
      }

      const waypoints = photoMetas.filter(p => p.photo_type === 'waypoint');
      const panos = photoMetas.filter(p => p.photo_type === 'panorama_tile');
      const videos = photoMetas.filter(p => p.photo_type === 'video');
      const missions = new Set(photoMetas.map(p => p.mission_name).filter(Boolean));
      const uniqueWpIndices = new Set(waypoints.map(p => p.waypoint_index).filter(i => i != null));
      const lensTypes = new Set(waypoints.map(p => p.lens_type).filter(Boolean));
      const hasMission = uniqueWpIndices.size >= 2 && waypoints.length > 0;

      const splitMissions = Object.keys(missionSeqOffset);

      const haversineM = (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const R = 6371000, p = Math.PI / 180;
        const dLat = (lat2 - lat1) * p, dLon = (lon2 - lon1) * p;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * p) * Math.cos(lat2 * p) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.asin(Math.sqrt(a));
      };

      const wpByIndex: Record<number, PhotoMeta> = {};
      for (const w of waypoints) {
        if (w.waypoint_index != null && w.latitude != null && w.longitude != null && !(w.waypoint_index in wpByIndex)) {
          wpByIndex[w.waypoint_index] = w;
        }
      }
      const wpUnique = Object.values(wpByIndex);
      const clustered = new Set<number>();
      let previewSiteCount = 0;
      for (const w of wpUnique) {
        if (clustered.has(w.waypoint_index!)) continue;
        previewSiteCount++;
        clustered.add(w.waypoint_index!);
        for (const w2 of wpUnique) {
          if (clustered.has(w2.waypoint_index!)) continue;
          if (haversineM(w.latitude!, w.longitude!, w2.latitude!, w2.longitude!) <= 15) {
            clustered.add(w2.waypoint_index!);
          }
        }
      }

      const siteInfo = previewSiteCount > 0 && previewSiteCount < uniqueWpIndices.size
        ? `${previewSiteCount} sites (nearby waypoints grouped)`
        : `${uniqueWpIndices.size} waypoint location(s)`;

      const msg = `DCIM Import Summary:\n\n` +
        `${photoMetas.length} files found\n` +
        (missions.size > 0 ? `Mission: ${[...missions].join(', ')}\n` : '') +
        (splitMissions.length > 0 ? `(${Object.keys(missionSeqMax[splitMissions[0]] || {}).length} folders merged — split mission detected)\n` : '') +
        `${siteInfo}` +
        (lensTypes.size > 0 ? ` (${[...lensTypes].sort().join('/')} lenses)\n` : '\n') +
        `${waypoints.length} waypoint photo(s)\n` +
        `${panos.length} panorama tile(s)\n` +
        (videos.length > 0 ? `${videos.length} video(s)\n` : '') +
        `\nThis will create ${previewSiteCount > 0 && previewSiteCount < uniqueWpIndices.size ? previewSiteCount + ' site' : uniqueWpIndices.size + ' map'} point(s) on the map.` +
        (hasMission ? `\nA flight path and mission will also be saved.` : '') +
        `\nContinue?`;

      if (!await confirm({ message: msg, confirmLabel: 'Import', danger: false })) return;

      let dcimTaskSetId: number | null = null;
      if (hasMission && taskSets.length > 0) {
        dcimTaskSetId = await new Promise<number | null>(resolve => {
          dcimTaskSetResolve.current = resolve;
          setDcimTaskSetPicker(true);
        });
        setDcimTaskSetPicker(false);
      }

      setDcimProgress({ phase: 'Creating records...', current: 0, total: 1 });

      let importTaskSetId = selectedTaskSetId || null;
      if (!importTaskSetId) {
        try {
          const tsRes = await fetch(`${API_URL}/api/task-sets?scope=${dataScope}`, {
            method: 'POST',
            headers: getAuthHeaders(true),
            body: JSON.stringify({ name: folderName })
          });
          if (tsRes.ok) {
            const ts = await tsRes.json();
            importTaskSetId = ts.id;
          }
        } catch {}
      }

      const manifest = {
        folder_name: folderName,
        photos: photoMetas.map(p => ({
          filename: p.filename,
          folder_path: p.folder_path,
          photo_type: p.photo_type,
          latitude: p.latitude,
          longitude: p.longitude,
          altitude: p.altitude,
          captured_at: p.captured_at,
          camera_make: p.camera_make,
          camera_model: p.camera_model,
          image_width: p.image_width,
          image_height: p.image_height,
          file_size: p.file_size,
          mission_name: p.mission_name,
          waypoint_index: p.waypoint_index,
          panorama_group: p.panorama_group,
          lens_type: p.lens_type,
          heading: p.heading,
        })),
        task_set_id: importTaskSetId,
        match_task_set_id: dcimTaskSetId,
        create_mission: hasMission,
      };

      const manifestRes = await fetch(`${API_URL}/api/dcim/upload?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(manifest),
      });

      if (!manifestRes.ok) {
        const err = await manifestRes.json();
        toast(err.detail || 'Failed to create DCIM upload', 'error');
        return;
      }

      const uploadResult = await manifestRes.json();
      const photoIdMap: Record<string, number> = {};
      const photoRecordMap: Record<string, DcimPhoto> = {};
      for (const photo of uploadResult.photos as DcimPhoto[]) {
        const key = `${photo.folder_path}/${photo.filename}`;
        photoIdMap[key] = photo.id;
        photoRecordMap[key] = photo;
      }

      const createdPointIds = (uploadResult.created_points || []).map((p: MapPoint) => p.id);
      if (createdPointIds.length > 0) {
        setHiddenPoints(prev => {
          const next = new Set(prev);
          let changed = false;
          for (const pid of createdPointIds) {
            if (next.has(pid)) { next.delete(pid); changed = true; }
          }
          if (changed) localStorage.setItem('toc_hiddenPoints', JSON.stringify([...next]));
          return changed ? next : prev;
        });
      }
      if (uploadResult.line_ids?.length) {
        setHiddenLines(prev => {
          const next = new Set(prev);
          let changed = false;
          for (const lid of uploadResult.line_ids) {
            if (next.has(lid)) { next.delete(lid); changed = true; }
          }
          if (changed) localStorage.setItem('toc_hiddenLines', JSON.stringify([...next]));
          return changed ? next : prev;
        });
      }
      if (importTaskSetId) {
        setHiddenTaskSetIds(prev => {
          if (!prev.has(importTaskSetId!)) return prev;
          const next = new Set(prev);
          next.delete(importTaskSetId!);
          localStorage.setItem('toc_hiddenTaskSets', JSON.stringify([...next]));
          return next;
        });
      }

      fetchPoints();
      fetchTaskSets();

      let uploaded = 0;
      const total = photoMetas.length;
      setDcimProgress({ phase: 'Uploading files...', current: 0, total });

      for (const meta of photoMetas) {
        const key = `${meta.folder_path}/${meta.filename}`;
        const photoId = photoIdMap[key];
        const photoRecord = photoRecordMap[key];
        if (!photoId) continue;

        const isVid = meta.photo_type === 'video';
        setDcimProgress({ phase: isVid ? `Uploading video: ${meta.filename}` : 'Uploading files...', current: uploaded, total });

        try {
          if (photoRecord?.presigned_upload) {
            const { url: s3Url, fields } = photoRecord.presigned_upload;
            const s3Form = new FormData();
            Object.entries(fields).forEach(([k, v]) => s3Form.append(k, v));
            s3Form.append('file', meta.file);
            await new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', s3Url);
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const pct = Math.round((e.loaded / e.total) * 100);
                  const loadedMB = (e.loaded / (1024 * 1024)).toFixed(0);
                  const totalMB = (e.total / (1024 * 1024)).toFixed(0);
                  setDcimProgress({ phase: `Uploading video: ${meta.filename} (${loadedMB}/${totalMB} MB — ${pct}%)`, current: uploaded, total });
                }
              };
              xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.responseText}`));
              xhr.onerror = () => reject(new Error('S3 upload network error'));
              xhr.send(s3Form);
            });
            setDcimProgress({ phase: `Video uploaded — confirming ${meta.filename}...`, current: uploaded, total });
            const confirmRes = await fetch(`${API_URL}/api/dcim/photos/${photoId}/confirm-upload?scope=${dataScope}`, {
              method: 'POST',
              headers: getAuthHeaders(),
            });
            if (!confirmRes.ok) throw new Error(`Confirm failed: ${confirmRes.status}`);
          } else {
            const formData = new FormData();
            formData.append('file', meta.file);
            await fetch(`${API_URL}/api/dcim/photos/${photoId}/file?scope=${dataScope}`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: formData,
            });
          }
        } catch (err) {
          console.error(`Failed to upload ${meta.filename}:`, err);
        }

        uploaded++;
        const isVidDone = meta.photo_type === 'video';
        setDcimProgress({ phase: isVidDone ? `Video saved — optimizing in background. Uploading remaining files...` : 'Uploading files...', current: uploaded, total });
      }

      const videoCount = photoMetas.filter(p => p.photo_type === 'video').length;
      const missionCount = uploadResult.mission_ids?.length || (uploadResult.mission_id ? 1 : 0);
      const missionMsg = missionCount > 0 ? `\n${missionCount} mission(s) saved (flight paths on map)` : '';
      const videoMsg = videoCount > 0 ? `\n${videoCount} video(s) uploaded — optimizing to 720p in background` : '';
      const siteCount = uploadResult.site_count || uploadResult.created_points?.length || 0;
      const wpCount = uploadResult.waypoint_count || 0;
      const siteLabel = siteCount < wpCount ? `${siteCount} sites (${wpCount} waypoints clustered)` : `${siteCount} map points`;
      toast(`DCIM import complete!\n${siteLabel}\n${uploaded}/${total} files uploaded${videoMsg}${missionMsg}`, 'success');
      fetchPoints();
      fetchLines();
      fetchMissions();
    } catch (error) {
      console.error('DCIM import error:', error);
      toast('Error during DCIM import', 'error');
    } finally {
      setDcimImporting(false);
      setDcimProgress(null);
    }
  };

  const uploadAttachment = async (pointId: number, file: File) => {
    setUploadingAttachment(prev => ({...prev, [`point-${pointId}`]: true}));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/points/${pointId}/attachments?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      if (response.ok) {
        const updatedPoint = await response.json();
        setSelectedPoint(updatedPoint);
        fetchPoints();
      } else {
        const err = await response.json();
        toast(err.detail || 'Failed to upload file', 'error');
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      toast('Error uploading file', 'error');
    } finally {
      setUploadingAttachment(prev => ({...prev, [`point-${pointId}`]: false}));
    }
  };

  const deleteAttachment = async (pointId: number, attachmentId: string) => {
    if (!await confirm({ message: 'Delete this attachment?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/points/${pointId}/attachments/${attachmentId}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const updatedPoint = await response.json();
        setSelectedPoint(updatedPoint);
        fetchPoints();
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const deletePolygon = async (id: number) => {
    if (!await confirm({ message: 'Delete this area?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/polygons/${id}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        fetchPolygons();
        setSelectedPolygon(null);
      }
    } catch (error) {
      console.error('Failed to delete polygon:', error);
    }
  };

  const cancelDraw = () => {
    setDrawMode('none');
    setTempPolygonCoords([]);
    setTempLineCoords([]);
    setTempLinePointIds([]);
    setNewPointName('');
    setNewPolygonName('');
    setNewLineName('');
    setNewSensorName('');
    setSelectedTaskSetId(undefined);
  };

  const deleteLine = async (id: number) => {
    if (!await confirm({ message: 'Delete this line?' })) return;
    try {
      const response = await fetch(`${API_URL}/api/lines/${id}?scope=${dataScope}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        setSelectedLine(null);
        fetchLines();
        if (result.deleted_missions > 0) {
          fetchMissions();
          toast(`Line deleted along with ${result.deleted_missions} linked mission(s)`, 'info');
        }
      }
    } catch (error) {
      console.error('Failed to delete line:', error);
    }
  };

  const toggleBulkSelect = (type: 'points' | 'polygons' | 'lines' | 'sensors', id: number) => {
    setBulkSelected(prev => {
      const s = new Set(prev[type]);
      if (s.has(id)) s.delete(id); else s.add(id);
      return { ...prev, [type]: s };
    });
  };

  const bulkDelete = async () => {
    const parts: string[] = [];
    if (bulkSelected.points.size) parts.push(`${bulkSelected.points.size} point(s)`);
    if (bulkSelected.polygons.size) parts.push(`${bulkSelected.polygons.size} area(s)`);
    if (bulkSelected.lines.size) parts.push(`${bulkSelected.lines.size} line(s)`);
    if (bulkSelected.sensors.size) parts.push(`${bulkSelected.sensors.size} sensor(s)`);
    if (!await confirm({ message: `Delete ${parts.join(', ')}?` })) return;
    try {
      const response = await fetch(`${API_URL}/api/bulk-delete?scope=${dataScope}`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          point_ids: [...bulkSelected.points],
          polygon_ids: [...bulkSelected.polygons],
          line_ids: [...bulkSelected.lines],
          sensor_ids: [...bulkSelected.sensors],
        }),
      });
      if (response.ok) {
        const result = await response.json();
        toast(`Deleted ${result.total} item(s)${result.deleted.missions ? ` and ${result.deleted.missions} linked mission(s)` : ''}`, 'success');
        setBulkSelected({ points: new Set(), polygons: new Set(), lines: new Set(), sensors: new Set() });
        setBulkSelectMode(false);
        setSelectedPoint(null);
        setSelectedPolygon(null);
        setSelectedLine(null);
        setSelectedSensor(null);
        fetchPoints();
        fetchPolygons();
        fetchLines();
        fetchSensors();
        fetchMissions();
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast('Bulk delete failed', 'error');
    }
  };

  const getCursor = () => {
    if (drawMode === 'point' || drawMode === 'polygon' || drawMode === 'line' || drawMode === 'sensor' || drawMode === 'unit') return 'crosshair';
    return 'grab';
  };

  const centerOnPoint = (longitude: number, latitude: number) => {
    if (mapRef.current) {
      const map = (mapRef.current as any).getMap();
      if (map) {
        map.flyTo({
          center: [longitude, latitude],
          zoom: 16,
          duration: 1000,
          essential: true
        });
      }
    }
  };

  const centerOnPolygon = (coordinates: number[][]) => {
    if (!coordinates || coordinates.length === 0) return;
    
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    
    coordinates.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });
    
    if (mapRef.current) {
      const map = (mapRef.current as any).getMap();
      if (map) {
        map.fitBounds(
          [[minLng, minLat], [maxLng, maxLat]],
          { padding: 50, duration: 1000, essential: true }
        );
      }
    }
  };

  const centerOnLine = (coordinates: number[][]) => {
    if (!coordinates || coordinates.length === 0) return;
    
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    
    coordinates.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });
    
    if (mapRef.current) {
      const map = (mapRef.current as any).getMap();
      if (map) {
        map.fitBounds(
          [[minLng, minLat], [maxLng, maxLat]],
          { padding: 50, duration: 1000, essential: true }
        );
      }
    }
  };

  const searchableItems = useMemo(() => {
    const items: { id: number | string; name: string; type: 'point' | 'line' | 'polygon' | 'sensor' | 'incident' | 'mission' | 'task-set'; latitude?: number; longitude?: number; coordinates?: number[][]; color?: string; extra?: string }[] = [];
    for (const p of points) items.push({ id: p.id, name: p.name, type: 'point', latitude: p.latitude, longitude: p.longitude, color: p.color });
    for (const p of polygons) items.push({ id: p.id, name: p.name, type: 'polygon', coordinates: p.coordinates, color: p.color });
    for (const l of lines) items.push({ id: l.id, name: l.name, type: 'line', coordinates: l.coordinates, color: l.color });
    for (const s of sensors) items.push({ id: s.id, name: s.name, type: 'sensor', latitude: s.latitude, longitude: s.longitude, color: s.color, extra: s.sensor_type });
    for (const i of incidents) items.push({ id: i.id, name: i.title, type: 'incident', latitude: i.latitude, longitude: i.longitude, extra: `${i.severity ? ['', 'Routine', 'Priority', 'Immediate', 'Flash', 'Override'][i.severity] : ''} · ${i.status}` });
    for (const m of missions) items.push({ id: m.id, name: m.name, type: 'mission', extra: m.status });
    for (const t of taskSets) items.push({ id: t.id, name: t.name, type: 'task-set', color: t.color, extra: `${t.item_count} items` });
    return items;
  }, [points, polygons, lines, sensors, incidents, missions, taskSets]);

  const handleSearchSelect = useCallback((item: typeof searchableItems[number]) => {
    setSelectedPoint(null);
    setSelectedPolygon(null);
    setSelectedLine(null);
    setSelectedSensor(null);
    setSelectedIncident(null);

    switch (item.type) {
      case 'point': {
        const p = points.find(x => x.id === item.id);
        if (p) { setSelectedPoint(p); centerOnPoint(p.longitude, p.latitude); }
        break;
      }
      case 'polygon': {
        const p = polygons.find(x => x.id === item.id);
        if (p) { setSelectedPolygon(p); centerOnPolygon(p.coordinates); }
        break;
      }
      case 'line': {
        const l = lines.find(x => x.id === item.id);
        if (l) { setSelectedLine(l); centerOnLine(l.coordinates); }
        break;
      }
      case 'sensor': {
        const s = sensors.find(x => x.id === item.id);
        if (s) { setSelectedSensor(s); centerOnPoint(s.longitude, s.latitude); }
        break;
      }
      case 'incident': {
        const i = incidents.find(x => x.id === item.id);
        if (i) { setSelectedIncident(i); centerOnPoint(i.longitude, i.latitude); }
        break;
      }
      case 'mission': {
        const m = missions.find(x => x.id === item.id);
        if (m && m.waypoints?.length) {
          const wp = m.waypoints[0];
          centerOnPoint(wp.lng, wp.lat);
        }
        break;
      }
      case 'task-set': {
        setSelectedTaskSetId(item.id as number);
        break;
      }
    }
  }, [points, polygons, lines, sensors, incidents, missions, centerOnPoint, centerOnPolygon, centerOnLine]);

  // Single seam between App and the map assistant — all of its logic lives in useAssistantBridge.
  const assistantBridge = useAssistantBridge({
    getAuthHeaders,
    mapRef,
    scope: dataScope,
    mapStyle,
    setMapStyle,
    drawMode,
    setDrawMode: (mode) => setDrawMode(mode as typeof drawMode),
    searchableItems,
    onSelectItem: handleSearchSelect,
    counts: {
      points: points.length, polygons: polygons.length, lines: lines.length,
      sensors: sensors.length, units: units.length, incidents: incidents.length,
      missions: missions.length, task_sets: taskSets.length, kaek_parcels: kaekParcels.length,
    },
    hidden: { points: hiddenPoints, polygons: hiddenPolygons, lines: hiddenLines, sensors: hiddenSensors, units: hiddenUnits },
    setHidden: { points: setHiddenPoints, polygons: setHiddenPolygons, lines: setHiddenLines, sensors: setHiddenSensors, units: setHiddenUnits },
    allIds: {
      points: points.map(p => p.id), polygons: polygons.map(p => p.id), lines: lines.map(l => l.id),
      sensors: sensors.map(s => s.id), units: units.map(u => u.id),
    },
    toggles: { kaek: [showKaekLayer, setShowKaekLayer], uspace: [showUspaceLayer, setShowUspaceLayer] },
    weather: [weatherLayer, setWeatherLayer],
    refresh: { point: fetchPoints, polygon: fetchPolygons, line: fetchLines },
    groupName: user?.group_name ?? null,
    selection: selectedPoint ? { type: 'point', id: selectedPoint.id, name: selectedPoint.name }
      : selectedPolygon ? { type: 'polygon', id: selectedPolygon.id, name: selectedPolygon.name }
      : selectedLine ? { type: 'line', id: selectedLine.id, name: selectedLine.name }
      : selectedSensor ? { type: 'sensor', id: selectedSensor.id, name: selectedSensor.name }
      : null,
    refreshTaskSets: fetchTaskSets,
    ghosts: assistantGhosts,
    setGhosts: setAssistantGhosts,
    openMarketplace: () => setShowMarketplacePanel(true),
  });

  const calculatePolygonArea = (coordinates: number[][]): number => {
    if (!coordinates || coordinates.length < 3) return 0;
    
    const R = 6371000;
    let area = 0;
    const n = coordinates.length;
    
    for (let i = 0; i < n; i++) {
      const [lng1, lat1] = coordinates[i];
      const [lng2, lat2] = coordinates[(i + 1) % n];
      
      const lambda1 = lng1 * Math.PI / 180;
      const phi1 = lat1 * Math.PI / 180;
      const lambda2 = lng2 * Math.PI / 180;
      const phi2 = lat2 * Math.PI / 180;
      
      area += (lambda2 - lambda1) * (Math.sin(phi1) + Math.sin(phi2));
    }
    
    area = Math.abs(area * R * R / 2);
    return area;
  };

  const calculateLineLength = (coordinates: number[][]): number => {
    if (!coordinates || coordinates.length < 2) return 0;
    
    const R = 6371000;
    let length = 0;
    
    for (let i = 0; i < coordinates.length - 1; i++) {
      const [lng1, lat1] = coordinates[i];
      const [lng2, lat2] = coordinates[i + 1];
      
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2);
      
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      length += R * c;
    }
    
    return length;
  };

  const formatArea = (areaSqMeters: number): string => {
    if (areaSqMeters >= 1000000) {
      return `${(areaSqMeters / 1000000).toFixed(2)} km²`;
    } else {
      return `${Math.round(areaSqMeters)} m²`;
    }
  };

  const formatLength = (lengthMeters: number): string => {
    if (lengthMeters >= 1000) {
      return `${(lengthMeters / 1000).toFixed(2)} km`;
    } else {
      return `${Math.round(lengthMeters)} m`;
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <img src="/logo.png" alt="Overwatch" className="loading-logo" />
        <div className="loading-spinner" />
        <div className="loading-text">Initializing tactical systems...</div>
      </div>
    );
  }

  if (isPending) {
    return <PendingApproval />;
  }

  if (!isAuthenticated) {
    return authView === 'login' ? (
      <Login onSwitchToSignup={() => setAuthView('signup')} />
    ) : (
      <Signup onSwitchToLogin={() => setAuthView('login')} />
    );
  }

  return (
    <div className="App">
      <aside ref={leftPaneRef} className={`sidebar ${leftPanelCollapsed ? 'collapsed' : ''}`} style={{ width: leftPanelCollapsed ? '0px' : `${leftPanelWidth}px` }}>
        <div className="sidebar-header">
          <img src="/logo.png" alt="Overwatch" className="sidebar-logo" />
        </div>
        <div className="sidebar-group-bar">
          {user?.groups && user.groups.length > 1 ? (
            <select
              className="group-switcher"
              value={user.group_id || ''}
              onChange={async (e) => {
                const gid = parseInt(e.target.value);
                if (!gid || gid === user.group_id) return;
                try {
                  const res = await fetch(`${API_URL}/api/groups/switch`, {
                    method: 'POST',
                    headers: getAuthHeaders(true),
                    body: JSON.stringify({ group_id: gid })
                  });
                  if (res.ok) {
                    const data = await res.json();
                    await refreshUser();
                    handleGroupChange(data.home_view || undefined);
                  }
                } catch { /* ignore */ }
              }}
            >
              {user.groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          ) : (
            <div
              className="group-indicator"
              onClick={() => setShowGroupPanel(!showGroupPanel)}
            >
              {user?.group_name ? user.group_name : 'No Group'}
            </div>
          )}
          {user?.group_role && <span className={`group-role-badge role-${user.group_role}`}>{user.group_role}</span>}
          <button
            className="group-settings-btn"
            onClick={() => setShowGroupPanel(!showGroupPanel)}
            title="Group settings"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>
        {showGroupPanel && (
          <div className="sidebar-group-inline">
            <GroupPanel onGroupChange={handleGroupChange} initialGroupId={user?.group_id} />
          </div>
        )}

        <div className="scope-switcher">
          <button
            className={`scope-btn ${dataScope === 'group' ? 'active' : ''}`}
            onClick={() => { setDataScope('group'); localStorage.setItem('toc_dataScope', 'group'); }}
          >
            Group
          </button>
          <button
            className={`scope-btn ${dataScope === 'personal' ? 'active' : ''}`}
            onClick={() => { setDataScope('personal'); localStorage.setItem('toc_dataScope', 'personal'); }}
          >
            Personal
          </button>
        </div>

        {showMap && (
          <>
            <div className="sidebar-section layers-section">
              <h4 className="section-header" onClick={() => toggleSection('layers')}>
                <span className="section-toggle"><Icon name={collapsedSections.has('layers') ? 'chev-right' : 'chev-down'} /></span> Visible Layers
              </h4>
              {!collapsedSections.has('layers') && (<>
              <div className="sidebar-search">
                <input
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Filter items..."
                  value={sidebarFilter}
                  onChange={(e) => setSidebarFilter(e.target.value)}
                />
                {sidebarFilter && (
                  <button className="sidebar-search-clear" onClick={() => setSidebarFilter('')} aria-label="Close"><Icon name="clear" /></button>
                )}
              </div>
              <div className="export-buttons">
                <button
                  className={`export-btn${bulkSelectMode ? ' active' : ''}`}
                  onClick={() => { setBulkSelectMode(!bulkSelectMode); if (bulkSelectMode) setBulkSelected({ points: new Set(), polygons: new Set(), lines: new Set(), sensors: new Set() }); }}
                  title={bulkSelectMode ? 'Exit select mode' : 'Select items for bulk delete'}
                  style={bulkSelectMode ? { background: 'var(--color-danger-fill)', color: 'var(--color-on-danger)', borderColor: 'var(--color-danger-fill)' } : {}}
                >{bulkSelectMode ? 'Cancel' : 'Select'}</button>
                <button className="export-btn" onClick={exportCSV} title="Export visible points as CSV">CSV</button>
                <button className="export-btn" onClick={exportGeoJSON} title="Export all visible items as GeoJSON">GeoJSON</button>
              </div>
              
              <div className="site-filter-row">
                <label className="site-filter-label">Sites</label>
                <select
                  className="site-filter-select"
                  value={siteFilterTaskSetId ?? ''}
                  onChange={(e) => { const v = e.target.value ? Number(e.target.value) : null; setSiteFilterTaskSetId(v); localStorage.setItem('toc_siteFilterTaskSet', v != null ? String(v) : ''); if (!v) { setSiteFilter('all'); localStorage.setItem('toc_siteFilter', 'all'); } }}
                >
                  <option value="">All Task Sets</option>
                  {taskSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
                </select>
                {siteFilterTaskSetId != null && (
                  <select
                    className="site-filter-select"
                    value={siteFilter}
                    onChange={(e) => { const v = e.target.value as 'all' | 'with-missions' | 'without-missions'; setSiteFilter(v); localStorage.setItem('toc_siteFilter', v); }}
                  >
                    <option value="all">All ({visiblePointCount.total})</option>
                    <option value="with-missions">With Missions ({visiblePointCount.withMissions})</option>
                    <option value="without-missions">Without Missions ({visiblePointCount.withoutMissions})</option>
                  </select>
                )}
              </div>

              <div className="layer-group">
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showDronePosition}
                    onChange={(e) => { setShowDronePosition(e.target.checked); localStorage.setItem('toc_showDronePosition', String(e.target.checked)); }}
                  />
                  <span><Icon name="drone" /> Drone Position</span>
                </label>
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showFovCone}
                    onChange={(e) => { setShowFovCone(e.target.checked); localStorage.setItem('toc_showFovCone', String(e.target.checked)); }}
                    disabled={!showDronePosition}
                  />
                  <span style={!showDronePosition ? { opacity: 0.5 } : undefined}><Icon name="fov" /> Camera FOV</span>
                </label>
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showAircraftLayer}
                    onChange={(e) => { setShowAircraftLayer(e.target.checked); localStorage.setItem('toc_showAircraftLayer', String(e.target.checked)); }}
                  />
                  <span><Icon name="aircraft" /> ADS-B Aircraft {showAircraftLayer && aircraftPositions.length > 0 ? `(${aircraftPositions.length})` : ''}</span>
                </label>
                {aisEnabled && (
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showVesselLayer}
                    onChange={(e) => { setShowVesselLayer(e.target.checked); localStorage.setItem('toc_showVesselLayer', String(e.target.checked)); }}
                  />
                  <span><Icon name="vessel" /> AIS Vessels {showVesselLayer && vesselPositions.length > 0 ? `(${vesselPositions.length})` : ''}</span>
                </label>
                )}
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showLiveCamLayer}
                    onChange={(e) => { setShowLiveCamLayer(e.target.checked); localStorage.setItem('toc_showLiveCamLayer', String(e.target.checked)); }}
                  />
                  <span><Icon name="webcam" /> Live Cameras {showLiveCamLayer && liveCameras.length > 0 ? `(${liveCameras.length})` : ''}</span>
                </label>
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showMGRSGrid}
                    onChange={(e) => { setShowMGRSGrid(e.target.checked); localStorage.setItem('toc_showMGRSGrid', String(e.target.checked)); }}
                  />
                  <span><Icon name="mgrs" /> MGRS Grid</span>
                </label>
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showKaekLayer}
                    onChange={(e) => { setShowKaekLayer(e.target.checked); localStorage.setItem('toc_showKaekLayer', String(e.target.checked)); }}
                  />
                  <span><Icon name="parcel" /> Cadastral (ΚΑΕΚ) {showKaekLayer && kaekParcels.length > 0 ? `(${kaekParcels.length})` : ''}</span>
                </label>
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={showUspaceLayer}
                    onChange={(e) => { setShowUspaceLayer(e.target.checked); localStorage.setItem('toc_showUspaceLayer', String(e.target.checked)); }}
                  />
                  <span><Icon name="uspace" /> U-space Zones {showUspaceLayer && geozones.length > 0 ? `(${geozones.length})` : ''}</span>
                </label>
                <div className="weather-layer-row">
                  <span className="weather-layer-label"><Icon name="weather" /> Weather</span>
                  <select
                    className="weather-layer-select"
                    value={weatherLayer}
                    onChange={(e) => { setWeatherLayer(e.target.value); localStorage.setItem('toc_weatherLayer', e.target.value); }}
                  >
                    <option value="off">Off</option>
                    <option value="radar">Radar</option>
                    {owmApiKey && <option value="clouds_new">Clouds</option>}
                    {owmApiKey && <option value="precipitation_new">Precipitation</option>}
                    {owmApiKey && <option value="temp_new">Temperature</option>}
                    {owmApiKey && <option value="wind_new">Wind Speed</option>}
                  </select>
                </div>
                {weatherLayer !== 'off' && (
                  <div className="weather-radar-controls">
                    <div className="weather-radar-slider">
                      <label>Opacity</label>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={weatherOpacity}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          setWeatherOpacity(val);
                          localStorage.setItem('toc_weatherOpacity', String(val));
                        }}
                      />
                      <span className="weather-radar-opacity-value">{Math.round(weatherOpacity * 100)}%</span>
                    </div>
                    {weatherLayer === 'radar' && weatherRadarTimestamp && (
                      <div className="weather-radar-timestamp">
                        Radar {new Date(weatherRadarTimestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                    {weatherConditions && (
                      <div className="weather-conditions">
                        <div className="weather-conditions-location">
                          <Icon name="site" /> {weatherConditions.lat.toFixed(4)}°, {weatherConditions.lng.toFixed(4)}°
                        </div>
                        <div className="weather-conditions-row">
                          <span><Icon name="wind" /> {weatherConditions.windSpeed} km/h {weatherConditions.windGust > weatherConditions.windSpeed ? `(G${Math.round(weatherConditions.windGust)})` : ''}</span>
                          <span><Icon name="temp" /> {weatherConditions.temp}°C</span>
                        </div>
                        <div className="weather-conditions-row">
                          <span><Icon name="cloud" /> {weatherConditions.cloudCover}%</span>
                          <span><Icon name="visibility" /> {weatherConditions.visibility >= 1000 ? `${(weatherConditions.visibility / 1000).toFixed(1)} km` : `${weatherConditions.visibility} m`}</span>
                        </div>
                      </div>
                    )}
                    {!weatherConditions && (
                      <div className="weather-conditions-hint">Click map for conditions</div>
                    )}
                    {!owmApiKey && weatherLayer === 'radar' && (
                      <div className="weather-owm-hint">
                        <input
                          type="text"
                          className="weather-owm-input"
                          placeholder="OWM API key for more layers"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const val = (e.target as HTMLInputElement).value.trim();
                              if (val) { setOwmApiKey(val); localStorage.setItem('toc_owmApiKey', val); }
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mgrs-search-row">
                <input
                  type="text"
                  className="mgrs-search-input"
                  placeholder="MGRS e.g. 34S EJ 92 07"
                  value={mgrsSearchQuery}
                  onChange={(e) => { setMgrsSearchQuery(e.target.value); setMgrsSearchError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleMGRSSearch(); }}
                />
                <button className="mgrs-search-btn" onClick={handleMGRSSearch} title="Go to MGRS coordinate">
                  Go
                </button>
              </div>
              {mgrsSearchError && <div className="mgrs-search-error">{mgrsSearchError}</div>}

              {points.filter(p => isItemVisible(p.task_set_id) && passesSiteFilter(p) && sidebarMatch(p.name)).length > 0 && (
                <div className="layer-group">
                  <h5 className="section-header-sm" onClick={() => toggleSection('points')}>
                    <span className="section-toggle"><Icon name={collapsedSections.has('points') ? 'chev-right' : 'chev-down'} /></span> Points ({points.filter(p => isItemVisible(p.task_set_id) && passesSiteFilter(p) && sidebarMatch(p.name)).length})
                  </h5>
                  {!collapsedSections.has('points') && (() => {
                    const visible = points.filter(p => isItemVisible(p.task_set_id) && passesSiteFilter(p) && sidebarMatch(p.name)).sort((a, b) => naturalSort(a.name, b.name));
                    const ungrouped = visible.filter(p => !p.task_set_id);
                    const grouped = taskSets
                      .map(ts => ({ ts, items: visible.filter(p => p.task_set_id === ts.id) }))
                      .filter(g => g.items.length > 0);
                    return (<>
                      {ungrouped.map((point) => (
                        <div key={point.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.points.has(point.id) ? ' bulk-selected' : ''}`}>
                          {bulkSelectMode
                            ? <input type="checkbox" checked={bulkSelected.points.has(point.id)} onChange={() => toggleBulkSelect('points', point.id)} />
                            : <input type="checkbox" checked={!hiddenPoints.has(point.id)} onChange={() => { const s = new Set(hiddenPoints); s.has(point.id) ? s.delete(point.id) : s.add(point.id); setHiddenPoints(s); localStorage.setItem('toc_hiddenPoints', JSON.stringify([...s])); }} />
                          }
                          <span style={{ color: point.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('points', point.id) : centerOnPoint(point.longitude, point.latitude)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name={resolvePointIcon(point.metadata, point.icon)} /> {point.name}</span>
                        </div>
                      ))}
                      {grouped.map(({ ts, items }) => (
                        <div key={`ts-points-${ts.id}`} className="task-set-group-indent">
                          <div className="section-header-sm task-set-group-header" style={{ color: ts.color }}
                            onClick={() => bulkSelectMode
                              ? setBulkSelected(prev => {
                                  const s = new Set(prev.points);
                                  const allSelected = items.every(p => s.has(p.id));
                                  items.forEach(p => allSelected ? s.delete(p.id) : s.add(p.id));
                                  return { ...prev, points: s };
                                })
                              : toggleSection(`points-ts-${ts.id}`)
                            }>
                            <span className="section-toggle"><Icon name={collapsedSections.has(`points-ts-${ts.id}`) ? 'chev-right' : 'chev-down'} /></span>
                            <span className="task-set-icon"><Icon name={resolveIcon(ts.icon)} /></span> <span>{ts.name} ({items.length})</span>
                          </div>
                          {!collapsedSections.has(`points-ts-${ts.id}`) && items.map((point) => (
                            <div key={point.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.points.has(point.id) ? ' bulk-selected' : ''}`} style={{ paddingLeft: '12px' }}>
                              {bulkSelectMode
                                ? <input type="checkbox" checked={bulkSelected.points.has(point.id)} onChange={() => toggleBulkSelect('points', point.id)} />
                                : <input type="checkbox" checked={!hiddenPoints.has(point.id)} onChange={() => { const s = new Set(hiddenPoints); s.has(point.id) ? s.delete(point.id) : s.add(point.id); setHiddenPoints(s); localStorage.setItem('toc_hiddenPoints', JSON.stringify([...s])); }} />
                              }
                              <span style={{ color: point.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('points', point.id) : centerOnPoint(point.longitude, point.latitude)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name={resolvePointIcon(point.metadata, point.icon)} /> {point.name}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>);
                  })()}
                </div>
              )}

              {polygons.filter(p => isItemVisible(p.task_set_id) && sidebarMatch(p.name)).length > 0 && (
                <div className="layer-group">
                  <h5 className="section-header-sm" onClick={() => toggleSection('areas')}>
                    <span className="section-toggle"><Icon name={collapsedSections.has('areas') ? 'chev-right' : 'chev-down'} /></span> Areas ({polygons.filter(p => isItemVisible(p.task_set_id) && sidebarMatch(p.name)).length})
                  </h5>
                  {!collapsedSections.has('areas') && (() => {
                    const visible = polygons.filter(p => isItemVisible(p.task_set_id) && sidebarMatch(p.name)).sort((a, b) => naturalSort(a.name, b.name));
                    const ungrouped = visible.filter(p => !p.task_set_id);
                    const grouped = taskSets
                      .map(ts => ({ ts, items: visible.filter(p => p.task_set_id === ts.id) }))
                      .filter(g => g.items.length > 0);
                    return (<>
                      {ungrouped.map((polygon) => (
                        <div key={polygon.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.polygons.has(polygon.id) ? ' bulk-selected' : ''}`}>
                          {bulkSelectMode
                            ? <input type="checkbox" checked={bulkSelected.polygons.has(polygon.id)} onChange={() => toggleBulkSelect('polygons', polygon.id)} />
                            : <input type="checkbox" checked={!hiddenPolygons.has(polygon.id)} onChange={() => { const s = new Set(hiddenPolygons); s.has(polygon.id) ? s.delete(polygon.id) : s.add(polygon.id); setHiddenPolygons(s); localStorage.setItem('toc_hiddenPolygons', JSON.stringify([...s])); }} />
                          }
                          <span style={{ color: polygon.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('polygons', polygon.id) : centerOnPolygon(polygon.coordinates)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name="area" /> {polygon.name}</span>
                        </div>
                      ))}
                      {grouped.map(({ ts, items }) => (
                        <div key={`ts-areas-${ts.id}`} className="task-set-group-indent">
                          <div className="section-header-sm task-set-group-header" style={{ color: ts.color }}
                            onClick={() => bulkSelectMode
                              ? setBulkSelected(prev => {
                                  const s = new Set(prev.polygons);
                                  const allSelected = items.every(p => s.has(p.id));
                                  items.forEach(p => allSelected ? s.delete(p.id) : s.add(p.id));
                                  return { ...prev, polygons: s };
                                })
                              : toggleSection(`areas-ts-${ts.id}`)
                            }>
                            <span className="section-toggle"><Icon name={collapsedSections.has(`areas-ts-${ts.id}`) ? 'chev-right' : 'chev-down'} /></span>
                            <span className="task-set-icon"><Icon name={resolveIcon(ts.icon)} /></span> <span>{ts.name} ({items.length})</span>
                          </div>
                          {!collapsedSections.has(`areas-ts-${ts.id}`) && items.map((polygon) => (
                            <div key={polygon.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.polygons.has(polygon.id) ? ' bulk-selected' : ''}`} style={{ paddingLeft: '12px' }}>
                              {bulkSelectMode
                                ? <input type="checkbox" checked={bulkSelected.polygons.has(polygon.id)} onChange={() => toggleBulkSelect('polygons', polygon.id)} />
                                : <input type="checkbox" checked={!hiddenPolygons.has(polygon.id)} onChange={() => { const s = new Set(hiddenPolygons); s.has(polygon.id) ? s.delete(polygon.id) : s.add(polygon.id); setHiddenPolygons(s); localStorage.setItem('toc_hiddenPolygons', JSON.stringify([...s])); }} />
                              }
                              <span style={{ color: polygon.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('polygons', polygon.id) : centerOnPolygon(polygon.coordinates)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name="area" /> {polygon.name}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>);
                  })()}
                </div>
              )}

              {lines.filter(l => isItemVisible(l.task_set_id) && sidebarMatch(l.name)).length > 0 && (
                <div className="layer-group">
                  <h5 className="section-header-sm" onClick={() => toggleSection('lines')}>
                    <span className="section-toggle"><Icon name={collapsedSections.has('lines') ? 'chev-right' : 'chev-down'} /></span> Lines ({lines.filter(l => isItemVisible(l.task_set_id) && sidebarMatch(l.name)).length})
                  </h5>
                  {!collapsedSections.has('lines') && (() => {
                    const visible = lines.filter(l => isItemVisible(l.task_set_id) && sidebarMatch(l.name)).sort((a, b) => naturalSort(a.name, b.name));
                    const ungrouped = visible.filter(l => !l.task_set_id);
                    const grouped = taskSets
                      .map(ts => ({ ts, items: visible.filter(l => l.task_set_id === ts.id) }))
                      .filter(g => g.items.length > 0);
                    return (<>
                      {ungrouped.map((line) => (
                        <div key={line.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.lines.has(line.id) ? ' bulk-selected' : ''}`}>
                          {bulkSelectMode
                            ? <input type="checkbox" checked={bulkSelected.lines.has(line.id)} onChange={() => toggleBulkSelect('lines', line.id)} />
                            : <input type="checkbox" checked={!hiddenLines.has(line.id)} onChange={() => toggleLineVisibility(line.id)} />
                          }
                          <span style={{ color: line.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('lines', line.id) : centerOnLine(line.coordinates)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name="path" /> {line.name}</span>
                        </div>
                      ))}
                      {grouped.map(({ ts, items }) => (
                        <div key={`ts-lines-${ts.id}`} className="task-set-group-indent">
                          <div className="section-header-sm task-set-group-header" style={{ color: ts.color }}
                            onClick={() => bulkSelectMode
                              ? setBulkSelected(prev => {
                                  const s = new Set(prev.lines);
                                  const allSelected = items.every(l => s.has(l.id));
                                  items.forEach(l => allSelected ? s.delete(l.id) : s.add(l.id));
                                  return { ...prev, lines: s };
                                })
                              : toggleSection(`lines-ts-${ts.id}`)
                            }>
                            <span className="section-toggle"><Icon name={collapsedSections.has(`lines-ts-${ts.id}`) ? 'chev-right' : 'chev-down'} /></span>
                            <span className="task-set-icon"><Icon name={resolveIcon(ts.icon)} /></span> <span>{ts.name} ({items.length})</span>
                          </div>
                          {!collapsedSections.has(`lines-ts-${ts.id}`) && items.map((line) => (
                            <div key={line.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.lines.has(line.id) ? ' bulk-selected' : ''}`} style={{ paddingLeft: '12px' }}>
                              {bulkSelectMode
                                ? <input type="checkbox" checked={bulkSelected.lines.has(line.id)} onChange={() => toggleBulkSelect('lines', line.id)} />
                                : <input type="checkbox" checked={!hiddenLines.has(line.id)} onChange={() => toggleLineVisibility(line.id)} />
                              }
                              <span style={{ color: line.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('lines', line.id) : centerOnLine(line.coordinates)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name="path" /> {line.name}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>);
                  })()}
                </div>
              )}

              {sensors.filter(s => isItemVisible(s.task_set_id) && sidebarMatch(s.name)).length > 0 && (
                <div className="layer-group">
                  <h5 className="section-header-sm" onClick={() => toggleSection('sensors')}>
                    <span className="section-toggle"><Icon name={collapsedSections.has('sensors') ? 'chev-right' : 'chev-down'} /></span> Sensors ({sensors.filter(s => isItemVisible(s.task_set_id) && sidebarMatch(s.name)).length})
                  </h5>
                  {!collapsedSections.has('sensors') && (() => {
                    const visible = sensors.filter(s => isItemVisible(s.task_set_id) && sidebarMatch(s.name)).sort((a, b) => naturalSort(a.name, b.name));
                    const ungrouped = visible.filter(s => !s.task_set_id);
                    const grouped = taskSets
                      .map(ts => ({ ts, items: visible.filter(s => s.task_set_id === ts.id) }))
                      .filter(g => g.items.length > 0);
                    return (<>
                      {ungrouped.map((sensor) => (
                        <div key={sensor.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.sensors.has(sensor.id) ? ' bulk-selected' : ''}`}>
                          {bulkSelectMode
                            ? <input type="checkbox" checked={bulkSelected.sensors.has(sensor.id)} onChange={() => toggleBulkSelect('sensors', sensor.id)} />
                            : <input type="checkbox" checked={!hiddenSensors.has(sensor.id)} onChange={() => { const s = new Set(hiddenSensors); s.has(sensor.id) ? s.delete(sensor.id) : s.add(sensor.id); setHiddenSensors(s); localStorage.setItem('toc_hiddenSensors', JSON.stringify([...s])); }} />
                          }
                          <span style={{ color: sensor.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('sensors', sensor.id) : centerOnPoint(sensor.longitude, sensor.latitude)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name={resolveIcon(sensor.icon)} /> {sensor.name}</span>
                          <span className={`sensor-status-dot ${sensor.status}`} title={sensor.status}></span>
                        </div>
                      ))}
                      {grouped.map(({ ts, items }) => (
                        <div key={`ts-sensors-${ts.id}`} className="task-set-group-indent">
                          <div className="section-header-sm task-set-group-header" style={{ color: ts.color }}
                            onClick={() => bulkSelectMode
                              ? setBulkSelected(prev => {
                                  const s = new Set(prev.sensors);
                                  const allSelected = items.every(x => s.has(x.id));
                                  items.forEach(x => allSelected ? s.delete(x.id) : s.add(x.id));
                                  return { ...prev, sensors: s };
                                })
                              : toggleSection(`sensors-ts-${ts.id}`)
                            }>
                            <span className="section-toggle"><Icon name={collapsedSections.has(`sensors-ts-${ts.id}`) ? 'chev-right' : 'chev-down'} /></span>
                            <span className="task-set-icon"><Icon name={resolveIcon(ts.icon)} /></span> <span>{ts.name} ({items.length})</span>
                          </div>
                          {!collapsedSections.has(`sensors-ts-${ts.id}`) && items.map((sensor) => (
                            <div key={sensor.id} className={`layer-toggle item-toggle${bulkSelectMode && bulkSelected.sensors.has(sensor.id) ? ' bulk-selected' : ''}`} style={{ paddingLeft: '12px' }}>
                              {bulkSelectMode
                                ? <input type="checkbox" checked={bulkSelected.sensors.has(sensor.id)} onChange={() => toggleBulkSelect('sensors', sensor.id)} />
                                : <input type="checkbox" checked={!hiddenSensors.has(sensor.id)} onChange={() => { const s = new Set(hiddenSensors); s.has(sensor.id) ? s.delete(sensor.id) : s.add(sensor.id); setHiddenSensors(s); localStorage.setItem('toc_hiddenSensors', JSON.stringify([...s])); }} />
                              }
                              <span style={{ color: sensor.color, cursor: 'pointer' }} onClick={() => bulkSelectMode ? toggleBulkSelect('sensors', sensor.id) : centerOnPoint(sensor.longitude, sensor.latitude)} title={bulkSelectMode ? 'Click to select' : 'Click to center on map'}><Icon name={resolveIcon(sensor.icon)} /> {sensor.name}</span>
                              <span className={`sensor-status-dot ${sensor.status}`} title={sensor.status}></span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>);
                  })()}
                </div>
              )}

              {units.filter(u => sidebarMatch(u.name)).length > 0 && (
                <div className="layer-group">
                  <h5 className="section-header-sm" onClick={() => toggleSection('units')}>
                    <span className="section-toggle"><Icon name={collapsedSections.has('units') ? 'chev-right' : 'chev-down'} /></span> Field Units ({units.filter(u => sidebarMatch(u.name)).length})
                    <label className="layer-master-toggle" onClick={(e) => e.stopPropagation()} title="Toggle Field Units layer">
                      <input
                        type="checkbox"
                        checked={showUnitsLayer}
                        onChange={() => { const v = !showUnitsLayer; setShowUnitsLayer(v); localStorage.setItem('toc_showUnitsLayer', String(v)); }}
                      />
                    </label>
                  </h5>
                  {!collapsedSections.has('units') && units
                    .filter(u => sidebarMatch(u.name))
                    .sort((a, b) => naturalSort(a.name, b.name))
                    .map((unit) => (
                      <div key={unit.id} className="layer-toggle item-toggle">
                        <input
                          type="checkbox"
                          checked={!hiddenUnits.has(unit.id)}
                          onChange={() => { const s = new Set(hiddenUnits); s.has(unit.id) ? s.delete(unit.id) : s.add(unit.id); setHiddenUnits(s); localStorage.setItem('toc_hiddenUnits', JSON.stringify([...s])); }}
                        />
                        <span
                          style={{ color: unit.status_color || STATUS_COLOR[unit.status], cursor: 'pointer' }}
                          onClick={() => (unit.latitude != null && unit.longitude != null) && centerOnPoint(unit.longitude, unit.latitude)}
                          title="Click to center on map"
                        >{unitIcon(unit)} {unit.name}</span>
                        <span className="sensor-status-dot" style={{ background: unit.status_color || STATUS_COLOR[unit.status] }} title={unit.status}></span>
                      </div>
                    ))}
                </div>
              )}

              {/* Always rendered for a group member: it holds the layer's only on/off switch,
                  and with the layer off nothing polls, so rows appear only while data is fresh. */}
              {!!user?.group_id && (
                <div className="layer-group">
                  <h5 className="section-header-sm" onClick={() => toggleSection('edge')}>
                    <span className="section-toggle"><Icon name={collapsedSections.has('edge') ? 'chev-right' : 'chev-down'} /></span> Edge Nodes{edgeNodes.data ? ` (${edgeNodes.data.nodes.filter(n => sidebarMatch(n.name)).length})` : ''}
                    <label className="layer-master-toggle" onClick={(e) => e.stopPropagation()} title="Toggle Edge Nodes layer">
                      <input
                        type="checkbox"
                        checked={showEdgeLayer}
                        onChange={() => { const v = !showEdgeLayer; setShowEdgeLayer(v); localStorage.setItem('toc_showEdgeLayer', String(v)); }}
                      />
                    </label>
                  </h5>
                  {!collapsedSections.has('edge') && (edgeNodes.data?.nodes ?? [])
                    .filter(n => sidebarMatch(n.name))
                    .map((node) => (
                      <div key={node.id} className="layer-toggle item-toggle">
                        <button
                          type="button"
                          className="edge-sidebar-node"
                          onClick={() => {
                            if (node.latitude != null && node.longitude != null) centerOnPoint(node.longitude, node.latitude);
                            setEdgeFocusId(node.id);
                            setShowEdgePanel(true);
                          }}
                          title={`${node.name}: open in Edge nodes`}
                        ><Icon name="edge" /> <span className="edge-sidebar-node__name">{node.name}</span></button>
                        <span className={`edge-sidebar-state ${livenessClass(node.liveness)}`}>{livenessLabel(node.liveness)}</span>
                      </div>
                    ))}
                </div>
              )}
              {bulkSelectMode && bulkTotal > 0 && (
                <div className="bulk-action-bar">
                  <span>{bulkTotal} selected</span>
                  <button className="bulk-delete-btn" onClick={bulkDelete}>Delete Selected</button>
                </div>
              )}
              </>)}
            </div>

            <div className="sidebar-section">
              <h4 className="section-header" onClick={() => toggleSection('drawing')}>
                <span className="section-toggle"><Icon name={collapsedSections.has('drawing') ? 'chev-right' : 'chev-down'} /></span> Drawing Tools
              </h4>
              {!collapsedSections.has('drawing') && (dataScope === 'group' && !user?.group_id ? (
                <div className="sidebar-empty-hint">
                  Join a group to add group data
                </div>
              ) : drawMode === 'none' ? (
                <div className="drawing-tools">
                  <button
                    className="tool-btn"
                    onClick={() => setDrawMode('point')}
                  >
                    <Icon name="site" /> Point
                  </button>
                  <button
                    className="tool-btn"
                    onClick={() => setDrawMode('polygon')}
                  >
                    <Icon name="area" /> Area
                  </button>
                  <button
                    className="tool-btn"
                    onClick={() => setDrawMode('line')}
                  >
                    <Icon name="path" /> Path
                  </button>
                  <button
                    className="tool-btn"
                    onClick={() => setDrawMode('sensor')}
                  >
                    <Icon name="sensor" /> Sensor
                  </button>
                  <button
                    className="tool-btn"
                    onClick={() => setDrawMode('unit')}
                  >
                    <Icon name="post" /> Field Unit
                  </button>
                  <div className="import-group">
                    {taskSets.length > 0 && (
                      <select
                        className="task-set-select"
                        value={selectedTaskSetId ?? ''}
                        onChange={(e) => setSelectedTaskSetId(e.target.value ? Number(e.target.value) : undefined)}
                      >
                        <option value="">Import to: No task set</option>
                        {taskSets.map(ts => (
                          <option key={ts.id} value={ts.id}>{ts.name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      className="tool-btn import-btn"
                      onClick={() => csvImportRef.current?.click()}
                      disabled={importing}
                    >
                      {importing ? <><Icon name="spinner" /> Importing...</> : <><Icon name="upload" /> Import CSV</>}
                    </button>
                    <input
                      ref={csvImportRef}
                      type="file"
                      accept=".csv"
                      style={{ display: 'none' }}
                      onChange={handleCsvImport}
                    />
                    <button
                      className="tool-btn import-btn dcim-btn"
                      onClick={() => dcimFolderRef.current?.click()}
                      disabled={dcimImporting}
                    >
                      {dcimImporting ? <><Icon name="spinner" /> Processing...</> : <><Icon name="import-dcim" /> Import DCIM</>}
                    </button>
                    <input
                      ref={dcimFolderRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleDcimImport}
                      {...{ webkitdirectory: '', directory: '', multiple: true } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                    {dcimProgress && (
                      <div className="dcim-progress">
                        <div className="dcim-progress-text">{dcimProgress.phase}</div>
                        <div className="dcim-progress-bar">
                          <div
                            className="dcim-progress-fill"
                            style={{ width: `${dcimProgress.total > 0 ? (dcimProgress.current / dcimProgress.total * 100) : 0}%` }}
                          />
                        </div>
                        <div className="dcim-progress-count">{dcimProgress.current} / {dcimProgress.total}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="draw-active">
                  <h5>
                    {drawMode === 'point' && 'Click to place point'}
                    {drawMode === 'polygon' && `Area: ${tempPolygonCoords.length} points`}
                    {drawMode === 'line' && `Path: ${tempLineCoords.length} points (${tempLinePointIds.filter(id => id !== null).length} snapped)`}
                    {drawMode === 'sensor' && (newSensorType === 'phone-camera' ? 'Click map to open PHONECAM setup' : 'Click to place sensor')}
                    {drawMode === 'unit' && 'Click to place field unit'}
                  </h5>
                  
                  {drawMode === 'point' && (
                    <>
                      <input
                        type="text"
                        placeholder="Point name"
                        value={newPointName}
                        onChange={(e) => setNewPointName(e.target.value)}
                      />
                      <div className="marker-image-picker">
                        <span className="picker-label">Marker:</span>
                        {MARKER_CHOICES.map(m => (
                          <button
                            key={m.key ?? 'default'}
                            className={`marker-option${selectedMarkerIcon === (m.key ?? '') ? ' selected' : ''}`}
                            onClick={() => setSelectedMarkerIcon(m.key && m.key !== selectedMarkerIcon ? m.key : '')}
                            title={m.label}
                            aria-label={m.label}
                          >
                            <span><Icon name={m.key ?? 'site'} size={16} /></span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  
                  {drawMode === 'polygon' && (
                    <>
                      <input
                        type="text"
                        placeholder="Area name"
                        value={newPolygonName}
                        onChange={(e) => setNewPolygonName(e.target.value)}
                      />
                      <button 
                        className="finish-btn"
                        onClick={finishPolygon}
                        disabled={tempPolygonCoords.length < 3}
                      >
                        Finish Area
                      </button>
                    </>
                  )}
                  
                  {drawMode === 'line' && (
                    <>
                      <input
                        type="text"
                        placeholder="Path name"
                        value={newLineName}
                        onChange={(e) => setNewLineName(e.target.value)}
                      />
                      <div className="draw-hint">Click map or existing points to build path</div>
                      <div className="line-actions">
                        <button
                          className="finish-btn"
                          onClick={finishLine}
                          disabled={tempLineCoords.length < 2}
                        >
                          Finish Path
                        </button>
                        {tempLineCoords.length > 0 && (
                          <button
                            className="undo-btn"
                            onClick={() => {
                              setTempLineCoords(tempLineCoords.slice(0, -1));
                              setTempLinePointIds(tempLinePointIds.slice(0, -1));
                            }}
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {drawMode === 'sensor' && (
                    <>
                      <input
                        type="text"
                        placeholder="Sensor name"
                        value={newSensorName}
                        onChange={(e) => setNewSensorName(e.target.value)}
                      />
                      <select
                        value={newSensorType}
                        onChange={(e) => setNewSensorType(e.target.value as MapSensor['sensor_type'])}
                        className="sensor-type-select"
                      >
                        {SENSOR_TYPES.map(st => (
                          <option key={st.value} value={st.value}>
                            {st.label}
                          </option>
                        ))}
                      </select>
                      {newSensorType === 'camera' && (
                        <input
                          type="text"
                          placeholder="RTMP URL (optional)"
                          value={newSensorRtmpUrl}
                          onChange={(e) => setNewSensorRtmpUrl(e.target.value)}
                          style={{ marginTop: 4, fontSize: '0.85em' }}
                        />
                      )}
                    </>
                  )}

                  {drawMode === 'unit' && (
                    <>
                      <input
                        type="text"
                        placeholder="Unit name"
                        value={newUnitName}
                        onChange={(e) => setNewUnitName(e.target.value)}
                      />
                      <select
                        value={newUnitType}
                        onChange={(e) => setNewUnitType(e.target.value as UnitType)}
                        className="sensor-type-select"
                      >
                        {UNIT_TYPES.map(ut => (
                          <option key={ut.value} value={ut.value}>
                            {ut.icon} {ut.label}
                          </option>
                        ))}
                      </select>
                    </>
                  )}

                  {taskSets.length > 0 && (
                    <select
                      value={selectedTaskSetId ?? ''}
                      onChange={(e) => setSelectedTaskSetId(e.target.value ? Number(e.target.value) : undefined)}
                      className="task-set-select"
                    >
                      <option value="">No task set</option>
                      {taskSets.map(ts => (
                        <option key={ts.id} value={ts.id}>{ts.name}</option>
                      ))}
                    </select>
                  )}

                  <button className="cancel-btn" onClick={cancelDraw} title="Cancel drawing (Esc)">
                    Cancel
                  </button>
                </div>
              ))}
            </div>

            <div className="sidebar-section task-sets-section">
              <h4 className="section-header" onClick={() => toggleSection('tasksets')}>
                <span className="section-toggle"><Icon name={collapsedSections.has('tasksets') ? 'chev-right' : 'chev-down'} /></span> Task Sets
              </h4>
              {!collapsedSections.has('tasksets') && (<>
              {taskSets.length === 0 && !isCreatingTaskSet && (
                <div className="sidebar-empty-hint-sm">
                  No task sets yet — create one to organize items
                </div>
              )}
              {taskSets.map(ts => (
                <div key={ts.id} className="task-set-item">
                  {confirmingDeleteTaskSetId === ts.id ? (
                    <div className="task-set-delete-confirm">
                      <span className="task-set-delete-confirm-label">Delete "{ts.name}"?</span>
                      <div className="task-set-delete-confirm-actions">
                        <button className="btn-delete-keep" onClick={() => deleteTaskSet(ts.id, false)}>Keep Items</button>
                        <button className="btn-delete-all" onClick={() => deleteTaskSet(ts.id, true)}>Delete Items Too</button>
                        <button className="btn-delete-cancel" onClick={() => setConfirmingDeleteTaskSetId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : confirmingClearTaskSetId === ts.id ? (
                    <div className="task-set-delete-confirm">
                      <span className="task-set-delete-confirm-label">Clear all content from "{ts.name}"?</span>
                      <div className="task-set-delete-confirm-actions">
                        <button className="btn-delete-all" onClick={() => clearTaskSetContent(ts.id)}>Clear All</button>
                        <button className="btn-delete-cancel" onClick={() => setConfirmingClearTaskSetId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : renamingTaskSetId === ts.id ? (
                    <div className="task-set-rename-form">
                      <input
                        type="text"
                        className="task-set-rename-input"
                        value={renamingTaskSetName}
                        onChange={(e) => setRenamingTaskSetName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') renameTaskSet(ts.id, renamingTaskSetName); if (e.key === 'Escape') setRenamingTaskSetId(null); }}
                        onBlur={() => renameTaskSet(ts.id, renamingTaskSetName)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <label className="layer-toggle">
                        <input
                          type="checkbox"
                          checked={!hiddenTaskSetIds.has(ts.id)}
                          onChange={() => toggleTaskSetVisibility(ts.id)}
                        />
                        <span
                          style={{ color: ts.color, cursor: 'text' }}
                          onClick={(e) => e.preventDefault()}
                          onDoubleClick={() => { if (!ts.is_shared) { setRenamingTaskSetId(ts.id); setRenamingTaskSetName(ts.name); } }}
                          title={ts.is_shared ? 'Shared task set (read-only)' : 'Double-click to rename'}
                        ><span className="task-set-icon"><Icon name={resolveIcon(ts.icon)} /></span> {ts.name}</span>
                        <span className="task-set-count">({ts.item_count})</span>
                        {ts.is_shared && <span className="shared-badge" title={`Shared by ${ts.created_by_email}`}>shared</span>}
                      </label>
                      {!ts.is_shared && (
                        <>
                          {user && user.groups.length > 0 && (
                            <button
                              className="task-set-share-btn"
                              onClick={() => { setSharingItemType(sharingItemType === 'task_set' && sharingItemId === ts.id ? null : 'task_set'); setSharingItemId(sharingItemType === 'task_set' && sharingItemId === ts.id ? null : ts.id); if (sharingItemType !== 'task_set' || sharingItemId !== ts.id) fetchShares('task_set', ts.id); }}
                              title="Share to group"
                            >
                              ↗
                            </button>
                          )}
                          <button
                            className="task-set-clear-btn"
                            onClick={() => setConfirmingClearTaskSetId(ts.id)}
                            title="Clear all content"
                          >
                            ⊘
                          </button>
                          <button
                            className="task-set-delete-btn"
                            onClick={() => setConfirmingDeleteTaskSetId(ts.id)}
                            title="Delete task set"
                            aria-label="Remove"
                          >
                            <Icon name="clear" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {sharingItemType === 'task_set' && sharingItemId === ts.id && (
                    <div className="share-dropdown">
                      <div className="share-dropdown-header">Share to group</div>
                      {user?.groups.filter(g => g.id !== ts.group_id).map(g => (
                        <button key={g.id} className="share-dropdown-item" onClick={() => shareItem('task_set', ts.id, g.id)}>
                          {g.name}
                        </button>
                      ))}
                      {user?.groups.filter(g => g.id !== ts.group_id).length === 0 && (
                        <div className="share-dropdown-empty">No other groups available</div>
                      )}
                      {activeShares.length > 0 && (
                        <>
                          <div className="share-dropdown-header" style={{ marginTop: '6px' }}>Active shares</div>
                          {activeShares.map(s => (
                            <div key={s.id} className="share-active-item">
                              <span>{s.group_name}</span>
                              <button className="share-remove-btn" onClick={() => unshareItem('task_set', ts.id, s.id)} title="Remove share" aria-label="Remove"><Icon name="clear" /></button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {isCreatingTaskSet ? (
                <div className="task-set-create-form">
                  <input
                    type="text"
                    placeholder="Task set name"
                    value={newTaskSetName}
                    onChange={(e) => setNewTaskSetName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createTaskSet()}
                    autoFocus
                  />
                  <div className="task-set-create-actions">
                    <button className="finish-btn" onClick={createTaskSet} disabled={!newTaskSetName.trim()}>
                      Create
                    </button>
                    <button className="cancel-btn" onClick={() => { setIsCreatingTaskSet(false); setNewTaskSetName(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button className="tool-btn" onClick={() => setIsCreatingTaskSet(true)} style={{ marginTop: '6px' }}>
                  + New Task Set
                </button>
              )}
              </>)}
            </div>


            <div className="sidebar-section incidents-section">
              <h4 className="section-header" onClick={() => toggleSection('incidents')}>
                <span className="section-toggle"><Icon name={collapsedSections.has('incidents') ? 'chev-right' : 'chev-down'} /></span> Incidents
                {incidents.filter(i => i.status !== 'Closed').length > 0 && (
                  <span className="nav-badge" style={{ backgroundColor: 'var(--color-danger-fill)', marginLeft: '6px' }}>
                    {incidents.filter(i => i.status !== 'Closed').length}
                  </span>
                )}
              </h4>
              {!collapsedSections.has('incidents') && (<>
                <button
                  className="tool-btn"
                  style={{ margin: '4px 8px', fontSize: '11px' }}
                  onClick={() => {
                    const mapCenter = (mapRef.current as any)?.getCenter();
                    if (mapCenter) {
                      setPendingIncidentCoords({ lat: mapCenter.lat, lng: mapCenter.lng });
                      setIncidentDraft({ type: 'Unknown', severity: 3, title: '', description: '', location_label: '' });
                      setShowIncidentModal(true);
                    }
                  }}
                >
                  + Log Incident
                </button>
                <div className="incident-list-scroll">
                  {incidents.filter(i => i.status !== 'Closed' && sidebarMatch(i.title)).length === 0 && (
                    <div className="incident-list-empty">
                      {sidebarFilter ? 'No matching incidents' : 'No active incidents — right-click map to log one'}
                    </div>
                  )}
                  {incidents.filter(i => i.status !== 'Closed' && sidebarMatch(i.title)).map(incident => (
                    <div
                      key={incident.id}
                      className={`incident-list-item${selectedIncident?.id === incident.id ? ' selected' : ''}`}
                      style={{ borderLeft: `3px solid ${INCIDENT_COLORS[incident.type]}` }}
                      onClick={() => {
                        setSelectedIncident(incident);
                        setSelectedPoint(null);
                        setSelectedPolygon(null);
                        setSelectedLine(null);
                        setSelectedSensor(null);
                        centerOnPoint(incident.longitude, incident.latitude);
                      }}
                    >
                      <span className="incident-icon" style={{ color: INCIDENT_COLORS[incident.type] }}>
                        {INCIDENT_ICONS[incident.type]}
                      </span>
                      <div className="incident-info">
                        <div className="incident-title">{incident.title}</div>
                        <div className="incident-meta">{incident.status} · S{incident.severity}</div>
                      </div>
                      <span className="incident-severity-dot" style={{
                        backgroundColor: incident.severity >= 4 ? 'var(--color-danger-fill)' : incident.severity >= 3 ? 'var(--color-warning)' : 'var(--color-info)'
                      }} />
                    </div>
                  ))}
                </div>
              </>)}
            </div>
          </>
        )}

            <div className="sidebar-section">
              <h4 className="section-header" onClick={() => toggleSection('kaek')}>
                <span className="section-toggle"><Icon name={collapsedSections.has('kaek') ? 'chev-right' : 'chev-down'} /></span> Cadastral (ΚΑΕΚ)
                {kaekParcels.filter(p => isItemVisible(p.task_set_id)).length > 0 && (
                  <span className="nav-badge" style={{ backgroundColor: 'var(--color-warning)', color: 'var(--color-on-accent)', marginLeft: '6px' }}>
                    {kaekParcels.filter(p => isItemVisible(p.task_set_id)).length}
                  </span>
                )}
              </h4>
              {!collapsedSections.has('kaek') && (<>
                <div style={{ padding: '4px 8px', display: 'flex', gap: '4px' }}>
                  <input
                    type="text"
                    placeholder="KAEK code e.g. 050500213002"
                    value={kaekLookupCode}
                    onChange={(e) => setKaekLookupCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && kaekLookupCode.trim()) {
                        handleKaekLookup();
                      }
                    }}
                    style={{
                      flex: 1, fontSize: '11px', padding: '4px 6px',
                      background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-primary)',
                      minWidth: 0
                    }}
                  />
                  <button
                    className="tool-btn"
                    style={{ fontSize: '11px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                    disabled={kaekLookupLoading || !kaekLookupCode.trim()}
                    onClick={handleKaekLookup}
                  >
                    {kaekLookupLoading ? '...' : 'Lookup'}
                  </button>
                </div>
                {kaekPreview && (
                  <div style={{ padding: '4px 8px', fontSize: '11px', color: 'var(--color-text-primary)', background: 'var(--color-accent-dim)', margin: '0 8px', borderRadius: 'var(--radius-sm)' }}>
                    <div>Found: {kaekPreview.kaek_code}</div>
                    <div>{kaekPreview.land_use} · {kaekPreview.area ? `${Math.round(kaekPreview.area)} m²` : ''}</div>
                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                      <button
                        className="tool-btn"
                        style={{ fontSize: '10px', padding: '2px 8px' }}
                        onClick={handleKaekSavePreview}
                      >
                        Save
                      </button>
                      <button
                        className="tool-btn"
                        style={{ fontSize: '10px', padding: '2px 8px' }}
                        onClick={() => setKaekPreview(null)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ padding: '2px 8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <label className="tool-btn" style={{ fontSize: '11px', cursor: 'pointer', display: 'inline-block' }}>
                    {kaekImporting ? <>Importing...</> : <><Icon name="import-excel" /> Import Excel</>}
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                      disabled={kaekImporting}
                      onChange={handleKaekImport}
                    />
                  </label>
                  <button
                    className="tool-btn"
                    style={{ fontSize: '11px', padding: '4px 8px' }}
                    disabled={kaekAnalyzing}
                    onClick={async () => {
                      setKaekAnalyzing(true);
                      try {
                        const tsParam = kaekMatchTaskSet ? `&points_task_set=${kaekMatchTaskSet}` : '';
                        const resp = await fetch(`${API_URL}/api/kaek/analysis?scope=${dataScope}${tsParam}`, { headers: getAuthHeaders() });
                        if (resp.ok) setKaekAnalysis(await resp.json());
                      } catch {} finally { setKaekAnalyzing(false); }
                    }}
                  >
                    {kaekAnalyzing ? '...' : 'Analyze'}
                  </button>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {kaekParcels.filter(p => isItemVisible(p.task_set_id) && sidebarMatch(p.kaek_code + ' ' + (p.address || ''))).length === 0 && (
                    <div style={{ padding: '8px', fontSize: '11px', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                      {sidebarFilter ? 'No matching parcels' : 'No parcels — enter a KAEK code above'}
                    </div>
                  )}
                  {(() => {
                    const visible = kaekParcels.filter(p => isItemVisible(p.task_set_id) && sidebarMatch(p.kaek_code + ' ' + (p.address || '')));
                    const ungrouped = visible.filter(p => !p.task_set_id);
                    const grouped = taskSets
                      .map(ts => ({ ts, items: visible.filter(p => p.task_set_id === ts.id) }))
                      .filter(g => g.items.length > 0);
                    const renderParcel = (parcel: KaekParcel, indent?: boolean) => (
                      <div
                        key={parcel.id}
                        className="layer-toggle item-toggle"
                        style={{ paddingLeft: indent ? '12px' : undefined }}
                        onClick={() => {
                          setSelectedKaekParcel(parcel);
                          setSelectedPoint(null);
                          setSelectedPolygon(null);
                          setSelectedLine(null);
                          setSelectedSensor(null);
                          setSelectedIncident(null);
                          if (parcel.geometry?.coordinates?.[0]) {
                            const coords = parcel.geometry.coordinates[0];
                            const lngs = coords.map((c: number[]) => c[0]);
                            const lats = coords.map((c: number[]) => c[1]);
                            const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                            centerOnPoint(centerLng, centerLat);
                          }
                        }}
                      >
                        <span style={{ color: parcel.color, cursor: 'pointer' }}>{'▣'} {parcel.kaek_code}</span>
                      </div>
                    );
                    return (<>
                      {ungrouped.map(p => renderParcel(p))}
                      {grouped.map(({ ts, items }) => (
                        <div key={`ts-kaek-${ts.id}`} className="task-set-group-indent">
                          <div className="section-header-sm task-set-group-header" style={{ color: ts.color }}
                            onClick={() => toggleSection(`kaek-ts-${ts.id}`)}>
                            <span className="section-toggle"><Icon name={collapsedSections.has(`kaek-ts-${ts.id}`) ? 'chev-right' : 'chev-down'} /></span>
                            <span className="task-set-icon"><Icon name={resolveIcon(ts.icon)} /></span> <span>{ts.name} ({items.length})</span>
                          </div>
                          {!collapsedSections.has(`kaek-ts-${ts.id}`) && items.map(p => renderParcel(p, true))}
                        </div>
                      ))}
                    </>);
                  })()}
                </div>
              </>)}
            </div>

        <div className="sidebar-footer">
          <a href="https://rtvp.cc/home" target="_blank" rel="noopener noreferrer" className="powered-by">
            <img src="https://rtvp.cc/assets/rtvpLogo_1762636990347-BhEYgg_x.png" alt="RTVP" className="rtvp-logo" />
          </a>
        </div>
      </aside>
      
      <div className={`resize-handle resize-handle-left ${leftPanelCollapsed ? 'hidden' : ''}`} onMouseDown={handleLeftResizeStart}></div>
      <button
        className={`panel-collapse-btn panel-collapse-left ${leftPanelCollapsed ? 'collapsed' : ''}`}
        onClick={() => {
          const next = !leftPanelCollapsed;
          setLeftPanelCollapsed(next);
          if (isPhoneLayout()) { if (!next) setRightPanelCollapsed(true); }
          else localStorage.setItem('toc_leftPanelCollapsed', String(next));
        }}
        title={leftPanelCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={leftPanelCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!leftPanelCollapsed}
      >
        <Icon name={leftPanelCollapsed ? 'chev-right' : 'chev-left'} />
      </button>

      <main className="main-area">
        <header className="top-bar">
          <div className="top-bar-left">
            <select
              className="map-style-selector"
              value={showMap ? mapStyle : 'none'}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'none') {
                  setShowMap(false);
                } else {
                  setShowMap(true);
                  setMapStyle(val);
                  localStorage.setItem('toc_mapStyle', val);
                }
              }}
            >
              <option value="dark">Tactical Dark</option>
              <option value="satellite">Satellite</option>
              <option value="street">Street Map</option>
              <option value="positron">Light</option>
              <option value="none">No Map</option>
            </select>
          </div>
          <div className="top-bar-center">
            <MapSearch items={searchableItems} onSelect={handleSearchSelect} />
            <span className="news-ticker">
              <span className="ticker-item" title="Points">{points.length}<span className="ticker-unit"> pts</span></span>
              <span className="ticker-sep">/</span>
              <span className="ticker-item" title="Areas">{polygons.length}<span className="ticker-unit"> areas</span></span>
              <span className="ticker-sep">/</span>
              <span className="ticker-item" title="Paths">{lines.length}<span className="ticker-unit"> paths</span></span>
              <span className="ticker-sep">/</span>
              <span className="ticker-item" title="Sensors">{sensors.length}<span className="ticker-unit"> sensors</span></span>
              {showAircraftLayer && aircraftPositions.length > 0 && <><span className="ticker-sep">·</span><span className="ticker-item" title="Aircraft">{aircraftPositions.length}<span className="ticker-unit"> aircraft</span></span></>}
              {showVesselLayer && vesselPositions.length > 0 && <><span className="ticker-sep">·</span><span className="ticker-item" title="Vessels">{vesselPositions.length}<span className="ticker-unit"> vessels</span></span></>}
              {showLiveCamLayer && liveCameras.length > 0 && <><span className="ticker-sep">·</span><span className="ticker-item" title="Live cameras">{liveCameras.length}<span className="ticker-unit"> cams</span></span></>}
            </span>
          </div>
          <div className="top-bar-right">
            <button
              className={`panel-toggle ${activeOpLog ? 'oplog-recording' : ''}`}
              onClick={async () => {
                if (activeOpLog) {
                  const ok = await confirm({ message: 'Stop the operation log?', confirmLabel: 'Stop Log', danger: false });
                  if (!ok) return;
                  try {
                    await stopLog(getAuthHeaders);
                    setActiveOpLog(null);
                    toast('Operation log stopped', 'success');
                  } catch (err: unknown) {
                    toast((err as Error).message || 'Failed to stop log', 'error');
                  }
                } else {
                  const title = prompt('Operation log title (optional):');
                  try {
                    const log = await startLog(getAuthHeaders, title || undefined);
                    setActiveOpLog({ id: log.id, started_at: log.started_at, title: log.title || undefined });
                    toast('Operation log started', 'success');
                  } catch (err: unknown) {
                    toast((err as Error).message || 'Failed to start log', 'error');
                  }
                }
              }}
              title={activeOpLog ? 'Stop operation log' : 'Start operation log'}
            >
              <span className="btn-glyph">{activeOpLog ? <Icon name="stop" /> : <span className="rec-dot" aria-hidden="true" />}</span>
              <span className="btn-label">{activeOpLog ? 'Stop log' : 'Start log'}</span>
            </button>
            <button className={`panel-toggle${showVideo ? ' is-active' : ''}`} aria-pressed={showVideo} onClick={() => { const v = !showVideo; setShowVideo(v); localStorage.setItem('toc_showVideo', String(v)); }} title={showVideo ? 'Hide video panel' : 'Show video panel'}>
              <span className="btn-glyph"><Icon name="webcam" /></span>
              <span className="btn-label">Video</span>
            </button>
            <button className="panel-toggle" onClick={() => setShowMatrixView(true)} title="Open multi-camera matrix view">
              <span className="btn-glyph"><Icon name="matrix" /></span>
              <span className="btn-label">Matrix</span>
            </button>
            <button className="panel-toggle" onClick={() => setShowUspacePanel(true)} title="U-space airspace: authorisations, geo-zones, conformance, USSP network">
              <span className="btn-glyph"><Icon name="uspace" /></span>
              <span className="btn-label">U-space</span>
            </button>
            <SystemStats apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />
            <div className="user-menu-container" ref={userMenuRef}>
              <button
                className="user-menu-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
                title={user?.email || 'User'}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <span className="user-avatar">{user?.email?.charAt(0).toUpperCase() || 'U'}</span>
              </button>
              {showUserMenu && (
                <div className="user-menu-dropdown" role="menu">
                  <div className="user-menu-info">
                    <span className="user-menu-email">{user?.email}</span>
                    <span className="user-menu-group">{user?.group_name || 'No Group'}</span>
                  </div>
                  <div className="user-menu-heading">Operations</div>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowFleetPanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="fleet" /></span> Fleet Management
                  </button>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setEdgeFocusId(null); setShowEdgePanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="edge" /></span> Edge nodes
                  </button>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowMqttPanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="mqtt" /></span> MQTT Devices
                  </button>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowOpLogPanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="oplog" /></span> Operation Logs
                  </button>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowCapturesPanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="captures" /></span> Stream Captures
                  </button>
                  <div className="user-menu-heading">Intelligence</div>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowMarketplacePanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="models" /></span> AI Models
                  </button>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowSurveillancePanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="surveillance" /></span> Surveillance
                  </button>
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowAnalyticsPanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="analytics" /></span> Analytics
                  </button>
                  {assistantBridge.enabled && (
                    <button role="menuitem" className="user-menu-item" onClick={() => { setShowAssistant(true); setAssistantMinimized(false); setShowUserMenu(false); }}>
                      <span className="user-menu-icon"><Icon name="assistant" /></span> Assistant
                    </button>
                  )}
                  <button role="menuitem" className="user-menu-item" onClick={() => { setShowUspacePanel(true); setShowUserMenu(false); }}>
                    <span className="user-menu-icon"><Icon name="uspace" /></span> U-space Airspace
                  </button>
                  {/* Hidden unless the backend reports the feature on here — the demo box
                      only. Same self-disabling arrangement as the assistant above. */}
                  {tankGaugeEnabled && (
                    <button role="menuitem" className="user-menu-item" onClick={() => { setShowTankRollup(true); setShowUserMenu(false); }}>
                      <span className="user-menu-icon"><Icon name="factory" /></span> Tank Inventory
                    </button>
                  )}
                  <div className="user-menu-divider" role="separator" />
                  {isAdmin && (
                    <button role="menuitem" className="user-menu-item" onClick={() => { setShowAdminPanel(true); setShowUserMenu(false); }}>
                      <span className="user-menu-icon"><Icon name="admin" /></span> Dashboard Management
                    </button>
                  )}
                  <button role="menuitem" className="user-menu-item user-menu-logout" onClick={logout}>
                    <span className="user-menu-icon"><Icon name="logout" /></span> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="content-area">
          {/* The calibration editor is asking for a ground point and the operator's eyes have
              moved from the video to the map — say so where they are looking. */}
          {groundPickActive && (
            <div style={{
              position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)',
              zIndex: 1200, background: 'var(--color-bg-overlay)', border: '1px solid var(--color-warning)',
              color: 'var(--color-warning)', borderRadius: 'var(--radius-md)', padding: '6px 14px', fontSize: 'var(--text-sm)',
              fontWeight: 500, boxShadow: 'var(--shadow-raised)', pointerEvents: 'none',
            }}>
              Click the same feature on the map
            </div>
          )}
          {showMap ? (
            <div className={`map-container ${!showVideo && !showTelemetry ? 'expanded' : ''}`}>
              <Map
                ref={mapRef}
                initialViewState={(() => {
                  try {
                    const saved = localStorage.getItem('toc_mapViewState');
                    if (saved) return JSON.parse(saved);
                  } catch {}
                  return { longitude: 22.9564, latitude: 40.6262, zoom: 15, pitch: 0, bearing: 0 };
                })()}
                maxZoom={mapStyle === 'satellite' ? 19 : 20}
                style={{ width: '100%', height: '100%' }}
                mapStyle={MAP_STYLES[mapStyle as keyof typeof MAP_STYLES]}
                onClick={handleMapClick}
                onContextMenu={(e: any) => {
                  e.originalEvent.preventDefault();
                  if (drawMode !== 'none') return;
                  setPendingIncidentCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
                  setIncidentDraft({ type: 'Unknown', severity: 3, title: '', description: '', location_label: '' });
                  setShowIncidentModal(true);
                }}
                onMoveEnd={handleMapMove}
                onMouseMove={handleMapMouseMove}
                cursor={getCursor()}
                interactiveLayerIds={[
                  ...(showKaekLayer ? kaekParcels.filter(p => isItemVisible(p.task_set_id)).map(p => `kaek-hit-${p.id}`) : []),
                  ...(showUspaceLayer ? geozones.map(z => `uspace-hit-${z.id}`) : []),
                  ...polygons.filter(p => !hiddenPolygons.has(p.id) && isItemVisible(p.task_set_id)).map(p => `polygon-hit-${p.id}`),
                  ...lines.filter(l => !hiddenLines.has(l.id) && isItemVisible(l.task_set_id)).map(l => `line-hit-${l.id}`),
                  ...(activeMission && (activeMission.status === 'executing' || activeMission.status === 'paused' || activeMission.status === 'created') ? ['mission-remaining-layer', 'mission-completed-layer'] : []),
                  ...(activeHall ? [HALL_STAND_LAYER] : [])
                ]}
              >
                <NavigationControl position="top-right" />

                {activeHall && (
                  <HallOverlay
                    plan={activeHall}
                    selectedCameraId={selectedCamera?.id ?? null}
                    onSelectCamera={(cam) => { setSelectedCamera(cam); setSelectedStand(null); }}
                  />
                )}

                {/* Tank fill levels painted over the tank polygons, plus the measurement
                    that produced the open tank's number. Source/Layer only work inside the
                    map subtree, hence a child here rather than a sibling. */}
                <TankGaugeLayer
                  polygons={polygons}
                  readings={tankReadings}
                  highlight={tankHighlight?.overlay ?? null}
                  visible={tankGaugeEnabled && Object.keys(tankReadings).length > 0}
                />

                {mapTilted && (
                  <button
                    className="map-reset-tilt"
                    title="Reset map tilt"
                    onClick={() => {
                      const map = (mapRef.current as any)?.getMap();
                      if (map) {
                        map.easeTo({ pitch: 0, bearing: 0, duration: 400 });
                      }
                    }}
                  >
                    <Icon name="reset" /> 2D
                  </button>
                )}

              {weatherLayer === 'radar' && weatherRadarTilePath && (
                <Source
                  key={`weather-radar-${weatherRadarTilePath}`}
                  id="weather-tiles"
                  type="raster"
                  tiles={[`https://tilecache.rainviewer.com${weatherRadarTilePath}/256/{z}/{x}/{y}/6/1_1.png`]}
                  tileSize={256}
                  maxzoom={7}
                  attribution="RainViewer"
                >
                  <Layer
                    id="weather-tiles-layer"
                    type="raster"
                    paint={{ 'raster-opacity': weatherOpacity, 'raster-fade-duration': 0 }}
                  />
                </Source>
              )}
              {weatherLayer !== 'off' && weatherLayer !== 'radar' && owmApiKey && (
                <Source
                  key={`weather-owm-${weatherLayer}`}
                  id="weather-tiles"
                  type="raster"
                  tiles={[`https://tile.openweathermap.org/map/${weatherLayer}/{z}/{x}/{y}.png?appid=${owmApiKey}`]}
                  tileSize={256}
                  attribution="OpenWeatherMap"
                >
                  <Layer
                    id="weather-tiles-layer"
                    type="raster"
                    paint={{ 'raster-opacity': weatherOpacity, 'raster-fade-duration': 0 }}
                  />
                </Source>
              )}

              {showMGRSGrid && mgrsGridData.features.length > 0 && (
                <Source id="mgrs-grid" type="geojson" data={mgrsGridData}>
                  <Layer
                    id="mgrs-grid-lines"
                    type="line"
                    filter={['!=', ['get', 'lineType'], 'label']}
                    paint={{
                      'line-color': 'rgba(255, 200, 0, 0.35)',
                      'line-width': 0.8,
                      'line-dasharray': [6, 4]
                    }}
                  />
                  <Layer
                    id="mgrs-grid-labels"
                    type="symbol"
                    filter={['==', ['get', 'lineType'], 'label']}
                    layout={{
                      'text-field': ['get', 'label'],
                      'text-size': 11,
                      'text-anchor': 'bottom-left',
                      'text-offset': [0.3, -0.3],
                      'text-allow-overlap': false,
                      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular']
                    }}
                    paint={{
                      'text-color': 'rgba(255, 200, 0, 0.75)',
                      'text-halo-color': 'rgba(0, 0, 0, 0.9)',
                      'text-halo-width': 1.5
                    }}
                  />
                </Source>
              )}

              {showDronePosition && Object.entries(dronePositions).map(([droneId, drone]) => (
                drone.connected && (
                  <Marker
                    key={`drone-${droneId}`}
                    longitude={drone.longitude}
                    latitude={drone.latitude}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setSelectedDroneId(droneId);
                    }}
                  >
                    <div
                      className={`drone-marker ${selectedDroneId === droneId ? 'selected' : ''}`}
                      style={{ transform: `rotate(${drone.heading}deg)` }}
                    >
                      <div className="marker-icon"><Icon name="drone" /></div>
                      <div className="marker-pulse"></div>
                      {Object.keys(dronePositions).length > 1 && (
                        <div className="drone-label">{droneId.slice(-4)}</div>
                      )}
                    </div>
                  </Marker>
                )
              ))}

              {showDronePosition && showFovCone && fovGeoJSON.features.length > 0 && (
                <Source id="drone-fov" type="geojson" data={fovGeoJSON}>
                  <Layer
                    id="drone-fov-fill"
                    type="fill"
                    filter={['==', ['get', 'type'], 'fov']}
                    paint={{ 'fill-color': COLORS.accent, 'fill-opacity': 0.12 }}
                  />
                  <Layer
                    id="drone-fov-line"
                    type="line"
                    filter={['==', ['get', 'type'], 'fov']}
                    paint={{ 'line-color': COLORS.accent, 'line-opacity': 0.35, 'line-width': 1.5 }}
                  />
                  <Layer
                    id="drone-heading-line"
                    type="line"
                    filter={['==', ['get', 'type'], 'heading']}
                    paint={{ 'line-color': COLORS.accent, 'line-opacity': 0.6, 'line-width': 2, 'line-dasharray': [4, 4] }}
                  />
                </Source>
              )}

              {clusters.map(cluster => {
                const [lng, lat] = cluster.geometry.coordinates;
                const props = cluster.properties;
                if (props.cluster) {
                  const count = props.point_count;
                  const size = count < 10 ? 32 : count < 50 ? 40 : 48;
                  return (
                    <Marker key={`cluster-${cluster.id}`} longitude={lng} latitude={lat} anchor="center"
                      onClick={(e) => {
                        e.originalEvent.stopPropagation();
                        const expansionZoom = clusterIndex.getClusterExpansionZoom(cluster.id as number);
                        (mapRef.current as any)?.getMap()?.flyTo({ center: [lng, lat], zoom: Math.min(expansionZoom, 20) });
                      }}
                    >
                      <div className="cluster-marker" style={{ width: size, height: size }}>
                        {count}
                      </div>
                    </Marker>
                  );
                }
                const point = pointsById[props.id];
                if (!point) return null;
                return (
                  <Marker
                    key={point.id}
                    longitude={point.longitude}
                    latitude={point.latitude}
                    anchor="center"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      if (drawMode === 'line') {
                        setTempLineCoords([...tempLineCoords, [point.longitude, point.latitude]]);
                        setTempLinePointIds([...tempLinePointIds, point.id]);
                      } else {
                        const nearby = points.filter(p => {
                          if (p.id === point.id || hiddenPoints.has(p.id) || !isItemVisible(p.task_set_id)) return false;
                          const dlat = (p.latitude - point.latitude) * 111320;
                          const dlng = (p.longitude - point.longitude) * 111320 * Math.cos(point.latitude * Math.PI / 180);
                          return Math.sqrt(dlat * dlat + dlng * dlng) < 30;
                        });
                        if (nearby.length > 0) {
                          setNearbyPointPicker({points: [point, ...nearby], lng: point.longitude, lat: point.latitude});
                        } else {
                          setNearbyPointPicker(null);
                          setSelectedPoint(point);
                          setSelectedSensor(null);
                        }
                      }
                    }}
                  >
                    <div className={`custom-marker${drawMode === 'line' && tempLinePointIds.includes(point.id) ? ' in-path' : ''}`} style={{ color: point.color }}>
                      {drawMode === 'line' && (
                        <span className="snap-hint">{tempLinePointIds.includes(point.id) ? <Icon name="done" /> : '+'}</span>
                      )}
                      {customMarkerImage(point.metadata) ? (
                        <img className="marker-image" src={customMarkerImage(point.metadata) as string} alt="" />
                      ) : (
                        <span className="marker-emoji">
                          <Icon name={resolvePointIcon(point.metadata, point.icon)} />
                        </span>
                      )}
                      <span className="marker-label">{point.name}</span>
                    </div>
                  </Marker>
                );
              })}

              {selectedSensor && selectedSensor.sensor_type === 'phone-camera' && sensorTrack.length >= 2 && (
                <>
                <Source
                  id="phone-sensor-track"
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: sensorTrack.map(p => [p.lng, p.lat]),
                    },
                    properties: {},
                  }}
                >
                  <Layer
                    id="phone-sensor-track-line"
                    type="line"
                    paint={{
                      'line-color': '#ff8800',
                      'line-width': 3,
                      'line-opacity': 0.85,
                    }}
                  />
                </Source>
                <Source
                  id="phone-sensor-track-pts"
                  type="geojson"
                  data={{
                    type: 'FeatureCollection',
                    features: sensorTrack.map(p => ({
                      type: 'Feature' as const,
                      geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
                      properties: {},
                    })),
                  }}
                >
                  <Layer
                    id="phone-sensor-track-dots"
                    type="circle"
                    paint={{
                      'circle-radius': 3,
                      'circle-color': '#ff8800',
                      'circle-stroke-width': 1,
                      'circle-stroke-color': '#000',
                    }}
                  />
                </Source>
                </>
              )}

              {mmwaveSectors.features.length > 0 && (
                <Source id="mmwave-sectors" type="geojson" data={mmwaveSectors}>
                  <Layer
                    id="mmwave-sector-fill"
                    type="fill"
                    paint={{
                      'fill-color': ['case', ['get', 'occupied'], COLORS.warning, COLORS.accent],
                      'fill-opacity': ['case', ['get', 'occupied'], 0.22, ['get', 'online'], 0.08, 0.03],
                    }}
                  />
                  <Layer
                    id="mmwave-sector-line"
                    type="line"
                    paint={{
                      'line-color': ['case', ['get', 'occupied'], COLORS.warning, COLORS.accent],
                      'line-opacity': 0.5,
                      'line-width': 1,
                    }}
                  />
                </Source>
              )}

              {sensors.filter(s => !hiddenSensors.has(s.id) && isItemVisible(s.task_set_id)).map((sensor) => (
                <Marker
                  key={`sensor-${sensor.id}`}
                  longitude={sensor.longitude}
                  latitude={sensor.latitude}
                  anchor="center"
                  draggable
                  onDragEnd={(e) => {
                    updateSensorLocation(sensor.id, e.lngLat.lat, e.lngLat.lng);
                  }}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedSensor(sensor);
                    setEditingSensorRtmpUrl(null);
                    setSensorPosDraft(null);
                    setSensorRecordings([]);
                    setSensorTrack([]);
                    if (sensor.sensor_type === 'phone-camera') {
                      listRecordings(sensor.id, getAuthHeaders).then(setSensorRecordings).catch(() => {});
                      getTrack(sensor.id, getAuthHeaders).then(setSensorTrack).catch(() => {});
                    }
                    setSelectedPoint(null);
                    setSelectedPolygon(null);
                    setSelectedLine(null);
                  }}
                >
                  <div className={`sensor-marker ${sensor.status}`}>
                    <span className="marker-emoji"><Icon name={resolveIcon(sensor.icon)} /></span>
                    <span className="marker-label">{sensor.name}</span>
                    {sensor.sensor_type === 'mmwave' && mmwaveView(sensor.status, sensor.metadata).presence === true && (
                      <span className="sensor-presence-badge"><Icon name="occupied" size={10} /> Occupied</span>
                    )}
                    <span className={`sensor-status-indicator ${sensor.status}`}></span>
                  </div>
                </Marker>
              ))}

              {showUnitsLayer && units.filter(u => u.latitude != null && u.longitude != null && !hiddenUnits.has(u.id)).map((unit) => {
                const ringColor = unit.status_color || STATUS_COLOR[unit.status];
                return (
                  <Marker
                    key={`unit-${unit.id}`}
                    longitude={unit.longitude as number}
                    latitude={unit.latitude as number}
                    anchor="center"
                    draggable
                    onDragEnd={(e) => {
                      updateUnit(getAuthHeaders, unit.id, { latitude: e.lngLat.lat, longitude: e.lngLat.lng }).then(fetchUnits);
                    }}
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setSelectedUnit(unit);
                      setSelectedPoint(null);
                      setSelectedPolygon(null);
                      setSelectedLine(null);
                      setSelectedSensor(null);
                    }}
                  >
                    <div className="sensor-marker unit-marker" style={{ ['--unit-color' as string]: ringColor }}>
                      <span className="marker-emoji unit-marker-emoji"><Icon name={unitIcon(unit)} /></span>
                      <span className="marker-label unit-marker-label">{unit.name}</span>
                      <span className="sensor-status-indicator unit-status-indicator"></span>
                    </div>
                  </Marker>
                );
              })}

              {showEdgeLayer && (edgeNodes.data?.nodes ?? []).filter(n => n.latitude != null && n.longitude != null).map((node) => (
                <Marker
                  key={`edge-${node.id}`}
                  longitude={node.longitude as number}
                  latitude={node.latitude as number}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setEdgeFocusId(node.id);
                    setShowEdgePanel(true);
                  }}
                >
                  <div
                    className={`edge-marker ${livenessClass(node.liveness)}${node.quarantined ? ' edge-marker--quarantined' : ''}`}
                    title={`${node.name} — ${livenessLabel(node.liveness)}${node.quarantined ? ' (quarantined)' : ''}`}
                  >
                    <span className="edge-marker__icon"><Icon name="edge" /></span>
                    <span className="edge-marker__label">
                      <span className="edge-marker__name">{node.name}</span>
                      <span className="edge-marker__state">{livenessLabel(node.liveness)}</span>
                    </span>
                  </div>
                </Marker>
              ))}

              {incidents.filter(i => i.status !== 'Closed').map(incident => (
                <Marker
                  key={`incident-${incident.id}`}
                  longitude={incident.longitude}
                  latitude={incident.latitude}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedIncident(incident);
                    setSelectedPoint(null);
                    setSelectedPolygon(null);
                    setSelectedLine(null);
                    setSelectedSensor(null);
                  }}
                >
                  <div
                    className={`incident-marker${incident.severity === 5 ? ' incident-pulse' : ''}`}
                    style={{ borderColor: INCIDENT_COLORS[incident.type] }}
                  >
                    <span style={{ color: INCIDENT_COLORS[incident.type], fontSize: '15px' }}>
                      <Icon name={INCIDENT_ICONS[incident.type]} />
                    </span>
                    <span className="incident-sev-badge" style={{
                      backgroundColor: incident.severity >= 4 ? 'var(--color-danger-fill)' : incident.severity >= 3 ? 'var(--color-warning)' : 'var(--color-info)'
                    }}>
                      {incident.severity}
                    </span>
                  </div>
                </Marker>
              ))}

              {mgrsSearchResult && (
                <Marker
                  longitude={mgrsSearchResult.lng}
                  latitude={mgrsSearchResult.lat}
                  anchor="bottom"
                >
                  <div className="mgrs-search-marker">
                    <div className="mgrs-search-pin"></div>
                    <div className="mgrs-search-label">{toMGRS(mgrsSearchResult.lat, mgrsSearchResult.lng)}</div>
                  </div>
                </Marker>
              )}


              {showAircraftLayer && aircraftPositions.map((ac) => (
                <Marker
                  key={ac.hex}
                  longitude={ac.lon}
                  latitude={ac.lat}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedAircraft(ac);
                  }}
                >
                  <div
                    className="aircraft-marker"
                    style={{ transform: `rotate(${ac.track || 0}deg)` }}
                    title={ac.flight || ac.hex}
                  >
                    <Icon name="heading" />
                  </div>
                </Marker>
              ))}

              {showVesselLayer && vesselPositions.map((v) => (
                <Marker
                  key={v.mmsi}
                  longitude={v.lon}
                  latitude={v.lat}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedVessel(v);
                  }}
                >
                  <div
                    className="vessel-marker"
                    style={{ transform: `rotate(${v.heading > 0 && v.heading < 360 ? v.heading : v.course}deg)` }}
                    title={v.name || String(v.mmsi)}
                  >
                    <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor">
                      <path d="M7 0L2 7v9l1 4h8l1-4V7L7 0z"/>
                    </svg>
                  </div>
                </Marker>
              ))}

              {showLiveCamLayer && liveCameras.map((cam) => (
                <Marker
                  key={cam.id}
                  longitude={cam.lon}
                  latitude={cam.lat}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedLiveCam(cam);
                  }}
                >
                  <div className="webcam-marker" title={cam.name}>
                    <Icon name="webcam" />
                  </div>
                </Marker>
              ))}


              {polygons.filter(p => !hiddenPolygons.has(p.id) && isItemVisible(p.task_set_id)).map((polygon) => (
                <Source
                  key={polygon.id}
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'Polygon',
                      coordinates: [polygon.coordinates]
                    },
                    properties: { id: polygon.id, name: polygon.name }
                  }}
                >
                  <Layer
                    id={`polygon-${polygon.id}`}
                    type="fill"
                    paint={{
                      'fill-color': polygon.color,
                      'fill-opacity': polygon.fill_opacity
                    }}
                  />
                  <Layer
                    id={`polygon-line-${polygon.id}`}
                    type="line"
                    paint={{
                      'line-color': polygon.color,
                      'line-width': 2
                    }}
                  />
                  <Layer
                    id={`polygon-hit-${polygon.id}`}
                    type="fill"
                    paint={{
                      'fill-color': 'transparent',
                      'fill-opacity': 0
                    }}
                  />
                </Source>
              ))}

              {showUspaceLayer && geozones.map(zone => (
                <Source
                  key={`uspace-${zone.id}`}
                  id={`uspace-${zone.id}`}
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: zone.geometry,
                    properties: { id: zone.id }
                  }}
                >
                  <Layer
                    id={`uspace-fill-${zone.id}`}
                    type="fill"
                    paint={{
                      'fill-color': zone.color,
                      'fill-opacity': zone.zone_type === 'no_fly' ? 0.28 : 0.16
                    }}
                  />
                  <Layer
                    id={`uspace-line-${zone.id}`}
                    type="line"
                    paint={{
                      'line-color': zone.color,
                      'line-width': zone.zone_type === 'no_fly' ? 2.5 : 1.8,
                      'line-dasharray': zone.zone_type === 'no_fly' ? [1, 0] : [3, 2]
                    }}
                  />
                  <Layer
                    id={`uspace-hit-${zone.id}`}
                    type="fill"
                    paint={{ 'fill-color': 'transparent', 'fill-opacity': 0 }}
                  />
                </Source>
              ))}

              {geozonePopup && (
                <Popup
                  longitude={geozonePopup.lng}
                  latitude={geozonePopup.lat}
                  anchor="top"
                  closeButton
                  closeOnClick={false}
                  onClose={() => setGeozonePopup(null)}
                >
                  <div style={{ fontSize: 12, minWidth: 170 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{geozonePopup.zone.name}</div>
                    <div style={{ textTransform: 'uppercase', color: geozonePopup.zone.color, fontWeight: 600, fontSize: 10, letterSpacing: 0.5 }}>
                      {geozonePopup.zone.zone_type.replace('_', '-')}
                    </div>
                    <div style={{ color: '#333', marginTop: 4 }}>
                      {geozonePopup.zone.lower_alt_m}–{geozonePopup.zone.upper_alt_m} m {geozonePopup.zone.alt_reference}
                    </div>
                    {geozonePopup.zone.reason && (
                      <div style={{ color: 'var(--color-text-muted)', marginTop: 4 }}>{geozonePopup.zone.reason}</div>
                    )}
                    {geozonePopup.zone.is_global && (
                      <div style={{ color: 'var(--color-text-secondary)', marginTop: 4, fontSize: 10 }}>Authority-published zone</div>
                    )}
                  </div>
                </Popup>
              )}

              {showUspaceLayer && authorisations
                .filter(a => a.status === 'APPROVED' || a.status === 'ACTIVE')
                .map(auth => {
                  const vColor = auth.status === 'ACTIVE' ? '#00e0ff' : '#2ecc71';
                  return (
                    <Source
                      key={`auth-${auth.id}`}
                      id={`auth-${auth.id}`}
                      type="geojson"
                      data={{ type: 'Feature', geometry: auth.volume, properties: { id: auth.id } }}
                    >
                      <Layer id={`auth-fill-${auth.id}`} type="fill"
                        paint={{ 'fill-color': vColor, 'fill-opacity': 0.12 }} />
                      <Layer id={`auth-line-${auth.id}`} type="line"
                        paint={{ 'line-color': vColor, 'line-width': 2, 'line-dasharray': [2, 1] }} />
                    </Source>
                  );
                })}

              {showUspaceLayer && operationalIntents
                .filter(oi => !oi.is_own && oi.state !== 'Ended')
                .flatMap(oi => oi.volumes.map((vol, vi) => ({ oi, vol, vi })))
                .map(({ oi, vol, vi }) => (
                  <Source
                    key={`oi-${oi.id}-${vi}`}
                    id={`oi-${oi.id}-${vi}`}
                    type="geojson"
                    data={{ type: 'Feature', geometry: vol.outline, properties: { id: oi.id } }}
                  >
                    <Layer id={`oi-fill-${oi.id}-${vi}`} type="fill"
                      paint={{ 'fill-color': '#c026d3', 'fill-opacity': 0.14 }} />
                    <Layer id={`oi-line-${oi.id}-${vi}`} type="line"
                      paint={{ 'line-color': '#e879f9', 'line-width': 2, 'line-dasharray': [1, 1] }} />
                  </Source>
                ))}

              {showUspaceLayer && ridFlights.map(f => (
                <Marker key={`rid-${f.id}`} longitude={f.position.lng} latitude={f.position.lat} anchor="center">
                  <div
                    title={`Network RID · ${f.operator_id} · ${f.id}${f.position.alt != null ? ` · ${Math.round(f.position.alt)}m` : ''}`}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto' }}
                  >
                    <div style={{ color: 'var(--color-info)', fontSize: 16, lineHeight: 1, transform: `rotate(${f.track ?? 0}deg)`, textShadow: '0 0 3px #000' }}><Icon name="heading" /></div>
                    <span style={{ fontSize: 8, color: 'var(--color-info)', background: 'rgba(0,0,0,0.55)', borderRadius: 2, padding: '0 2px', marginTop: 1, whiteSpace: 'nowrap' }}>
                      RID {f.operator_id}
                    </span>
                  </div>
                </Marker>
              ))}

              {showKaekLayer && kaekParcels.filter(p => isItemVisible(p.task_set_id)).map(parcel => (
                <Source
                  key={`kaek-${parcel.id}`}
                  id={`kaek-${parcel.id}`}
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: parcel.geometry,
                    properties: { id: parcel.id, kaek_code: parcel.kaek_code }
                  }}
                >
                  <Layer
                    id={`kaek-fill-${parcel.id}`}
                    type="fill"
                    paint={{
                      'fill-color': parcel.color,
                      'fill-opacity': selectedKaekParcel?.id === parcel.id || selectedPoint?.kaek_parcel_id === parcel.id ? 0.5 : 0.25
                    }}
                  />
                  <Layer
                    id={`kaek-line-${parcel.id}`}
                    type="line"
                    paint={{
                      'line-color': selectedPoint?.kaek_parcel_id === parcel.id ? '#ffffff' : parcel.color,
                      'line-width': selectedKaekParcel?.id === parcel.id || selectedPoint?.kaek_parcel_id === parcel.id ? 3 : 1.5
                    }}
                  />
                  <Layer
                    id={`kaek-hit-${parcel.id}`}
                    type="fill"
                    paint={{
                      'fill-color': 'transparent',
                      'fill-opacity': 0
                    }}
                  />
                </Source>
              ))}

              {kaekPreview?.geometry && (
                <Source
                  id="kaek-preview"
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: kaekPreview.geometry,
                    properties: {}
                  }}
                >
                  <Layer
                    id="kaek-preview-fill"
                    type="fill"
                    paint={{ 'fill-color': COLORS.accent, 'fill-opacity': 0.3 }}
                  />
                  <Layer
                    id="kaek-preview-line"
                    type="line"
                    paint={{ 'line-color': COLORS.accent, 'line-width': 2, 'line-dasharray': [4, 2] }}
                  />
                </Source>
              )}

              {tempPolygonCoords.length > 0 && (
                <Source
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: tempPolygonCoords
                    },
                    properties: {}
                  }}
                >
                  <Layer
                    id="temp-polygon-line"
                    type="line"
                    paint={{
                      'line-color': COLORS.accent,
                      'line-width': 2,
                      'line-dasharray': [2, 1]
                    }}
                  />
                </Source>
              )}

              {assistantGhosts.length > 0 && <AssistantGhost features={assistantGhosts} />}

              {pendingFix && (
                <GeolocatedPreview
                  fix={pendingFix}
                  busy={savingFix}
                  onConfirm={confirmPendingFix}
                  onDismiss={() => setPendingFix(null)}
                />
              )}

              {tempPolygonCoords.map((coord, index) => (
                <Marker
                  key={`temp-${index}`}
                  longitude={coord[0]}
                  latitude={coord[1]}
                  anchor="center"
                >
                  <div className="temp-point">{index + 1}</div>
                </Marker>
              ))}

              {/* topmostLast: render order IS z-order IS click priority, and the API
                  returns newest-first — so rendering as-received put the OLDEST line on
                  top. With a 20px hit layer on every line, the imported coastline sat
                  above every route drawn since and swallowed their clicks. See
                  mapLayerOrder.ts. */}
              {topmostLast(lines.filter((l: MapLine) => !hiddenLines.has(l.id) && isItemVisible(l.task_set_id))).map((line: MapLine) => (
                <Source
                  key={line.id}
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: line.coordinates
                    },
                    properties: { id: line.id, name: line.name }
                  }}
                >
                  <Layer
                    id={`line-${line.id}`}
                    type="line"
                    paint={{
                      'line-color': line.color,
                      'line-width': line.line_width
                    }}
                  />
                  <Layer
                    id={`line-hit-${line.id}`}
                    type="line"
                    paint={{
                      'line-color': 'transparent',
                      'line-width': 20
                    }}
                  />
                </Source>
              ))}

              {tempLineCoords.length > 0 && (
                <Source
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: tempLineCoords
                    },
                    properties: {}
                  }}
                >
                  <Layer
                    id="temp-line"
                    type="line"
                    paint={{
                      'line-color': '#00ffff',
                      'line-width': 3,
                      'line-dasharray': [2, 1]
                    }}
                  />
                </Source>
              )}

              {tempLineCoords.map((coord, index) => (
                <Marker
                  key={`temp-line-${index}`}
                  longitude={coord[0]}
                  latitude={coord[1]}
                  anchor="center"
                >
                  <div className={`temp-point${tempLinePointIds[index] ? ' snapped' : ''}`}>{index + 1}</div>
                </Marker>
              ))}

              {activeMission && (activeMission.status === 'executing' || activeMission.status === 'paused' || activeMission.status === 'created') && (
                <>
                  <Source
                    id="mission-remaining"
                    type="geojson"
                    data={{
                      type: 'Feature',
                      geometry: {
                        type: 'LineString',
                        coordinates: activeMission.waypoints.map(wp => [wp.lng, wp.lat])
                      },
                      properties: {}
                    }}
                  >
                    <Layer
                      id="mission-remaining-layer"
                      type="line"
                      paint={{
                        'line-color': 'transparent',
                        'line-width': 14
                      }}
                    />
                    <Layer
                      id="mission-remaining-visible"
                      type="line"
                      paint={{
                        'line-color': '#ffaa00',
                        'line-width': 2,
                        'line-dasharray': [4, 2]
                      }}
                    />
                  </Source>
                  {activeMission.current_waypoint_index > 0 && (
                    <Source
                      id="mission-completed"
                      type="geojson"
                      data={{
                        type: 'Feature',
                        geometry: {
                          type: 'LineString',
                          coordinates: activeMission.waypoints.slice(0, activeMission.current_waypoint_index + 1).map(wp => [wp.lng, wp.lat])
                        },
                        properties: {}
                      }}
                    >
                      <Layer
                        id="mission-completed-layer"
                        type="line"
                        paint={{
                          'line-color': 'transparent',
                          'line-width': 14
                        }}
                      />
                      <Layer
                        id="mission-completed-visible"
                        type="line"
                        paint={{
                          'line-color': COLORS.accent,
                          'line-width': 3
                        }}
                      />
                    </Source>
                  )}
                </>
              )}
              {activeMission && activeMission.waypoints.map((wp, i) => (
                <Marker
                  key={`mission-wp-${i}`}
                  longitude={wp.lng}
                  latitude={wp.lat}
                  anchor="center"
                >
                  <div
                    className={`mission-waypoint ${i === activeMission.current_waypoint_index ? 'mission-wp-active' : i < activeMission.current_waypoint_index ? 'mission-wp-done' : ''}`}
                    title={wp.site_names?.length ? `WP${wp.index}: ${wp.site_names.join(', ')}` : wp.site_name ? `WP${wp.index}: ${wp.site_name}` : `WP${wp.index}`}
                    onClick={(e) => { e.stopPropagation(); setMissionPopupCoords({ lng: wp.lng, lat: wp.lat }); setMissionPopupWpIndex(wp.index ?? i); }}
                  >
                    {i + 1}
                  </div>
                </Marker>
              ))}

              {activeMission && missionPopupCoords && (() => {
                const popupWp = missionPopupWpIndex != null
                  ? (activeMission.waypoints.find(w => w.index === missionPopupWpIndex) ?? activeMission.waypoints[missionPopupWpIndex] ?? null)
                  : null;
                const wpSiteIds = popupWp?.site_ids ?? (popupWp?.site_id != null ? [popupWp.site_id] : []);
                const wpSiteNames = popupWp?.site_names ?? (popupWp?.site_name ? [popupWp.site_name] : []);
                return (
                <Popup
                  longitude={missionPopupCoords.lng}
                  latitude={missionPopupCoords.lat}
                  anchor="bottom"
                  onClose={() => { setMissionPopupCoords(null); setMissionPopupWpIndex(null); }}
                  closeButton={true}
                  className="mission-popup"
                >
                  <div className="mission-popup-content">
                    <div className="mission-popup-header">
                      <span className="mission-popup-name">{activeMission.name}</span>
                      <span className={`mission-status-badge status-${activeMission.status}`}>{activeMission.status.toUpperCase()}</span>
                    </div>
                    {popupWp && (
                      <div className="wp-sites-section">
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>WP{popupWp.index} — Linked Sites ({wpSiteIds.length})</div>
                        {wpSiteNames.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>
                            {wpSiteIds.map((sid, idx) => (
                              <span key={sid} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>
                                {wpSiteNames[idx] || `#${sid}`}
                                <span style={{ cursor: 'pointer', color: 'var(--color-danger)', fontWeight: 600, marginLeft: '2px' }} onClick={() => updateWaypointSites(activeMission.id, popupWp.index, wpSiteIds.filter(id => id !== sid))}>&times;</span>
                              </span>
                            ))}
                          </div>
                        )}
                        <select
                          style={{ width: '100%', fontSize: '10px', padding: '2px 4px', background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: '3px' }}
                          value=""
                          onChange={e => {
                            const newId = Number(e.target.value);
                            if (newId && !wpSiteIds.includes(newId)) {
                              updateWaypointSites(activeMission.id, popupWp.index, [...wpSiteIds, newId]);
                            }
                          }}
                        >
                          <option value="">+ Add site...</option>
                          {points
                            .filter(p => !wpSiteIds.includes(p.id) && p.icon !== '📸')
                            .map(p => ({ ...p, _dist: Math.hypot(p.latitude - popupWp.lat, p.longitude - popupWp.lng) }))
                            .sort((a, b) => a._dist - b._dist)
                            .map(p => {
                              const dm = Math.round(p._dist * 111_320);
                              return <option key={p.id} value={p.id}>{p.name} ({dm}m)</option>;
                            })}
                        </select>
                      </div>
                    )}
                    <div className="mission-popup-stats">
                      <span>WP {activeMission.current_waypoint_index}/{activeMission.waypoints.length}</span>
                      <span>{activeMission.progress_percent.toFixed(0)}%</span>
                      <span>Alt: {activeMission.altitude}m</span>
                      <span>Spd: {activeMission.speed}m/s</span>
                    </div>
                    <div className="mission-popup-controls">
                      {activeMission.status === 'created' && (
                        <button className="mission-btn mission-btn-execute" onClick={() => sendMissionCommand(activeMission.id, 'execute')}>Simulate</button>
                      )}
                      {activeMission.status === 'created' && activeMission.flight_id && (
                        <button className="mission-btn mission-btn-execute" style={{ background: 'var(--color-info)' }} onClick={() => openSendToDrone(activeMission)}>Send to Drone</button>
                      )}
                      {activeMission.status === 'executing' && (
                        <button className="mission-btn mission-btn-pause" onClick={() => sendMissionCommand(activeMission.id, 'pause')}>Pause</button>
                      )}
                      {activeMission.status === 'paused' && (
                        <button className="mission-btn mission-btn-execute" onClick={() => sendMissionCommand(activeMission.id, 'resume')}>Resume</button>
                      )}
                      {(activeMission.status === 'executing' || activeMission.status === 'paused' || activeMission.status === 'created') && (
                        <button className="mission-btn mission-btn-cancel" onClick={() => sendMissionCommand(activeMission.id, 'cancel')}>Cancel</button>
                      )}
                    </div>
                    <div className="mission-popup-downloads">
                      <button className="mission-btn mission-btn-download" disabled={!!downloadingMission} onClick={() => downloadMissionKmz(activeMission.id, activeMission.name)}>{downloadingMission === 'kmz' ? '...' : 'KMZ'}</button>
                      <button className="mission-btn mission-btn-download" disabled={!!downloadingMission} onClick={() => downloadMissionReport(activeMission.id, activeMission.name)}>{downloadingMission === 'report' ? '...' : 'Report'}</button>
                      <button className="mission-btn mission-btn-download" disabled={!!downloadingMission} onClick={() => downloadMissionReportPdf(activeMission.id, activeMission.name)}>{downloadingMission === 'pdf' ? '...' : 'PDF'}</button>
                      <button className="mission-btn mission-btn-download" disabled={!!downloadingMission} onClick={() => downloadMissionArdupilot(activeMission.id, activeMission.name)}>{downloadingMission === 'apm' ? '...' : 'APM'}</button>
                    </div>
                  </div>
                </Popup>
                );
              })()}



              {nearbyPointPicker && (
                <Popup
                  longitude={nearbyPointPicker.lng}
                  latitude={nearbyPointPicker.lat}
                  anchor="bottom"
                  closeOnClick={false}
                  onClose={() => setNearbyPointPicker(null)}
                  className="nearby-point-picker-popup"
                >
                  <div className="nearby-point-picker">
                    <div className="nearby-picker-title">{nearbyPointPicker.points.length} overlapping points</div>
                    {nearbyPointPicker.points.map(p => (
                      <button
                        key={p.id}
                        className="nearby-picker-item"
                        onClick={() => {
                          setSelectedPoint(p);
                          setSelectedSensor(null);
                          setNearbyPointPicker(null);
                        }}
                      >
                        <span className="nearby-picker-icon">{p.icon}</span>
                        <span className="nearby-picker-name">{p.name}</span>
                      </button>
                    ))}
                  </div>
                </Popup>
              )}
            </Map>
              {drawMode !== 'none' && (
                <div className="draw-mode-indicator">
                  {drawMode === 'point' && <><Icon name="site" /> Placing Point — click map</>}
                  {drawMode === 'polygon' && <><Icon name="area" /> Drawing Area — {tempPolygonCoords.length} points</>}
                  {drawMode === 'line' && <><Icon name="path" /> Drawing Path — {tempLineCoords.length} points</>}
                  {drawMode === 'sensor' && <><Icon name="sensor" /> Placing Sensor — click map</>}
                  {drawMode === 'unit' && <><Icon name="post" /> Placing Field Unit — click map</>}
                  <button className="draw-cancel-btn" onClick={() => { setDrawMode('none'); setTempPolygonCoords([]); setTempLineCoords([]); setTempLinePointIds([]); }}>
                    Cancel (Esc)
                  </button>
                </div>
              )}
              {showMGRSGrid && cursorMGRS && (
                <div className="mgrs-cursor-readout">
                  MGRS: {cursorMGRS}
                </div>
              )}
              {dcimPanoramaView && dcimPhotosForPoint.some(p => p.photo_type === 'panorama_tile' && p.uploaded) && (
                <div className="dcim-panorama-overlay">
                  <div className="dcim-panorama-header">
                    <span>{selectedPoint?.name || 'Panorama'}</span>
                    <button className="dcim-panorama-close" onClick={() => setDcimPanoramaView(false)} aria-label="Close"><Icon name="clear" /></button>
                  </div>
                  <div className="dcim-panorama-view">
                    <img
                      src={`${API_URL}${dcimPhotosForPoint.find(p => p.photo_type === 'panorama_tile' && p.uploaded)!.url}`}
                      alt="Panorama"
                      className="dcim-panorama-img"
                    />
                  </div>
                </div>
              )}

              {/* Exit control for the indoor 3D view. Entering happens from a
                  pavilion's detail panel, so this only needs to show the way out. */}
              {activeHall && <button
                onClick={() => exitHall3D()}
                style={{
                  position: 'absolute', bottom: 16, left: 16, zIndex: 5,
                  background: 'rgba(11, 16, 32, 0.9)', color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border-light)', borderRadius: 999,
                  padding: '7px 14px', fontSize: 13, cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
                }}
              >
                {`⤢ Exit ${activeHall.name}`}
              </button>}

              {selectedStand && (
                <div
                  style={{
                    position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 6,
                    background: 'rgba(11, 16, 32, 0.94)', color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-border-light)', borderRadius: 8,
                    padding: '10px 14px', minWidth: 160,
                    display: 'flex', alignItems: 'center', gap: 12,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.5)'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Stand</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{selectedStand}</div>
                  </div>
                  <button
                    onClick={() => setSelectedStand(null)}
                    style={{ marginLeft: 'auto', background: 'transparent', color: 'var(--color-text-secondary)', border: 'none', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
                    aria-label="Close"
                  ><Icon name="clear" /></button>
                </div>
              )}

              {selectedCamera && (() => {
                // The stream URL lives on the linked sensor, not in the hall plan.
                const camSensor = selectedCamera.sensorName
                  ? sensors.find(s => s.name === selectedCamera.sensorName)
                  : undefined;
                const camRtmp = camSensor?.metadata?.rtmp_url as string | undefined;
                return (
                <div
                  style={{
                    position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 6,
                    background: 'rgba(11, 16, 32, 0.94)', color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-info)', borderRadius: 8,
                    padding: '10px 14px', minWidth: 240,
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.5)'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Camera</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>
                      <Icon name="camera" /> {selectedCamera.sensorName || `Stand ${selectedCamera.stand}`}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 3 }}>
                      {`Stand ${selectedCamera.stand} · Heading ${Math.round(selectedCamera.heading)}° · FOV ${selectedCamera.fov}° · Range ${selectedCamera.rangeM} m`}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                      {`${selectedCamera.position[1].toFixed(6)}, ${selectedCamera.position[0].toFixed(6)}`}
                    </div>
                    {camRtmp ? (
                      <button
                        onClick={() => watchRtmpStream(
                          `cam-rtmp-${camSensor!.id}`, camSensor!.name, camRtmp,
                        )}
                        style={{
                          marginTop: 8, padding: '6px 12px', width: '100%',
                          background: 'rgba(34, 211, 238, 0.14)', color: 'var(--color-info)',
                          border: '1px solid var(--color-info)', borderRadius: 5,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        ▶ Watch Stream
                      </button>
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 8, fontStyle: 'italic' }}>
                        {selectedCamera.sensorName
                          ? `No stream on sensor “${selectedCamera.sensorName}”`
                          : 'No camera sensor linked'}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedCamera(null)}
                    style={{ marginLeft: 'auto', background: 'transparent', color: 'var(--color-text-secondary)', border: 'none', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
                    aria-label="Close"
                  ><Icon name="clear" /></button>
                </div>
                );
              })()}
          </div>
        ) : (
          <div className="no-map-message">
            <div className="no-map-icon"><Icon name="worldmap" /></div>
            <h2>Map View Disabled</h2>
            <p>Select a map style from the top-left dropdown to restore the tactical map.</p>
            <p>Sidebar, video feeds, and all panels remain active.</p>
          </div>
        )}
        </div>
        {floorplanViewer && (
          <Suspense fallback={null}>
            <FloorplanViewer
              pointId={floorplanViewer.pointId}
              pointName={floorplanViewer.pointName}
              apiUrl={API_URL}
              getAuthHeaders={getAuthHeaders}
              dataScope={dataScope}
              onClose={() => setFloorplanViewer(null)}
              sensorTypes={SENSOR_TYPES}
              onWatchStream={async (sensorId, sensorName, rtmpUrl) => {
                const camId = `fp-cam-${sensorId}`;
                setStreamSources(prev => {
                  if (prev.some(s => s.id === camId)) return prev;
                  return [...prev, { id: camId, name: sensorName, url: rtmpUrl, type: 'rtmp' as const }];
                });
                try {
                  const resp = await fetch(`${API_URL}/api/drone/video-source`, {
                    method: 'POST',
                    headers: getAuthHeaders(true),
                    body: JSON.stringify({ source: 'rtmp', rtmp_url: rtmpUrl }),
                  });
                  if (resp.ok) {
                    setActiveSourceId(camId);
                    await new Promise(r => setTimeout(r, 500));
                    setVideoKey(Date.now());
                  } else if (resp.status === 400) {
                    toast('Invalid RTMP URL', 'error');
                  } else if (resp.status === 429) {
                    toast('Too many active streams', 'warning');
                  }
                } catch { toast('Failed to start stream', 'error'); }
              }}
            />
          </Suspense>
        )}
      </main>

      <button
        className={`panel-collapse-btn panel-collapse-right ${rightPanelCollapsed ? 'collapsed' : ''}`}
        onClick={() => {
          const next = !rightPanelCollapsed;
          setRightPanelCollapsed(next);
          if (isPhoneLayout()) { if (!next) setLeftPanelCollapsed(true); }
          else localStorage.setItem('toc_rightPanelCollapsed', String(next));
        }}
        title={rightPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
        aria-label={rightPanelCollapsed ? 'Expand video and telemetry panel' : 'Collapse video and telemetry panel'}
        aria-expanded={!rightPanelCollapsed}
      >
        <Icon name={rightPanelCollapsed ? 'chev-left' : 'chev-right'} />
      </button>
      <div className={`resize-handle resize-handle-right ${rightPanelCollapsed ? 'hidden' : ''}`} onMouseDown={handleRightResizeStart}></div>

      <div ref={rightPaneRef} className={`right-panels ${rightPanelCollapsed ? 'collapsed' : ''}`} style={{ width: rightPanelCollapsed ? '0px' : `${rightPanelWidth}px` }}>
            {showVideo && !videoFloating && (
              <VideoPanel
                isFloating={false}
                /* WebRTC on the side panel ONLY while the Matrix is open.
                   Alone, this panel is a single feed at ~7.7 Mbit/s, which is not
                   a problem, and MJPEG is measurably lower latency (162 ms against
                   271) — someone looking at one feed closely is exactly who wants
                   the faster path.

                   While the Matrix IS open, `.matrix-overlay` is `position: fixed`
                   at `z-index: 9000` and this panel carries no z-index, so it is
                   completely covered — it was streaming full-rate video for a
                   picture nobody could see.

                   Suspending it outright was considered and is WORSE. This panel
                   may be the feed's only viewer, and the reaper stops a reader
                   15 s after its last one leaves (_IDLE_SLOT_GRACE_SECONDS), so
                   closing the Matrix would then cost a full ffmpeg reconnect of
                   1-4 s. Switching keeps the reader pinned and the feed warm for
                   ~0.6 Mbit/s — 2% of an eight-camera budget, to make closing the
                   Matrix instant. Do not "optimise" this into a suspend. */
                /* Always, not just under the Matrix: the per-feed path 409s on
                   the processing key and falls through to the primary room, so
                   this only bites when the panel shows a feed that is NOT the
                   processing one. MJPEG remains the fallback in every case. */
                preferWebrtc
                onToggleFloat={() => setVideoFloating(true)}
                onStopPhoneStream={() => {
                  const phoneId = activeSourceId;
                  setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[phoneId]; return next; });
                  setStreamSources(prev => prev.filter(s => s.id !== phoneId));
                  setActiveSourceId(NO_SOURCE_ID);
                }}
                onTerminateStream={terminateStream}
                onRequestGroundPick={handleRequestGroundPick}
                onGeoFix={handleGeoFix}
                aiLayerRows={aiLayers.rows}
                aiLayerActive={aiLayerActive}
                aiLayerBusy={aiLayerBusy}
                aiLayerError={aiLayerError}
                onSelectAiLayer={selectAiLayer}
                privacyMode={privacyMode}
                onSelectPrivacy={selectPrivacyMode}
                privacyBusy={privacyBusy}
                privacyError={privacyError}
                privacyUnavailable={privacyUnavailable}
                roadSegSettingsOpen={roadSegSettingsOpen}
                onToggleRoadSegSettings={() => setRoadSegSettingsOpen(v => !v)}
                onSwitchRoadSegMode={switchRoadSegMode}
                roadSegEnabled={roadSegEnabled}
                roadSegMode={roadSegMode}
                geoMaskEnabled={geoMaskEnabled}
                geoMaskMode={geoMaskMode}
                geoMaskAltSource={geoMaskAltSource}
                geoMaskYawOffset={geoMaskYawOffset}
                geoMaskCameraLabel={geoMaskCameraLabel}
                onToggleGeoMask={toggleGeoMask}
                onSwitchGeoMaskMode={switchGeoMaskMode}
                onCycleGeoMaskAlt={cycleGeoMaskAlt}
                onAdjustYawOffset={adjustYawOffset}
                geoMaskPitchOffset={geoMaskPitchOffset}
                geoMaskHeightM={geoMaskHeightM}
                geoMaskRefused={geoMaskRefused}
                onAdjustPitchOffset={adjustPitchOffset}
                onSetGeoMaskHeight={setGeoMaskHeight}
                activeSourceId={activeSourceId}
                activeStreamSource={activeStreamSource}
                phoneSensorCredentialsMap={phoneSensorCredentialsMap}
                hlsCredentialsMap={hlsCredentialsMap}
                djiStreamCredentials={djiStreamCredentials}
                djiLowLatency={djiLowLatency}
                processedStreamCreds={processedStreamCreds}
                feedToken={feedToken}
                videoKey={videoKey}
                detectionEnabled={detectionEnabled}
                apiUrl={API_URL}
                authHeaders={getAuthHeaders}
                djiStreamError={djiStreamError}
                onDjiError={(msg) => setDjiStreamError(msg)}
                onDjiDisconnected={() => setDjiStreamCredentials(null)}
                onPhoneDisconnected={(sourceId) => setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[sourceId]; return next; })}
                onRecordingSaved={() => toast('Recording saved', 'success')}
                onProcessedStreamError={() => { setProcessedStreamCreds(null); setTimeout(() => setProcessedStreamRetry(n => n + 1), 3000); }}
                onFeedError={handleFeedError}
                onCaptureEvent={(msg, type) => toast(msg, type)}
              />
            )}

            {selectedPoint && (
              <div className="detail-overlay" onClick={() => setSelectedPoint(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  {editingPointName !== null ? (
                    <input
                      className="point-name-edit"
                      value={editingPointName}
                      onChange={(e) => setEditingPointName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && editingPointName.trim()) savePointName(selectedPoint.id, editingPointName.trim());
                        if (e.key === 'Escape') setEditingPointName(null);
                      }}
                      onBlur={() => { if (editingPointName.trim()) savePointName(selectedPoint.id, editingPointName.trim()); else setEditingPointName(null); }}
                      autoFocus
                    />
                  ) : (
                    <h3 onClick={() => setEditingPointName(selectedPoint.name)} style={{ cursor: 'pointer' }} title="Click to rename">{selectedPoint.icon} {selectedPoint.name}</h3>
                  )}
                  <button className="detail-close-btn" onClick={() => { setSelectedPoint(null); setEditingPointName(null); }} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  {/* Pavilions with a registered indoor plan get a way into the 3D view. */}
                  {(() => {
                    const plan = hallPlanForPoint(selectedPoint);
                    if (!plan) return null;
                    return (
                      <button
                        onClick={() => enterHall3D(plan)}
                        style={{
                          width: '100%', marginBottom: 10, padding: '9px 12px',
                          background: 'rgba(34, 211, 238, 0.12)', color: 'var(--color-info)',
                          border: '1px solid var(--color-info)', borderRadius: 6,
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}
                        title={`Open the indoor 3D stand plan for ${plan.name}`}
                      >
                        <Icon name="hall" /> 3D κάτοψη — {plan.name}
                      </button>
                    );
                  })()}
                  <div className="detail-meta">
                    <div className="detail-row"><label>Lat</label><span>{selectedPoint.latitude.toFixed(6)}</span></div>
                    <div className="detail-row"><label>Lon</label><span>{selectedPoint.longitude.toFixed(6)}</span></div>
                    <div className="detail-row mgrs-row"><label>MGRS</label><span className="mgrs-value">{toMGRS(selectedPoint.latitude, selectedPoint.longitude)}</span></div>
                    {selectedPoint.description && <div className="detail-row"><label>Desc</label><span>{selectedPoint.description}</span></div>}
                    {selectedPoint.kaek_code && (
                      <div style={{ margin: '6px 0', padding: '6px 8px', background: '#1a1a0d', borderRadius: '4px', border: '1px solid #3a3a1a' }}>
                        <div style={{ fontSize: '10px', color: 'var(--color-warning)', marginBottom: '4px', fontWeight: 600 }}>Linked Parcel</div>
                        <div className="detail-row" style={{ fontSize: '11px' }}>
                          <label>KAEK</label>
                          <span style={{ color: 'var(--color-info)', cursor: 'pointer' }} onClick={() => {
                            const parcel = kaekParcels.find(p => p.id === selectedPoint.kaek_parcel_id);
                            if (parcel) { setSelectedKaekParcel(parcel); setSelectedPoint(null); }
                          }}>{selectedPoint.kaek_code}</span>
                        </div>
                        {selectedPoint.parcel_area != null && (
                          <div className="detail-row" style={{ fontSize: '11px' }}><label>Area</label><span>{Math.round(selectedPoint.parcel_area).toLocaleString()} m²</span></div>
                        )}
                        {selectedPoint.land_use && (
                          <div className="detail-row" style={{ fontSize: '11px' }}><label>Land Use</label><span>{selectedPoint.land_use}</span></div>
                        )}
                        {selectedPoint.registry_link && (
                          <a href={selectedPoint.registry_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', color: 'var(--color-info)' }}>Ktimatologio.gr ↗</a>
                        )}
                        <button className="tool-btn" style={{ fontSize: '10px', padding: '2px 8px', marginTop: '4px' }}
                          onClick={async () => {
                            try {
                              const resp = await fetch(`${API_URL}/api/kaek/snap/${selectedPoint.id}`, {
                                method: 'POST', headers: getAuthHeaders()
                              });
                              if (resp.ok) {
                                const result = await resp.json();
                                fetchPoints();
                                centerOnPoint(result.longitude, result.latitude);
                              }
                            } catch {}
                          }}>
                          Snap to parcel center
                        </button>
                      </div>
                    )}
                    {(() => {
                      const pointMission = selectedPoint.line_id != null ? missions.find(m => m.line_id === selectedPoint.line_id) : null;
                      if (!pointMission) {
                        const siteNames = selectedPoint.metadata?.matched_site_names || (selectedPoint.metadata?.matched_site_name ? [selectedPoint.metadata.matched_site_name] : null);
                        return siteNames ? (
                          <div className="detail-row"><label>Sites</label><span style={{ fontWeight: 600 }}>{Array.isArray(siteNames) ? siteNames.join(', ') : String(siteNames)}</span></div>
                        ) : null;
                      }
                      const thisWp = pointMission.waypoints.find(wp => {
                        const ids = wp.site_ids ?? (wp.site_id != null ? [wp.site_id] : []);
                        return ids.includes(selectedPoint.id);
                      }) ?? pointMission.waypoints.reduce<{wp: typeof pointMission.waypoints[0]; dist: number} | null>((best, wp) => {
                        const d = Math.hypot(wp.lat - selectedPoint.latitude, wp.lng - selectedPoint.longitude);
                        return (!best || d < best.dist) ? { wp, dist: d } : best;
                      }, null)?.wp ?? null;
                      if (!thisWp) return null;
                      const ids = thisWp.site_ids ?? (thisWp.site_id != null ? [thisWp.site_id] : []);
                      const names = thisWp.site_names ?? (thisWp.site_name ? [thisWp.site_name] : []);
                      return (
                        <div style={{ margin: '6px 0', padding: '6px 8px', background: '#0d1a26', borderRadius: '4px', border: '1px solid #1a2a3a' }}>
                          <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                            {pointMission.name} — WP{thisWp.index} Linked Sites ({ids.length})
                          </div>
                          {ids.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>
                              {ids.map((sid: number, idx: number) => (
                                <span key={sid} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>
                                  {names[idx] || `#${sid}`}
                                  <span style={{ cursor: 'pointer', color: 'var(--color-danger)', fontWeight: 600, marginLeft: '2px' }} onClick={() => updateWaypointSites(pointMission.id, thisWp.index, ids.filter((id: number) => id !== sid))}>&times;</span>
                                </span>
                              ))}
                            </div>
                          )}
                          <select
                            style={{ width: '100%', fontSize: '10px', padding: '2px 4px', background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: '3px' }}
                            value=""
                            onChange={e => {
                              const newId = Number(e.target.value);
                              if (newId && !ids.includes(newId)) {
                                updateWaypointSites(pointMission.id, thisWp.index, [...ids, newId]);
                              }
                            }}
                          >
                            <option value="">+ Add site...</option>
                            {points
                              .filter(p => !ids.includes(p.id) && p.icon !== '📸')
                              .map(p => ({ ...p, _dist: Math.hypot(p.latitude - thisWp.lat, p.longitude - thisWp.lng) }))
                              .sort((a, b) => a._dist - b._dist)
                              .map(p => {
                                const dm = Math.round(p._dist * 111_320);
                                return <option key={p.id} value={p.id}>{p.name} ({dm}m)</option>;
                              })}
                          </select>
                        </div>
                      );
                    })()}
                    {selectedPoint.created_by_email && <div className="detail-row"><label>Added by</label><span>{selectedPoint.created_by_email}</span></div>}
                    {selectedPoint.created_at && <div className="detail-row"><label>Created</label><span>{new Date(selectedPoint.created_at).toLocaleString()}</span></div>}
                    <div className="detail-row open-in-row">
                      <label>Open in</label>
                      <span className="open-in-links">
                        <a href={`https://www.openstreetmap.org/?mlat=${selectedPoint.latitude}&mlon=${selectedPoint.longitude}#map=17/${selectedPoint.latitude}/${selectedPoint.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">OSM</a>
                        <a href={`https://earth.google.com/web/search/${selectedPoint.latitude},${selectedPoint.longitude}/@${selectedPoint.latitude},${selectedPoint.longitude},0a,500d,35y,0h,0t,0r`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Earth</a>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${selectedPoint.latitude},${selectedPoint.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Maps</a>
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Task Set</label>
                      <select
                        className="task-set-assign-select"
                        value={selectedPoint.task_set_id ?? ''}
                        onChange={(e) => updateItemTaskSet('points', selectedPoint.id, e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">None</option>
                        {taskSets.map(ts => (
                          <option key={ts.id} value={ts.id}>{ts.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('pt-marker')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('pt-marker') ? 'chev-right' : 'chev-down'} /></span> Marker Icon
                    </div>
                    {!collapsedSections.has('pt-marker') && (
                      <div className="marker-change-section">
                        <div className="marker-image-picker">
                          {MARKER_CHOICES.map(m => (
                            <button
                              key={m.key ?? 'default'}
                              className={`marker-option${markerOverride(selectedPoint.metadata) === m.key ? ' selected' : ''}`}
                              onClick={() => updatePointMarkerIcon(selectedPoint.id, m.key ?? '')}
                              title={m.label}
                              aria-label={m.label}
                            >
                              <span><Icon name={m.key ?? 'site'} size={16} /></span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('pt-notes')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('pt-notes') ? 'chev-right' : 'chev-down'} /></span> Notes ({pointNotes.length})
                    </div>
                    {!collapsedSections.has('pt-notes') && (
                      <div className="notes-section">
                        <div className="notes-list">
                          {loadingNotes ? (
                            <div className="comments-display"><span className="no-comments">Loading...</span></div>
                          ) : pointNotes.length === 0 ? (
                            <div className="comments-display"><span className="no-comments">No notes added</span></div>
                          ) : (
                            pointNotes.map((note) => (
                              <div key={note.id} className="note-item">
                                {editingNoteId === note.id ? (
                                  <>
                                    <textarea value={editNoteContent} onChange={(e) => setEditNoteContent(e.target.value)} rows={2} autoFocus />
                                    <div className="comments-actions">
                                      <button className="save-comments-btn" onClick={() => updatePointNote(selectedPoint.id, note.id, editNoteContent)} disabled={savingNote || !editNoteContent.trim()}>{savingNote ? 'Saving...' : 'Save'}</button>
                                      <button className="cancel-btn" onClick={() => { setEditingNoteId(null); setEditNoteContent(''); }}>Cancel</button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="note-content">{note.content}</div>
                                    <div className="note-meta">{note.created_by_email && <span className="note-author">{note.created_by_email}</span>}{new Date(note.created_at).toLocaleString()}{note.updated_at !== note.created_at && ' (edited)'}</div>
                                    <div className="note-actions">
                                      <button className="note-action-btn" onClick={() => { setEditingNoteId(note.id); setEditNoteContent(note.content); }}>Edit</button>
                                      <button className="note-action-btn note-delete-btn" onClick={() => deletePointNote(selectedPoint.id, note.id)}>Delete</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                        <div className="add-note-form">
                          <textarea value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)} placeholder="Add a note..." rows={2} />
                          <button className="edit-comments-btn" onClick={() => addPointNote(selectedPoint.id, newNoteContent)} disabled={savingNote || !newNoteContent.trim()}>{savingNote ? 'Saving...' : 'Add Note'}</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {dcimPhotosForPoint.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header" onClick={() => toggleSection('pt-dcim')}>
                        <span className="section-toggle"><Icon name={collapsedSections.has('pt-dcim') ? 'chev-right' : 'chev-down'} /></span> DCIM Media ({dcimPhotosForPoint.filter(p => p.uploaded).length})
                      </div>
                      {!collapsedSections.has('pt-dcim') && (
                        <div className="dcim-photos-section">
                          {dcimPhotosForPoint.some(p => p.photo_type === 'panorama_tile' && p.uploaded) && (
                            <button
                              className="dcim-pano-view-btn"
                              onClick={() => setDcimPanoramaView(!dcimPanoramaView)}
                            >
                              {dcimPanoramaView ? <><Icon name="clear" /> Close Panorama</> : <><Icon name="expand" /> View Panorama</>}
                            </button>
                          )}
                          {dcimPhotosForPoint.some(p => p.uploaded) && (
                            <div className="dcim-photos-gallery dcim-pano-grid">
                              {dcimPhotosForPoint
                                .filter(p => p.uploaded)
                                .sort((a, b) => a.filename.localeCompare(b.filename))
                                .map(photo => (
                                  <div key={photo.id} className="dcim-photo-item dcim-photo-tile" onClick={() => photo.photo_type !== 'video' && setDcimViewingPhoto(photo)}>
                                    {photo.photo_type === 'video' && photo.transcode_status === 'pending' ? (
                                      <div style={{ padding: '24px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 4, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                                        Processing video...
                                      </div>
                                    ) : photo.photo_type === 'video' ? (
                                      <video src={`${API_URL}${photo.url}`} controls preload="metadata" style={{ width: '100%', borderRadius: 4 }} />
                                    ) : (
                                      <img src={`${API_URL}${photo.url}`} alt={photo.filename} loading="lazy" />
                                    )}
                                    <div className="dcim-photo-label">{photo.photo_type === 'video' ? <><Icon name="captures" /> </> : photo.lens_type ? `[${photo.lens_type}] ` : ''}{photo.filename}</div>
                                    {photo.latitude != null && photo.longitude != null && (
                                      <div className="dcim-photo-coords">{photo.latitude.toFixed(6)}, {photo.longitude.toFixed(6)}{photo.altitude != null ? ` | ${photo.altitude.toFixed(0)}m` : ''}</div>
                                    )}
                                    <button className="dcim-photo-delete-btn" onClick={(e) => { e.stopPropagation(); deleteDcimPhoto(photo.id); }} aria-label="Remove"><Icon name="clear" /></button>
                                  </div>
                                ))}
                            </div>
                          )}
                          <div className="dcim-photo-meta-row">
                            {dcimPhotosForPoint[0]?.camera_make && <span>{dcimPhotosForPoint[0].camera_make} {dcimPhotosForPoint[0].camera_model}</span>}
                            {dcimPhotosForPoint[0]?.captured_at && <span>{new Date(dcimPhotosForPoint[0].captured_at).toLocaleString()}</span>}
                            {dcimPhotosForPoint[0]?.altitude && <span>Alt: {dcimPhotosForPoint[0].altitude.toFixed(0)}m</span>}
                            {(() => {
                              const wpIndices = new Set(dcimPhotosForPoint.filter(p => p.waypoint_index != null).map(p => p.waypoint_index));
                              return wpIndices.size > 1 ? <span>{wpIndices.size} waypoints</span> : null;
                            })()}
                          </div>
                          <div className="dcim-actions-row">
                            <button className="dcim-report-btn" onClick={() => downloadDcimReport(dcimPhotosForPoint[0].upload_id, dcimPhotosForPoint[0].mission_name || 'mission')}>
                              Download Report
                            </button>
                            <button className="dcim-delete-all-btn" onClick={() => deleteDcimUpload(dcimPhotosForPoint[0].upload_id)}>
                              Delete All DCIM Photos
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('pt-floorplans')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('pt-floorplans') ? 'chev-right' : 'chev-down'} /></span> Floorplans
                    </div>
                    {!collapsedSections.has('pt-floorplans') && (
                      <div style={{ padding: '4px 0' }}>
                        <button className="edit-comments-btn" onClick={() => { setFloorplanViewer({ pointId: selectedPoint.id, pointName: selectedPoint.name }); setSelectedPoint(null); }}>
                          View / Manage Floorplans
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('pt-attachments')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('pt-attachments') ? 'chev-right' : 'chev-down'} /></span> Attachments {selectedPoint.attachments?.length ? `(${selectedPoint.attachments.length})` : ''}
                    </div>
                    {!collapsedSections.has('pt-attachments') && (
                      <div className="attachments-section">
                        {selectedPoint.attachments && selectedPoint.attachments.length > 0 ? (
                          <div className="attachments-gallery">
                            {selectedPoint.attachments.map((att) => (
                              <div key={att.id} className="attachment-item">
                                {att.type === 'image' ? (
                                  <img src={`${API_URL}${att.url}`} alt={att.filename} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} />
                                ) : att.type === 'video' ? (
                                  <video src={`${API_URL}${att.url}`} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} muted />
                                ) : (
                                  <div className="attachment-file" onClick={() => window.open(`${API_URL}${att.url}`, '_blank')}>{att.filename}</div>
                                )}
                                <div className="attachment-meta">{att.uploaded_by && <span>{att.uploaded_by}</span>}{att.uploaded_at && <span>{new Date(att.uploaded_at).toLocaleString()}</span>}</div>
                                <button className="attachment-delete-btn" onClick={(e) => { e.stopPropagation(); deleteAttachment(selectedPoint.id, att.id); }} aria-label="Remove"><Icon name="clear" /></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="comments-display"><span className="no-comments">No attachments</span></div>
                        )}
                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { uploadAttachment(selectedPoint.id, file); e.target.value = ''; } }} />
                        <button className="edit-comments-btn" onClick={() => fileInputRef.current?.click()} disabled={uploadingAttachment[`point-${selectedPoint.id}`]}>
                          {uploadingAttachment[`point-${selectedPoint.id}`] ? 'Uploading...' : 'Add Attachment'}
                        </button>
                      </div>
                    )}
                  </div>

                  <button className="delete-btn" onClick={() => deletePoint(selectedPoint.id)}>Delete Point</button>
                </div>
              </div>
              </div>
            )}

            {showTankRollup && (
              <TankGaugePanel
                scope={dataScope}
                onClose={() => setShowTankRollup(false)}
                onBatch={(readings) => setTankReadings((prev) => {
                  const next = { ...prev };
                  readings.forEach((r) => { if (r.ok) next[r.polygon_id] = r; });
                  return next;
                })}
                onFlyTo={(lat, lng) => {
                  const m = (mapRef.current as any)?.getMap?.();
                  m?.flyTo({ center: [lng, lat], zoom: 17, duration: 1200, essential: true });
                }}
              />
            )}

            {tankGaugeFor && (
              <TankGaugePanel
                scope={dataScope}
                polygonId={tankGaugeFor.id}
                polygonName={tankGaugeFor.name}
                onClose={() => { setTankGaugeFor(null); setTankHighlight(null); }}
                onReading={(r) => {
                  setTankHighlight(r);
                  if (r) setTankReadings((prev) => ({ ...prev, [r.polygon_id]: r }));
                }}
              />
            )}

            {selectedPolygon && (
              <div className="detail-overlay" onClick={() => setSelectedPolygon(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3><Icon name="area" /> {selectedPolygon.name}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedPolygon(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  {/* Storage tanks get a way into the satellite gauging panel. Hidden unless
                      the backend says the feature is on here AND the polygon is actually a
                      tank — same shape as the 3D κάτοψη button on points. */}
                  {tankGaugeEnabled && isTankPolygon(selectedPolygon) && (
                    <button
                      onClick={() => { setTankGaugeFor(selectedPolygon); setSelectedPolygon(null); }}
                      style={{
                        width: '100%', marginBottom: 10, padding: '9px 12px',
                        background: 'rgba(249, 115, 22, 0.12)', color: 'var(--color-warning)',
                        border: '1px solid #f97316', borderRadius: 6,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                      title="Estimate how full this tank is from the shadow on its floating roof"
                    >
                      <Icon name="factory" /> Στάθμη από δορυφόρο
                    </button>
                  )}
                  <div className="detail-meta">
                    <div className="detail-row"><label>Area</label><span>{formatArea(calculatePolygonArea(selectedPolygon.coordinates))}</span></div>
                    <div className="detail-row"><label>Vertices</label><span>{selectedPolygon.coordinates.length}</span></div>
                    {selectedPolygon.coordinates.length > 0 && (
                      <div className="detail-row mgrs-row"><label>MGRS</label><span className="mgrs-value">{toMGRS(selectedPolygon.coordinates[0][1], selectedPolygon.coordinates[0][0])}</span></div>
                    )}
                    {selectedPolygon.description && <div className="detail-row"><label>Desc</label><span>{selectedPolygon.description}</span></div>}
                    {selectedPolygon.created_by_email && <div className="detail-row"><label>Added by</label><span>{selectedPolygon.created_by_email}</span></div>}
                    {selectedPolygon.created_at && <div className="detail-row"><label>Created</label><span>{new Date(selectedPolygon.created_at).toLocaleString()}</span></div>}
                    <div className="detail-row">
                      <label>Task Set</label>
                      <select
                        className="task-set-assign-select"
                        value={selectedPolygon.task_set_id ?? ''}
                        onChange={(e) => updateItemTaskSet('polygons', selectedPolygon.id, e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">None</option>
                        {taskSets.map(ts => (
                          <option key={ts.id} value={ts.id}>{ts.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('poly-notes')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('poly-notes') ? 'chev-right' : 'chev-down'} /></span> Notes ({polygonNotes.length})
                    </div>
                    {!collapsedSections.has('poly-notes') && (
                      <div className="notes-section">
                        <div className="notes-list">
                          {loadingNotes ? (
                            <div className="comments-display"><span className="no-comments">Loading...</span></div>
                          ) : polygonNotes.length === 0 ? (
                            <div className="comments-display"><span className="no-comments">No notes added</span></div>
                          ) : (
                            polygonNotes.map((note) => (
                              <div key={note.id} className="note-item">
                                {editingNoteId === note.id ? (
                                  <>
                                    <textarea value={editNoteContent} onChange={(e) => setEditNoteContent(e.target.value)} rows={2} autoFocus />
                                    <div className="comments-actions">
                                      <button className="save-comments-btn" onClick={() => updatePolygonNote(selectedPolygon.id, note.id, editNoteContent)} disabled={savingNote || !editNoteContent.trim()}>{savingNote ? 'Saving...' : 'Save'}</button>
                                      <button className="cancel-btn" onClick={() => { setEditingNoteId(null); setEditNoteContent(''); }}>Cancel</button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="note-content">{note.content}</div>
                                    <div className="note-meta">{note.created_by_email && <span className="note-author">{note.created_by_email}</span>}{new Date(note.created_at).toLocaleString()}{note.updated_at !== note.created_at && ' (edited)'}</div>
                                    <div className="note-actions">
                                      <button className="note-action-btn" onClick={() => { setEditingNoteId(note.id); setEditNoteContent(note.content); }}>Edit</button>
                                      <button className="note-action-btn note-delete-btn" onClick={() => deletePolygonNote(selectedPolygon.id, note.id)}>Delete</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                        <div className="add-note-form">
                          <textarea value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)} placeholder="Add a note..." rows={2} />
                          <button className="edit-comments-btn" onClick={() => addPolygonNote(selectedPolygon.id, newNoteContent)} disabled={savingNote || !newNoteContent.trim()}>{savingNote ? 'Saving...' : 'Add Note'}</button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('poly-attachments')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('poly-attachments') ? 'chev-right' : 'chev-down'} /></span> Attachments {selectedPolygon.attachments?.length ? `(${selectedPolygon.attachments.length})` : ''}
                    </div>
                    {!collapsedSections.has('poly-attachments') && (
                      <div className="attachments-section">
                        {selectedPolygon.attachments && selectedPolygon.attachments.length > 0 ? (
                          <div className="attachments-gallery">
                            {selectedPolygon.attachments.map((att) => (
                              <div key={att.id} className="attachment-item">
                                {att.type === 'image' ? (
                                  <img src={`${API_URL}${att.url}`} alt={att.filename} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} />
                                ) : att.type === 'video' ? (
                                  <video src={`${API_URL}${att.url}`} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} muted />
                                ) : (
                                  <div className="attachment-file" onClick={() => window.open(`${API_URL}${att.url}`, '_blank')}>{att.filename}</div>
                                )}
                                <div className="attachment-meta">{att.uploaded_by && <span>{att.uploaded_by}</span>}{att.uploaded_at && <span>{new Date(att.uploaded_at).toLocaleString()}</span>}</div>
                                <button className="attachment-delete-btn" onClick={(e) => { e.stopPropagation(); deletePolygonAttachment(selectedPolygon.id, att.id); }} aria-label="Remove"><Icon name="clear" /></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="comments-display"><span className="no-comments">No attachments</span></div>
                        )}
                        <input type="file" ref={polygonFileInputRef} style={{ display: 'none' }} accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { uploadPolygonAttachment(selectedPolygon.id, file); e.target.value = ''; } }} />
                        <button className="edit-comments-btn" onClick={() => polygonFileInputRef.current?.click()} disabled={uploadingAttachment[`polygon-${selectedPolygon.id}`]}>
                          {uploadingAttachment[`polygon-${selectedPolygon.id}`] ? 'Uploading...' : 'Add Attachment'}
                        </button>
                      </div>
                    )}
                  </div>

                  <button className="delete-btn" onClick={() => deletePolygon(selectedPolygon.id)}>Delete Area</button>
                </div>
              </div>
              </div>
            )}

            {selectedLine && (
              <div className="detail-overlay" onClick={() => setSelectedLine(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3><Icon name="path" /> {selectedLine.name}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedLine(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="detail-meta">
                    <div className="detail-row"><label>Length</label><span>{formatLength(calculateLineLength(selectedLine.coordinates))}</span></div>
                    <div className="detail-row"><label>Points</label><span>{selectedLine.coordinates.length}</span></div>
                    {selectedLine.coordinates.length > 0 && (
                      <div className="detail-row mgrs-row"><label>MGRS</label><span className="mgrs-value">{toMGRS(selectedLine.coordinates[0][1], selectedLine.coordinates[0][0])}</span></div>
                    )}
                    {selectedLine.description && <div className="detail-row"><label>Desc</label><span>{selectedLine.description}</span></div>}
                    {selectedLine.created_by_email && <div className="detail-row"><label>Added by</label><span>{selectedLine.created_by_email}</span></div>}
                    {selectedLine.created_at && <div className="detail-row"><label>Created</label><span>{new Date(selectedLine.created_at).toLocaleString()}</span></div>}
                    <div className="detail-row">
                      <label>Task Set</label>
                      <select
                        className="task-set-assign-select"
                        value={selectedLine.task_set_id ?? ''}
                        onChange={(e) => updateItemTaskSet('lines', selectedLine.id, e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">None</option>
                        {taskSets.map(ts => (
                          <option key={ts.id} value={ts.id}>{ts.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('ln-notes')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('ln-notes') ? 'chev-right' : 'chev-down'} /></span> Notes ({lineNotes.length})
                    </div>
                    {!collapsedSections.has('ln-notes') && (
                      <div className="notes-section">
                        <div className="notes-list">
                          {loadingNotes ? (
                            <div className="comments-display"><span className="no-comments">Loading...</span></div>
                          ) : lineNotes.length === 0 ? (
                            <div className="comments-display"><span className="no-comments">No notes added</span></div>
                          ) : (
                            lineNotes.map((note) => (
                              <div key={note.id} className="note-item">
                                {editingNoteId === note.id ? (
                                  <>
                                    <textarea value={editNoteContent} onChange={(e) => setEditNoteContent(e.target.value)} rows={2} autoFocus />
                                    <div className="comments-actions">
                                      <button className="save-comments-btn" onClick={() => updateLineNote(selectedLine.id, note.id, editNoteContent)} disabled={savingNote || !editNoteContent.trim()}>{savingNote ? 'Saving...' : 'Save'}</button>
                                      <button className="cancel-btn" onClick={() => { setEditingNoteId(null); setEditNoteContent(''); }}>Cancel</button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="note-content">{note.content}</div>
                                    <div className="note-meta">{note.created_by_email && <span className="note-author">{note.created_by_email}</span>}{new Date(note.created_at).toLocaleString()}{note.updated_at !== note.created_at && ' (edited)'}</div>
                                    <div className="note-actions">
                                      <button className="note-action-btn" onClick={() => { setEditingNoteId(note.id); setEditNoteContent(note.content); }}>Edit</button>
                                      <button className="note-action-btn note-delete-btn" onClick={() => deleteLineNote(selectedLine.id, note.id)}>Delete</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                        <div className="add-note-form">
                          <textarea value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)} placeholder="Add a note..." rows={2} />
                          <button className="edit-comments-btn" onClick={() => addLineNote(selectedLine.id, newNoteContent)} disabled={savingNote || !newNoteContent.trim()}>{savingNote ? 'Saving...' : 'Add Note'}</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {dcimVideosForLine.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header" onClick={() => toggleSection('ln-dcim')}>
                        <span className="section-toggle"><Icon name={collapsedSections.has('ln-dcim') ? 'chev-right' : 'chev-down'} /></span> DCIM Videos ({dcimVideosForLine.filter(v => v.uploaded).length})
                      </div>
                      {!collapsedSections.has('ln-dcim') && (
                        <div className="dcim-photos-section">
                          {dcimVideosForLine.filter(v => v.uploaded).map(video => {
                            const quality = dcimVideoQuality[video.id] || '720p';
                            const videoSrc = video.url ? `${API_URL}${video.url}${video.url.includes('?') ? '&' : '?'}quality=${quality}` : '';
                            return (
                            <div key={video.id} className="dcim-video-item" style={{ marginBottom: 8 }}>
                              {video.transcode_status === 'pending' ? (
                                <div style={{ padding: '24px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 4, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                                  Processing video... This may take a few minutes.
                                </div>
                              ) : video.transcode_status === 'failed' ? (
                                <div style={{ padding: '24px 16px', background: 'rgba(255,80,80,0.1)', borderRadius: 4, textAlign: 'center', color: 'var(--color-danger)' }}>
                                  Video processing failed — original available
                                </div>
                              ) : null}
                              {video.transcode_status !== 'pending' && (
                                <video key={quality} src={videoSrc} controls preload="metadata" style={{ width: '100%', borderRadius: 4 }} />
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
                                <div className="dcim-photo-label" style={{ margin: 0 }}>WP{video.waypoint_index} — {video.filename}</div>
                                {video.s3_original_version_id && video.transcode_status === 'done' && (
                                  <select
                                    value={quality}
                                    onChange={(e) => setDcimVideoQuality(prev => ({ ...prev, [video.id]: e.target.value }))}
                                    style={{ fontSize: 10, background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 3, padding: '1px 4px', cursor: 'pointer' }}
                                  >
                                    <option value="720p">720p ({video.file_size ? `${(video.file_size / (1024*1024)).toFixed(0)}MB` : 'compressed'})</option>
                                    <option value="original">Original ({video.s3_original_size ? `${(video.s3_original_size / (1024*1024)).toFixed(0)}MB` : 'full res'})</option>
                                  </select>
                                )}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('ln-attachments')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('ln-attachments') ? 'chev-right' : 'chev-down'} /></span> Attachments {selectedLine.attachments?.length ? `(${selectedLine.attachments.length})` : ''}
                    </div>
                    {!collapsedSections.has('ln-attachments') && (
                      <div className="attachments-section">
                        {selectedLine.attachments && selectedLine.attachments.length > 0 ? (
                          <div className="attachments-gallery">
                            {selectedLine.attachments.map((att) => (
                              <div key={att.id} className="attachment-item">
                                {att.type === 'image' ? (
                                  <img src={`${API_URL}${att.url}`} alt={att.filename} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} />
                                ) : att.type === 'video' ? (
                                  <video src={`${API_URL}${att.url}`} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} muted />
                                ) : (
                                  <div className="attachment-file" onClick={() => window.open(`${API_URL}${att.url}`, '_blank')}>{att.filename}</div>
                                )}
                                <div className="attachment-meta">{att.uploaded_by && <span>{att.uploaded_by}</span>}{att.uploaded_at && <span>{new Date(att.uploaded_at).toLocaleString()}</span>}</div>
                                <button className="attachment-delete-btn" onClick={(e) => { e.stopPropagation(); deleteLineAttachment(selectedLine.id, att.id); }} aria-label="Remove"><Icon name="clear" /></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="comments-display"><span className="no-comments">No attachments</span></div>
                        )}
                        <input type="file" ref={lineFileInputRef} style={{ display: 'none' }} accept="image/*,video/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { uploadLineAttachment(selectedLine.id, file); e.target.value = ''; } }} />
                        <button className="edit-comments-btn" onClick={() => lineFileInputRef.current?.click()} disabled={uploadingAttachment[`line-${selectedLine.id}`]}>
                          {uploadingAttachment[`line-${selectedLine.id}`] ? 'Uploading...' : 'Add Attachment'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('ln-mission')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('ln-mission') ? 'chev-right' : 'chev-down'} /></span> Mission {(() => { const m = missions.find(m => m.line_id === selectedLine.id); return m ? `— ${m.name}` : ''; })()}
                    </div>
                    {!collapsedSections.has('ln-mission') && (
                    <div className="mission-section">
                    {(() => {
                      const linkedMission = missions.find(m => m.line_id === selectedLine.id);
                      if (linkedMission) {
                        return (
                          <div className="linked-mission-controls">
                            <div className="mission-status-header" style={{ marginBottom: '6px' }}>
                              <span className="mission-name-label">{linkedMission.name}</span>
                              <span className={`mission-status-badge status-${linkedMission.status}`}>{linkedMission.status.toUpperCase()}</span>
                            </div>
                            <div className="mission-stats" style={{ marginBottom: '6px' }}>
                              <span>WP: {linkedMission.current_waypoint_index}/{linkedMission.waypoints.length}</span>
                              <span>Alt: {linkedMission.altitude}m</span>
                              <span>Speed: {linkedMission.speed} m/s</span>
                            </div>
                            {(linkedMission.status === 'executing' || linkedMission.status === 'paused' || linkedMission.status === 'created') && (
                              <div className="mission-controls" style={{ marginBottom: '6px' }}>
                                {linkedMission.status === 'created' && (
                                  <button className="mission-btn mission-btn-execute" onClick={() => sendMissionCommand(linkedMission.id, 'execute')}>Simulate</button>
                                )}
                                {linkedMission.status === 'executing' && (
                                  <button className="mission-btn mission-btn-pause" onClick={() => sendMissionCommand(linkedMission.id, 'pause')}>Pause</button>
                                )}
                                {linkedMission.status === 'paused' && (
                                  <button className="mission-btn mission-btn-execute" onClick={() => sendMissionCommand(linkedMission.id, 'resume')}>Resume</button>
                                )}
                                <button className="mission-btn mission-btn-cancel" onClick={() => sendMissionCommand(linkedMission.id, 'cancel')}>Cancel</button>
                              </div>
                            )}
                            {linkedMission.status === 'created' && linkedMission.flight_id && (
                              <div className="mission-controls" style={{ marginBottom: '6px' }}>
                                <button className="mission-btn mission-btn-execute" style={{ background: 'var(--color-info)' }} onClick={() => openSendToDrone(linkedMission)}>
                                  Send to Drone
                                </button>
                              </div>
                            )}
                            <div className="mission-controls">
                              <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'kmz'} onClick={() => downloadMissionKmz(linkedMission.id, linkedMission.name)}>{downloadingMission === 'kmz' ? 'Loading...' : 'KMZ'}</button>
                              <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'report'} onClick={() => downloadMissionReport(linkedMission.id, linkedMission.name)}>{downloadingMission === 'report' ? 'Loading...' : 'Report'}</button>
                              <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'pdf'} onClick={() => downloadMissionReportPdf(linkedMission.id, linkedMission.name)}>{downloadingMission === 'pdf' ? 'Loading...' : 'PDF'}</button>
                              <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'apm'} onClick={() => downloadMissionArdupilot(linkedMission.id, linkedMission.name)}>{downloadingMission === 'apm' ? 'Loading...' : 'APM'}</button>
                            </div>
                            {user && user.groups.length > 0 && linkedMission.created_by === user.id && (
                              <div style={{ marginTop: '6px' }}>
                                <button
                                  className="mission-btn"
                                  style={{ background: '#2a5a3a', fontSize: '10px' }}
                                  onClick={() => { setSharingItemType(sharingItemType === 'mission' && sharingItemId === linkedMission.id ? null : 'mission'); setSharingItemId(sharingItemType === 'mission' && sharingItemId === linkedMission.id ? null : linkedMission.id); if (sharingItemType !== 'mission' || sharingItemId !== linkedMission.id) fetchShares('mission', linkedMission.id); }}
                                >
                                  {sharingItemType === 'mission' && sharingItemId === linkedMission.id ? 'Cancel' : '↗ Share Mission'}
                                </button>
                                {sharingItemType === 'mission' && sharingItemId === linkedMission.id && (
                                  <div className="share-dropdown" style={{ marginTop: '4px' }}>
                                    {user.groups.filter(g => g.id !== linkedMission.group_id).map(g => (
                                      <button key={g.id} className="share-dropdown-item" onClick={() => shareItem('mission', linkedMission.id, g.id)}>
                                        {g.name}
                                      </button>
                                    ))}
                                    {user.groups.filter(g => g.id !== linkedMission.group_id).length === 0 && (
                                      <div className="share-dropdown-empty">No other groups available</div>
                                    )}
                                    {activeShares.length > 0 && (
                                      <>
                                        <div className="share-dropdown-header" style={{ marginTop: '4px' }}>Active shares</div>
                                        {activeShares.map(s => (
                                          <div key={s.id} className="share-active-item">
                                            <span>{s.group_name}</span>
                                            <button className="share-remove-btn" onClick={() => unshareItem('mission', linkedMission.id, s.id)} title="Remove share" aria-label="Remove"><Icon name="clear" /></button>
                                          </div>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return !showMissionForm ? (
                        <button className="edit-comments-btn" onClick={() => { setShowMissionForm(true); setMissionName(selectedLine.name + ' Mission'); }}>
                          Create Mission from Line
                        </button>
                      ) : (
                        <div className="mission-form">
                          <input
                            type="text"
                            value={missionName}
                            onChange={(e) => setMissionName(e.target.value)}
                            placeholder="Mission name"
                            className="mission-input"
                          />
                          <div className="mission-param">
                            <label>Drone Model</label>
                            <select value={missionDroneModel} onChange={(e) => setMissionDroneModel(e.target.value)} className="mission-input" style={{marginTop: 2}}>
                              <option value="100-1">Matrice 4TD</option>
                              <option value="100">Matrice 4D</option>
                              <option value="99-1">Matrice 4T</option>
                              <option value="99">Matrice 4E</option>
                              <option value="67-1">Matrice 30T</option>
                              <option value="67">Matrice 30</option>
                              <option value="77-1">Mavic 3T</option>
                              <option value="77">Mavic 3E</option>
                              <option value="89">Matrice 350 RTK</option>
                              <option value="60">Matrice 300 RTK</option>
                            </select>
                          </div>
                          <div className="mission-param">
                            <label>Altitude: {missionAltitude}m</label>
                            <input type="range" min={10} max={500} value={missionAltitude} onChange={(e) => setMissionAltitude(Number(e.target.value))} />
                          </div>
                          <div className="mission-param">
                            <label>Speed: {missionSpeed} m/s</label>
                            <input type="range" min={1} max={15} value={missionSpeed} onChange={(e) => setMissionSpeed(Number(e.target.value))} />
                          </div>
                          <div className="mission-form-actions">
                            <button className="edit-comments-btn" onClick={() => createMission(selectedLine.id)} disabled={creatingMission || !missionName.trim()}>
                              {creatingMission ? 'Creating...' : 'Create'}
                            </button>
                            <button className="cancel-btn" onClick={() => setShowMissionForm(false)}>Cancel</button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                    )}
                  </div>

                  <button className="delete-btn" onClick={() => deleteLine(selectedLine.id)}>Delete Line</button>
                </div>
              </div>
              </div>
            )}

            {selectedSensor && (
              <div className="detail-overlay" onClick={() => setSelectedSensor(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3><Icon name={resolveIcon(selectedSensor.icon)} /> {selectedSensor.name}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedSensor(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="detail-meta">
                    <div className="detail-row">
                      <label>Type</label>
                      <span>{SENSOR_TYPES.find(t => t.value === selectedSensor.sensor_type)?.label || selectedSensor.sensor_type}</span>
                    </div>
                    <div className="detail-row">
                      <label>Status</label>
                      <span className={`sensor-status-badge ${selectedSensor.status}`}>{selectedSensor.status}</span>
                    </div>
                    <div className="detail-row" title="Use this number in a zone's covering sensor IDs">
                      <label>ID</label>
                      <span className="sensor-id-value">
                        {selectedSensor.id}
                        <button
                          type="button"
                          className="sensor-inline-btn"
                          onClick={() => {
                            navigator.clipboard?.writeText(String(selectedSensor.id))
                              .then(() => toast(`Sensor ID ${selectedSensor.id} copied`, 'success'))
                              .catch(() => toast('Could not copy — select the number instead', 'warning'));
                          }}
                        >
                          Copy
                        </button>
                      </span>
                    </div>
                    {sensorPosDraft ? (
                      <form
                        className="sensor-pos-edit"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const lat = parseFloat(sensorPosDraft.lat.replace(',', '.'));
                          const lon = parseFloat(sensorPosDraft.lon.replace(',', '.'));
                          if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lon) || lon < -180 || lon > 180) {
                            toast('Latitude must be -90..90 and longitude -180..180', 'warning');
                            return;
                          }
                          if (await updateSensorLocation(selectedSensor.id, lat, lon)) setSensorPosDraft(null);
                        }}
                      >
                        <label>
                          Lat
                          <input
                            inputMode="decimal"
                            value={sensorPosDraft.lat}
                            onChange={(e) => {
                              // A 'lat, lon' pair pasted from Google Maps fills both fields.
                              const pair = e.target.value.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
                              setSensorPosDraft(pair ? { lat: pair[1], lon: pair[2] } : { ...sensorPosDraft, lat: e.target.value });
                            }}
                          />
                        </label>
                        <label>
                          Lon
                          <input
                            inputMode="decimal"
                            value={sensorPosDraft.lon}
                            onChange={(e) => setSensorPosDraft({ ...sensorPosDraft, lon: e.target.value })}
                          />
                        </label>
                        <div className="sensor-pos-actions">
                          <button type="submit" className="save-btn">Save</button>
                          <button type="button" className="sensor-inline-btn" onClick={() => setSensorPosDraft(null)}>Cancel</button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="detail-row"><label>Lat</label><span>{selectedSensor.latitude.toFixed(6)}</span></div>
                        <div className="detail-row"><label>Lon</label><span>{selectedSensor.longitude.toFixed(6)}</span></div>
                        <div className="detail-row">
                          <label>Position</label>
                          <span className="sensor-id-value">
                            <button
                              type="button"
                              className="sensor-inline-btn"
                              onClick={() => setSensorPosDraft({ lat: selectedSensor.latitude.toFixed(6), lon: selectedSensor.longitude.toFixed(6) })}
                            >
                              <Icon name="edit" /> Edit
                            </button>
                            <button
                              type="button"
                              className="sensor-inline-btn"
                              disabled={locatingSensor || !navigator.geolocation}
                              title="Stand next to the sensor and use this device's GPS"
                              onClick={() => {
                                const target = selectedSensor;
                                setLocatingSensor(true);
                                navigator.geolocation.getCurrentPosition(
                                  (pos) => {
                                    setLocatingSensor(false);
                                    const { latitude, longitude, accuracy } = pos.coords;
                                    const ok = window.confirm(
                                      `Move ${target.name} to ${latitude.toFixed(6)}, ${longitude.toFixed(6)}?\n\n` +
                                      `This device reports ±${Math.round(accuracy)} m. Indoors that is often much worse — ` +
                                      'dragging the marker on the satellite map is more precise.'
                                    );
                                    if (ok) updateSensorLocation(target.id, latitude, longitude);
                                  },
                                  (err) => {
                                    setLocatingSensor(false);
                                    toast(`Could not get this device's location: ${err.message}`, 'error');
                                  },
                                  { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
                                );
                              }}
                            >
                              {locatingSensor ? 'Locating…' : 'Use my location'}
                            </button>
                          </span>
                        </div>
                      </>
                    )}
                    <div className="detail-row mgrs-row"><label>MGRS</label><span className="mgrs-value">{toMGRS(selectedSensor.latitude, selectedSensor.longitude)}</span></div>
                    {selectedSensor.description && <div className="detail-row"><label>Desc</label><span>{selectedSensor.description}</span></div>}
                    {selectedSensor.created_by_email && <div className="detail-row"><label>Added by</label><span>{selectedSensor.created_by_email}</span></div>}
                    {selectedSensor.created_at && <div className="detail-row"><label>Created</label><span>{new Date(selectedSensor.created_at).toLocaleString()}</span></div>}
                    <div className="detail-row open-in-row">
                      <label>Open in</label>
                      <span className="open-in-links">
                        <a href={`https://www.openstreetmap.org/?mlat=${selectedSensor.latitude}&mlon=${selectedSensor.longitude}#map=17/${selectedSensor.latitude}/${selectedSensor.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">OSM</a>
                        <a href={`https://earth.google.com/web/search/${selectedSensor.latitude},${selectedSensor.longitude}/@${selectedSensor.latitude},${selectedSensor.longitude},0a,500d,35y,0h,0t,0r`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Earth</a>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${selectedSensor.latitude},${selectedSensor.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Maps</a>
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Task Set</label>
                      <select
                        className="task-set-assign-select"
                        value={selectedSensor.task_set_id ?? ''}
                        onChange={(e) => updateItemTaskSet('sensors', selectedSensor.id, e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">None</option>
                        {taskSets.map(ts => (
                          <option key={ts.id} value={ts.id}>{ts.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {selectedSensor.sensor_type === 'mmwave' && (
                    <MmwaveSection
                      sensorId={selectedSensor.id}
                      status={selectedSensor.status}
                      metadata={selectedSensor.metadata}
                      getAuthHeaders={getAuthHeaders}
                    />
                  )}

                  {selectedSensor.sensor_type === 'camera' && streamSources.some(s => s.id === `cam-rtmp-${selectedSensor.id}`) && (
                    <button
                      className="save-btn"
                      style={{ width: '100%', marginTop: 8, padding: '8px 0', fontSize: 13, fontWeight: 600 }}
                      disabled={isSwitching}
                      onClick={() => {
                        const source = streamSources.find(s => s.id === `cam-rtmp-${selectedSensor.id}`);
                        if (!source) return;
                        activateSource(source);
                        setShowVideo(true);
                        localStorage.setItem('toc_showVideo', 'true');
                        setSelectedSensor(null);
                      }}
                    >
                      ▶ View Feed
                    </button>
                  )}

                  {selectedSensor.sensor_type === 'camera' && (
                    <div style={{ marginTop: 8, marginBottom: 12 }}>
                      <div className="detail-row" style={{ alignItems: 'center' }}>
                        <label>RTMP URL</label>
                        {editingSensorRtmpUrl !== null ? (
                          <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                            <input
                              type="text"
                              value={editingSensorRtmpUrl}
                              onChange={(e) => setEditingSensorRtmpUrl(e.target.value)}
                              placeholder="rtmp://host:1935/live/key or rtsp://camera/stream1"
                              style={{ flex: 1, fontSize: '0.85em', padding: '4px 6px' }}
                            />
                            <button
                              className="save-btn"
                              style={{ padding: '4px 8px', fontSize: 12 }}
                              onClick={async () => {
                                const url = editingSensorRtmpUrl.trim();
                                if (url && !/^rtmps?:\/\//i.test(url)) {
                                  toast(PULL_URL_HINT, 'warning');
                                  return;
                                }
                                try {
                                  const resp = await fetch(`${API_URL}/api/sensors/${selectedSensor.id}/rtmp-url?scope=${dataScope}`, {
                                    method: 'PUT',
                                    headers: getAuthHeaders(true),
                                    body: JSON.stringify({ rtmp_url: url || null })
                                  });
                                  if (resp.ok) {
                                    const updated = await resp.json();
                                    setSensors(prev => prev.map(s => s.id === selectedSensor.id ? { ...s, metadata: updated.metadata } : s));
                                    setSelectedSensor(prev => prev ? { ...prev, metadata: updated.metadata } : prev);
                                    setEditingSensorRtmpUrl(null);
                                    if (!url) {
                                      const camId = `cam-rtmp-${selectedSensor.id}`;
                                      setStreamSources(prev => prev.filter(s => s.id !== camId));
                                      setActiveSourceId(prev => prev === camId ? NO_SOURCE_ID : prev);
                                    }
                                    toast(url ? 'RTMP URL saved' : 'RTMP URL removed', 'success');
                                  } else {
                                    const err = await resp.json().catch(() => ({ detail: 'Failed to save' }));
                                    toast(err.detail || 'Failed to save RTMP URL', 'error');
                                  }
                                } catch { toast('Failed to save RTMP URL', 'error'); }
                              }}
                            >
                              Save
                            </button>
                            <button
                              style={{ padding: '4px 8px', fontSize: 12, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'inherit', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => setEditingSensorRtmpUrl(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                            {(selectedSensor.metadata?.rtmp_url as string) ? (
                              <span style={{ fontSize: '0.85em', opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                                {selectedSensor.metadata?.rtmp_url as string}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.85em', opacity: 0.4 }}>Not set</span>
                            )}
                            <button
                              style={{ padding: '2px 8px', fontSize: 11, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'inherit', borderRadius: 4, cursor: 'pointer', marginLeft: 'auto' }}
                              onClick={() => setEditingSensorRtmpUrl((selectedSensor.metadata?.rtmp_url as string) || '')}
                            >
                              Edit
                            </button>
                          </span>
                        )}
                      </div>
                      {(selectedSensor.metadata?.rtmp_url as string) && (
                        <button
                          className="save-btn"
                          style={{ width: '100%', marginTop: 8, padding: '8px 12px', fontSize: 13 }}
                          onClick={async () => {
                            const ok = await watchRtmpStream(
                              `cam-rtmp-${selectedSensor.id}`,
                              selectedSensor.name,
                              selectedSensor.metadata?.rtmp_url as string,
                            );
                            if (ok) setSelectedSensor(null);
                          }}
                        >
                          Watch Stream
                        </button>
                      )}
                    </div>
                  )}

                  {selectedSensor.sensor_type === 'phone-camera' && (
                    <>
                    <div className="phone-sensor-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12, marginTop: 8 }}>
                      <button
                        className="save-btn"
                        style={{ padding: '8px 12px', fontSize: 13, opacity: selectedSensor.status === 'active' ? 1 : 0.4 }}
                        disabled={selectedSensor.status !== 'active'}
                        onClick={async () => {
                          try {
                            const data = await getViewerToken(selectedSensor.id, getAuthHeaders);
                            const phoneId = `phone-${selectedSensor.id}`;
                            setPhoneSensorCredentialsMap(prev => ({ ...prev, [phoneId]: { livekitUrl: data.livekit_url, token: data.livekit_token, sensorId: selectedSensor.id } }));
                            setStreamSources(prev => {
                              if (prev.some(s => s.id === phoneId)) return prev;
                              return [...prev, { id: phoneId, name: selectedSensor.name, url: '', type: 'phone' as const }];
                            });
                            setDjiStreamCredentials(null);
                            setActiveSourceId(phoneId);
                            setSelectedSensor(null);
                          } catch { /* ignore */ }
                        }}
                      >
                        View Stream
                      </button>
                      <button
                        className="save-btn"
                        style={{ padding: '8px 12px', fontSize: 13, opacity: selectedSensor.status === 'active' ? 1 : 0.4 }}
                        disabled={selectedSensor.status !== 'active'}
                        onClick={async () => {
                          try {
                            await refreshPhoneSensorGps(selectedSensor.id, getAuthHeaders);
                            toast('GPS refresh requested — phone will update on next cycle', 'success');
                          } catch { toast('Failed to request GPS refresh', 'error'); }
                        }}
                      >
                        Refresh GPS
                      </button>
                      <button
                        className="save-btn"
                        style={{ padding: '8px 12px', fontSize: 13 }}
                        onClick={() => {
                          setReconnectSensorId(selectedSensor.id);
                          setShowPhoneSensorModal(true);
                          setSelectedSensor(null);
                        }}
                      >
                        Reconnect
                      </button>
                      <button
                        className="delete-btn"
                        style={{ padding: '8px 12px', fontSize: 13, margin: 0 }}
                        onClick={async () => {
                          try {
                            await deletePhoneSensor(selectedSensor.id, getAuthHeaders);
                            setSensors(prev => prev.filter(s => s.id !== selectedSensor.id));
                            const phoneId = `phone-${selectedSensor.id}`;
                            setStreamSources(prev => prev.filter(s => s.id !== phoneId));
                            setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[phoneId]; return next; });
                            setSelectedSensor(null);
                            fetchSensors();
                          } catch { /* ignore */ }
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {/* Track section */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                          Track {sensorTrack.length > 0 && <span style={{ color: 'var(--color-warning)' }}>({sensorTrack.length} pts)</span>}
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {sensorTrack.length >= 2 && (
                            <>
                              <a
                                href={getTrackKmlUrl(selectedSensor.id)}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const headers = getAuthHeaders();
                                  fetch(getTrackKmlUrl(selectedSensor.id), { headers })
                                    .then(r => r.blob())
                                    .then(blob => {
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `phonecam-track-${selectedSensor.id}.kml`;
                                      a.click();
                                      URL.revokeObjectURL(url);
                                    })
                                    .catch(() => toast('Failed to download KML', 'error'));
                                }}
                                style={{ background: 'none', border: 'none', color: 'var(--color-warning)', fontSize: 11, cursor: 'pointer', textDecoration: 'none' }}
                              >
                                KML
                              </a>
                              <button
                                style={{ background: 'none', border: 'none', color: 'var(--color-danger)', fontSize: 11, cursor: 'pointer' }}
                                onClick={async () => {
                                  if (!window.confirm(`Clear ${sensorTrack.length} track points?`)) return;
                                  try {
                                    await clearTrack(selectedSensor.id, getAuthHeaders);
                                    setSensorTrack([]);
                                    toast('Track cleared', 'success');
                                  } catch { toast('Failed to clear track', 'error'); }
                                }}
                              >
                                Clear
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {sensorTrack.length === 0 && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center', padding: '4px 0' }}>
                          No track — GPS updates will appear here
                        </div>
                      )}
                    </div>

                    {/* Recordings section */}
                    {(() => {
                      const sId = selectedSensor.id;
                      return (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 600 }}>Recordings</span>
                            <button
                              style={{ background: 'none', border: 'none', color: 'var(--color-info)', fontSize: 11, cursor: 'pointer' }}
                              onClick={() => listRecordings(sId, getAuthHeaders).then(setSensorRecordings)}
                            >
                              Refresh
                            </button>
                          </div>
                          {sensorRecordings.length === 0 ? (
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'center', padding: '8px 0' }}>
                              No recordings
                            </div>
                          ) : (
                            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                              {sensorRecordings.map(rec => (
                                <div key={rec.id} style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '4px 6px', background: 'var(--color-bg-surface)', borderRadius: 4, marginBottom: 4, fontSize: 11,
                                }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: 'var(--color-text-primary)' }}>{new Date(rec.created_at).toLocaleString()}</div>
                                    <div style={{ color: 'var(--color-text-muted)' }}>
                                      {rec.duration_seconds ? `${Math.floor(rec.duration_seconds / 60)}:${String(Math.floor(rec.duration_seconds % 60)).padStart(2, '0')}` : '--:--'}
                                      {' · '}
                                      {rec.file_size > 1048576 ? `${(rec.file_size / 1048576).toFixed(1)} MB` : `${(rec.file_size / 1024).toFixed(0)} KB`}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button
                                      style={{ background: '#0a3a4a', border: '1px solid var(--color-info)', borderRadius: 4, color: 'var(--color-info)', padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}
                                      onClick={() => setPlayingRecording(rec)}
                                    >
                                      Play
                                    </button>
                                    <button
                                      style={{ background: '#3a1a1a', border: '1px solid var(--color-danger)', borderRadius: 4, color: 'var(--color-danger)', padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}
                                      onClick={async () => {
                                        if (!window.confirm('Delete this recording?')) return;
                                        try {
                                          await deleteRecording(rec.id, getAuthHeaders);
                                          setSensorRecordings(prev => prev.filter(r => r.id !== rec.id));
                                        } catch { toast('Failed to delete', 'error'); }
                                      }}
                                    >
                                      Del
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    </>
                  )}

                  <button className="delete-btn" onClick={() => deleteSensor(selectedSensor.id)}>Delete Sensor</button>
                </div>
              </div>
              </div>
            )}

            {selectedUnit && (
              <div className="detail-overlay" onClick={() => setSelectedUnit(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3>{unitIcon(selectedUnit)} {selectedUnit.name}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedUnit(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="detail-meta">
                    {selectedUnit.callsign && (
                      <div className="detail-row"><label>Callsign</label><span>{selectedUnit.callsign}</span></div>
                    )}
                    <div className="detail-row">
                      <label>Type</label>
                      <span>{UNIT_TYPES.find(t => t.value === selectedUnit.unit_type)?.label || selectedUnit.unit_type}</span>
                    </div>
                    <div className="detail-row">
                      <label>Status</label>
                      <select
                        className="sensor-type-select"
                        value={selectedUnit.status}
                        onChange={(e) => {
                          changeUnitStatus(getAuthHeaders, selectedUnit.id, e.target.value as UnitStatus).then(u => { setSelectedUnit(u); fetchUnits(); });
                        }}
                      >
                        {UNIT_STATUSES.map(st => (
                          <option key={st.value} value={st.value}>{st.label}</option>
                        ))}
                      </select>
                    </div>
                    {selectedUnit.latitude != null && selectedUnit.longitude != null && (<>
                      <div className="detail-row"><label>Lat</label><span>{selectedUnit.latitude.toFixed(6)}</span></div>
                      <div className="detail-row"><label>Lon</label><span>{selectedUnit.longitude.toFixed(6)}</span></div>
                      <div className="detail-row mgrs-row"><label>MGRS</label><span className="mgrs-value">{toMGRS(selectedUnit.latitude, selectedUnit.longitude)}</span></div>
                    </>)}
                    {selectedUnit.speed != null && (
                      <div className="detail-row"><label>Speed</label><span>{selectedUnit.speed.toFixed(1)} m/s</span></div>
                    )}
                    {selectedUnit.battery != null && (
                      <div className="detail-row"><label>Battery</label><span>{selectedUnit.battery}%</span></div>
                    )}
                    {selectedUnit.description && <div className="detail-row"><label>Desc</label><span>{selectedUnit.description}</span></div>}
                    {selectedUnit.created_at && <div className="detail-row"><label>Created</label><span>{new Date(selectedUnit.created_at).toLocaleString()}</span></div>}
                    {selectedUnit.latitude != null && selectedUnit.longitude != null && (
                      <div className="detail-row open-in-row">
                        <label>Open in</label>
                        <span className="open-in-links">
                          <a href={`https://www.openstreetmap.org/?mlat=${selectedUnit.latitude}&mlon=${selectedUnit.longitude}#map=17/${selectedUnit.latitude}/${selectedUnit.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">OSM</a>
                          <a href={`https://earth.google.com/web/search/${selectedUnit.latitude},${selectedUnit.longitude}/@${selectedUnit.latitude},${selectedUnit.longitude},0a,500d,35y,0h,0t,0r`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Earth</a>
                          <a href={`https://www.google.com/maps/search/?api=1&query=${selectedUnit.latitude},${selectedUnit.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Maps</a>
                        </span>
                      </div>
                    )}
                  </div>
                  {(() => {
                    const stream = unitStream(selectedUnit);
                    if (!stream) return null;
                    return (
                      <button
                        className="save-btn"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 13, marginBottom: 8 }}
                        onClick={async () => {
                          const ok = await watchStream(`unit-feed-${selectedUnit.id}`, selectedUnit.name, stream);
                          if (ok) setSelectedUnit(null);
                        }}
                      >
                        Watch Feed
                      </button>
                    );
                  })()}
                  <button className="delete-btn" onClick={() => {
                    if (!window.confirm('Delete this field unit?')) return;
                    deleteUnit(getAuthHeaders, selectedUnit.id).then(() => { setSelectedUnit(null); fetchUnits(); });
                  }}>Delete Unit</button>
                </div>
              </div>
              </div>
            )}

            {selectedAircraft && (
              <div className="detail-overlay" onClick={() => setSelectedAircraft(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3><Icon name="aircraft" /> {selectedAircraft.flight || selectedAircraft.hex}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedAircraft(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="detail-meta">
                    <div className="detail-row"><label>ICAO</label><span>{selectedAircraft.hex.toUpperCase()}</span></div>
                    {selectedAircraft.registration && <div className="detail-row"><label>Reg</label><span>{selectedAircraft.registration}</span></div>}
                    {selectedAircraft.type && <div className="detail-row"><label>Type</label><span>{selectedAircraft.type}</span></div>}
                    <div className="detail-row"><label>Altitude</label><span>{selectedAircraft.alt_baro ?? '---'} ft</span></div>
                    <div className="detail-row"><label>Speed</label><span>{selectedAircraft.gs != null ? `${selectedAircraft.gs} kts` : '---'}</span></div>
                    <div className="detail-row"><label>Heading</label><span>{selectedAircraft.track != null ? `${selectedAircraft.track.toFixed(0)}°` : '---'}</span></div>
                    {selectedAircraft.squawk && <div className="detail-row"><label>Squawk</label><span>{selectedAircraft.squawk}</span></div>}
                  </div>
                </div>
              </div>
              </div>
            )}

            {selectedVessel && (
              <div className="detail-overlay" onClick={() => setSelectedVessel(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3><Icon name="ship" /> {selectedVessel.name || `MMSI ${selectedVessel.mmsi}`}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedVessel(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="detail-meta">
                    <div className="detail-row"><label>MMSI</label><span>{selectedVessel.mmsi}</span></div>
                    <div className="detail-row"><label>Type</label><span>{selectedVessel.ship_type_name}</span></div>
                    <div className="detail-row"><label>Speed</label><span>{selectedVessel.speed} kts</span></div>
                    <div className="detail-row"><label>Heading</label><span>{selectedVessel.heading > 0 && selectedVessel.heading < 360 ? `${selectedVessel.heading.toFixed(0)}°` : '---'}</span></div>
                    <div className="detail-row"><label>Course</label><span>{selectedVessel.course > 0 ? `${selectedVessel.course.toFixed(0)}°` : '---'}</span></div>
                    {selectedVessel.destination && <div className="detail-row"><label>Destination</label><span>{selectedVessel.destination}</span></div>}
                    {selectedVessel.length > 0 && <div className="detail-row"><label>Size</label><span>{selectedVessel.length}m × {selectedVessel.width}m</span></div>}
                    <div className="detail-row"><label>Last Update</label><span>{new Date(selectedVessel.last_seen).toLocaleString()}</span></div>
                  </div>
                </div>
              </div>
              </div>
            )}

            {selectedLiveCam && (
              <div className="detail-overlay" onClick={() => setSelectedLiveCam(null)}>
              <div className="detail-panel webcam-detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3><Icon name="webcam" /> {selectedLiveCam.name}</h3>
                  <button className="detail-close-btn" onClick={() => setSelectedLiveCam(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="webcam-player">
                    {selectedLiveCam.provider === 'youtube' ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${selectedLiveCam.video_id}?autoplay=1&mute=1`}
                        width="100%"
                        height="270"
                        frameBorder="0"
                        allow="autoplay; encrypted-media; fullscreen"
                        allowFullScreen
                        title={selectedLiveCam.name}
                        style={{ borderRadius: '4px', background: '#000' }}
                      />
                    ) : (
                      <>
                        <video
                          ref={liveCamVideoRef}
                          muted
                          autoPlay
                          playsInline
                          controls
                          style={{ width: '100%', borderRadius: '4px', background: '#000' }}
                        />
                        {!liveCamStreamUrl && (
                          <div className="webcam-loading">Loading stream...</div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="detail-meta">
                    <div className="detail-row"><label>Source</label><span>{selectedLiveCam.source}</span></div>
                    <div className="detail-row"><label>Lat</label><span>{selectedLiveCam.lat.toFixed(6)}</span></div>
                    <div className="detail-row"><label>Lng</label><span>{selectedLiveCam.lon.toFixed(6)}</span></div>
                    <div className="detail-row open-in-row">
                      <label>Open in</label>
                      <span className="open-in-links">
                        {selectedLiveCam.provider === 'youtube' && (
                          <a href={`https://www.youtube.com/watch?v=${selectedLiveCam.video_id}`} target="_blank" rel="noopener noreferrer" className="open-in-link">YouTube</a>
                        )}
                        <a href={`https://www.google.com/maps/search/?api=1&query=${selectedLiveCam.lat},${selectedLiveCam.lon}`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Maps</a>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            )}

            {selectedIncident && (
              <div className="detail-overlay" onClick={() => setSelectedIncident(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: INCIDENT_COLORS[selectedIncident.type] }}>{INCIDENT_ICONS[selectedIncident.type]}</span>
                    {selectedIncident.title}
                  </h3>
                  <button className="detail-close-btn" onClick={() => setSelectedIncident(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  <div className="detail-meta">
                    <div className="detail-row">
                      <label>Type</label>
                      <span style={{ color: INCIDENT_COLORS[selectedIncident.type] }}>{selectedIncident.type}</span>
                    </div>
                    <div className="detail-row">
                      <label>Severity</label>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                        backgroundColor: selectedIncident.severity >= 4 ? 'var(--color-danger-dim)' : selectedIncident.severity >= 3 ? 'var(--color-warning-dim)' : 'var(--color-info-dim)',
                        color: selectedIncident.severity >= 4 ? 'var(--color-danger)' : selectedIncident.severity >= 3 ? 'var(--color-warning)' : 'var(--color-info)'
                      }}>
                        {selectedIncident.severity}/5 — {SEVERITY_LABELS[selectedIncident.severity]}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Status</label>
                      <span>{selectedIncident.status}</span>
                    </div>
                    <div className="detail-row"><label>Lat</label><span>{selectedIncident.latitude.toFixed(6)}</span></div>
                    <div className="detail-row"><label>Lng</label><span>{selectedIncident.longitude.toFixed(6)}</span></div>
                    {selectedIncident.location_label && (
                      <div className="detail-row"><label>Location</label><span>{selectedIncident.location_label}</span></div>
                    )}
                    {selectedIncident.description && (
                      <div className="detail-row"><label>Desc</label><span>{selectedIncident.description}</span></div>
                    )}
                    <div className="detail-row"><label>Created</label><span>{new Date(selectedIncident.created_at).toLocaleString()}</span></div>
                    {selectedIncident.assigned_units.length > 0 && (
                      <div className="detail-row">
                        <label>Assigned</label>
                        <span>{selectedIncident.assigned_units.join(', ')}</span>
                      </div>
                    )}
                    <div className="detail-row open-in-row">
                      <label>Open in</label>
                      <span className="open-in-links">
                        <a href={`https://www.openstreetmap.org/?mlat=${selectedIncident.latitude}&mlon=${selectedIncident.longitude}#map=17/${selectedIncident.latitude}/${selectedIncident.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">OSM</a>
                        <a href={`https://earth.google.com/web/search/${selectedIncident.latitude},${selectedIncident.longitude}/@${selectedIncident.latitude},${selectedIncident.longitude},0a,500d,35y,0h,0t,0r`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Earth</a>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${selectedIncident.latitude},${selectedIncident.longitude}`} target="_blank" rel="noopener noreferrer" className="open-in-link">Google Maps</a>
                      </span>
                    </div>
                  </div>

                  {STATUS_TRANSITIONS[selectedIncident.status]?.length > 0 && (
                    <div style={{ padding: '8px 0', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {STATUS_TRANSITIONS[selectedIncident.status].map(next => (
                        <button
                          key={next}
                          className="tool-btn"
                          style={{ fontSize: '11px' }}
                          onClick={() => updateIncidentStatus(selectedIncident.id, next)}
                        >
                          {'→'} {next}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('inc-notes')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('inc-notes') ? 'chev-right' : 'chev-down'} /></span> Notes & Activity ({incidentNotes.length})
                    </div>
                    {!collapsedSections.has('inc-notes') && (
                      <div className="notes-section">
                        <div className="notes-list">
                          {incidentNotes.length === 0 ? (
                            <div className="comments-display"><span className="no-comments">No notes yet</span></div>
                          ) : (
                            incidentNotes.map((note) => (
                              <div key={note.id} className="note-item" style={{ borderLeft: `2px solid ${
                                note.activity_type === 'mission' ? 'var(--color-info)' :
                                note.activity_type === 'trial' ? 'var(--color-warning)' :
                                note.activity_type === 'observation' ? 'var(--color-accent)' : 'var(--color-border-light)'
                              }`, paddingLeft: '6px' }}>
                                {editingIncidentNoteId === note.id ? (
                                  <>
                                    <textarea value={editIncidentNoteContent} onChange={(e) => setEditIncidentNoteContent(e.target.value)} rows={2} autoFocus />
                                    <div className="comments-actions">
                                      <button className="save-comments-btn" onClick={() => updateIncidentNote(selectedIncident.id, note.id, editIncidentNoteContent)} disabled={savingIncidentNote || !editIncidentNoteContent.trim()}>
                                        {savingIncidentNote ? 'Saving...' : 'Save'}
                                      </button>
                                      <button className="cancel-btn" onClick={() => { setEditingIncidentNoteId(null); setEditIncidentNoteContent(''); }}>Cancel</button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                                      <span style={{
                                        fontSize: '9px', textTransform: 'uppercase', fontWeight: 600, padding: '1px 4px',
                                        borderRadius: '2px',
                                        backgroundColor: note.activity_type === 'mission' ? 'var(--color-info-dim)' :
                                          note.activity_type === 'trial' ? 'var(--color-warning-dim)' :
                                          note.activity_type === 'observation' ? 'var(--color-accent-dim)' : 'var(--color-bg-active)',
                                        color: note.activity_type === 'mission' ? 'var(--color-info)' :
                                          note.activity_type === 'trial' ? 'var(--color-warning)' :
                                          note.activity_type === 'observation' ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                                      }}>
                                        {note.activity_type}
                                      </span>
                                      {note.author_email && <span style={{ fontSize: '9px', color: 'var(--color-text-muted)' }}>{note.author_email}</span>}
                                    </div>
                                    <div className="note-content">{note.content}</div>
                                    <div className="note-meta">
                                      {new Date(note.created_at).toLocaleString()}
                                      {note.updated_at !== note.created_at && ' (edited)'}
                                    </div>
                                    <div className="note-actions">
                                      <button className="note-action-btn" onClick={() => { setEditingIncidentNoteId(note.id); setEditIncidentNoteContent(note.content); }} title="Edit">Edit</button>
                                      <button className="note-action-btn note-delete-btn" onClick={() => deleteIncidentNote(selectedIncident.id, note.id)} title="Delete">Delete</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                        <div className="add-note-form">
                          <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
                            {(['note', 'mission', 'trial', 'observation'] as const).map(t => (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setIncidentNoteType(t)}
                                style={{
                                  flex: 1, padding: '3px', fontSize: '9px', textTransform: 'uppercase', fontWeight: 600,
                                  borderRadius: '3px', cursor: 'pointer', border: 'none',
                                  backgroundColor: incidentNoteType === t ? (
                                    t === 'mission' ? 'var(--color-info-dim)' : t === 'trial' ? 'var(--color-warning-dim)' : t === 'observation' ? 'var(--color-accent-dim)' : 'var(--color-bg-active)'
                                  ) : 'var(--color-bg-surface)',
                                  color: incidentNoteType === t ? (
                                    t === 'mission' ? 'var(--color-info)' : t === 'trial' ? 'var(--color-warning)' : t === 'observation' ? 'var(--color-accent)' : 'var(--color-text-secondary)'
                                  ) : 'var(--color-text-muted)'
                                }}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={newIncidentNote}
                            onChange={(e) => setNewIncidentNote(e.target.value)}
                            placeholder={incidentNoteType === 'mission' ? 'Describe the mission...' : incidentNoteType === 'trial' ? 'Describe the trial/attempt...' : incidentNoteType === 'observation' ? 'Log observation...' : 'Add a note...'}
                            rows={2}
                          />
                          <button
                            className="edit-comments-btn"
                            onClick={() => addIncidentNote(selectedIncident.id, newIncidentNote, incidentNoteType)}
                            disabled={savingIncidentNote || !newIncidentNote.trim()}
                          >
                            {savingIncidentNote ? 'Saving...' : `Add ${incidentNoteType.charAt(0).toUpperCase() + incidentNoteType.slice(1)}`}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('inc-attachments')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('inc-attachments') ? 'chev-right' : 'chev-down'} /></span> Attachments ({selectedIncident.attachments?.length || 0})
                    </div>
                    {!collapsedSections.has('inc-attachments') && (
                      <div className="attachments-section">
                        {selectedIncident.attachments && selectedIncident.attachments.length > 0 ? (
                          <div className="attachments-gallery">
                            {selectedIncident.attachments.map((att) => (
                              <div key={att.id} className="attachment-item">
                                {att.type === 'image' ? (
                                  <img src={`${API_URL}${att.url}`} alt={att.filename} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} />
                                ) : att.type === 'video' ? (
                                  <video src={`${API_URL}${att.url}`} className="attachment-thumbnail" onClick={() => setViewingAttachment(att)} muted />
                                ) : (
                                  <div className="attachment-file" onClick={() => window.open(`${API_URL}${att.url}`, '_blank')}>{att.filename}</div>
                                )}
                                <button className="attachment-delete-btn" onClick={(e) => { e.stopPropagation(); deleteIncidentAttachment(selectedIncident.id, att.id); }} title="Remove" aria-label="Remove"><Icon name="clear" /></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="comments-display"><span className="no-comments">No attachments</span></div>
                        )}
                        <input
                          type="file"
                          ref={incidentFileInputRef}
                          style={{ display: 'none' }}
                          accept="image/*,video/*,.pdf,.txt,.json,.md,.csv"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) { uploadIncidentAttachment(selectedIncident.id, file); e.target.value = ''; }
                          }}
                        />
                        <button className="edit-comments-btn" onClick={() => incidentFileInputRef.current?.click()} disabled={uploadingIncidentAttachment}>
                          {uploadingIncidentAttachment ? 'Uploading...' : 'Add Attachment'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('inc-linked')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('inc-linked') ? 'chev-right' : 'chev-down'} /></span> Linked Items ({incidentLinkedDetails.length})
                    </div>
                    {!collapsedSections.has('inc-linked') && (
                    <div>
                    {incidentLinkedDetails.length > 0 ? (
                      <div style={{ marginBottom: '4px' }}>
                        {incidentLinkedDetails.map((item, i) => (
                          <div key={`${item.type}-${item.id}`} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '3px 6px', fontSize: '11px', borderBottom: '1px solid var(--color-border)'
                          }}>
                            <span>
                              <span style={{ color: 'var(--color-text-secondary)', marginRight: '4px', fontSize: '9px', textTransform: 'uppercase' }}>{item.type}</span>
                              <span
                                style={{ color: 'var(--color-info)', cursor: 'pointer' }}
                                onClick={() => {
                                  if (item.type === 'point') {
                                    const p = points.find(pt => pt.id === item.id);
                                    if (p) { centerOnPoint(p.longitude, p.latitude); setSelectedPoint(p); setSelectedIncident(null); }
                                  } else if (item.type === 'polygon') {
                                    const pg = polygons.find(pg => pg.id === item.id);
                                    if (pg) { centerOnPolygon(pg.coordinates); setSelectedPolygon(pg); setSelectedIncident(null); }
                                  } else if (item.type === 'line') {
                                    const ln = lines.find(l => l.id === item.id);
                                    if (ln) { centerOnLine(ln.coordinates); setSelectedLine(ln); setSelectedIncident(null); }
                                  } else if (item.type === 'sensor') {
                                    const s = sensors.find(s => s.id === item.id);
                                    if (s) { centerOnPoint(s.longitude, s.latitude); setSelectedSensor(s); setSelectedIncident(null); }
                                  }
                                }}
                              >
                                {item.name}
                              </span>
                            </span>
                            <button
                              style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '12px', padding: '0 2px' }}
                              onClick={() => {
                                const updated = (selectedIncident.linked_items || []).filter(
                                  li => !(li.type === item.type && li.id === item.id)
                                );
                                updateIncidentLinkedItems(selectedIncident.id, updated);
                              }}
                              title="Unlink"
                              aria-label="Close"
                            >
                              <Icon name="clear" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="comments-display"><span className="no-comments">No linked items</span></div>
                    )}
                    {showIncidentLinkPicker ? (
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: '4px', padding: '6px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Select items to link</span>
                          <button style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '12px' }} onClick={() => setShowIncidentLinkPicker(false)} aria-label="Close"><Icon name="clear" /></button>
                        </div>
                        {points.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '2px' }}>Points</div>
                            {points.map(p => {
                              const isLinked = (selectedIncident.linked_items || []).some(li => li.type === 'point' && li.id === p.id);
                              return (
                                <div key={p.id} style={{ fontSize: '11px', padding: '2px 4px', cursor: isLinked ? 'default' : 'pointer', color: isLinked ? 'var(--color-text-muted)' : 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                  onClick={() => {
                                    if (isLinked) return;
                                    const updated = [...(selectedIncident.linked_items || []), { type: 'point' as const, id: p.id }];
                                    updateIncidentLinkedItems(selectedIncident.id, updated);
                                  }}>
                                  <span>{p.icon}</span> {p.name} {isLinked && <span style={{ color: 'var(--color-accent)', fontSize: '9px' }}>linked</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {polygons.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '2px' }}>Areas</div>
                            {polygons.map(p => {
                              const isLinked = (selectedIncident.linked_items || []).some(li => li.type === 'polygon' && li.id === p.id);
                              return (
                                <div key={p.id} style={{ fontSize: '11px', padding: '2px 4px', cursor: isLinked ? 'default' : 'pointer', color: isLinked ? 'var(--color-text-muted)' : 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                  onClick={() => {
                                    if (isLinked) return;
                                    const updated = [...(selectedIncident.linked_items || []), { type: 'polygon' as const, id: p.id }];
                                    updateIncidentLinkedItems(selectedIncident.id, updated);
                                  }}>
                                  <span style={{ color: p.color }}><Icon name="area" /></span> {p.name} {isLinked && <span style={{ color: 'var(--color-accent)', fontSize: '9px' }}>linked</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {lines.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '2px' }}>Lines</div>
                            {lines.map(l => {
                              const isLinked = (selectedIncident.linked_items || []).some(li => li.type === 'line' && li.id === l.id);
                              return (
                                <div key={l.id} style={{ fontSize: '11px', padding: '2px 4px', cursor: isLinked ? 'default' : 'pointer', color: isLinked ? 'var(--color-text-muted)' : 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                  onClick={() => {
                                    if (isLinked) return;
                                    const updated = [...(selectedIncident.linked_items || []), { type: 'line' as const, id: l.id }];
                                    updateIncidentLinkedItems(selectedIncident.id, updated);
                                  }}>
                                  <Icon name="path" /> {l.name} {isLinked && <span style={{ color: 'var(--color-accent)', fontSize: '9px' }}>linked</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {sensors.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '2px' }}>Sensors</div>
                            {sensors.map(s => {
                              const isLinked = (selectedIncident.linked_items || []).some(li => li.type === 'sensor' && li.id === s.id);
                              return (
                                <div key={s.id} style={{ fontSize: '11px', padding: '2px 4px', cursor: isLinked ? 'default' : 'pointer', color: isLinked ? 'var(--color-text-muted)' : 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                                  onClick={() => {
                                    if (isLinked) return;
                                    const updated = [...(selectedIncident.linked_items || []), { type: 'sensor' as const, id: s.id }];
                                    updateIncidentLinkedItems(selectedIncident.id, updated);
                                  }}>
                                  <Icon name={resolveIcon(s.icon)} /> {s.name} {isLinked && <span style={{ color: 'var(--color-accent)', fontSize: '9px' }}>linked</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button className="edit-comments-btn" onClick={() => setShowIncidentLinkPicker(true)} style={{ marginTop: '4px' }}>
                        Link Map Item
                      </button>
                    )}
                    </div>
                    )}
                  </div>

                  {selectedIncident.audit_log.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header" onClick={() => toggleSection('inc-audit')}>
                        <span className="section-toggle"><Icon name={collapsedSections.has('inc-audit') ? 'chev-right' : 'chev-down'} /></span> Audit Log ({selectedIncident.audit_log.length})
                      </div>
                      {!collapsedSections.has('inc-audit') && (
                        <div style={{ marginTop: '4px' }}>
                          {selectedIncident.audit_log.map((entry, i) => (
                            <div key={i} style={{
                              padding: '4px 0', borderBottom: '1px solid var(--color-border)',
                              fontSize: '10px', color: 'var(--color-text-secondary)'
                            }}>
                              <span style={{ color: 'var(--color-accent)' }}>{entry.actor}</span>
                              {' '}
                              {entry.from ? `${entry.from} → ${entry.to}` : entry.event}
                              {entry.note && <span style={{ color: 'var(--color-text-muted)' }}> — {entry.note}</span>}
                              <div style={{ color: 'var(--color-text-muted)' }}>{new Date(entry.ts).toLocaleString()}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    className="delete-btn"
                    style={{ marginTop: '8px' }}
                    onClick={() => deleteIncident(selectedIncident.id)}
                  >
                    Delete Incident
                  </button>
                </div>
              </div>
              </div>
            )}

            {kaekAnalysis && (
              <div className="detail-overlay" onClick={() => setKaekAnalysis(null)}>
              <div className="detail-panel" style={{ maxWidth: '620px', maxHeight: '80vh', overflow: 'auto', fontSize: '13px' }} onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3>KAEK ↔ Sites Analysis</h3>
                  <button className="detail-close-btn" onClick={() => setKaekAnalysis(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  {kaekAnalysis.available_task_sets.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header">Match Sites From</div>
                      <select
                        value={kaekAnalysis.points_task_set_id ?? ''}
                        onChange={async (e) => {
                          const tsId = e.target.value ? parseInt(e.target.value) : null;
                          setKaekMatchTaskSet(tsId);
                          setKaekAnalyzing(true);
                          try {
                            const tsParam = tsId ? `&points_task_set=${tsId}` : '';
                            const resp = await fetch(`${API_URL}/api/kaek/analysis?scope=${dataScope}${tsParam}`, { headers: getAuthHeaders() });
                            if (resp.ok) setKaekAnalysis(await resp.json());
                          } catch {} finally { setKaekAnalyzing(false); }
                        }}
                        style={{ width: '100%', fontSize: '13px', padding: '5px', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: '#fff', borderRadius: '3px' }}
                      >
                        <option value="">All points</option>
                        {kaekAnalysis.available_task_sets.map(ts => (
                          <option key={ts.id} value={ts.id}>{ts.name} ({ts.point_count})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="detail-section">
                    <div className="detail-section-header">Summary</div>
                    <div className="detail-row"><span className="detail-label">Total Parcels</span><span className="detail-value">{kaekAnalysis.summary.total_parcels}</span></div>
                    <div className="detail-row"><span className="detail-label">Total Sites</span><span className="detail-value">{kaekAnalysis.summary.total_points}</span></div>
                    <div className="detail-row"><span className="detail-label">Matched</span><span className="detail-value" style={{ color: '#2ecc71' }}>{kaekAnalysis.summary.matched}</span></div>
                    <div className="detail-row"><span className="detail-label">Orphan Sites</span><span className="detail-value" style={{ color: 'var(--color-warning)' }}>{kaekAnalysis.summary.orphan_points}</span></div>
                    <div className="detail-row"><span className="detail-label">Orphan Parcels</span><span className="detail-value" style={{ color: 'var(--color-warning)' }}>{kaekAnalysis.summary.orphan_parcels}</span></div>
                    <div className="detail-row"><span className="detail-label">Already Linked</span><span className="detail-value">{kaekAnalysis.summary.already_linked}</span></div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('kaek-meta-fields')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('kaek-meta-fields') ? 'chev-right' : 'chev-down'} /></span> Metadata Coverage
                    </div>
                    {!collapsedSections.has('kaek-meta-fields') && Object.entries(kaekAnalysis.metadata_summary.fields).map(([field, info]) => (
                      <div key={field} className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                        <span className="detail-label" style={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}</span>
                        <span className="detail-value">
                          {info.populated}/{kaekAnalysis.metadata_summary.total_parcels} populated
                          {info.min != null && ` · ${Math.round(info.min)}–${Math.round(info.max!)} ${field === 'area' ? 'm²' : field === 'perimeter' ? 'm' : ''}`}
                          {info.mean != null && ` (avg ${Math.round(info.mean)})`}
                          {info.distinct_values && info.distinct_values.length > 0 && (
                            <span style={{ display: 'block', color: 'var(--color-text-secondary)', marginTop: '2px' }}>{info.distinct_values.slice(0, 5).join(', ')}{info.distinct_values.length > 5 ? ` +${info.distinct_values.length - 5} more` : ''}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('kaek-matches')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('kaek-matches') ? 'chev-right' : 'chev-down'} /></span> Matches ({kaekAnalysis.matches.filter(m => m.match_type !== 'conflict').length})
                    </div>
                    {!collapsedSections.has('kaek-matches') && (
                      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {kaekAnalysis.matches.filter(m => m.match_type !== 'conflict').map((m, i) => {
                          const typeColor = m.match_type.includes('name') ? '#2ecc71' : m.match_type.includes('address') ? '#e67e22' : m.match_type === 'spatial' ? '#3498db' : '#888';
                          return (
                          <div key={i} style={{ fontSize: '12px', padding: '4px 0', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', gap: '6px', cursor: 'pointer' }}
                            onClick={() => {
                              const pt = points.find(p => p.id === m.point_id);
                              if (pt) { setSelectedPoint(pt); setKaekAnalysis(null); centerOnPoint(pt.longitude, pt.latitude); }
                            }}
                          >
                            <span style={{ color: 'var(--color-text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.point_name}</span>
                            <span style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}>{m.kaek_code}</span>
                            <span style={{ color: typeColor, flexShrink: 0, fontSize: '11px' }}>{m.match_type}</span>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {kaekAnalysis.matches.filter(m => m.match_type === 'conflict').length > 0 && (() => {
                    const conflicts = kaekAnalysis.matches.filter(m => m.match_type === 'conflict') as (typeof kaekAnalysis.matches[0] & { conflict_detail?: string })[];
                    const resolved = kaekAnalysis.matches.filter(m => m.match_type !== 'conflict');
                    return (
                    <div className="detail-section">
                      <div className="detail-section-header" onClick={() => toggleSection('kaek-conflicts')} style={{ color: 'var(--color-danger)' }}>
                        <span className="section-toggle"><Icon name={collapsedSections.has('kaek-conflicts') ? 'chev-right' : 'chev-down'} /></span> Conflicts ({conflicts.length})
                      </div>
                      {!collapsedSections.has('kaek-conflicts') && (
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', padding: '3px 0 8px', borderBottom: '1px solid var(--color-border)' }}>
                            Address matched a different parcel than spatial location. Choose which to link.
                          </div>
                          {conflicts.map((c, i) => {
                            const linked = resolved.find(m => m.point_id === c.point_id);
                            const resolveConflict = async (pointId: number, parcelId: number) => {
                              try {
                                const resp = await fetch(`${API_URL}/api/kaek/link/${pointId}`, {
                                  method: 'POST', headers: getAuthHeaders(true),
                                  body: JSON.stringify({ parcel_id: parcelId })
                                });
                                if (resp.ok) {
                                  fetchPoints();
                                  fetchKaekParcels();
                                  setKaekAnalysis(prev => prev ? {
                                    ...prev,
                                    matches: prev.matches.filter(m => !(m.point_id === pointId && m.match_type === 'conflict'))
                                  } : null);
                                }
                              } catch {}
                            };
                            return (
                              <div key={i} style={{ fontSize: '12px', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                                <div style={{ color: 'var(--color-text-primary)', cursor: 'pointer', marginBottom: '4px' }}
                                  onClick={() => {
                                    const pt = points.find(p => p.id === c.point_id);
                                    if (pt) { setSelectedPoint(pt); setKaekAnalysis(null); centerOnPoint(pt.longitude, pt.latitude); }
                                  }}>
                                  {c.point_name}
                                </div>
                                <div style={{ paddingLeft: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  <button className="tool-btn" style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid var(--color-warning)', color: 'var(--color-warning)' }}
                                    onClick={() => resolveConflict(c.point_id, c.parcel_id)}
                                    title="Link to the text-matched parcel">
                                    Use text: {c.kaek_code}
                                  </button>
                                  {linked && (
                                    <button className="tool-btn" style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid var(--color-info)', color: 'var(--color-info)' }}
                                      onClick={() => resolveConflict(c.point_id, linked.parcel_id)}
                                      title="Link to the spatially-matched parcel">
                                      Use spatial: {linked.kaek_code}
                                    </button>
                                  )}
                                  <span style={{ fontSize: '13px', cursor: 'pointer' }}
                                    onClick={() => { const p = kaekParcels.find(pp => pp.id === c.parcel_id); if (p) { setSelectedKaekParcel(p); setKaekAnalysis(null); } }}
                                    title="Inspect text-matched parcel"><Icon name="search" /></span>
                                  {linked && (
                                    <span style={{ fontSize: '13px', cursor: 'pointer' }}
                                      onClick={() => { const p = kaekParcels.find(pp => pp.id === linked.parcel_id); if (p) { setSelectedKaekParcel(p); setKaekAnalysis(null); } }}
                                      title="Inspect spatial-matched parcel"><Icon name="search" /></span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {kaekAnalysis.orphan_points.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header" onClick={() => toggleSection('kaek-orphan-pts')}>
                        <span className="section-toggle"><Icon name={collapsedSections.has('kaek-orphan-pts') ? 'chev-right' : 'chev-down'} /></span> Orphan Sites ({kaekAnalysis.orphan_points.length})
                      </div>
                      {!collapsedSections.has('kaek-orphan-pts') && (
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {kaekAnalysis.orphan_points.map(p => (
                            <div key={p.id} style={{ fontSize: '12px', padding: '3px 0', color: 'var(--color-text-secondary)', cursor: 'pointer' }}
                              onClick={() => {
                                const pt = points.find(pp => pp.id === p.id);
                                if (pt) { setSelectedPoint(pt); setKaekAnalysis(null); centerOnPoint(pt.longitude, pt.latitude); }
                              }}>
                              {p.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {kaekAnalysis.orphan_parcels.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header" onClick={() => toggleSection('kaek-orphan-parcels')}>
                        <span className="section-toggle"><Icon name={collapsedSections.has('kaek-orphan-parcels') ? 'chev-right' : 'chev-down'} /></span> Orphan Parcels ({kaekAnalysis.orphan_parcels.length})
                      </div>
                      {!collapsedSections.has('kaek-orphan-parcels') && (
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {kaekAnalysis.orphan_parcels.map(p => (
                            <div key={p.id} style={{ fontSize: '12px', padding: '3px 0', color: 'var(--color-text-secondary)', cursor: 'pointer' }}
                              onClick={() => {
                                const parcel = kaekParcels.find(pp => pp.id === p.id);
                                if (parcel) { setSelectedKaekParcel(parcel); setKaekAnalysis(null); }
                              }}>
                              {p.kaek_code} {p.address && `· ${p.address}`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {(kaekAnalysis.matches.filter(m => m.match_type !== 'conflict').length > 0) && (
                    <button
                      className="tool-btn"
                      style={{ width: '100%', marginTop: '8px', padding: '6px', fontSize: '12px', fontWeight: 600 }}
                      disabled={kaekLinking}
                      onClick={async () => {
                        setKaekLinking(true);
                        try {
                          const tsParam = kaekAnalysis.points_task_set_id ? `&points_task_set=${kaekAnalysis.points_task_set_id}` : '';
                          const resp = await fetch(`${API_URL}/api/kaek/link-sites?scope=${dataScope}${tsParam}`, {
                            method: 'POST', headers: getAuthHeaders()
                          });
                          if (resp.ok) {
                            const result = await resp.json();
                            fetchPoints();
                            fetchKaekParcels();
                            if (result.conflicts?.length > 0) {
                              const resp2 = await fetch(`${API_URL}/api/kaek/analysis?scope=${dataScope}${tsParam}`, { headers: getAuthHeaders() });
                              if (resp2.ok) setKaekAnalysis(await resp2.json());
                              alert(`Linked ${result.linked} sites. ${result.conflicts.length} conflict(s) need manual resolution.`);
                            } else {
                              alert(`Linked ${result.linked} sites to parcels.`);
                              setKaekAnalysis(null);
                            }
                          }
                        } catch {} finally { setKaekLinking(false); }
                      }}
                    >
                      {kaekLinking ? 'Linking...' : `Link ${kaekAnalysis.matches.filter(m => m.match_type !== 'conflict').length} Sites to Parcels`}
                    </button>
                  )}

                  {kaekAnalysis.summary.already_linked > 0 && (
                    <button
                      className="tool-btn"
                      style={{ width: '100%', marginTop: '6px', padding: '6px', fontSize: '12px' }}
                      onClick={async () => {
                        if (!window.confirm(`Snap ${kaekAnalysis.summary.already_linked} linked sites to their parcel centers?`)) return;
                        try {
                          const resp = await fetch(`${API_URL}/api/kaek/snap-all?scope=${dataScope}`, {
                            method: 'POST', headers: getAuthHeaders()
                          });
                          if (resp.ok) {
                            const result = await resp.json();
                            alert(`Snapped ${result.snapped} sites to parcel centers.`);
                            fetchPoints();
                          }
                        } catch {}
                      }}
                    >
                      Snap {kaekAnalysis.summary.already_linked} linked sites to parcel centers
                    </button>
                  )}
                </div>
              </div>
              </div>
            )}

            {selectedKaekParcel && (
              <div className="detail-overlay" onClick={() => setSelectedKaekParcel(null)}>
              <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: selectedKaekParcel.color }}>▣</span>
                    {selectedKaekParcel.kaek_code}
                  </h3>
                  <button className="detail-close-btn" onClick={() => setSelectedKaekParcel(null)} aria-label="Close"><Icon name="clear" /></button>
                </div>
                <div className="detail-content">
                  <div className="detail-section">
                    {selectedKaekParcel.address && (
                      <div className="detail-row">
                        <span className="detail-label">Address</span>
                        <span className="detail-value">{selectedKaekParcel.address}</span>
                      </div>
                    )}
                    {selectedKaekParcel.land_use && (
                      <div className="detail-row">
                        <span className="detail-label">Land Use</span>
                        <span className="detail-value">{selectedKaekParcel.land_use}</span>
                      </div>
                    )}
                    {selectedKaekParcel.area != null && (
                      <div className="detail-row">
                        <span className="detail-label">Area</span>
                        <span className="detail-value">{Math.round(selectedKaekParcel.area).toLocaleString()} m²</span>
                      </div>
                    )}
                    {selectedKaekParcel.perimeter != null && (
                      <div className="detail-row">
                        <span className="detail-label">Perimeter</span>
                        <span className="detail-value">{Math.round(selectedKaekParcel.perimeter).toLocaleString()} m</span>
                      </div>
                    )}
                    {selectedKaekParcel.land_use_code && (
                      <div className="detail-row">
                        <span className="detail-label">Use Code</span>
                        <span className="detail-value">{selectedKaekParcel.land_use_code}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Created</span>
                      <span className="detail-value">{new Date(selectedKaekParcel.created_at).toLocaleString()}</span>
                    </div>
                    <div style={{ margin: '8px 0', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {selectedKaekParcel.registry_link && (
                        <a href={selectedKaekParcel.registry_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--color-info)' }}>
                          Ktimatologio.gr ↗
                        </a>
                      )}
                      {(() => {
                        const coords = selectedKaekParcel.geometry?.coordinates?.[0];
                        if (!coords) return null;
                        const lngs = coords.map((c: number[]) => c[0]);
                        const lats = coords.map((c: number[]) => c[1]);
                        const lat = ((Math.min(...lats) + Math.max(...lats)) / 2).toFixed(6);
                        const lng = ((Math.min(...lngs) + Math.max(...lngs)) / 2).toFixed(6);
                        return (
                          <>
                            <a href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--color-info)' }}>
                              Google Maps ↗
                            </a>
                            <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--color-info)' }}>
                              OSM ↗
                            </a>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header" onClick={() => toggleSection('kaek-color')}>
                      <span className="section-toggle"><Icon name={collapsedSections.has('kaek-color') ? 'chev-right' : 'chev-down'} /></span> Color
                    </div>
                    {!collapsedSections.has('kaek-color') && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                      {['#e67e22', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f1c40f', '#1abc9c', '#e91e63'].map(c => (
                        <div
                          key={c}
                          style={{
                            width: '24px', height: '24px', borderRadius: '4px', backgroundColor: c, cursor: 'pointer',
                            border: selectedKaekParcel.color === c ? '2px solid #fff' : '2px solid transparent'
                          }}
                          onClick={async () => {
                            try {
                              const resp = await fetch(`${API_URL}/api/kaek/parcels/${selectedKaekParcel.id}/color`, {
                                method: 'PUT',
                                headers: getAuthHeaders(true),
                                body: JSON.stringify({ color: c })
                              });
                              if (resp.ok) {
                                const updated = await resp.json();
                                setSelectedKaekParcel(updated);
                                fetchKaekParcels();
                              }
                            } catch {}
                          }}
                        />
                      ))}
                    </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="detail-section-header">Task Set</div>
                    <select
                      value={selectedKaekParcel.task_set_id || ''}
                      onChange={async (e) => {
                        const taskSetId = e.target.value ? parseInt(e.target.value) : null;
                        try {
                          const resp = await fetch(`${API_URL}/api/kaek/parcels/${selectedKaekParcel.id}/task-set`, {
                            method: 'PUT',
                            headers: getAuthHeaders(true),
                            body: JSON.stringify({ task_set_id: taskSetId })
                          });
                          if (resp.ok) {
                            const updated = await resp.json();
                            setSelectedKaekParcel(updated);
                            fetchKaekParcels();
                            fetchTaskSets();
                          }
                        } catch {}
                      }}
                      style={{ width: '100%', marginTop: '4px', fontSize: '11px', padding: '4px', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: '#fff', borderRadius: '3px' }}
                    >
                      <option value="">None</option>
                      {taskSets.map(ts => (
                        <option key={ts.id} value={ts.id}>{ts.name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedKaekParcel.linked_sites && selectedKaekParcel.linked_sites.length > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-header">Linked Sites ({selectedKaekParcel.linked_sites.length})</div>
                      {selectedKaekParcel.linked_sites.map(site => (
                        <div key={site.id} style={{ fontSize: '11px', padding: '3px 0', color: 'var(--color-info)', cursor: 'pointer' }}
                          onClick={() => {
                            const pt = points.find(p => p.id === site.id);
                            if (pt) { setSelectedPoint(pt); setSelectedKaekParcel(null); centerOnPoint(pt.longitude, pt.latitude); }
                          }}>
                          <Icon name="site" /> {site.name}
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className="delete-btn"
                    style={{ marginTop: '8px' }}
                    onClick={() => deleteKaekParcel(selectedKaekParcel.id)}
                  >
                    Delete Parcel
                  </button>
                </div>
              </div>
              </div>
            )}

            <div className="stream-sources-panel">
              <div
                className="panel-header stream-panel-header"
                onClick={() => setShowStreamPanel(!showStreamPanel)}
              >
                <h3><Icon name="streams" /> Stream Sources</h3>
                <span className="toggle-icon"><Icon name={showStreamPanel ? 'chev-down' : 'chev-right'} /></span>
              </div>
              
              {showStreamPanel && (
                <div className="stream-panel-content">
                  <div className="stream-list">
                    {streamSources.filter(s => allowedFeedTypes.has(feedAccessType(s.type))).map((source) => (
                      <div key={source.id}>
                        {editingSourceId === source.id ? (
                          <div className="stream-item editing">
                            <div className="edit-source-form">
                              <input
                                type="text"
                                placeholder="Source name"
                                value={editSourceName}
                                onChange={(e) => setEditSourceName(e.target.value)}
                                disabled={isSwitching}
                              />
                              <input
                                type="text"
                                placeholder="Stream URL (rtmp:// or rtsp://)"
                                value={editSourceUrl}
                                onChange={(e) => setEditSourceUrl(e.target.value)}
                                disabled={isSwitching}
                              />
                              <div className="edit-source-actions">
                                <button
                                  className="cancel-btn"
                                  onClick={cancelEditingSource}
                                  disabled={isSwitching}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="save-btn"
                                  onClick={saveEditedSource}
                                  disabled={isSwitching || !editSourceName.trim()}
                                >
                                  Save & Switch
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className={`stream-item ${activeSourceId === source.id ? 'active' : ''}`}
                          >
                            <div className="stream-info">
                              <span className="stream-name">{source.name}</span>
                              <span className="stream-url">{source.type === 'picam' ? 'Pi guidance — draw a box to approach' : (source.url || 'Simulated')}</span>
                            </div>
                            <div className="stream-actions">
                              <button
                                className="stream-switch-btn"
                                onClick={() => activateSource(source)}
                                disabled={isSwitching || activeSourceId === source.id}
                              >
                                {activeSourceId === source.id ? 'Active' : 'Switch'}
                              </button>
                              {!source.id.startsWith('cam-rtmp-') && !source.id.startsWith('picam-') && (
                                <button
                                  className="stream-edit-btn"
                                  onClick={() => startEditingSource(source)}
                                  disabled={isSwitching}
                                  title="Edit source"
                                >
                                  <Icon name="edit" />
                                </button>
                              )}
                              {!source.id.startsWith('cam-rtmp-') && !source.id.startsWith('picam-') && streamSources.length > 1 && (
                                <button
                                  className="stream-delete-btn"
                                  onClick={() => deleteStreamSource(source.id)}
                                  disabled={isSwitching}
                                  title="Delete source"
                                  aria-label="Remove"
                                >
                                  <Icon name="clear" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {!isAddingSource ? (
                    <button
                      className="add-source-btn"
                      onClick={() => setIsAddingSource(true)}
                      disabled={isSwitching}
                    >
                      + Add New Source
                    </button>
                  ) : (
                    <div className="add-source-form">
                      <input
                        type="text"
                        placeholder="Source name (e.g., Streamlabs Phone)"
                        value={newSourceName}
                        onChange={(e) => setNewSourceName(e.target.value)}
                        disabled={isSwitching}
                      />
                      <input
                        type="text"
                        placeholder="Stream URL (rtmp://host:1935/live/key or rtsp://camera/stream1)"
                        value={newSourceUrl}
                        onChange={(e) => setNewSourceUrl(e.target.value)}
                        disabled={isSwitching}
                      />
                      <div className="add-source-actions">
                        <button
                          className="cancel-btn"
                          onClick={() => {
                            setIsAddingSource(false);
                            setNewSourceName('');
                            setNewSourceUrl('');
                          }}
                          disabled={isSwitching}
                        >
                          Cancel
                        </button>
                        <button
                          className="save-btn"
                          onClick={addStreamSource}
                          disabled={isSwitching || !newSourceName.trim() || !newSourceUrl.trim()}
                        >
                          Save & Switch
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mission-panel">
              <div
                className="panel-header stream-panel-header"
                onClick={() => setShowMissionPanel(!showMissionPanel)}
              >
                <h3>Mission Control</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {activeMission && (activeMission.status === 'executing' || activeMission.status === 'paused') && (
                    <span className={`status-badge ${activeMission.status === 'executing' ? 'active' : 'warning'}`}>
                      {activeMission.status === 'executing' ? 'FLYING' : 'PAUSED'}
                    </span>
                  )}
                  <span className="toggle-icon"><Icon name={showMissionPanel ? 'chev-down' : 'chev-right'} /></span>
                </div>
              </div>
              {showMissionPanel && (
                <div className="mission-control-content">
                  {activeMission ? (
                    <div className="active-mission">
                      <div className="mission-info">
                        <div className="mission-status-header">
                          <span className="mission-name-label">{activeMission.name}</span>
                          <span className={`mission-status-badge status-${activeMission.status}`}>{activeMission.status.toUpperCase()}</span>
                          {(activeMission.status === 'completed' || activeMission.status === 'cancelled' || activeMission.status === 'failed') && (
                            <button className="mission-dismiss-btn" onClick={() => setActiveMission(null)} title="Dismiss" aria-label="Close"><Icon name="clear" /></button>
                          )}
                        </div>
                        <div className="mission-progress-bar">
                          <div className="mission-progress-fill" style={{ width: `${activeMission.progress_percent}%` }}></div>
                        </div>
                        <div className="mission-stats">
                          <span>Progress: {activeMission.progress_percent.toFixed(0)}%</span>
                          <span>WP: {activeMission.current_waypoint_index} / {activeMission.waypoints.length}</span>
                        </div>
                        <div className="mission-stats">
                          <span>Alt: {activeMission.altitude}m</span>
                          <span>Speed: {activeMission.speed} m/s</span>
                        </div>
                      </div>
                      <div className="mission-controls">
                        {activeMission.status === 'created' && (
                          <button className="mission-btn mission-btn-execute" onClick={() => sendMissionCommand(activeMission.id, 'execute')}>Simulate</button>
                        )}
                        {activeMission.status === 'created' && activeMission.flight_id && (
                          <button className="mission-btn mission-btn-execute" style={{ background: 'var(--color-info)' }} onClick={() => openSendToDrone(activeMission)}>Send to Drone</button>
                        )}
                        {activeMission.status === 'executing' && (
                          <button className="mission-btn mission-btn-pause" onClick={() => sendMissionCommand(activeMission.id, 'pause')}>Pause</button>
                        )}
                        {activeMission.status === 'paused' && (
                          <button className="mission-btn mission-btn-execute" onClick={() => sendMissionCommand(activeMission.id, 'resume')}>Resume</button>
                        )}
                        {(activeMission.status === 'executing' || activeMission.status === 'paused' || activeMission.status === 'created') && (
                          <button className="mission-btn mission-btn-cancel" onClick={() => sendMissionCommand(activeMission.id, 'cancel')}>Cancel</button>
                        )}
                        <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'kmz'} onClick={() => downloadMissionKmz(activeMission.id, activeMission.name)}>{downloadingMission === 'kmz' ? '...' : 'KMZ'}</button>
                        <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'report'} onClick={() => downloadMissionReport(activeMission.id, activeMission.name)}>{downloadingMission === 'report' ? '...' : 'Report'}</button>
                        <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'pdf'} onClick={() => downloadMissionReportPdf(activeMission.id, activeMission.name)}>{downloadingMission === 'pdf' ? '...' : 'PDF'}</button>
                        <button className="mission-btn mission-btn-download" disabled={downloadingMission === 'apm'} onClick={() => downloadMissionArdupilot(activeMission.id, activeMission.name)}>{downloadingMission === 'apm' ? '...' : 'APM'}</button>
                        <button className="mission-btn mission-btn-cancel" onClick={() => deleteMission(activeMission.id)}>Delete</button>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                        <span>Waypoints: {activeMission.waypoints.length}</span>
                        {activeMission.waypoints.some(wp => wp.site_names?.length || wp.site_name) && (
                          <span style={{ color: 'var(--color-accent)', marginLeft: '6px' }}>
                            {activeMission.waypoints.filter(wp => wp.site_names?.length || wp.site_name).length} matched
                          </span>
                        )}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                          <select
                            style={{ flex: 1, fontSize: '10px', padding: '2px 4px', background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: '3px' }}
                            value={matchSitesTaskSetId ?? ''}
                            onChange={e => setMatchSitesTaskSetId(e.target.value ? Number(e.target.value) : undefined)}
                          >
                            <option value="">Select task set...</option>
                            {taskSets.map(ts => (
                              <option key={ts.id} value={ts.id}>{ts.name}</option>
                            ))}
                          </select>
                          <button
                            className="mission-btn mission-btn-execute"
                            style={{ fontSize: '10px', padding: '2px 8px' }}
                            disabled={!matchSitesTaskSetId}
                            onClick={() => matchSitesTaskSetId && matchMissionSites(activeMission.id, matchSitesTaskSetId)}
                          >Match</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-mission">
                      <span className="no-comments">No active mission</span>
                    </div>
                  )}
                  {missions.length > 0 && (
                    <div className="mission-history">
                      <label>History ({missions.length})</label>
                      {missions.map(m => (
                        <div key={m.id} className="mission-history-item" onClick={() => setActiveMission(m)}>
                          <span className="mission-history-name">{m.name}</span>
                          <span className={`mission-status-badge status-${m.status}`}>{m.status}</span>
                          {(() => {
                            const auth = authorisations.find(a => a.mission_id === m.id);
                            if (auth) {
                              const bg = auth.status === 'APPROVED' ? '#1e7e34' : auth.status === 'ACTIVE' ? '#0d7a8c'
                                : auth.status === 'REJECTED' ? '#a12622' : auth.status === 'REQUESTED' ? '#8a6d00' : '#555';
                              return (
                                <span
                                  title={auth.conflicts?.length ? auth.conflicts.map(c => c.detail).join('\n') : 'No conflicts'}
                                  style={{ fontSize: 10, fontWeight: 600, color: '#fff', background: bg, borderRadius: 3, padding: '1px 5px', whiteSpace: 'nowrap' }}
                                >
                                  <Icon name="clearance" /> {auth.status}
                                </span>
                              );
                            }
                            return (
                              <button
                                title="Request U-space clearance (strategic deconfliction against geo-zones + active flights)"
                                disabled={requestingAuthMissionId === m.id}
                                onClick={(e) => { e.stopPropagation(); requestAuthorisation(m.id); }}
                                style={{ fontSize: 10, background: '#2a3f5f', color: 'var(--color-text-primary)', border: '1px solid #3a5f8f', borderRadius: 3, padding: '1px 6px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                {requestingAuthMissionId === m.id ? '…' : <><Icon name="clearance" /> Clearance</>}
                              </button>
                            );
                          })()}
                          {(m.status === 'completed' || m.status === 'cancelled' || m.status === 'failed') && (
                            <button className="attachment-delete-btn" onClick={(e) => { e.stopPropagation(); deleteMission(m.id); }} aria-label="Remove"><Icon name="clear" /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="telemetry-panel">
              <div
                className="panel-header stream-panel-header"
                onClick={() => { const v = !showTelemetry; setShowTelemetry(v); localStorage.setItem('toc_showTelemetry', String(v)); }}
              >
                <h3>Telemetry{connectedDroneCount > 0 ? ` (${connectedDroneCount})` : ''}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className={`status-badge ${connectedDroneCount > 0 ? 'active' : 'error'}`}>
                    {connectedDroneCount > 0 ? `${connectedDroneCount} LIVE` : 'OFFLINE'}
                  </span>
                  <span className="toggle-icon"><Icon name={showTelemetry ? 'chev-down' : 'chev-right'} /></span>
                </div>
              </div>
              {showTelemetry && (
                <>
                  {Object.keys(dronePositions).length > 1 && (
                    <div className="drone-selector">
                      {Object.entries(dronePositions).filter(([, d]) => d.connected).map(([id, d]) => (
                        <button
                          key={id}
                          className={`drone-select-btn ${selectedDroneId === id ? 'active' : ''}`}
                          onClick={() => setSelectedDroneId(id)}
                        >
                          {id.slice(-4)} {d.battery.toFixed(0)}%
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedDrone ? (
                    <>
                    {!selectedDrone.connected && (
                      <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px 0', color: 'var(--color-warning)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                        <Icon name="warning" /> Link lost — showing last known data
                      </div>
                    )}
                    <div className={`telemetry-grid${selectedDrone.connected ? '' : ' is-stale'}`}>
                      <div className="telemetry-item">
                        <label>Battery</label>
                        <span className={`value ${selectedDrone.battery < 20 ? 'warning' : ''}`}>
                          {selectedDrone.battery}%
                        </span>
                      </div>
                      <div className="telemetry-item">
                        <label>Latitude</label>
                        <span className="value">{selectedDrone.latitude.toFixed(6)}</span>
                      </div>
                      <div className="telemetry-item">
                        <label>Longitude</label>
                        <span className="value">{selectedDrone.longitude.toFixed(6)}</span>
                      </div>
                      <div className="telemetry-item">
                        <label>MGRS</label>
                        <span className="value mgrs-value" title={toMGRS(selectedDrone.latitude, selectedDrone.longitude)}>{toMGRS(selectedDrone.latitude, selectedDrone.longitude)}</span>
                      </div>
                      <div className="telemetry-item">
                        <label>Altitude</label>
                        <span className="value">{selectedDrone.altitude.toFixed(1)} m</span>
                      </div>
                      <div className="telemetry-item">
                        <label>Speed</label>
                        <span className="value">{selectedDrone.speed} m/s</span>
                      </div>
                      <div className="telemetry-item">
                        <label>Heading</label>
                        <span className="value">{selectedDrone.heading.toFixed(1)}°</span>
                      </div>
                      {selectedDrone.mode && (
                        <div className="telemetry-item">
                          <label>Mode</label>
                          <span className="value">
                            {selectedDrone.mode}{selectedDrone.armed === true ? ' · ARMED' : selectedDrone.armed === false ? ' · disarmed' : ''}
                          </span>
                        </div>
                      )}
                      {selectedDrone.gimbal && (
                        <>
                          <div className="telemetry-item">
                            <label>Gimbal Pitch</label>
                            <span className="value">{selectedDrone.gimbal.pitch.toFixed(1)}°</span>
                          </div>
                          <div className="telemetry-item">
                            <label>Gimbal Yaw</label>
                            <span className="value">{selectedDrone.gimbal.yaw.toFixed(1)}°</span>
                          </div>
                        </>
                      )}
                      <div className="telemetry-item">
                        <label>Connection</label>
                        <span className={`value ${selectedDrone.connected ? '' : 'warning'}`}>
                          {selectedDrone.connected ? 'WebSocket' : 'Disconnected'}
                        </span>
                      </div>
                    </div>
                    </>
                  ) : (
                    <div style={{ padding: '12px', color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                      No drone connected. Connect via DJI Pilot 2.
                    </div>
                  )}
                </>
              )}
            </div>
      </div>

      {viewingAttachment && (
        <div className="attachment-lightbox" onClick={() => setViewingAttachment(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setViewingAttachment(null)} aria-label="Close"><Icon name="clear" /></button>
            {viewingAttachment.type === 'image' ? (
              <img src={`${API_URL}${viewingAttachment.url}`} alt={viewingAttachment.filename} />
            ) : viewingAttachment.type === 'video' ? (
              <video src={`${API_URL}${viewingAttachment.url}`} controls autoPlay />
            ) : null}
            <div className="lightbox-filename">{viewingAttachment.filename}</div>
          </div>
        </div>
      )}

      {showVideo && videoFloating && (
        <FloatingPanel
          initialPosition={{ x: floatingVideoPos.x, y: floatingVideoPos.y }}
          initialSize={{ width: floatingVideoPos.w, height: floatingVideoPos.h }}
          title="VIDEO"
          onClose={() => setVideoFloating(false)}
          onPositionChange={(p) => {
            const next = { ...floatingVideoPos, x: p.x, y: p.y };
            setFloatingVideoPos(next);
            localStorage.setItem('toc_floatingVideoPos', JSON.stringify(next));
          }}
          onSizeChange={(s) => {
            const next = { ...floatingVideoPos, w: s.width, h: s.height };
            setFloatingVideoPos(next);
            localStorage.setItem('toc_floatingVideoPos', JSON.stringify(next));
          }}
          headerExtra={
            <>
              <span className="live-indicator" style={{ fontSize: 10 }}>● LIVE</span>
              <AiLayerMenu
                compact
                buttonClass="floating-panel-btn"
                rows={aiLayers.rows}
                active={aiLayerActive}
                busy={aiLayerBusy}
                error={aiLayerError}
                onSelect={selectAiLayer}
                roadSegMode={roadSegMode}
                onSwitchRoadSegMode={switchRoadSegMode}
                onOpenRoadSegSettings={() => setRoadSegSettingsOpen(v => !v)}
                geoMaskEnabled={geoMaskEnabled}
                geoMaskMode={geoMaskMode}
                geoMaskAltSource={geoMaskAltSource}
                geoMaskYawOffset={geoMaskYawOffset}
                geoMaskCameraLabel={geoMaskCameraLabel}
                onToggleGeoMask={toggleGeoMask}
                onSwitchGeoMaskMode={switchGeoMaskMode}
                onCycleGeoMaskAlt={cycleGeoMaskAlt}
                onAdjustYawOffset={adjustYawOffset}
                geoMaskPitchOffset={geoMaskPitchOffset}
                geoMaskHeightM={geoMaskHeightM}
                geoMaskRefused={geoMaskRefused}
                onAdjustPitchOffset={adjustPitchOffset}
                onSetGeoMaskHeight={setGeoMaskHeight}
                privacyMode={privacyMode}
                onSelectPrivacy={selectPrivacyMode}
                privacyBusy={privacyBusy}
                privacyError={privacyError}
                privacyUnavailable={privacyUnavailable}
              />
              {activeSourceId.startsWith('phone-') && (
                <button
                  className="floating-panel-btn"
                  onClick={() => {
                    const phoneId = activeSourceId;
                    setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[phoneId]; return next; });
                    setStreamSources(prev => prev.filter(s => s.id !== phoneId));
                    setActiveSourceId(NO_SOURCE_ID);
                  }}
                  title="Stop phone stream"
                  aria-label="Stop phone stream"
                  style={{ color: 'var(--color-danger)' }}
                >
                  <Icon name="stop" />
                </button>
              )}
              {(isPullSource(activeStreamSource?.type || '') || activeStreamSource?.type === 'demo') && (
                <button
                  className="floating-panel-btn"
                  onClick={terminateStream}
                  title="Terminate stream (stops the server-side reader)"
                  aria-label="End stream"
                  style={{ color: 'var(--color-danger)' }}
                >
                  <Icon name="power" />
                </button>
              )}
            </>
          }
        >
          <VideoPanel
            isFloating={true}
            /* Same rule as the docked panel above — see the note there. */
            preferWebrtc
            onToggleFloat={() => setVideoFloating(false)}
            onStopPhoneStream={() => {
              const phoneId = activeSourceId;
              setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[phoneId]; return next; });
              setStreamSources(prev => prev.filter(s => s.id !== phoneId));
              setActiveSourceId(NO_SOURCE_ID);
            }}
            onTerminateStream={terminateStream}
                onRequestGroundPick={handleRequestGroundPick}
                onGeoFix={handleGeoFix}
            aiLayerRows={aiLayers.rows}
            aiLayerActive={aiLayerActive}
            aiLayerBusy={aiLayerBusy}
            aiLayerError={aiLayerError}
            onSelectAiLayer={selectAiLayer}
            privacyMode={privacyMode}
            onSelectPrivacy={selectPrivacyMode}
            privacyBusy={privacyBusy}
            privacyError={privacyError}
            privacyUnavailable={privacyUnavailable}
            roadSegSettingsOpen={roadSegSettingsOpen}
            onToggleRoadSegSettings={() => setRoadSegSettingsOpen(v => !v)}
            onSwitchRoadSegMode={switchRoadSegMode}
            roadSegEnabled={roadSegEnabled}
            roadSegMode={roadSegMode}
            geoMaskEnabled={geoMaskEnabled}
            geoMaskMode={geoMaskMode}
            geoMaskAltSource={geoMaskAltSource}
            geoMaskYawOffset={geoMaskYawOffset}
            geoMaskCameraLabel={geoMaskCameraLabel}
            onToggleGeoMask={toggleGeoMask}
            onSwitchGeoMaskMode={switchGeoMaskMode}
            onCycleGeoMaskAlt={cycleGeoMaskAlt}
            onAdjustYawOffset={adjustYawOffset}
            geoMaskPitchOffset={geoMaskPitchOffset}
            geoMaskHeightM={geoMaskHeightM}
            geoMaskRefused={geoMaskRefused}
            onAdjustPitchOffset={adjustPitchOffset}
            onSetGeoMaskHeight={setGeoMaskHeight}
            activeSourceId={activeSourceId}
            activeStreamSource={activeStreamSource}
            phoneSensorCredentialsMap={phoneSensorCredentialsMap}
            hlsCredentialsMap={hlsCredentialsMap}
            djiStreamCredentials={djiStreamCredentials}
            djiLowLatency={djiLowLatency}
            processedStreamCreds={processedStreamCreds}
            feedToken={feedToken}
            videoKey={videoKey}
            detectionEnabled={detectionEnabled}
            apiUrl={API_URL}
            authHeaders={getAuthHeaders}
            djiStreamError={djiStreamError}
            onDjiError={(msg) => setDjiStreamError(msg)}
            onDjiDisconnected={() => setDjiStreamCredentials(null)}
            onPhoneDisconnected={(sourceId) => setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[sourceId]; return next; })}
            onRecordingSaved={() => toast('Recording saved', 'success')}
            onProcessedStreamError={() => { setProcessedStreamCreds(null); setTimeout(() => setProcessedStreamRetry(n => n + 1), 3000); }}
            onFeedError={handleFeedError}
            onCaptureEvent={(msg, type) => toast(msg, type)}
          />
        </FloatingPanel>
      )}

      {dcimViewingPhoto && (() => {
        const pointMission = selectedPoint?.line_id != null
          ? missions.find(m => m.line_id === selectedPoint.line_id)
          : null;
        const resolvedWaypoints = activeMission?.waypoints || pointMission?.waypoints;
        return (
          <DcimLightbox
            photo={dcimViewingPhoto}
            photos={dcimPhotosForPoint.filter(p => p.uploaded).sort((a, b) => a.filename.localeCompare(b.filename))}
            apiUrl={API_URL}
            onClose={() => setDcimViewingPhoto(null)}
            onChange={setDcimViewingPhoto}
            waypoints={resolvedWaypoints}
            dataScope={dataScope}
            onPhotoUpdated={(updated) => {
              setDcimPhotosForPoint(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
              setDcimViewingPhoto(updated);
            }}
            matchedSiteNames={selectedPoint?.metadata?.matched_site_names as string[] | undefined}
          />
        );
      })()}

      {showIncidentModal && pendingIncidentCoords && (
        <IncidentCreateModal
          lat={pendingIncidentCoords.lat}
          lng={pendingIncidentCoords.lng}
          draft={incidentDraft}
          setDraft={setIncidentDraft}
          onSubmit={submitIncident}
          onClose={() => {
            setShowIncidentModal(false);
            setPendingIncidentCoords(null);
          }}
          submitting={submittingIncident}
        />
      )}

      {showMatrixView && (
        <MatrixView
          streamSources={streamSources.filter(s => allowedFeedTypes.has(feedAccessType(s.type)))}
          liveCameras={liveCameras}
          detectionEnabled={detectionEnabled}
          onClose={() => setShowMatrixView(false)}
          mainPanelSourceId={activeSourceId}
          onSetDetection={syncDetectionState}
          overlayState={aiLayers.state}
          phoneSensorCredentialsMap={phoneSensorCredentialsMap}
          hlsCredentialsMap={hlsCredentialsMap}
          djiStreamCredentials={djiStreamCredentials}
          djiLowLatency={djiLowLatency}
          feedToken={feedToken}
          videoKey={videoKey}
          apiUrl={API_URL}
          authHeaders={getAuthHeaders}
          djiStreamError={djiStreamError}
          onDjiError={(msg) => setDjiStreamError(msg)}
          onDjiDisconnected={() => setDjiStreamCredentials(null)}
          onPhoneDisconnected={(sourceId) => setPhoneSensorCredentialsMap(prev => { const next = { ...prev }; delete next[sourceId]; return next; })}
          onRecordingSaved={() => toast('Recording saved', 'success')}
          onFeedError={handleFeedError}
        />
      )}

      {showAdminPanel && (
        <Suspense fallback={null}>
          <AdminPanel
            onClose={() => setShowAdminPanel(false)}
            getAuthHeaders={getAuthHeaders}
          />
        </Suspense>
      )}

      {showFleetPanel && (
        <Suspense fallback={null}>
          <FleetPanel
            onClose={() => setShowFleetPanel(false)}
            getAuthHeaders={getAuthHeaders}
          />
        </Suspense>
      )}

      {showEdgePanel && (
        <FloatingPanel
          initialPosition={{ x: Math.max(16, window.innerWidth - Math.min(460, window.innerWidth - 32) - 40), y: 80 }}
          initialSize={{ width: Math.min(460, window.innerWidth - 32), height: 560 }}
          title="Edge nodes"
          zIndex={9000}
          onClose={() => setShowEdgePanel(false)}
          closeIcon="clear"
          closeTitle="Close"
        >
          <EdgeNodesPanel state={edgeNodes} getAuthHeaders={getAuthHeaders} focusNodeId={edgeFocusId} />
        </FloatingPanel>
      )}

      {showOpLogPanel && (
        <Suspense fallback={null}>
          <OpLogPanel
            onClose={() => setShowOpLogPanel(false)}
            getAuthHeaders={getAuthHeaders}
          />
        </Suspense>
      )}

      {showCapturesPanel && (
        <Suspense fallback={null}>
          <CapturesPanel
            apiUrl={API_URL}
            authHeaders={getAuthHeaders}
            onClose={() => setShowCapturesPanel(false)}
            onToast={(msg, type) => toast(msg, type)}
          />
        </Suspense>
      )}

      {showMqttPanel && (
        <Suspense fallback={null}>
          <MqttPanel
            onClose={() => setShowMqttPanel(false)}
            getAuthHeaders={getAuthHeaders}
            onHlsStreamStart={(deviceSn, hlsUrl, displayName) => {
              const sourceId = `hls-${deviceSn}`;
              const label = displayName ? `${displayName} Live` : `${deviceSn.slice(-4)} Live`;
              setStreamSources(prev => {
                const existing = prev.find(s => s.id === sourceId);
                if (existing) {
                  if (displayName && existing.name !== label) {
                    return prev.map(s => s.id === sourceId ? { ...s, name: label } : s);
                  }
                  return prev;
                }
                return [...prev, { id: sourceId, name: label, url: hlsUrl, type: 'hls' as const }];
              });
              setHlsCredentialsMap(prev => ({ ...prev, [sourceId]: { hlsUrl, deviceSn, displayName: displayName || prev[sourceId]?.displayName } }));
              setActiveSourceId(sourceId);
            }}
            onHlsStreamStop={(deviceSn) => {
              const sourceId = `hls-${deviceSn}`;
              setStreamSources(prev => prev.filter(s => s.id !== sourceId));
              setHlsCredentialsMap(prev => { const next = { ...prev }; delete next[sourceId]; return next; });
              if (activeSourceId === sourceId) setActiveSourceId(NO_SOURCE_ID);
            }}
            isAdmin={isAdmin}
            isViewer={user?.group_role === 'viewer'}
          />
        </Suspense>
      )}

      {sendToDrone && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSendToDrone(null)}>
          <div style={{ backgroundColor: '#1a2332', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '24px', width: '420px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 4px', color: 'var(--color-text-primary)', fontSize: '15px' }}>Send Mission to Drone</h4>
            <p style={{ margin: '0 0 16px', color: 'var(--color-text-secondary)', fontSize: '12px' }}>
              "{sendToDrone.missionName}" — The drone will fly this route autonomously.
            </p>
            {sendToDrone.loading && <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>Loading devices...</p>}
            {sendToDrone.error && <p style={{ color: 'var(--color-danger)', fontSize: '13px', margin: '0 0 12px' }}>{sendToDrone.error}</p>}
            {!sendToDrone.loading && !sendToDrone.error && sendToDrone.devices.length === 0 && (
              <p style={{ color: 'var(--color-warning)', fontSize: '13px' }}>No online MQTT devices found. Connect a drone via DJI Pilot 2 first.</p>
            )}
            {!sendToDrone.loading && sendToDrone.devices.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '11px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Select Device</label>
                {sendToDrone.devices.map(d => (
                  <label key={d.sn} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', marginBottom: '4px', borderRadius: '6px', cursor: 'pointer', backgroundColor: sendToDrone.selectedSn === d.sn ? '#0088cc22' : '#0d1520', border: `1px solid ${sendToDrone.selectedSn === d.sn ? '#0088cc' : '#2a3a4a'}` }}>
                    <input type="radio" name="drone-sn" checked={sendToDrone.selectedSn === d.sn} onChange={() => setSendToDrone(prev => prev ? { ...prev, selectedSn: d.sn } : null)} style={{ accentColor: 'var(--color-info)' }} />
                    <div>
                      <div style={{ color: 'var(--color-text-primary)', fontSize: '13px' }}>{d.model_name || d.callsign || `Device ${d.device_type}`}</div>
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}>{d.sn}{d.gateway_sn ? ` (via ${d.gateway_sn})` : ''}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={{ background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }} onClick={() => setSendToDrone(null)}>Cancel</button>
              <button
                style={{ background: sendToDrone.selectedSn && !sendToDrone.sending ? 'var(--color-info)' : '#334455', color: '#fff', border: 'none', padding: '6px 20px', borderRadius: '6px', cursor: sendToDrone.selectedSn && !sendToDrone.sending ? 'pointer' : 'not-allowed', fontSize: '13px', opacity: sendToDrone.selectedSn && !sendToDrone.sending ? 1 : 0.5 }}
                disabled={!sendToDrone.selectedSn || sendToDrone.sending}
                onClick={executeSendToDrone}
              >
                {sendToDrone.sending ? 'Sending...' : 'Send Flight Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {dcimTaskSetPicker && (
        <div className="confirm-overlay" onClick={() => { dcimTaskSetResolve.current?.(null); }}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()} style={{ minWidth: '320px' }}>
            <p className="confirm-message">Match waypoints with sites?</p>
            <p style={{ fontSize: '11px', color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
              Each waypoint matches its closest site, plus any others within 20m.
            </p>
            <select
              id="dcim-ts-picker"
              style={{ width: '100%', padding: '8px', background: 'var(--color-bg-surface)', color: '#fff', border: '1px solid var(--color-border-light)', borderRadius: '4px', fontSize: '12px', marginBottom: '12px' }}
              defaultValue=""
            >
              <option value="">-- No matching --</option>
              {taskSets.map(ts => (
                <option key={ts.id} value={ts.id}>{ts.name} ({ts.item_count} items)</option>
              ))}
            </select>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => { dcimTaskSetResolve.current?.(null); }}>Skip</button>
              <button
                className="confirm-ok"
                autoFocus
                onClick={() => {
                  const sel = (document.getElementById('dcim-ts-picker') as HTMLSelectElement)?.value;
                  dcimTaskSetResolve.current?.(sel ? Number(sel) : null);
                }}
              >Continue</button>
            </div>
          </div>
        </div>
      )}

      {showPhoneSensorModal && (
        <Suspense fallback={null}>
          <PhoneSensorSetup
            authHeaders={getAuthHeaders}
            dataScope={dataScope}
            latitude={phoneSensorLocation?.lat}
            longitude={phoneSensorLocation?.lng}
            existingSensorId={reconnectSensorId}
            sensorName={reconnectSensorId ? undefined : newSensorName.trim() || undefined}
            onCreated={() => { fetchSensors(); setNewSensorName(''); }}
            onClose={() => { setShowPhoneSensorModal(false); setPhoneSensorLocation(null); setReconnectSensorId(undefined); }}
          />
        </Suspense>
      )}

      {playingRecording && (
        <div className="modal-overlay" onClick={() => setPlayingRecording(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-primary)' }}>
                Recording — {new Date(playingRecording.created_at).toLocaleString()}
              </h3>
              <button className="close-btn" onClick={() => setPlayingRecording(null)} style={{
                background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 20, cursor: 'pointer',
              }} aria-label="Close"><Icon name="clear" /></button>
            </div>
            <video
              src={getRecordingStreamUrl(playingRecording.id)}
              controls
              autoPlay
              style={{ width: '100%', borderRadius: 8, background: '#000' }}
            />
          </div>
        </div>
      )}

      {showMarketplacePanel && (
        <Suspense fallback={null}>
          <MarketplacePanel
            onClose={() => setShowMarketplacePanel(false)}
            getAuthHeaders={getAuthHeaders}
            isAdmin={isAdmin}
          />
        </Suspense>
      )}

      {showSurveillancePanel && (
        <Suspense fallback={null}>
          <SurveillancePanel
            onClose={() => setShowSurveillancePanel(false)}
            getAuthHeaders={getAuthHeaders}
            isAdmin={isAdmin}
            onFlyTo={(lng, lat) => {
              const m = (mapRef.current as any)?.getMap?.();
              if (m) { m.flyTo({ center: [lng, lat], zoom: 16, duration: 1500, essential: true }); }
              setShowSurveillancePanel(false);
            }}
          />
        </Suspense>
      )}
      {showAnalyticsPanel && (
        <Suspense fallback={null}>
          <AnalyticsPanel
            onClose={() => setShowAnalyticsPanel(false)}
            getAuthHeaders={getAuthHeaders}
            isAdmin={isAdmin}
          />
        </Suspense>
      )}
      {showUspacePanel && (
        <Suspense fallback={null}>
          <UspacePanel
            apiUrl={API_URL}
            getAuthHeaders={getAuthHeaders}
            toast={toast}
            onClose={() => setShowUspacePanel(false)}
            onChanged={() => { fetchAuthorisations(); fetchGeozones(); fetchOperationalIntents(); }}
          />
        </Suspense>
      )}

      {showAssistant && assistantBridge.enabled && (
        <Suspense fallback={null}>
          <AssistantPanel
            onClose={() => { setShowAssistant(false); setAssistantMinimized(false); }}
            minimized={assistantMinimized}
            onMinimize={() => setAssistantMinimized(true)}
            onRestore={() => setAssistantMinimized(false)}
            getAuthHeaders={getAuthHeaders}
            bridge={assistantBridge}
          />
        </Suspense>
      )}
    </div>
  );
}

export default App;
